// ════════════════════════════════════════════════════════════════════════════
// GAME STATE
// Single source of truth for everything the UI reads from.
// render() rebuilds the DOM from this object on every change.
// ════════════════════════════════════════════════════════════════════════════
const state = {
  turn: 1,
  phase: 'Main Phase',
  phases: ['Start Phase', 'Draw Phase', 'Resource Phase', 'Main Phase', 'End Phase'],
  phaseIndex: 3,         // index into phases[] — cycled by NEXT PHASE button
  playerShield: 6,
  oppShield: 6,
  playerLevel: 0,
  oppLevel: 4,
  deckCount: 50,
  oppDeckCount: 50,
  trashCount: 0,
  oppTrashCount: 0,
  oppRes: 0,
  oppExRes: 0,
  oppExResRested: 0,
  oppResBase: 0,
  oppHandCount: 0,   // opponent's hand size — updated via COUNTER_SYNC/PHASE_SYNC
  playerTrash: [],   // card objects sent to player trash
  oppTrash: [],      // card objects sent to opponent trash
  playerExile: [],   // card objects removed from the game (e.g. GD04-130's cost)
  oppExile: [],      // mirrored exile list for the opponent's client
  res: 0,      // current spendable RES — goes up each resource phase, down when cards are played
  resBase: 0,  // accumulated RES — only ever increases; permanent LV floor
  exRes: 0,    // temporary EX RES — affects LV while present, lost when spent
  exResRested: 0, // rested EX RES — affects LV but cannot be spent to pay costs
  hand: [],              // array of card objects currently in the player's hand
  playerField: [null, null, null, null, null, null],  // 6 field slots; null = empty
  oppField: [null, null, null, null, null, null],
  playerBase: null,  // card placed in the player's base zone
  oppBase: null,     // card placed in the opponent's base zone
  log: [],               // event log entries; newest first — capped to 50, see addLog()
  dragCard: null,        // the card object currently being dragged
  dragSource: null,      // zone string where the drag started (e.g. 'hand', 'player-slot-0')
  tempDeck: null,        // flat array of { cardNo, copyVer, state } entries for the active game
  myTurn: true,          // networked matches only — whether this client currently owns the turn
  networked: false,      // whether the current game is a networked match (vs. solo practice)
  attackInProgress: false, // true while an attack's Block/Action/Damage step pipeline is resolving
  modalHidden: false,      // true while a picker/waiting modal is temporarily hidden for field view
  superpowerDeployedThisTurn: false, // true once a (Superpower Bloc)/(UN) Unit was deployed this turn
  neoZeonEffectDestroyedOwnUnitThisTurn: false, // true once effectDestroyOwnUnit fires with a (Neo Zeon) sourceCard this turn (GD05-129 Axis)
  specialMoveCommandActivatedThisTurn: false, // true once a (Special Move) Command's Main/Action fires this turn (GD05-089 Master Asia)
  oppSpecialMoveCommandActivatedThisTurn: false, // mirror of the OPPONENT's own specialMoveCommandActivatedThisTurn, via SPECIAL_MOVE_COMMAND_ACTIVATED (GD05-068 Shining Gundam (Super Mode))
  causedEnemyDiscardThisTurn: false, // true once one of my effects confirms the enemy discarded a card this turn (GD05-041 Gaia Gundam MA Mode)
  effectDrawOccurredThisTurn: false, // true once drawCardsFromTempDeck(..., fromEffect=true) draws at least 1 card this turn (GD06-080 Kou Uraki)
  pendingDefenderOverride: null,     // set by CHANGE_ATTACK_TARGET (ST07-013) before runDamageStep
  battleShieldImmuneLv: 0,           // max attacker Lv blocked from hitting shields (cleared per-battle in runDamageStep)
  battleShieldImmuneAp: 0,           // max attacker AP blocked from hitting shields (cleared per-battle in runDamageStep; GD06-106 Presence Detected)
  forcedAttackTargetUid: null,       // UID of the unit all enemies must attack this turn (GD04-107 Destined Battle)
  attackerBdProxyUid: null,          // UID of the unit absorbing the attacker's counter-damage this battle (GD04-087 Elan)
};

// Phase-transition speed multiplier. Used to be togglable via a fast-forward
// button; the button is gone but the 2x timing it enabled is kept as default.
const PHASE_SPEED = 2;

// ════════════════════════════════════════════════════════════════════════════
// ACTION DISPATCHER
// Single choke point for every game-state mutation. Handlers are registered
// by name and invoked via dispatch(type, payload) instead of being mutated
// inline from click/drop handlers. This is prep for networked multiplayer:
// a future networking layer can call setActionListener() to observe (and
// eventually broadcast/replay) every action without touching call sites.
// Handlers keep doing their own render()/setupDropZones()/setTimeout calls
// internally, exactly as before — dispatch() does not force a render.
// ════════════════════════════════════════════════════════════════════════════
const actionHandlers = {};
function registerAction(type, handler) { actionHandlers[type] = handler; }

let onAction = null;
function setActionListener(fn) { onAction = fn; }

// ── LOG ACTOR TRACKING ──────────────────────────────────────────────────────
// Who addLog() should attribute the *next* log line to. Not a fully threaded
// context — just a "last known actor" set/restored at three chokepoints
// (dispatch() below, triggerCardEffect()/direct CardEffects[...] call sites,
// and network.js's incoming-message handler), which covers every addLog()
// call site without having to touch any of them individually. Correct for
// the overwhelming common case; a log line emitted from deep inside a
// setTimeout-deferred animation can in rare interleavings carry whichever
// actor was last set rather than the one that started that specific chain.
let currentLogActor = null; // 'player' | 'opp' | null

// Ambient "which card's effect is currently resolving" — set/restored by
// triggerCardEffect() the same way currentLogActor is (save previous, set,
// restore in finally, so nested triggerCardEffect calls unwind correctly).
// Read by shared low-level mutators (dealDamageToUnit, setRested,
// bounceEnemyUnit, ...) that don't otherwise know which card's effect called
// them, so they can attribute a toast like "CARD_NAME dealt 2 damage to
// CARD_NAME" without every one of their many call sites needing to pass a
// source explicitly. Only ever non-null while synchronously inside a
// registered handler's own call frame — an async callback (e.g. a card
// picker's onChoice, fired much later by a user click) runs AFTER
// triggerCardEffect has already restored this to its previous value, so
// those callers must capture the source card themselves before the async
// gap if they want attribution (see the reveal/discard/exile toast sites).
let currentEffectSourceCard = null;

// The live combat ctx ({ attacker, attackerSide, defenderSide, defender, ... })
// for whichever battle is currently in progress on THIS client, set at the
// same point as state.attackInProgress = true (resolveAttack) and cleared in
// runEpilogue. Lets dealDamageToUnit's "an enemy Unit was just destroyed by
// effect damage" branch answer "was the unit that just scored this kill via
// effect damage also the attacker of the battle currently in progress" for
// hooks gated on card text like "while this Unit is attacking" (e.g.
// ST13-012 Suletta Mercury) — dealDamageToUnit itself has no battle ctx
// parameter, so this is the only way it can see that.
let currentAttackCtx = null;

// Display name for a log pill: the side's effective username (see
// getEffectiveUsername() — a saved Edit Profile username, else a stable
// per-session random callsign like "EFSF-AB001", never a bare "Player 1"/
// "Player 2"-style placeholder) so both clients' independently-rendered
// logs agree on who did what even when neither side ever set a real
// username. My own comes straight from getEffectiveUsername(); the
// opponent's was learned once at match start from server/relay.js's ROLE
// message (see NET.oppUsername in network.js), since a username never
// otherwise leaves this browser. Falls back to 'Player 1'/'Player 2' by
// absolute seat only if the opponent's client is too old to have sent one
// (e.g. mid-deploy version skew). Returns '' for anything else — system log
// lines (phase changes, etc.) get no pill.
function actorDisplayName(side) {
  if (side !== 'player' && side !== 'opp') return '';
  if (side === 'player') return getEffectiveUsername();
  const iAmPlayer2 = window.NET && NET.role === 'player2';
  const oppName = (window.NET && typeof NET.oppUsername === 'string') ? NET.oppUsername.trim() : '';
  return oppName || (iAmPlayer2 ? 'Player 1' : 'Player 2');
}

function dispatch(type, payload = {}) {
  const handler = actionHandlers[type];
  if (!handler) { console.warn(`[dispatch] no handler for "${type}"`); return; }
  const prevActor = currentLogActor;
  currentLogActor = payload.mine === false ? 'opp' : 'player';
  let result;
  try {
    result = handler(payload);
  } catch (err) {
    // A handler (or a triggered card effect it calls synchronously) throwing
    // must never skip the network mirror below — state mutations made before
    // the throw are real and already applied, and the peer needs to hear
    // about them regardless of a downstream bug. Without this guard, a single
    // buggy card effect silently desyncs the match instead of just breaking
    // its own feature (see e.g. the PAIR_PILOT/Flit Asuno bug this guards).
    console.error(`[dispatch] handler for "${type}" threw`, err);
  } finally {
    currentLogActor = prevActor;
  }
  if (onAction) onAction(type, payload);
  return result;
}

// ════════════════════════════════════════════════════════════════════════════
// INIT
// Populates the initial state with the starting board and hand, then renders.
// ════════════════════════════════════════════════════════════════════════════
function initGame(deckData = null, { networked = false, forceMyTurn = null, botDeckDef = null } = {}) {
  // Full state reset so a new game after surrender/return starts clean
  Object.assign(state, {
    turn: 1,
    phase: 'Main Phase',
    phaseIndex: 3,
    playerShield: 6,
    oppShield: 6,
    playerLevel: 0,
    oppLevel: 4,
    deckCount: 50,
    oppDeckCount: 50,
    trashCount: 0,
    oppTrashCount: 0,
    oppRes: 0,
    oppExRes: 0,
    oppExResRested: 0,
    oppResBase: 0,
    oppHandCount: 0,
    playerTrash: [],
    oppTrash: [],
    playerExile: [],
    oppExile: [],
    res: 0,
    resBase: 0,
    exRes: 0,
    exResRested: 0,
    hand: [],
    playerField: [null, null, null, null, null, null],
    oppField: [null, null, null, null, null, null],
    playerBase: null,
    oppBase: null,
    // Peer's chosen Resource/EX Base/EX Resource cardNos (see Card
    // Appearance in Edit Profile) — filled in by the CARD_APPEARANCE
    // network message (js/network.js) sent once at match start so the
    // opponent's mirrored tray/base show what THEY picked, not my own
    // choice. null fields fall back to the standard printing (see
    // resolveCardAppearanceEntry() in game.js) until that message arrives.
    oppCardAppearance: { resource: null, exBase: null, exResource: null },
    log: [],
    dragCard: null,
    dragSource: null,
    phaseLocked: false,
    tempDeck: null,
    // Bot-match-only fields (see js/bot_logic/bot-engine.js). isBotMatch gates
    // every bot-specific branch in game.js so nothing changes for human/
    // networked play. oppHand/oppTempDeck hold the bot's REAL hand/deck —
    // unlike real networked play's oppHandCount-only privacy model, a bot has
    // no second client to keep its hand hidden from, so it's safe to keep the
    // full card objects locally (never rendered face-up to the human).
    isBotMatch: false,
    botDeckId: null,
    oppHand: [],
    oppTempDeck: null,
    // forceMyTurn lets a Best of 3 series' loser-picks-turn-order rule (see
    // proceedToNextBo3Game) override the socket-role default for games 2/3 —
    // must be applied here, BEFORE the "going second" EX Resource bonus below
    // reads state.myTurn, not patched in afterward. Bot matches have no
    // socket role to key off of — who goes first is a straight coin flip
    // instead (forceMyTurn is never actually passed for a bot match today,
    // but honored here too in case that ever changes, e.g. a future bot-
    // match Bo3 series).
    myTurn: networked
      ? (forceMyTurn !== null ? forceMyTurn : (NET.role === 'player1'))
      : (botDeckDef ? (forceMyTurn !== null ? forceMyTurn : Math.random() < 0.5) : true),
    networked,
    attackInProgress: false,
    endPhaseStep: false,
    // Set true by any effect that sets a Resource active outside the normal
    // Start Phase refill (e.g. ST09-008, ST07-001, ST14-015 itself) — read by
    // ST14-015's own "if you have not set one of your Resources as active
    // with an effect this turn" condition. Reset each Start Phase.
    resourceSetActiveThisTurn: false,
    pendingDefenderOverride: null,
    battleShieldImmuneLv: 0,
    battleShieldImmuneAp: 0,
    forcedAttackTargetUid: null,
    attackerBdProxyUid: null,
  });

  // Reset per-game watcher arrays so closures from a previous game don't linger
  unitEffectResSpentWatchers = [];
  turnDestroyWatchers = [];
  mulliganLocalDone  = false;
  mulliganRemoteDone = false;

  // Build and shuffle the temp deck from the selected deck data
  if (deckData) {
    state.tempDeck = buildTempDeck(deckData);
    state.deckCount = state.tempDeck.length;
  }

  if (networked) {
    // Real opponent — every player starts with an EX Base regardless of
    // side, so the opponent's base zone is seeded the same way as mine
    // (only the 3 dummy practice units are suppressed, not the base).
    // It's then overwritten for real once the opponent deploys/replaces it,
    // mirrored via the DEPLOY_BASE message.
    state.playerBase = makeBaseCardFromEntry(getCardAppearanceEntry('exBase'), 'EXB');
    state.oppBase    = makeBaseCard('assets/images/resource/EXB-001.webp', 'EXB'); // placeholder — overwritten by the opponent's own DEPLOY_BASE
    state.oppField   = [null, null, null, null, null, null];

    // Tell the peer which printings I picked in Edit Profile, so my Resource/
    // EX Resource/EX Base show up correctly on THEIR screen (their mirrored
    // view of my field) instead of falling back to their own choice or the
    // standard art. Sent once, right away — safely before either side could
    // have taken any real game action (deploying a different base, etc.).
    NET.send({ type: 'CARD_APPEARANCE', appearance: loadCardAppearance() });

    addLog(`${actorDisplayName(state.myTurn ? 'player' : 'opp')} is going first.`, { noPill: true });
    // Catch-up bonus — whoever goes second starts with 1 EX Resource.
    if (!state.myTurn) {
      state.exRes = 1;
      addLog('Going second — gained 1 EX Resource.', { noPill: true });
    }
  } else if (botDeckDef) {
    // Bot match — real opponent deck/hand/shields built by BotEngine (see
    // js/bot_logic/bot-engine.js), not the static dummy board below. Turn
    // order (state.myTurn, set above) is a coin flip rather than always
    // human-first — mirror the networked branch's "going first" log and
    // going-second EX Resource catch-up bonus for the human's own side here
    // (the bot's own equivalent bonus is handled inside setupBotSide below).
    addLog(`${actorDisplayName(state.myTurn ? 'player' : 'opp')} is going first.`, { noPill: true });
    if (!state.myTurn) {
      state.exRes = 1;
      addLog('Going second — gained 1 EX Resource.', { noPill: true });
    }
    state.playerBase = makeBaseCardFromEntry(getCardAppearanceEntry('exBase'), 'EXB');
    state.oppBase    = makeBaseCard('assets/images/resource/EXB-001.webp', 'EXB');
    state.oppField   = [null, null, null, null, null, null];
    BotEngine.setupBotSide(botDeckDef);
  } else {
    // Solo practice — static dummy opponent board built from the card DB.
    // Dead code today (every real call site passes either networked:true or
    // a botDeckDef) — left as-is rather than repurposed, since the bot
    // feature needed a real shuffled deck/hand/shields instead of 3
    // hardcoded dummy units and got its own branch above.
    state.playerBase = makeBaseCardFromEntry(getCardAppearanceEntry('exBase'), 'EXB');
    state.oppBase    = makeBaseCard('assets/images/resource/EXB-001.webp', 'EXB'); // dummy opponent — always the standard art
    const _mkPractice = (cardNo, extraProps = {}) => {
      const entry = cardDb[cardNo];
      if (!entry) return null;
      return Object.assign(buildGameCardFromDb(entry, `practice-${cardNo}`), extraProps);
    };
    state.oppField = [
      _mkPractice('ST02-001'),
      _mkPractice('ST02-002'),
      _mkPractice('GD01-026', { rested: true }), // starts rested so deploy effects like Guntank can target it
      null, null, null,
    ];
  }

  state.playerField = [null, null, null, null, null, null];

  // Draw starting hand from temp deck
  state.hand = state.tempDeck ? drawCardsFromTempDeck(5) : [];

  // Start at Start Phase
  state.phaseIndex = 0;
  state.phase = state.phases[0];

  // Shields are dealt after mulligan — start at 0 so they don't appear during intro
  state.playerShield = 0;

  addLog('Turn 1 — Game start.', { turnMarker: true });

  render();
  setupDropZones();

  // Wire the in-game MENU button
  const menuBtn = document.querySelector('.btn-menu');
  if (menuBtn) {
    menuBtn.onclick = () => showInGameMenu();
  }

  // Show battle-start intro, then mulligan after it finishes
  setTimeout(() => showBattleStart(showMulliganModal), 400);
}

function showInGameMenu() {
  if (document.getElementById('ingame-menu-overlay')) return;

  const overlay = document.createElement('div');
  overlay.id = 'ingame-menu-overlay';

  const modal = document.createElement('div');
  modal.id = 'ingame-menu-modal';
  modal.innerHTML = `
    <div class="igm-title">Menu</div>
    <button class="igm-btn igm-surrender">Surrender</button>
    <button class="igm-btn igm-settings">Settings</button>
    <button class="igm-btn igm-close">Resume</button>
  `;

  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });

  modal.querySelector('.igm-close').addEventListener('click', () => overlay.remove());

  modal.querySelector('.igm-settings').addEventListener('click', () => showSettingsModal());

  modal.querySelector('.igm-surrender').addEventListener('click', () => {
    overlay.remove();
    showSurrenderConfirm();
  });
}

function showSurrenderConfirm() {
  const overlay = document.createElement('div');
  overlay.id = 'ingame-menu-overlay';

  const modal = document.createElement('div');
  modal.id = 'ingame-menu-modal';
  modal.innerHTML = `
    <div class="igm-title">Surrender?</div>
    <div class="igm-body">Are you sure you want to surrender and return to the main menu?</div>
    <div class="igm-btn-row">
      <button class="igm-btn igm-cancel">Cancel</button>
      <button class="igm-btn igm-confirm-surrender">Yes, Surrender</button>
    </div>
  `;

  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  modal.querySelector('.igm-cancel').addEventListener('click', () => overlay.remove());

  modal.querySelector('.igm-confirm-surrender').addEventListener('click', () => {
    overlay.remove();
    if (state.networked) {
      NET.send({ type: 'SURRENDER' });
      if (leaveLobbyMatch('loss')) return;
      NET.disconnect();
    }
    clearTempDeck();
    showMainMenu(Promise.resolve());
  });
}

// ════════════════════════════════════════════════════════════════════════════
// TEMP DECK  — per-game flat card list with location state tracking
// ════════════════════════════════════════════════════════════════════════════

// Expands a saved deck's copies into individual tracked entries, then shuffles.
function buildTempDeck(deckData) {
  const entries = [];
  (Array.isArray(deckData) ? deckData : []).forEach(item => {
    const copies = Math.min(parseInt(item.copies) || 1, 4);
    for (let v = 1; v <= copies; v++) {
      entries.push({ cardNo: item.cardNo, copyVer: String(v), state: 'deck' });
    }
  });
  return shuffleDeck(entries);
}

// Fisher–Yates (a.k.a. Knuth) shuffle: produces a mathematically uniform
// random permutation (every ordering equally likely) in O(n). Used as the
// building block for shuffleDeck() below — both to shuffle within a single
// stratification band and to shuffle band order itself.
function fisherYatesShuffle(arr) {
  const result = arr.slice();
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

// ────────────────────────────────────────────────────────────────────────────
// Deck shuffle — stratified shuffle: true-random within bands, with copies of
// the same card forced into DIFFERENT bands.
//
// This used to be a straight Fisher-Yates shuffle (see fisherYatesShuffle()
// above), which replaced an even older "physical shuffle simulation" (one
// pile shuffle + five overhand shuffles + one cut) that clustered duplicate
// copies far more than true randomness would — see the old comment this
// replaced, preserved in git history. Fisher-Yates fixed the mathematically
// biased part of that, but true uniform randomness still clusters duplicates
// some of the time purely by chance (a well-known player-perception problem
// in digital card games — real randomness reads as "not random enough").
// Players reported hands still feeling clumped with same-name duplicates
// even after the Fisher-Yates fix, so this deliberately re-introduces a
// SMALL controlled bias against clustering, same idea as "smart shuffle"
// systems in other digital TCGs:
//   1. Group entries by cardNo. bandCount = the largest group size (i.e. the
//      most copies of any single card in this deck, capped at 4 by
//      buildTempDeck) — a deck of all-unique cards degrades to bandCount=1,
//      i.e. plain Fisher-Yates, unchanged.
//   2. For each group with 2+ copies, STRATIFY_PROBABILITY decides whether
//      that group's copies get spread across DISTINCT bands (chosen via a
//      random permutation of band indices, so which bands get them is still
//      random — just never the SAME band twice for one card) or dropped
//      into independently random bands, where a same-band collision (and
//      the resulting duplicate-in-hand clumping this whole mechanism exists
//      to reduce) can happen at its natural random rate. First fully-
//      stratified version measured ~0% chance of 2+ copies of a card in an
//      8-card opening window (on a 50-card / twelve-4-ofs test deck) — that
//      read as suspiciously perfect/"too consistent" rather than merely
//      lucky, so STRATIFY_PROBABILITY=0.78 was calibrated (by simulation,
//      same test harness) to bring that back up to ~25%, keeping the shuffle
//      believably random-feeling without regressing all the way to the
//      87%+ clump rate plain Fisher-Yates produced on the same test deck.
//   3. Fisher-Yates shuffle each band's contents independently, then
//      Fisher-Yates shuffle the ORDER the bands are concatenated in — so
//      "band 1" isn't always the physical first quarter of the deck across
//      games, and draw order within a band is still fully random.
// Used both for the initial deck build and any later reshuffle (e.g.
// mulligan) so deck randomization is consistent throughout a game.
// ────────────────────────────────────────────────────────────────────────────
const STRATIFY_PROBABILITY = 0.78;
function shuffleDeck(arr) {
  const groups = new Map();
  arr.forEach(entry => {
    const key = entry.cardNo;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(entry);
  });

  const bandCount = Math.max(1, ...Array.from(groups.values()).map(g => g.length));
  const bands = Array.from({ length: bandCount }, () => []);

  groups.forEach(copies => {
    if (copies.length > 1 && Math.random() >= STRATIFY_PROBABILITY) {
      // Skip stratification for this group — each copy lands in an
      // independently random band, so they CAN collide into the same one.
      copies.forEach(entry => {
        const randomBand = Math.floor(Math.random() * bandCount);
        bands[randomBand].push(entry);
      });
      return;
    }
    const bandOrder = fisherYatesShuffle(Array.from({ length: bandCount }, (_, i) => i));
    copies.forEach((entry, i) => bands[bandOrder[i]].push(entry));
  });

  const shuffledBands = bands.map(fisherYatesShuffle);
  return fisherYatesShuffle(shuffledBands).flat();
}

// Converts a cardDb entry into a game-compatible card object.
function buildGameCardFromDb(entry, copyVer) {
  const cat = (entry.categoryData || '').toLowerCase().trim();
  return {
    img:      entry.image || '',
    name:     entry.name  || entry.cardNo,
    cardNo:   entry.cardNo,
    copyVer:  copyVer,
    uid:      `${entry.cardNo}_${copyVer}`,
    level:    parseStatValue(entry.level) ?? 0,
    cost:     parseStatValue(entry.cost)  ?? 0,
    bp:       parseStatValue(entry.ap)    ?? 0,
    hp:       parseStatValue(entry.hp)    ?? 0,
    type:     cat,
    color:    entry.color ? entry.color.toLowerCase() : null,
    trait:    entry.trait  || null,
    effect:   cleanEffect(entry.effectData),
    // Raw, un-stripped effect text — cleanEffect() converts known [Keyword]
    // tags (Burst, Action, Main, ...) into bracket-less <span> badges, so
    // any literal `.includes('[Keyword]')` check must use this field instead
    // of `effect`, which no longer contains the brackets.
    effectRaw: entry.effectData || '',
    nativeKeyword: entry.nativeKeyword || '',
    // Multi-option Link Reqs are stored as "[Name A] / [Name B]" — strip every
    // bracket, not just the outer pair, so it reads as "Name A / Name B".
    link:     entry.link ? entry.link.replace(/[[\]]/g, '') : null,
    pilotName: entry.pilot ? entry.pilot.replace(/^\[|\]$/g, '') : null,
    // Additional pilot identities from "treated as [X]" effect text (e.g.
    // Quattro Bajeena is also treated as [Char Aznable]). Checked by
    // isLinkUnit() so units with that link requirement accept this pilot.
    pilotAliases: (() => {
      const aliases = [];
      const re = /treated as \[([^\]]+)\]/g;
      let m;
      while ((m = re.exec(entry.effectData || '')) !== null) aliases.push(m[1]);
      return aliases.length ? aliases : null;
    })(),
    rarity:   entry.rarity || null,
    canPilot: cat === 'pilot' || cat === 'command pilot' ||
              (cat === 'command' && (entry.effectData || '').includes('[Pilot]')),
    // A Command or Command Pilot whose only timing keyword is [Action] (no
    // [Main]) can only ever be played during the Action Step — it can't be
    // dragged onto the field during the Main Phase.
    isActionOnly: (cat === 'command' || cat === 'command pilot') &&
                  /\[Action\]/.test(entry.effectData || '') &&
                  !/\[Main\]/.test(entry.effectData || ''),
    rested:   false,
    damaged:  false,
    pilot:    null,
  };
}

// Draws `count` cards from the top of the deck into the hand. `state.tempDeck`
// is shuffled once in buildTempDeck()/shuffleTempDeck() and its array order is
// preserved from then on — index 0 (the first remaining 'deck'-state entry)
// is the top of the deck. Draws must consume from that end, not a random
// index, so that top-of-deck effects (e.g. ST06-009 "Look at the top card of
// your deck") and draws agree on what "the top" means. See also
// peekTopOfDeck(), sendDeckEntryToBottom() and sendDeckEntryToTop().
function drawCardsFromTempDeck(count, fromEffect = false) {
  const drawn = [];
  if (!state.tempDeck) return drawn;
  const available = state.tempDeck.filter(isInDeck);
  for (let i = 0; i < count && available.length > 0; i++) {
    const entry = available.splice(0, 1)[0];
    const dbEntry = cardDb[entry.cardNo];
    if (!dbEntry) {
      // Card has no DB entry — log and skip without consuming the draw slot
      console.warn(`[drawCardsFromTempDeck] no DB entry for ${entry.cardNo} — skipped`);
      i--; // don't count this as a successful draw
      continue;
    }
    entry.state = 'hand';
    const card = buildGameCardFromDb(dbEntry, entry.copyVer);
    drawn.push(card);
    glowHandCard(card.uid); // RAF fires after render() — element will exist
  }
  state.deckCount = state.tempDeck.filter(isInDeck).length;
  // Notify pilot passives that react to effect-based draws (e.g. ST08-011 Lane Aim)
  if (fromEffect && drawn.length > 0) {
    state.effectDrawOccurredThisTurn = true;
    state.playerField.forEach(c => {
      if (!c?.pilot) return;
      const ph = window.CardEffects?.[c.pilot.cardNo];
      if (ph?.onEffectDraw) ph.onEffectDraw(c.pilot, c, drawn.length);
    });
  }
  return drawn;
}

// Re-shuffles the entire temp deck (see shuffleDeck() — stratified shuffle).
// Needed any time cards are returned to the deck pool outside of normal
// deck-building order (e.g. a mulligan), since draws now consume strictly
// from the top — without a reshuffle, cards just returned to the pool would
// be redrawn in a deterministic (non-random) order.
function shuffleTempDeck() {
  if (!state.tempDeck) return;
  state.tempDeck = shuffleDeck(state.tempDeck);
}

// Looks at the top `count` cards of the deck without removing them, for
// "Look at the top card(s) of your deck" effects. Returns an array of
// { entry, card } in top-to-bottom order; `entry` is the raw tempDeck
// tracking object (pass it to sendDeckEntryToBottom/Top or updateTempState
// to resolve it), `card` is the built game-card object for display/checks.
function peekTopOfDeck(count) {
  if (!state.tempDeck) return [];
  return state.tempDeck.filter(isInDeck).slice(0, count).map(entry => {
    const dbEntry = cardDb[entry.cardNo];
    return dbEntry ? { entry, card: buildGameCardFromDb(dbEntry, entry.copyVer) } : null;
  }).filter(Boolean);
}

// Moves a tempDeck entry (as returned by peekTopOfDeck, or found via
// updateTempState's lookup pattern) to the bottom of the deck, preserving
// its 'deck' state. No-op if the entry isn't currently tracked.
function sendDeckEntryToBottom(entry) {
  if (!state.tempDeck) return;
  const idx = state.tempDeck.indexOf(entry);
  if (idx === -1) return;
  state.tempDeck.splice(idx, 1);
  entry.state = 'deck';
  state.tempDeck.push(entry);
}

// Moves a tempDeck entry to the top of the deck, preserving its 'deck' state.
function sendDeckEntryToTop(entry) {
  if (!state.tempDeck) return;
  const idx = state.tempDeck.indexOf(entry);
  if (idx === -1) return;
  state.tempDeck.splice(idx, 1);
  entry.state = 'deck';
  state.tempDeck.unshift(entry);
}

// Updates the state field of a specific card in the temp deck.
function updateTempState(cardNo, copyVer, newState) {
  if (!state.tempDeck || !cardNo || !copyVer) return;
  const entry = state.tempDeck.find(e => e.cardNo === cardNo && e.copyVer === copyVer);
  if (entry) entry.state = newState;
  state.deckCount = state.tempDeck.filter(isInDeck).length;
}

// Returns true for any entry that belongs in the deck — explicit 'deck' state
// or any unrecognised value that isn't one of the known placed states.
const PLACED_STATES = new Set(['hand', 'field', 'shield', 'trash', 'base', 'exile']);
function isInDeck(entry) {
  return entry.state === 'deck' || !PLACED_STATES.has(entry.state);
}

// Deals the top `count` cards of the deck as shields (called once, right
// after the keep/mulligan decision is made — see KEEP_HAND/MULLIGAN below).
// Deterministic, not random: the deck is already shuffled by this point
// (initial buildTempDeck() shuffle, or MULLIGAN's own reshuffle), so taking
// a fixed slice off the top is equivalent in fairness to a random pick
// while keeping "top of deck" a single consistent concept throughout setup.
function dealPlayerShields(count) {
  if (!state.tempDeck) { state.playerShield = count; return; }
  const available = state.tempDeck.filter(isInDeck).slice(0, count);
  available.forEach(entry => { entry.state = 'shield'; });
  state.playerShield = available.length;
  state.deckCount = state.tempDeck.filter(isInDeck).length;
}

// Wipes the temp deck on game end.
function clearTempDeck() {
  state.tempDeck = null;
}

// ════════════════════════════════════════════════════════════════════════════
// DECK SELECT SCREEN
// Shown between "Play Game" and initGame — lets the player pick a 50-card deck.
// ════════════════════════════════════════════════════════════════════════════
// onCancel defaults to the original "back to main menu" behavior — every
// existing call site passes only onConfirm and is unaffected. The Private
// Games lobby (showPrivateGamesLobby()) passes its own onCancel so backing
// out of deck-select mid-challenge-handshake notifies the opponent
// (LOBBY_DECK_CANCEL) instead of silently leaving them waiting forever.
function showDeckSelectScreen(onConfirm, onCancel = () => showMainMenu(Promise.resolve()), opts = {}) {
  const GDECK_PREFIX = 'gdeck:';
  const allNames = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(GDECK_PREFIX)) allNames.push(key.slice(GDECK_PREFIX.length));
    }
  } catch (e) {
    console.warn('[showDeckSelectScreen] localStorage unavailable:', e);
  }

  const validDecks = allNames.map(name => {
    try {
      const data = JSON.parse(localStorage.getItem(GDECK_PREFIX + name) || '[]');
      const total = (Array.isArray(data) ? data : []).reduce((s, c) => s + (parseInt(c.copies) || 0), 0);
      if (total < 50) return null;
      // Sideboard is read alongside the deck purely for the Best of 3
      // eligibility check below (opts.requireBo3Sideboard) — untouched for
      // every other caller (Quick Play/public queue never set that opt).
      let sbData = [];
      try { sbData = JSON.parse(localStorage.getItem('gdeck-sb:' + name) || '[]'); } catch { sbData = []; }
      if (!Array.isArray(sbData)) sbData = [];
      const sbTotal = sbData.reduce((s, c) => s + (parseInt(c.copies) || 0), 0);
      return { name: name.replace(/_DRAFT$/, ''), key: name, data, sbData, sbTotal };
    } catch { return null; }
  }).filter(Boolean).sort((a, b) => a.name.localeCompare(b.name));

  const ov = document.createElement('div');
  ov.id = 'deck-select-overlay';
  ov.innerHTML = `
    <div class="ds-modal">
      <div class="ds-header">
        <div class="ds-title">Select Your Deck</div>
        <div class="ds-subtitle">Only complete decks (50 cards) are shown</div>
      </div>
      <div class="ds-list" id="ds-deck-list">
        ${validDecks.length === 0
          ? '<div class="ds-empty">No complete decks found.<br>Build a 50-card deck (50 cards) in the Deck Builder first.<br><button class="ds-btn ds-go-builder" style="margin-top:14px;">Open Deck Builder</button></div>'
          : ''}
      </div>
      <div class="ds-footer">
        <button class="ds-btn ds-cancel">← Back</button>
        <button class="ds-btn ds-confirm" id="ds-play-btn" disabled>Play</button>
      </div>
    </div>`;
  document.body.appendChild(ov);

  const listEl = ov.querySelector('#ds-deck-list');
  const playBtn = ov.querySelector('#ds-play-btn');
  let selectedDeck = null;

  validDecks.forEach(deck => {
    const issues = getDeckComplianceIssues(getDeckCardCounts(deck.data), opts);
    const noncompliant = issues.length > 0;
    // Best of 3 lobbies only accept decks whose sideboard is either untouched
    // (0 cards — sideboarding is simply skipped for that player) or exactly
    // the required 10 — anything in between can't be reasoned about mid-series.
    const bo3Ineligible = !!opts.requireBo3Sideboard && deck.sbTotal !== 0 && deck.sbTotal !== 10;
    const blocked = noncompliant || bo3Ineligible;
    const row = document.createElement('div');
    row.className = 'ds-deck-row' + (noncompliant ? ' ds-deck-row-noncompliant' : '') + (bo3Ineligible ? ' ds-deck-row-ineligible' : '');
    // Built without deck.name inside the markup string, then set via
    // .textContent right after — deck names are untrusted free text (they
    // can predate sanitizeDeckName(), e.g. imported from an older save), so
    // this must never be parsed as markup.
    row.innerHTML = `<span class="material-symbols-outlined ds-deck-icon">playing_cards</span><span class="ds-deck-pills">${deckColorPillsHtml(deck.data)}</span><span class="ds-deck-name"></span>${noncompliant ? `<span class="material-symbols-outlined ds-deck-warning-icon" title="${issues.join(' ').replace(/"/g, '&quot;')}">warning</span>` : ''}${bo3Ineligible ? '<span class="ds-not-eligible-tag">NOT ELIGIBLE</span>' : ''}`;
    row.querySelector('.ds-deck-name').textContent = deck.name;
    if (blocked) {
      row.addEventListener('click', () => {
        if (noncompliant) {
          showToastHtml(`${issues.join(' ')} <a href="${DB_RESTRICTIONS_URL}" target="_blank" rel="noopener">More info</a>`, true);
        } else {
          showToast('Best of 3 is enabled in this lobby — this deck\'s sideboard must have exactly 0 or 10 cards to be eligible.', true);
        }
      });
    } else {
      row.addEventListener('click', () => {
        listEl.querySelectorAll('.ds-deck-row').forEach(r => r.classList.remove('selected'));
        row.classList.add('selected');
        selectedDeck = deck;
        playBtn.disabled = false;
      });
      row.addEventListener('dblclick', () => {
        if (selectedDeck) { ov.remove(); onConfirm(selectedDeck.data, selectedDeck.sbData); }
      });
    }
    listEl.appendChild(row);
  });

  ov.querySelector('.ds-cancel').addEventListener('click', () => {
    ov.remove();
    onCancel();
  });

  const goBuilderBtn = ov.querySelector('.ds-go-builder');
  if (goBuilderBtn) {
    goBuilderBtn.addEventListener('click', () => {
      ov.remove();
      showDeckBuilder(() => showDeckSelectScreen(onConfirm, onCancel));
    });
  }

  playBtn.addEventListener('click', () => {
    if (!selectedDeck) return;
    ov.remove();
    onConfirm(selectedDeck.data, selectedDeck.sbData);
  });
}

// ════════════════════════════════════════════════════════════════════════════
// BOT DECK SELECT SCREEN
// Entry point for the main menu's "Play Against Bot" button. Lists every
// deck registered via registerBotDeck() (js/bot_logic/registry.js, populated
// by js/bot_logic/decks/*.js) as a card: name, deckbox art with the deck's
// representative card behind it, a View Deck preview, and a Challenge button
// that hands off to the EXISTING human showDeckSelectScreen (the human still
// picks their own 50-card deck) before starting the match via
// initGame(humanDeckData, { botDeckDef }).
// ════════════════════════════════════════════════════════════════════════════
function showBotDeckSelectScreen() {
  const existing = document.getElementById('bot-deck-select-overlay');
  if (existing) existing.remove();

  const decks = Object.values(window.BotDecks || {});
  const overlay = document.createElement('div');
  overlay.id = 'bot-deck-select-overlay';
  overlay.innerHTML = `
    <div class="bds-modal">
      <div class="bds-header">
        <div class="bds-title">Play Against Bot</div>
        <div class="bds-subtitle">Choose a bot deck to challenge</div>
      </div>
      <div class="bds-grid" id="bds-grid">
        ${decks.length === 0 ? '<div class="bds-empty">No bot decks available yet.</div>' : ''}
      </div>
      <div class="bds-footer">
        <button class="bds-btn bds-cancel">← Back</button>
      </div>
    </div>`;
  document.body.appendChild(overlay);

  const grid = overlay.querySelector('#bds-grid');
  decks.forEach(deck => {
    const repEntry = cardDb[deck.representativeCard];
    const card = document.createElement('div');
    card.className = 'bds-card';
    card.innerHTML = `
      <div class="bds-card-name"></div>
      <div class="bds-card-pills">${deckColorPillsHtml(expandBotDecklist(deck.decklist))}</div>
      <div class="bds-card-art">
        ${repEntry ? `<img class="bds-card-art-rep" src="${repEntry.image}" alt="" />` : ''}
        <img class="bds-card-art-box" src="assets/deckbox.webp" alt="" />
        ${deck.faceIcon ? `<img class="bds-card-faction" src="${deck.faceIcon}" alt="" />` : ''}
      </div>
      <div class="bds-card-actions">
        <button class="bds-btn bds-view">View Deck</button>
        <button class="bds-btn bds-btn-primary bds-challenge">Challenge</button>
      </div>`;
    // deck.name is a trusted, developer-authored registry string (not user
    // input), but set via textContent anyway — same convention
    // showDeckSelectScreen uses for untrusted deck names, kept consistent.
    card.querySelector('.bds-card-name').textContent = deck.name;
    card.querySelector('.bds-view').addEventListener('click', () => showBotDeckPreviewModal(deck));
    card.querySelector('.bds-challenge').addEventListener('click', () => {
      overlay.remove();
      // Signed-out guests only ever have the one fixed deck slot (gdeck:My
      // Deck — same slot showDeckBuilder() auto-loads for them since their
      // Load Deck is disabled), so there's nothing for a deck-select modal
      // to offer them beyond that single deck — skip straight to the match
      // with it. Falls through to the normal picker if that slot is missing
      // or incomplete, which still shows its own "build a deck first" state.
      const isLoggedIn = !!(window.Supa && window.Supa.getSession());
      if (!isLoggedIn) {
        let guestDeckData = null;
        try {
          const raw = localStorage.getItem('gdeck:My Deck');
          if (raw) {
            const parsed = JSON.parse(raw);
            const total = (Array.isArray(parsed) ? parsed : []).reduce((s, c) => s + (parseInt(c.copies) || 0), 0);
            if (total >= 50) guestDeckData = parsed;
          }
        } catch {}
        if (guestDeckData) {
          initGame(guestDeckData, { networked: false, botDeckDef: deck });
          return;
        }
      }
      showDeckSelectScreen(
        (humanDeckData) => { initGame(humanDeckData, { networked: false, botDeckDef: deck }); },
        () => showBotDeckSelectScreen()
      );
    });
    grid.appendChild(card);
  });

  overlay.querySelector('.bds-cancel').addEventListener('click', () => {
    overlay.remove();
    showMainMenu(Promise.resolve());
  });
}

// Expands a bot deck's compact { cardNo: copies } decklist into the same
// denormalized per-card shape buildDeckEntryFromDb() already produces for
// human decks, so it flows through buildTempDeck() unchanged.
function expandBotDecklist(decklist) {
  return Object.entries(decklist || {})
    .map(([cardNo, copies]) => buildDeckEntryFromDb(cardNo, copies))
    .filter(Boolean);
}

// Same TYPE_ORDER/level sort as buildDeckTxtLines()/renderDeck() in the Deck
// Builder, so a bot deck's grid reads the same way a human's own does.
// Shared by showBotDeckPreviewModal (pre-match "View Deck") and
// showBotProfileModal (in-match "View Profile").
const BOT_DECK_GRID_TYPE_ORDER = { unit: 0, pilot: 1, 'command pilot': 1.5, command: 2, base: 3 };
function botDeckGridCatOf(entry) { return (entry.categoryData || '').toLowerCase().trim(); }
function expandBotDeckForGrid(decklist) {
  return Object.entries(decklist || {})
    .map(([cardNo, copies]) => {
      const entry = cardDb[cardNo];
      return entry ? { cardNo, copies: parseInt(copies) || 0, entry } : null;
    })
    .filter(Boolean)
    .sort((a, b) => {
      const typeA = BOT_DECK_GRID_TYPE_ORDER[botDeckGridCatOf(a.entry)] ?? 99;
      const typeB = BOT_DECK_GRID_TYPE_ORDER[botDeckGridCatOf(b.entry)] ?? 99;
      if (typeA !== typeB) return typeA - typeB;
      const lvA = parseInt(a.entry.level) || parseInt(a.entry.cost) || 0;
      const lvB = parseInt(b.entry.level) || parseInt(b.entry.cost) || 0;
      return lvA - lvB;
    });
}
// Read-only version of the Deck Builder's own .db-deck-cell/.db-deck-card
// tile (image + ×N badge, no add/remove buttons — this is a preview, not an
// editor). Hovering shows the normal card info preview (showCardPreview),
// same as every other card tile/picker grid in the game — built from a
// fresh buildGameCardFromDb() copy since cardDb's raw entries don't have the
// shape showCardPreview expects (.img vs .image, .bp vs .ap, cleaned effect
// text, ...).
function buildBotDeckGrid(cards) {
  const grid = document.createElement('div');
  grid.className = 'bdp-grid';
  cards.forEach(({ cardNo, copies, entry }) => {
    const cell = document.createElement('div');
    cell.className = 'bdp-cell';
    cell.innerHTML = `
      <div class="bdp-card">
        <img src="${entry.image}" alt="${escapeHtml(entry.name || cardNo)}" loading="lazy" />
        ${copies > 1 ? `<div class="bdp-card-count">×${copies}</div>` : ''}
      </div>`;
    const previewCard = buildGameCardFromDb(entry, 'preview');
    cell.addEventListener('mouseenter', () => showCardPreview(previewCard, cell, { gameUi: false }));
    cell.addEventListener('mouseleave', hideCardPreview);
    grid.appendChild(cell);
  });
  return grid;
}

// View Deck preview: a card-image grid (same look as the Deck Builder's own
// db-deck-grid) grouped by type/level, an icon-based type summary (same
// icons as renderDeckTypeSummary()), color pills, a Copy Deck button, and
// the sideboard (if any) in its own grid below. Built on the same
// .mk-overlay/.mk-modal shell as showComingSoonModal/showSettingsModal.
function showBotDeckPreviewModal(botDeckDef) {
  const existing = document.getElementById('bot-deck-preview-overlay');
  if (existing) existing.remove();

  const mainCards = expandBotDeckForGrid(botDeckDef.decklist);
  const sideCards = expandBotDeckForGrid(botDeckDef.sideboard);
  const countWhere = (cards, pred) => cards.filter(pred).reduce((sum, c) => sum + c.copies, 0);
  const totalMain    = countWhere(mainCards, () => true);
  const unitCount    = countWhere(mainCards, c => botDeckGridCatOf(c.entry) === 'unit' || botDeckGridCatOf(c.entry) === 'unit-token');
  const pilotCount   = countWhere(mainCards, c => botDeckGridCatOf(c.entry) === 'pilot');
  const commandCount = countWhere(mainCards, c => botDeckGridCatOf(c.entry) === 'command' || botDeckGridCatOf(c.entry) === 'command pilot');
  const baseCount    = countWhere(mainCards, c => botDeckGridCatOf(c.entry) === 'base' || botDeckGridCatOf(c.entry) === 'ex base');

  const overlay = document.createElement('div');
  overlay.id = 'bot-deck-preview-overlay';
  overlay.className = 'mk-overlay';
  overlay.style.zIndex = '13600';
  overlay.innerHTML = `
    <div class="bdp-modal">
      <div class="bdp-header">
        <div class="mk-title"></div>
        <div class="bdp-pills" id="bdp-pills"></div>
      </div>
      <div class="bdp-type-summary">
        <div class="bdp-type-item"><span class="material-symbols-outlined">smart_toy</span><span>Unit</span><strong>${unitCount}</strong></div>
        <div class="bdp-type-item"><span class="material-symbols-outlined">person</span><span>Pilot</span><strong>${pilotCount}</strong></div>
        <div class="bdp-type-item"><span class="material-symbols-outlined">sd_card_alert</span><span>Command</span><strong>${commandCount}</strong></div>
        <div class="bdp-type-item"><span class="material-symbols-outlined">castle</span><span>Base</span><strong>${baseCount}</strong></div>
        <div class="bdp-type-item bdp-type-total"><span>Total</span><strong>${totalMain}</strong></div>
      </div>
      <div class="bdp-grid-wrap" id="bdp-grid-wrap"></div>
      ${sideCards.length > 0 ? '<div class="bdp-section-label">Side Deck</div><div class="bdp-grid-wrap" id="bdp-side-grid-wrap"></div>' : ''}
      <div class="bdp-footer">
        <button class="bdp-btn" id="bdp-copy-btn" type="button">
          <span class="material-symbols-outlined">content_copy</span> Copy Deck
        </button>
        <button class="bdp-btn bdp-btn-primary" id="bdp-close-btn" type="button">Close</button>
      </div>
    </div>`;
  overlay.querySelector('.mk-title').textContent = botDeckDef.name;
  overlay.querySelector('#bdp-pills').innerHTML = deckColorPillsHtml(mainCards.map(c => c.entry));
  overlay.querySelector('#bdp-grid-wrap').appendChild(buildBotDeckGrid(mainCards));
  const sideWrap = overlay.querySelector('#bdp-side-grid-wrap');
  if (sideWrap) sideWrap.appendChild(buildBotDeckGrid(sideCards));

  document.body.appendChild(overlay);
  const close = () => overlay.remove();
  overlay.querySelector('#bdp-close-btn').addEventListener('click', close);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

  overlay.querySelector('#bdp-copy-btn').addEventListener('click', async () => {
    const text = mainCards.map(c => `${c.copies}x ${c.cardNo}`).join('\n') + '\n';
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      // Clipboard API can be unavailable (permissions, insecure context) —
      // same select+execCommand fallback showExportTextModal() uses.
      const tmp = document.createElement('textarea');
      tmp.value = text;
      tmp.style.position = 'fixed';
      tmp.style.opacity = '0';
      document.body.appendChild(tmp);
      tmp.select();
      document.execCommand('copy');
      tmp.remove();
    }
    showToast('Decklist copied to clipboard!');
  });
}

// In-match "View Profile" modal (opened from renderBotProfilePanel()'s
// button, right sidebar) — the full persona/deck/playstyle/decklist-source
// breakdown that the sidebar panel itself no longer shows inline, plus the
// same deck grid (+ Copy Deck) as showBotDeckPreviewModal above, built from
// the same shared expandBotDeckForGrid/buildBotDeckGrid helpers so the grid
// looks and behaves identically (including hover-to-preview).
function showBotProfileModal(botDeckDef) {
  const existing = document.getElementById('bot-profile-modal-overlay');
  if (existing) existing.remove();

  const mainCards = expandBotDeckForGrid(botDeckDef.decklist);
  const sideCards = expandBotDeckForGrid(botDeckDef.sideboard);
  const countWhere = (cards, pred) => cards.filter(pred).reduce((sum, c) => sum + c.copies, 0);
  const totalMain    = countWhere(mainCards, () => true);
  const unitCount    = countWhere(mainCards, c => botDeckGridCatOf(c.entry) === 'unit' || botDeckGridCatOf(c.entry) === 'unit-token');
  const pilotCount   = countWhere(mainCards, c => botDeckGridCatOf(c.entry) === 'pilot');
  const commandCount = countWhere(mainCards, c => botDeckGridCatOf(c.entry) === 'command' || botDeckGridCatOf(c.entry) === 'command pilot');
  const baseCount    = countWhere(mainCards, c => botDeckGridCatOf(c.entry) === 'base' || botDeckGridCatOf(c.entry) === 'ex base');

  const overlay = document.createElement('div');
  overlay.id = 'bot-profile-modal-overlay';
  overlay.className = 'mk-overlay';
  overlay.style.zIndex = '13600';
  overlay.innerHTML = `
    <div class="bdp-modal">
      <div class="bpm-profile-header">
        <img class="bpm-face" src="${botDeckDef.faceIcon || ''}" alt="${escapeHtml(botDeckDef.personaName || botDeckDef.name)}">
        <div class="bpm-profile-info">
          <div class="bpm-name"></div>
          <div class="bpm-row"><span class="bpm-label">Deck</span><span class="bpm-value" id="bpm-deck-value"></span></div>
          <div class="bdp-pills" id="bdp-pills"></div>
          ${botDeckDef.playstyle ? `<div class="bpm-row"><span class="bpm-label">Playstyle</span><span class="bpm-value" id="bpm-playstyle-value"></span></div>` : ''}
          ${botDeckDef.decklistSource ? `<div class="bpm-row"><span class="bpm-label">Decklist From</span><span class="bpm-value" id="bpm-source-value"></span></div>` : ''}
        </div>
      </div>
      <div class="bdp-section-label">Decklist</div>
      <div class="bdp-type-summary">
        <div class="bdp-type-item"><span class="material-symbols-outlined">smart_toy</span><span>Unit</span><strong>${unitCount}</strong></div>
        <div class="bdp-type-item"><span class="material-symbols-outlined">person</span><span>Pilot</span><strong>${pilotCount}</strong></div>
        <div class="bdp-type-item"><span class="material-symbols-outlined">sd_card_alert</span><span>Command</span><strong>${commandCount}</strong></div>
        <div class="bdp-type-item"><span class="material-symbols-outlined">castle</span><span>Base</span><strong>${baseCount}</strong></div>
        <div class="bdp-type-item bdp-type-total"><span>Total</span><strong>${totalMain}</strong></div>
      </div>
      <div class="bdp-grid-wrap" id="bdp-grid-wrap"></div>
      ${sideCards.length > 0 ? '<div class="bdp-section-label">Side Deck</div><div class="bdp-grid-wrap" id="bdp-side-grid-wrap"></div>' : ''}
      <div class="bdp-footer">
        <button class="bdp-btn" id="bdp-copy-btn" type="button">
          <span class="material-symbols-outlined">content_copy</span> Copy Deck
        </button>
        <button class="bdp-btn bdp-btn-primary" id="bdp-close-btn" type="button">Close</button>
      </div>
    </div>`;
  overlay.querySelector('.bpm-name').textContent = botDeckDef.personaName || botDeckDef.name;
  overlay.querySelector('#bpm-deck-value').textContent = botDeckDef.name;
  overlay.querySelector('#bdp-pills').innerHTML = deckColorPillsHtml(mainCards.map(c => c.entry));
  if (botDeckDef.playstyle) overlay.querySelector('#bpm-playstyle-value').textContent = botDeckDef.playstyle;
  if (botDeckDef.decklistSource) overlay.querySelector('#bpm-source-value').textContent = botDeckDef.decklistSource;
  overlay.querySelector('#bdp-grid-wrap').appendChild(buildBotDeckGrid(mainCards));
  const sideWrap = overlay.querySelector('#bdp-side-grid-wrap');
  if (sideWrap) sideWrap.appendChild(buildBotDeckGrid(sideCards));

  document.body.appendChild(overlay);
  const close = () => overlay.remove();
  overlay.querySelector('#bdp-close-btn').addEventListener('click', close);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

  overlay.querySelector('#bdp-copy-btn').addEventListener('click', async () => {
    const text = mainCards.map(c => `${c.copies}x ${c.cardNo}`).join('\n') + '\n';
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      const tmp = document.createElement('textarea');
      tmp.value = text;
      tmp.style.position = 'fixed';
      tmp.style.opacity = '0';
      document.body.appendChild(tmp);
      tmp.select();
      document.execCommand('copy');
      tmp.remove();
    }
    showToast('Decklist copied to clipboard!');
  });
}

// ════════════════════════════════════════════════════════════════════════════
// MATCHMAKING SCREEN
// Shown after deck select, before initGame(). Waits for the relay to report
// a second connected player (NET's MATCH_READY hook), then starts a
// networked match. Cancel backs out to deck select rather than leaving the
// player stuck if no opponent (or no relay) ever shows up.
//
// Gives up after MATCHMAKING_TIMEOUT_SECONDS with no opponent found — shown
// as a live countdown in the modal — rather than waiting indefinitely; the
// relay's own heartbeat (server/relay.js) keeps the socket itself alive well
// past that, so this is a UX bound, not a workaround for the connection
// dying underneath it.
// ════════════════════════════════════════════════════════════════════════════
const MATCHMAKING_TIMEOUT_SECONDS = 90;

function showMatchmakingScreen(deckData) {
  const overlay = document.createElement('div');
  overlay.id = 'matchmaking-overlay';
  overlay.innerHTML = `
    <div class="mk-modal">
      <div class="mk-spinner"></div>
      <div class="mk-title" id="mk-status">Searching for opponent…</div>
      <div class="mk-countdown" id="mk-countdown"></div>
      <button class="mk-cancel-btn" id="mk-cancel-btn">Cancel</button>
    </div>`;
  document.body.appendChild(overlay);

  let cancelled = false;
  let remaining = MATCHMAKING_TIMEOUT_SECONDS;
  const countdownEl = overlay.querySelector('#mk-countdown');
  const renderCountdown = () => {
    countdownEl.textContent = `Giving up in ${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, '0')}`;
  };
  renderCountdown();
  const countdownInterval = setInterval(() => {
    remaining--;
    if (remaining <= 0) { clearInterval(countdownInterval); return; }
    renderCountdown();
  }, 1000);

  const giveUpTimeout = setTimeout(() => {
    if (cancelled) return;
    cancelled = true;
    clearInterval(countdownInterval);
    NET.disconnect();
    overlay.remove();
    // showMatchEndOverlay() is for a match that already started ending
    // early (opponent surrendered/disconnected) — it guards on
    // state.networked, which is still false here since no match ever
    // started. Calling it silently no-op'd: the overlay above was already
    // removed, but nothing redirected anywhere, leaving the player stranded
    // on whatever was underneath. Redirect directly instead.
    clearTempDeck();
    showToast('No match-up found, please try again in a few minutes.', true);
    showMainMenu(Promise.resolve());
  }, MATCHMAKING_TIMEOUT_SECONDS * 1000);

  overlay.querySelector('#mk-cancel-btn').addEventListener('click', () => {
    cancelled = true;
    clearTimeout(giveUpTimeout);
    clearInterval(countdownInterval);
    NET.disconnect();
    overlay.remove();
    showDeckSelectScreen(nextDeckData => showMatchmakingScreen(nextDeckData));
  });

  // Register the listener before connecting so a MATCH_READY that arrives
  // right after the socket opens can never be missed.
  NET.onMatchReady(() => {
    if (cancelled) return;
    cancelled = true;
    clearTimeout(giveUpTimeout);
    clearInterval(countdownInterval);
    const statusEl = overlay.querySelector('#mk-status');
    if (statusEl) statusEl.textContent = 'Opponent found!';
    setTimeout(() => {
      overlay.remove();
      initGame(deckData, { networked: true });
      setupDropZones();
    }, 800);
  });

  NET.connect();
}

// Shared metadata for every host-configurable lobby rule — one source of
// truth for the rules island every member sees (renderInLobby() below) and
// the host-only settings panel that edits them. `key` matches the field name
// server/relay.js's lobby.settings object (and LOBBY_SETTINGS_UPDATE) uses.
const LOBBY_RULES = [
  {
    key: 'allowNewCards',
    label: 'Allow "New" Cards In Decks',
    desc: "Allows decks that have NEW announced cards that haven't been physically released yet.",
  },
  {
    key: 'bo3',
    label: 'Games Are Best of 3',
    desc: 'Every game played in this lobby is a Best of 3 match instead of a single game.',
  },
  {
    key: 'rankings',
    label: 'Show Leaderboard',
    desc: 'Shows a live leaderboard of every lobby member ranked by wins, updated after every game played in this lobby.',
  },
];

// ════════════════════════════════════════════════════════════════════════════
// PRIVATE GAMES LOBBY
// A shareable-code lobby (up to 8 members) that sits ALONGSIDE the public
// matchmaking queue above without touching it — connects with
// NET.connect({mode:'lobby'}), which server/relay.js reads to opt the socket
// out of the random queue entirely (see relay.js's ?mode=lobby gating).
// Deck selection happens only AFTER a challenge is accepted (the lobby
// itself is deck-agnostic); once both sides confirm a deck, the server pairs
// them via the exact same ROLE/MATCH_READY mechanism the public queue uses,
// so the actual match start below needs zero lobby-specific handling.
// ════════════════════════════════════════════════════════════════════════════
function showPrivateGamesLobby(dbReady = Promise.resolve()) {
  const overlay = document.createElement('div');
  overlay.id = 'private-games-overlay';
  overlay.innerHTML = `
    <div class="pg-header">
      <button class="pg-btn" id="pg-back-btn">← Back</button>
      <span class="pg-title">Private Lobby</span>
      <div class="pg-header-actions">
        <button class="pg-btn" id="pg-deckbuilder-btn" type="button">Deck Builder</button>
        <button class="pg-btn pg-btn-danger" id="pg-close-lobby-btn" type="button" hidden>Close Lobby</button>
      </div>
    </div>
    <div class="pg-body" id="pg-body"></div>
  `;
  document.body.appendChild(overlay);

  const bodyEl = overlay.querySelector('#pg-body');
  let selfId = null;
  let myCode = null;
  let latestMembers = [];
  let myDeckData = null;
  let activeModal = null;
  let iAmCreator = false;
  let mySideboardData = null;
  // Host-controlled match rules for this lobby, kept in sync via every
  // LOBBY_STATE broadcast (see NET.onLobbyState below) — read by the
  // always-visible settings panel in renderInLobby() and passed to
  // showDeckSelectScreen() when a challenge is accepted, so "Allow New
  // Cards"/"Best of 3" actually gate deck choice and match format.
  let lobbySettings = { allowNewCards: false, bo3: false, rankings: false };

  overlay.querySelector('#pg-deckbuilder-btn').addEventListener('click', () => {
    overlay.style.display = 'none';
    showDeckBuilder(() => { overlay.style.display = ''; });
  });

  // Close Lobby lives in the persistent header (created once above, unlike
  // the in-lobby body which gets rebuilt by renderInLobby()) so it stays put
  // next to Deck Builder across re-renders. Host-only and hidden everywhere
  // else — see updateHeaderActionsVisibility(), called from renderInLobby()/
  // renderPreLobby() whenever iAmCreator could have changed. Lobby Settings
  // itself now lives in the always-visible right-side panel inside
  // renderInLobby() instead of a header-triggered modal.
  const closeLobbyBtn = overlay.querySelector('#pg-close-lobby-btn');
  closeLobbyBtn.addEventListener('click', () => {
    showPgModal({
      title: 'Close this lobby for everyone?',
      spinner: false,
      buttons: [
        { label: 'Cancel', onClick: () => closeActiveModal() },
        {
          label: 'Close Lobby', danger: true, onClick: () => {
            closeActiveModal();
            closeLobbyBtn.disabled = true;
            NET.send({ type: 'LOBBY_CLOSE' });
          },
        },
      ],
    });
  });
  function updateHeaderActionsVisibility() {
    closeLobbyBtn.hidden = !iAmCreator;
  }
  // Guards the page-scoped socket-error handling below against firing from
  // a stale earlier visit (this page can be entered/left/re-entered) and
  // against firing at all once a real match has actually started — from
  // that point on, state.networked is true and the existing PERMANENT
  // global handler (handleNetworkDisconnectError, registered once for the
  // whole page lifetime) correctly owns disconnect handling instead.
  let visitActive = true;

  const myUsername = getEffectiveUsername();

  function closeActiveModal() {
    if (activeModal) { activeModal.remove(); activeModal = null; }
  }

  // Small modal layered above the lobby page (z-index above #private-games-
  // overlay's 11000 — same inline-override pattern showSettingsModal()/
  // showEditProfileModal() already use for .mk-overlay's default 600).
  // Text is always set via textContent, never interpolated into innerHTML —
  // usernames are user-controlled and this codebase has no HTML-escaping
  // helper.
  function showPgModal({ title, body, spinner = true, buttons = [] }) {
    closeActiveModal();
    const modalOverlay = document.createElement('div');
    modalOverlay.className = 'mk-overlay';
    modalOverlay.style.zIndex = '13000';
    const modal = document.createElement('div');
    modal.className = 'mk-modal';
    if (spinner) {
      const spinnerEl = document.createElement('div');
      spinnerEl.className = 'mk-spinner';
      modal.appendChild(spinnerEl);
    }
    const titleEl = document.createElement('div');
    titleEl.className = 'mk-title';
    titleEl.textContent = title;
    modal.appendChild(titleEl);
    if (body) {
      // Reuses the ingame-menu-modal's existing secondary-text style (e.g.
      // showSurrenderConfirm's "Are you sure you want to..." line) rather
      // than adding a near-identical pg-specific class.
      const bodyEl = document.createElement('div');
      bodyEl.className = 'igm-body';
      bodyEl.textContent = body;
      modal.appendChild(bodyEl);
    }
    if (buttons.length) {
      const btnRow = document.createElement('div');
      btnRow.className = 'igm-btn-row';
      buttons.forEach(({ label, primary, danger, onClick }) => {
        const btn = document.createElement('button');
        btn.className = danger ? 'btn-danger' : (primary ? 'btn-primary' : 'btn-secondary');
        btn.type = 'button';
        btn.textContent = label;
        btn.addEventListener('click', onClick);
        btnRow.appendChild(btn);
      });
      modal.appendChild(btnRow);
    }
    modalOverlay.appendChild(modal);
    document.body.appendChild(modalOverlay);
    activeModal = modalOverlay;
    return modal;
  }

  function closePrivateGames() {
    visitActive = false;
    closeActiveModal();
    document.getElementById('deck-select-overlay')?.remove();
    NET.disconnect();
    overlay.classList.add('mm-fade-out');
    setTimeout(() => overlay.remove(), 400);
    showMainMenu(dbReady);
  }
  overlay.querySelector('#pg-back-btn').addEventListener('click', () => {
    // Only the lobby's creator leaving actually closes it for everyone else
    // (see cleanupLobbyMembership in server/relay.js) — a non-host member
    // backing out just quietly leaves, same as before, so only the host
    // needs to be warned first.
    if (!iAmCreator) { closePrivateGames(); return; }
    showPgModal({
      title: 'Leave the lobby?',
      body: 'If you leave, the lobby will be closed and everyone in it will be kicked.',
      spinner: false,
      buttons: [
        { label: 'Cancel', onClick: () => closeActiveModal() },
        { label: 'Ok', danger: true, onClick: () => { closeActiveModal(); closePrivateGames(); } },
      ],
    });
  });

  // ── State: pre-lobby (create/join) ─────────────────────────────────────
  function renderPreLobby() {
    // No lobby exists yet to configure or close — hide both regardless of
    // iAmCreator's current value (it's reset to false by the callers of
    // renderPreLobby() anyway, but this stays correct even if that ordering
    // ever changes).
    updateHeaderActionsVisibility();
    bodyEl.innerHTML = `
      <div class="pg-prelobby">
        <button class="pg-btn pg-btn-primary" id="pg-create-btn" type="button">Create Lobby</button>
        <div class="pg-join-row">
          <input type="text" id="pg-code-input" class="pg-code-input" placeholder="Enter Code" maxlength="6" autocomplete="off" />
          <button class="pg-btn" id="pg-join-btn" type="button">Join</button>
        </div>
      </div>
    `;
    const createBtn = bodyEl.querySelector('#pg-create-btn');
    const joinBtn = bodyEl.querySelector('#pg-join-btn');
    const codeInput = bodyEl.querySelector('#pg-code-input');
    createBtn.addEventListener('click', () => {
      createBtn.disabled = true;
      NET.send({ type: 'LOBBY_CREATE', username: myUsername });
    });
    joinBtn.addEventListener('click', () => {
      const code = codeInput.value.trim().toUpperCase();
      if (!code) return;
      joinBtn.disabled = true;
      // Restores a previously-cached tally for this lobby code (e.g. a page
      // refresh while idle mid-lobby) instead of always starting at 0/0.
      const tally = loadLobbyRecord(code);
      NET.send({ type: 'LOBBY_JOIN', code, username: myUsername, wins: tally.wins, losses: tally.losses });
    });
    codeInput.addEventListener('keydown', e => { if (e.key === 'Enter') joinBtn.click(); });
  }

  // ── State: in-lobby ──────────────────────────────────────────────────────
  function renderInLobby() {
    updateHeaderActionsVisibility();
    // A fresh in-lobby render (new lobby created/joined, or resumed after a
    // match) always starts from a clean slate — without this, closing one
    // lobby then creating/joining another in the same visit would find the
    // button still disabled from the previous LOBBY_CLOSE click, since it's
    // now a persistent header element instead of being rebuilt every time.
    closeLobbyBtn.disabled = false;

    // The lobby code is host-only (non-hosts already joined and can't
    // share/copy it) — it gets its own hovering pill island bottom-center of
    // pg-body (.pg-code-share-island below) rather than living inside the
    // settings panel, so it reads as "the thing you hand to a friend" rather
    // than just another settings row. Hidden by default (masked with *
    // characters) with a show/hide toggle next to it.
    let codeRevealed = false;
    const maskedCode = '*'.repeat(myCode ? myCode.length : 6);
    // Settings panel is host-only, full stop — non-hosts never see it at all
    // (not even read-only), matching "only the host can see the settings for
    // the lobby and toggle them". The server independently re-enforces
    // creator-only on LOBBY_SETTINGS_UPDATE regardless, but that guard is now
    // moot for the UI since a non-host has no toggle to click in the first
    // place. The rules island is the mirror image of that: host-EXCLUDED,
    // since the settings panel already shows the host the exact same
    // information with editable toggles — a non-host has no settings panel,
    // so the read-only rules island is the only view they get. Exactly one
    // of the two ever renders into .pg-corner-stack (top-right corner) for
    // a given member. The Ranking island (renderRankingPanel(), left corner),
    // the code-share island (bottom-center, host-only), and the member list
    // (.pg-lobby-main, centered) are the only other pieces of this page.
    bodyEl.innerHTML = `
      <div class="pg-corner-stack">
        ${!iAmCreator ? `
        <div class="pg-rules-island">
          <div class="mk-title">Lobby Rules</div>
          <div class="pg-rules-pills">
            ${LOBBY_RULES.map(rule => `
            <span class="pg-rule-pill${lobbySettings[rule.key] ? ' pg-rule-pill-active' : ''}" id="pg-rule-pill-${rule.key}">${rule.label}</span>
            `).join('')}
          </div>
          <div class="pg-rules-list">
            ${LOBBY_RULES.map(rule => `
            <div class="pg-rule-item">
              <span class="pg-rule-item-name">${rule.label}</span>
              <span class="pg-rule-item-desc">${rule.desc}</span>
            </div>
            `).join('')}
          </div>
        </div>` : ''}
        ${iAmCreator ? `
        <div class="pg-settings-panel">
          <div class="mk-title">Lobby Settings</div>
          <div class="pg-settings-row">
            <div class="pg-settings-summary">
              Enable this if you want to allow decks that have NEW announced cards that haven't been physically released yet.
            </div>
            <div class="pg-settings-toggle-row">
              <span class="pg-settings-toggle-label">Allow "NEW" Cards In Decks</span>
              <label class="switch-toggle">
                <input type="checkbox" class="switch-toggle-input" id="pg-allow-new-toggle" ${lobbySettings.allowNewCards ? 'checked' : ''} />
                <span class="switch-toggle-track"><span class="switch-toggle-thumb"></span></span>
              </label>
            </div>
          </div>
          <div class="pg-settings-row">
            <div class="pg-settings-summary">
              Enabling this makes every game played in this lobby a Best of 3 match instead of a single game. If a deck has a sideboard, the player is able to switch between cards otherwise decks without just skip this.
            </div>
            <div class="pg-settings-toggle-row">
              <span class="pg-settings-toggle-label">Games Are Best Of 3 (with sideboard)</span>
              <label class="switch-toggle">
                <input type="checkbox" class="switch-toggle-input" id="pg-bo3-toggle" ${lobbySettings.bo3 ? 'checked' : ''} />
                <span class="switch-toggle-track"><span class="switch-toggle-thumb"></span></span>
              </label>
            </div>
          </div>
          <div class="pg-settings-row">
            <div class="pg-settings-summary">
              Enable this to show a live leaderboard of lobby members ranked by wins, updated after every game played in this lobby.
            </div>
            <div class="pg-settings-toggle-row">
              <span class="pg-settings-toggle-label">Show Leaderboard</span>
              <label class="switch-toggle">
                <input type="checkbox" class="switch-toggle-input" id="pg-rankings-toggle" ${lobbySettings.rankings ? 'checked' : ''} />
                <span class="switch-toggle-track"><span class="switch-toggle-thumb"></span></span>
              </label>
            </div>
          </div>
        </div>` : ''}
      </div>
      ${iAmCreator ? `
      <div class="pg-code-share-island">
        <div class="pg-code-share-label">Share this code to invite friends to play in your lobby</div>
        <div class="pg-code-share-pill">
          <span class="pg-code-share-value" id="pg-code-value">${maskedCode}</span>
          <button class="pg-btn pg-icon-btn" id="pg-code-toggle-btn" type="button" title="Show lobby code">
            <span class="material-symbols-outlined">visibility</span>
          </button>
          <button class="pg-btn pg-icon-btn" id="pg-copy-btn" type="button" title="Copy lobby code">
            <span class="material-symbols-outlined">content_copy</span>
          </button>
        </div>
      </div>` : ''}
      <div class="pg-lobby-main">
        <div class="pg-member-list" id="pg-member-list"></div>
      </div>
    `;

    if (iAmCreator) {
      bodyEl.querySelector('#pg-allow-new-toggle').addEventListener('change', (e) => {
        NET.send({ type: 'LOBBY_SETTINGS_UPDATE', allowNewCards: e.target.checked, bo3: lobbySettings.bo3, rankings: lobbySettings.rankings });
      });
      bodyEl.querySelector('#pg-bo3-toggle').addEventListener('change', (e) => {
        NET.send({ type: 'LOBBY_SETTINGS_UPDATE', allowNewCards: lobbySettings.allowNewCards, bo3: e.target.checked, rankings: lobbySettings.rankings });
      });
      bodyEl.querySelector('#pg-rankings-toggle').addEventListener('change', (e) => {
        NET.send({ type: 'LOBBY_SETTINGS_UPDATE', allowNewCards: lobbySettings.allowNewCards, bo3: lobbySettings.bo3, rankings: e.target.checked });
      });
    }

    if (iAmCreator) {
      // myCode is server-generated (6 chars, fixed alphabet — see
      // generateLobbyCode() in server/relay.js), never user-typed text, so
      // this one interpolation above is safe.
      const codeValueEl = bodyEl.querySelector('#pg-code-value');
      const codeToggleBtn = bodyEl.querySelector('#pg-code-toggle-btn');
      codeToggleBtn.addEventListener('click', () => {
        codeRevealed = !codeRevealed;
        codeValueEl.textContent = codeRevealed ? myCode : maskedCode;
        codeToggleBtn.innerHTML = `<span class="material-symbols-outlined">${codeRevealed ? 'visibility_off' : 'visibility'}</span>`;
        codeToggleBtn.title = codeRevealed ? 'Hide lobby code' : 'Show lobby code';
      });

      // Icon-only button (was a wider "Copy" text button before this moved
      // into the pill-shaped code-share island) — success feedback swaps
      // the icon glyph briefly instead of the old textContent swap, since
      // there's no label text left to swap.
      const copyBtn = bodyEl.querySelector('#pg-copy-btn');
      const copyIcon = copyBtn.querySelector('.material-symbols-outlined');
      copyBtn.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(myCode);
        } catch (e) {
          // Clipboard API can be unavailable (permissions, insecure context) —
          // fall back to the older select+execCommand copy path (same pattern
          // showExportTextModal() uses for the decklist Copy button).
          const tmp = document.createElement('input');
          tmp.value = myCode;
          tmp.style.position = 'fixed';
          tmp.style.opacity = '0';
          document.body.appendChild(tmp);
          tmp.select();
          document.execCommand('copy');
          tmp.remove();
        }
        copyIcon.textContent = 'check';
        copyBtn.title = 'Copied!';
        setTimeout(() => { copyIcon.textContent = 'content_copy'; copyBtn.title = 'Copy lobby code'; }, 1500);
        showToast('Lobby code copied to clipboard!');
      });
    }
    renderMemberList();
    renderRankingPanel();
  }

  function renderMemberList() {
    const listEl = bodyEl.querySelector('#pg-member-list');
    if (!listEl) return;
    const mySelf = latestMembers.find(m => m.id === selfId);
    const iAmIdle = !!mySelf && mySelf.status === 'idle';
    listEl.innerHTML = '';
    latestMembers.forEach(m => {
      // Wraps .pg-member-row so the host crown can sit outside the row's own
      // box (to its left) instead of competing with the row's internal
      // icon/name/stats layout — see .pg-member-row-outer in style.css.
      // The crown slot is always created, host or not, and given a fixed
      // width in CSS — leaving it out entirely for non-host rows would let
      // .pg-member-row (flex:1) stretch further on those rows than on the
      // host's, making the rows visibly different widths.
      const rowOuter = document.createElement('div');
      rowOuter.className = 'pg-member-row-outer';

      const crownSlot = document.createElement('span');
      crownSlot.className = 'material-symbols-outlined pg-host-crown';
      if (m.isHost) {
        crownSlot.textContent = 'crown';
        crownSlot.title = 'Lobby Host';
      } else {
        crownSlot.setAttribute('aria-hidden', 'true');
      }
      rowOuter.appendChild(crownSlot);

      const row = document.createElement('div');
      row.className = 'pg-member-row';

      const iconSpan = document.createElement('span');
      iconSpan.className = 'material-symbols-outlined pg-member-icon';
      iconSpan.textContent = 'account_circle';
      row.appendChild(iconSpan);

      const nameSpan = document.createElement('span');
      nameSpan.className = 'pg-member-name';
      nameSpan.textContent = m.username; // .textContent, not innerHTML — usernames are user-controlled
      row.appendChild(nameSpan);

      if (m.id === selfId) {
        const youTag = document.createElement('span');
        youTag.className = 'pg-member-you';
        youTag.textContent = '(You)';
        row.appendChild(youTag);
      }

      const winsStat = document.createElement('span');
      winsStat.className = 'pg-member-stat';
      winsStat.innerHTML = '<span class="material-symbols-outlined">emoji_events</span>';
      winsStat.append(String(m.wins || 0));
      row.appendChild(winsStat);

      const lossesStat = document.createElement('span');
      lossesStat.className = 'pg-member-stat';
      lossesStat.innerHTML = '<span class="material-symbols-outlined">skull</span>';
      lossesStat.append(String(m.losses || 0));
      row.appendChild(lossesStat);

      if (m.status !== 'idle') {
        const badge = document.createElement('span');
        badge.className = 'pg-member-badge';
        badge.textContent = m.status === 'pending' ? 'Challenge Pending'
          : m.status === 'deckSelect' ? 'Picking Deck…'
          : 'In a Match';
        row.appendChild(badge);
      }

      // Both trailing icon buttons below share ONE .pg-member-actions wrapper
      // (margin-left:auto lives on the wrapper, not on the buttons) so they
      // sit close together as a group flush right. Giving each button its
      // own margin-left:auto instead (the previous approach) is a classic
      // flexbox trap: when two siblings both have an auto left-margin, the
      // row's free space is split BETWEEN them rather than absorbed by the
      // first one, which is what produced the large gap between the
      // challenge and remove buttons.
      const actions = document.createElement('div');
      actions.className = 'pg-member-actions';

      if (m.id !== selfId && m.status === 'idle' && iAmIdle) {
        const swordBtn = document.createElement('button');
        swordBtn.className = 'pg-sword-btn';
        swordBtn.type = 'button';
        swordBtn.title = `Challenge ${m.username}`;
        swordBtn.innerHTML = '<span class="material-symbols-outlined">swords</span>';
        swordBtn.addEventListener('click', () => {
          swordBtn.disabled = true;
          NET.send({ type: 'LOBBY_CHALLENGE', targetId: m.id });
        });
        actions.appendChild(swordBtn);
      }

      // Host-only: removes + bans an idle member from this lobby (see
      // LOBBY_KICK in server/relay.js). Doesn't require the host themselves
      // to be idle — unlike challenging, removing a disruptive player is a
      // moderation action the host should be able to take even mid-match.
      if (iAmCreator && m.id !== selfId && m.status === 'idle') {
        const removeBtn = document.createElement('button');
        removeBtn.className = 'pg-remove-btn';
        removeBtn.type = 'button';
        removeBtn.title = `Remove ${m.username}`;
        removeBtn.innerHTML = '<span class="material-symbols-outlined">person_remove</span>';
        removeBtn.addEventListener('click', () => {
          showPgModal({
            title: `Remove ${m.username} from the lobby?`,
            spinner: false,
            buttons: [
              { label: 'Cancel', onClick: () => closeActiveModal() },
              {
                label: 'Remove Player', danger: true, onClick: () => {
                  closeActiveModal();
                  NET.send({ type: 'LOBBY_KICK', targetId: m.id });
                },
              },
            ],
          });
        });
        actions.appendChild(removeBtn);
      }

      if (actions.children.length > 0) row.appendChild(actions);

      rowOuter.appendChild(row);
      listEl.appendChild(rowOuter);
    });
  }

  // Left-side floating island — a live wins-ranked leaderboard of every
  // lobby member, shown to everyone (host or not) only while the host has
  // the "Player Rankings" lobby setting on. Called alongside
  // renderMemberList() any time membership or settings change (renderInLobby
  // and NET.onLobbyState below), so both a win/loss update (LOBBY_RETURN)
  // and the host flipping the setting keep it in sync. Built by direct DOM
  // manipulation rather than baked into renderInLobby()'s innerHTML template
  // — unlike the rules/settings islands, this one needs to appear/disappear
  // without a full bodyEl re-render whenever just the setting toggles.
  function renderRankingPanel() {
    let panel = bodyEl.querySelector('#pg-ranking-island');
    if (!lobbySettings.rankings) {
      if (panel) panel.remove();
      return;
    }
    if (!panel) {
      panel = document.createElement('div');
      panel.className = 'pg-ranking-island';
      panel.id = 'pg-ranking-island';
      panel.innerHTML = `<div class="mk-title">Ranking</div><div class="pg-ranking-list" id="pg-ranking-list"></div>`;
      bodyEl.appendChild(panel);
    }
    const listEl = panel.querySelector('#pg-ranking-list');
    listEl.innerHTML = '';
    // Most wins first; ties broken by fewest losses, then name, so the order
    // is fully deterministic instead of flickering between equal members on
    // every re-render.
    const ranked = [...latestMembers].sort((a, b) => {
      if ((b.wins || 0) !== (a.wins || 0)) return (b.wins || 0) - (a.wins || 0);
      if ((a.losses || 0) !== (b.losses || 0)) return (a.losses || 0) - (b.losses || 0);
      return a.username.localeCompare(b.username);
    });
    ranked.forEach((m, i) => {
      const row = document.createElement('div');
      row.className = 'pg-rank-row';
      row.innerHTML = `<span class="pg-rank-number">${i + 1}</span><span class="pg-rank-name"></span><span class="pg-rank-record">${m.wins || 0}W - ${m.losses || 0}L</span>`;
      // .textContent, not innerHTML — usernames are user-controlled, same
      // reasoning as renderMemberList()'s nameSpan above.
      row.querySelector('.pg-rank-name').textContent = m.username + (m.id === selfId ? ' (You)' : '');
      listEl.appendChild(row);
    });
  }

  // ── Wire NET hooks BEFORE connecting (same ordering showMatchmakingScreen
  // documents/relies on) — a fast server round-trip must never arrive before
  // a listener is registered to catch it. ──────────────────────────────────
  NET.onLobbyError(message => {
    showToast(message, true);
    const createBtn = bodyEl.querySelector('#pg-create-btn');
    const joinBtn = bodyEl.querySelector('#pg-join-btn');
    if (createBtn) createBtn.disabled = false;
    if (joinBtn) joinBtn.disabled = false;
  });

  NET.onLobbyCreated(({ code, selfId: id }) => {
    myCode = code;
    selfId = id;
    iAmCreator = true;
    renderInLobby();
  });

  NET.onLobbyJoined(({ code, selfId: id }) => {
    myCode = code;
    selfId = id;
    renderInLobby();
  });

  NET.onLobbyState(({ members, settings }) => {
    latestMembers = members;
    if (settings) {
      lobbySettings = settings;
      // The settings panel (and its toggles) only ever renders for the host
      // (see renderInLobby()) — these querySelectors are always null for a
      // non-host, and for the host they just reconcile the checkbox with the
      // server's own echo of the change that was just sent, in case some
      // other update arrives in between and re-renders things out of order.
      const allowNewToggle = bodyEl.querySelector('#pg-allow-new-toggle');
      if (allowNewToggle) allowNewToggle.checked = lobbySettings.allowNewCards;
      const bo3Toggle = bodyEl.querySelector('#pg-bo3-toggle');
      if (bo3Toggle) bo3Toggle.checked = lobbySettings.bo3;
      const rankingsToggle = bodyEl.querySelector('#pg-rankings-toggle');
      if (rankingsToggle) rankingsToggle.checked = lobbySettings.rankings;
      // The rules island's pills (unlike the toggles above) are visible to
      // EVERY member, host or not — this is how a non-host actually finds
      // out the host just flipped a rule, since they have no settings panel
      // of their own to watch.
      LOBBY_RULES.forEach(rule => {
        const pill = bodyEl.querySelector(`#pg-rule-pill-${rule.key}`);
        if (pill) pill.classList.toggle('pg-rule-pill-active', !!lobbySettings[rule.key]);
      });
    }
    renderMemberList();
    renderRankingPanel();
  });

  NET.onChallengeSent(({ toUsername }) => {
    showPgModal({ title: `Waiting for ${toUsername} to respond…` });
  });

  NET.onChallengeReceived(({ fromId, fromUsername }) => {
    showPgModal({
      title: `${fromUsername} has challenged you to a duel!`,
      spinner: false,
      buttons: [
        { label: 'Decline', onClick: () => { NET.send({ type: 'LOBBY_CHALLENGE_DECLINE', fromId }); closeActiveModal(); } },
        { label: 'Accept', primary: true, onClick: () => { NET.send({ type: 'LOBBY_CHALLENGE_ACCEPT', fromId }); closeActiveModal(); } },
      ],
    });
  });

  NET.onChallengeDeclined(byUsername => {
    closeActiveModal();
    showToast(`${byUsername} declined your challenge.`, true);
  });

  NET.onChallengeTimeout(() => {
    closeActiveModal();
    showToast('Challenge timed out — no response.', true);
  });

  NET.onChallengeAccepted(({ opponentUsername }) => {
    closeActiveModal();
    // Hide (not remove) the lobby page's own content while deck-select runs
    // on top — the socket must stay open through the whole handshake, so
    // this deliberately does NOT call NET.disconnect().
    overlay.style.display = 'none';
    dbReady.then(() => {
      showDeckSelectScreen(
        (deckData, sbData) => {
          myDeckData = deckData;
          mySideboardData = sbData || [];
          NET.send({ type: 'LOBBY_DECK_READY' });
          overlay.style.display = '';
          showPgModal({ title: `Waiting for ${opponentUsername} to pick a deck…` });
        },
        () => {
          NET.send({ type: 'LOBBY_DECK_CANCEL' });
          overlay.style.display = '';
        },
        // Gates deck choice by this lobby's "Allow New Cards" setting and,
        // when Best of 3 is on, by sideboard eligibility (see the always-
        // visible settings panel in renderInLobby() above) — either flags a
        // deck the same way a banlist violation would.
        { disallowNewCards: !lobbySettings.allowNewCards, requireBo3Sideboard: lobbySettings.bo3 }
      );
    });
  });

  NET.onMatchAborted(reason => {
    closeActiveModal();
    document.getElementById('deck-select-overlay')?.remove();
    overlay.style.display = '';
    showToast(reason || 'Match cancelled.', true);
  });

  NET.onMatchReady(() => {
    // From here on this is a completely normal networked match — hand off
    // to the existing global disconnect handler (game.js's
    // handleNetworkDisconnectError) instead of this page's own.
    visitActive = false;
    closeActiveModal();
    // Kept alive (hidden, not removed) so leaveLobbyMatch() can bring the
    // player straight back to this same lobby once the match ends — see
    // resume() below. This applies uniformly whether or not this player
    // created the lobby: finishing a match (win, loss, or surrender) never
    // closes the lobby by itself, for anyone — only the explicit Close
    // Lobby button, or the creator actually disconnecting/leaving while
    // idle (see cleanupLobbyMembership in server/relay.js), does that.
    _lobbyResumeState = {
      code: myCode,
      resume() {
        visitActive = true; // this page's own disconnect handling owns the socket again
        overlay.style.display = '';
        renderInLobby();
      },
    };
    // When this lobby has Best of 3 enabled, showVictoryModal()/
    // handleBo3GameEnd() (module scope, near _lobbyResumeState's own
    // declaration) drive the whole series from here instead of routing
    // straight back to the lobby after game 1.
    _bo3MatchState = lobbySettings.bo3
      ? { code: myCode, myDeckData, mySideboardData: mySideboardData || [], gamesWonByMe: 0, gamesWonByOpp: 0, gameNumber: 1 }
      : null;
    setTimeout(() => {
      overlay.style.display = 'none';
      initGame(myDeckData, { networked: true });
      setupDropZones();
    }, 800);
  });

  NET.onLobbyClosed(reason => {
    closeActiveModal();
    document.getElementById('deck-select-overlay')?.remove();
    // Can arrive while this player's own match hasn't wrapped up on their
    // end yet — e.g. the creator closed the lobby (button, or their
    // connection dropped) while this player is still mid-match against a
    // third member, with a still-open surrender-confirm/victory modal or
    // showMatchEndOverlay's 3s timer pending. Remove them outright so a
    // real user click can't race this handler and re-trigger a return — a
    // real click can never reach a DOM node that's already detached.
    document.getElementById('opponent-left-overlay')?.remove();
    document.getElementById('victory-overlay')?.remove();
    document.getElementById('ingame-menu-overlay')?.remove();
    // Cuts the session for the one race a DOM removal can't stop: a timer
    // (showMatchEndOverlay's 3s setTimeout) whose callback still runs
    // regardless of whether its overlay was already removed. That callback
    // re-checks state.networked itself before acting, so setting it false
    // here makes it a no-op instead of clobbering the pre-lobby view this
    // handler is about to show. Harmless no-op for any other case.
    state.networked = false;
    _lobbyResumeState = null;
    clearLobbyRecord(myCode);
    myCode = null;
    selfId = null;
    latestMembers = [];
    iAmCreator = false;
    lobbySettings = { allowNewCards: false, bo3: false, rankings: false };
    mySideboardData = null;
    overlay.style.display = '';
    renderPreLobby();
    showToast(reason || 'The lobby was closed.', true);
  });

  // Sent only to me, when the host removes me via the Remove Player button
  // (see LOBBY_KICK in server/relay.js) — the lobby itself keeps running for
  // everyone else, so this just drops me back to the pre-lobby screen,
  // mirroring onLobbyClosed's reset above without touching any other
  // member's view.
  NET.onLobbyKicked(() => {
    closeActiveModal();
    _lobbyResumeState = null;
    clearLobbyRecord(myCode);
    myCode = null;
    selfId = null;
    latestMembers = [];
    iAmCreator = false;
    lobbySettings = { allowNewCards: false, bo3: false, rankings: false };
    mySideboardData = null;
    overlay.style.display = '';
    renderPreLobby();
    showToast('You were removed from this lobby by the host.', true);
  });

  // The lobby flow spends most of its time with state.networked still
  // false (that only flips true once initGame({networked:true}) actually
  // runs — see initGame() above), so the existing PERMANENT global handler
  // (handleNetworkDisconnectError, which early-returns while
  // !state.networked) is a total no-op for a relay hiccup that happens
  // while just sitting in the lobby. This page needs its own scoped
  // handling for that window, guarded by visitActive so a stale registration
  // from an earlier visit — or one that's already handed off to the global
  // handler via onMatchReady above — can't double-fire.
  NET.onSocketError(() => {
    if (!visitActive) return;
    showToast('Connection error.', true);
    closePrivateGames();
  });
  NET.onSocketClosedUnexpectedly(() => {
    if (!visitActive) return;
    showToast('Connection lost.', true);
    closePrivateGames();
  });

  renderPreLobby();
  NET.connect({ mode: 'lobby' });
}

// ════════════════════════════════════════════════════════════════════════════
// ACTIVE PLAYERS BADGE (main menu)
// Polls the relay's public /api/active-players endpoint (server/relay.js) —
// players currently in a match (public matchmaking or private lobby alike)
// plus players waiting in the public matchmaking queue. Private lobby
// members who are merely idle/challenging don't count.
// ════════════════════════════════════════════════════════════════════════════
const ACTIVE_PLAYERS_POLL_MS = 10 * 1000; // 10 seconds
let activePlayersPollTimer = null;

function startActivePlayersPolling(countEl) {
  // showMainMenu() runs this every time it's (re)shown — clear any timer a
  // previous visit left running so they don't pile up over a long session.
  if (activePlayersPollTimer) clearInterval(activePlayersPollTimer);

  async function refresh() {
    try {
      const res = await fetch('/api/active-players');
      if (!res.ok) throw new Error(`bad response (${res.status})`);
      const data = await res.json();
      const count = parseInt(data.count, 10);
      if (Number.isFinite(count) && count >= 0) countEl.textContent = String(count);
    } catch (err) {
      // Transient network hiccup — leave whatever count is already showing
      // rather than blanking it out or showing an error in its place.
      console.warn('[startActivePlayersPolling] refresh failed', err);
    }
  }
  refresh();
  activePlayersPollTimer = setInterval(refresh, ACTIVE_PLAYERS_POLL_MS);
}

// ════════════════════════════════════════════════════════════════════════════
// UPDATE-NOTICE TOAST (main menu)
// Fetches /api/update-notice (server/relay.js), which scans the project
// root for whichever "update_notice_vX.Y.txt" file currently exists —
// renaming that file to bump the version (v1.13 -> v1.14, v1.14.2, ...)
// needs no code change here, since the client never hardcodes a filename or
// version, only the /api/update-notice route. Each non-blank line of that
// file becomes one message: shown one at a time under .mm-social-row for
// UPDATE_NOTICE_CYCLE_MS each, with the upcoming message faintly stacked
// underneath (.mm-toast-next) until it becomes the active one. Loops for as
// long as the main menu is open. A single-message notice just displays
// statically — nothing to cycle to.
// ════════════════════════════════════════════════════════════════════════════
const UPDATE_NOTICE_CYCLE_MS = 10 * 1000; // 10 seconds
let updateNoticeCycleTimer = null;

// Plain-text -> HTML escape for free text injected via innerHTML, whether as
// element content (update-notice messages: patch notes plausibly contain
// '<', '>', or '&' — e.g. "HP<3", "AP+2 & AP+3" — which would otherwise get
// parsed as markup) or inside a double-quoted attribute (addLog()'s
// highlightCardNames()/renderLogPanel(), where escaping '"' too keeps a
// quote in the text from breaking out of the attribute).
function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/"/g, '&quot;')
    .replace(/>/g, '&gt;');
}

async function startUpdateNoticeToast(toastEl) {
  // showMainMenu() runs this every time it's (re)shown — same clear-and-
  // restart convention as startActivePlayersPolling above, so repeat visits
  // to the main menu don't pile up overlapping intervals.
  if (updateNoticeCycleTimer) clearInterval(updateNoticeCycleTimer);

  // Settings modal's "Show Update Notifications" toggle (default on) — the
  // single gate both showMainMenu()'s initial call and setShowUpdateNotice()'s
  // live re-invoke go through, so toggling it off hides the toast (and stops
  // its cycle, already cleared above) without a wasted fetch.
  if (!getShowUpdateNotice()) {
    toastEl.classList.add('mm-update-toast-empty');
    return;
  }

  // toastEl is the outer avatar+messages row (only its mm-update-toast-empty
  // class is toggled — its static dev_icon avatar markup is left alone).
  // messagesEl is the inner position:relative wrapper that actually holds
  // the cycling message pills.
  const messagesEl = toastEl.querySelector('#mm-update-toast-messages');

  let messages = [];
  try {
    const res = await fetch('/api/update-notice');
    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data.messages)) messages = data.messages;
    }
  } catch (err) {
    console.warn('[startUpdateNoticeToast] fetch failed', err);
  }

  if (messages.length === 0) {
    toastEl.classList.add('mm-update-toast-empty');
    messagesEl.innerHTML = '';
    return;
  }
  toastEl.classList.remove('mm-update-toast-empty');
  messagesEl.innerHTML = '';

  // The two message layers are stacked via position:absolute (see
  // .mm-update-toast-msg in style.css) so the "next" one can peek out
  // faintly behind the active one instead of pushing it down the page —
  // but that means the wrapper itself needs an explicit height, since an
  // absolutely-positioned child can't contribute to its parent's height.
  // Messages are free-text from the update_notice file and can wrap to
  // different numbers of lines, so rather than guessing one fixed height,
  // measure the tallest one (rendered at the real width) and use that —
  // otherwise a long message could overflow the wrapper and run into the
  // .mm-disclaimer text underneath it.
  let maxHeight = 0;
  messages.forEach(text => {
    const probe = makeMsgEl(text, 'mm-toast-active');
    probe.style.position = 'static';
    probe.style.visibility = 'hidden';
    probe.style.opacity = '0';
    probe.style.transform = 'none';
    probe.style.transition = 'none';
    messagesEl.appendChild(probe);
    maxHeight = Math.max(maxHeight, probe.offsetHeight);
    probe.remove();
  });
  // +14px matches .mm-toast-next's translateY offset so its peeking edge
  // still has room to show below the active card instead of being clipped
  // by the wrapper's own height.
  if (maxHeight > 0) messagesEl.style.minHeight = `${maxHeight + 14}px`;

  function makeMsgEl(text, variantClass) {
    const el = document.createElement('div');
    el.className = `mm-update-toast-msg ${variantClass}`;
    el.innerHTML = `<span class="material-symbols-outlined">campaign</span><span>${escapeHtml(text)}</span>`;
    return el;
  }

  let index = 0;
  // activeEl/nextEl are persistent DOM nodes, not rebuilt every cycle —
  // promoting nextEl to active below is a class change on that SAME node,
  // which is what actually makes the opacity/transform CSS transition
  // play. Replacing the whole toast's innerHTML every tick (an earlier
  // version of this did that) paints the new "active" element already in
  // its final state with nothing to transition from, so it would just pop
  // in instead of fading in.
  let activeEl = makeMsgEl(messages[index], 'mm-toast-active');
  messagesEl.appendChild(activeEl);
  let nextEl = null;
  if (messages.length > 1) {
    nextEl = makeMsgEl(messages[(index + 1) % messages.length], 'mm-toast-next');
    messagesEl.appendChild(nextEl);
    updateNoticeCycleTimer = setInterval(() => {
      index = (index + 1) % messages.length;
      activeEl.remove();
      activeEl = nextEl;
      activeEl.className = 'mm-update-toast-msg mm-toast-active';
      nextEl = makeMsgEl(messages[(index + 1) % messages.length], 'mm-toast-next');
      messagesEl.appendChild(nextEl);
    }, UPDATE_NOTICE_CYCLE_MS);
  }
}

function showMainMenu(dbReady = Promise.resolve()) {
  const overlay = document.createElement('div');
  overlay.id = 'main-menu-overlay';
  overlay.innerHTML = `
    <div class="mm-top-left" id="mm-top-left"></div>
    <div class="mm-left">
      <button class="mm-btn mm-btn-primary" data-action="play"><span class="mm-btn-inner">Quick Play</span></button>
      <button class="mm-btn" data-action="private"><span class="mm-btn-inner">Private Lobby</span></button>
      <button class="mm-btn" data-action="deck"><span class="mm-btn-inner">Deck Builder</span></button>
      <button class="mm-btn" data-action="bot"><span class="mm-btn-inner">Play Against Bot</span></button>
      <button class="mm-btn" data-action="carddb"><span class="mm-btn-inner">Card Database</span></button>
      <button class="mm-btn mm-btn-disabled" data-action="analytics"><span class="mm-btn-inner">Deck Analytics</span></button>
    </div>
    <div class="mm-right">
      <div class="mm-title">Newtype MS<br>Battleground</div>
      <div class="mm-subtitle">TEST YOUR SKILLS WITH THE FREE GUNDAM TCG SIM</div>
      <div class="mm-social-row">
        <a class="mm-social-link mm-social-discord" href="https://discord.gg/b6y4dsyU4j" target="_blank" rel="noopener">
          <span class="mm-social-icon"><img src="assets/social/discord_icon.webp" alt="" /></span>
          Join the Community
        </a>
        <a class="mm-social-link mm-social-patreon" href="https://www.patreon.com/newtypeboitcg" target="_blank" rel="noopener">
          <span class="mm-social-icon"><img src="assets/social/patreon_icon.webp" alt="" /></span>
          Support the Dev
        </a>
        <a class="mm-social-link mm-social-youtube" href="https://www.youtube.com/@NewtypeBoiTCG" target="_blank" rel="noopener">
          <span class="mm-social-icon"><img src="assets/social/youtube_icon.webp" alt="" /></span>
          Get Updates & Content
        </a>
      </div>
      <div class="mm-update-toast mm-update-toast-empty" id="mm-update-toast">
        <div class="mm-update-toast-avatar"><img src="assets/dev_icon.webp" alt="" /></div>
        <div class="mm-update-toast-messages" id="mm-update-toast-messages"></div>
      </div>
    </div>
    <div class="mm-version">v1.71</div>
    <div class="mm-disclaimer">
      Newtype MS Battleground is an unofficial, non-commercial project made by fans, for fans — it isn't affiliated with or endorsed by Bandai, Sunrise, or Sotsu, who hold all rights to the Gundam names, mobile suits, and artwork referenced here. Enjoying the sim? Go check out the real card game at <a href="https://www.gundam-gcg.com" target="_blank" rel="noopener">gundam-gcg.com</a>.
    </div>
    <div class="mm-top-right">
      <div class="mm-active-players" id="mm-active-players" title="Players currently in a match or queued for one">
        <span class="material-symbols-outlined mm-active-players-icon">wifi_tethering</span>
        <span class="mm-active-players-count" id="mm-active-players-count">—</span>
      </div>
      <button class="mm-report-bug" id="mm-report-bug">
        <span class="material-symbols-outlined">bug_report</span>
        Report Bug
      </button>
      <button class="mm-icon-btn" id="mm-settings-btn" title="Settings">
        <span class="material-symbols-outlined">settings</span>
      </button>
    </div>
  `;
  document.body.appendChild(overlay);

  renderMainMenuTopLeft(overlay);
  if (window.Supa) window.Supa.onSessionChange(() => renderMainMenuTopLeft(overlay));

  overlay.querySelector('#mm-report-bug').addEventListener('click', showReportBugModal);
  startActivePlayersPolling(overlay.querySelector('#mm-active-players-count'));
  startUpdateNoticeToast(overlay.querySelector('#mm-update-toast'));

  overlay.querySelector('[data-action="play"]').addEventListener('click', () => {
    // Wait for the card DB (deck select needs it for compliance/pill
    // rendering) BEFORE starting the fade — previously the fade started
    // immediately and only *then* awaited dbReady, so if the DB take any
    // time at all to resolve, the main menu had already fully faded away
    // and the raw, unblurred game board sat exposed underneath for however
    // long the DB load took. Mounting deck select first (like the Deck
    // Builder button already does) means its blurred backdrop is in place
    // before the main menu starts fading, so the board is never exposed.
    dbReady.then(() => {
      showDeckSelectScreen(deckData => showMatchmakingScreen(deckData));
      overlay.classList.add('mm-fade-out');
      setTimeout(() => overlay.remove(), 400);
    });
  });

  overlay.querySelector('[data-action="deck"]').addEventListener('click', () => {
    overlay.classList.add('mm-fade-out');
    showDeckBuilder(); // mount deck builder first so it covers the game board immediately
    setTimeout(() => overlay.remove(), 400);
  });

  overlay.querySelector('[data-action="private"]').addEventListener('click', () => {
    overlay.classList.add('mm-fade-out');
    showPrivateGamesLobby(dbReady); // mount first so it covers the game board immediately
    setTimeout(() => overlay.remove(), 400);
  });

  overlay.querySelector('#mm-settings-btn').addEventListener('click', () => {
    showSettingsModal();
  });
  overlay.querySelector('[data-action="bot"]').addEventListener('click', () => {
    dbReady.then(() => {
      showBotDeckSelectScreen();
      overlay.classList.add('mm-fade-out');
      setTimeout(() => overlay.remove(), 400);
    });
  });
  overlay.querySelector('[data-action="carddb"]').addEventListener('click', () => {
    dbReady.then(() => {
      showCardDatabaseModal();
      overlay.classList.add('mm-fade-out');
      setTimeout(() => overlay.remove(), 400);
    });
  });
  overlay.querySelector('[data-action="analytics"]').addEventListener('click', () => {
    showComingSoonModal('Deck Analytics');
  });
}

// Builds #mm-top-left's contents based on Discord sign-in state — called
// once when the main menu mounts and again every time Supabase's auth state
// changes (sign-in completing after the OAuth redirect bounces back here,
// or signing out from inside Edit Profile) via window.Supa.onSessionChange.
// Signed OUT: just the Discord sign-in button, no profile icon/welcome text
// — there's no account yet to open Edit Profile "for". Signed IN: the usual
// profile icon + "Welcome X!" text, same as before Discord sign-in existed.
// Guarded by overlay.isConnected so a listener registered by a main menu
// instance the player already navigated away from doesn't keep touching
// detached DOM on every later auth event (onSessionChange has no
// unsubscribe — see its own definition in js/supabase.js).
function renderMainMenuTopLeft(overlay) {
  if (!overlay.isConnected) return;
  const container = overlay.querySelector('#mm-top-left');
  if (!container) return;
  const session = window.Supa && window.Supa.getSession();
  if (session && session.user) {
    const avatar = loadProfileData().avatar;
    container.innerHTML = `
      <button class="mm-icon-btn" id="mm-profile-btn" title="Edit Profile">
        ${avatar ? `<img class="mm-profile-avatar-img" src="${profileAvatarPath(avatar)}" alt="" />` : '<span class="material-symbols-outlined">account_circle</span>'}
      </button>
      <div class="mm-welcome" id="mm-welcome"></div>
    `;
    container.querySelector('#mm-welcome').textContent = `Welcome ${getEffectiveUsername()}!`;
    container.querySelector('#mm-profile-btn').addEventListener('click', () => showEditProfileModal());
  } else {
    container.innerHTML = `
      <button type="button" class="mm-discord-signin-btn" id="mm-discord-signin-btn">
        <img src="assets/social/discord_icon.webp" alt="" />
        Sign Up / Login
      </button>
    `;
    container.querySelector('#mm-discord-signin-btn').addEventListener('click', () => {
      window.Supa && window.Supa.signInWithDiscord();
    });
  }
}

// Generic "this isn't built yet" placeholder for main-menu buttons with no
// feature behind them yet (currently just Deck Analytics) — reuses the same
// .mk-overlay/.mk-modal shell as every other simple info modal (see
// showReportBugModal) so it matches whichever theme is active.
function showComingSoonModal(featureName) {
  const overlay = document.createElement('div');
  overlay.className = 'mk-overlay';
  overlay.style.zIndex = '13500';
  overlay.innerHTML = `
    <div class="mk-modal">
      <div class="mk-title">Coming Soon</div>
      <div class="igm-body">${escapeHtml(featureName)} isn't available yet, but it's on the way — stay tuned!</div>
      <button class="btn-primary" id="coming-soon-close-btn" type="button">Close</button>
    </div>
  `;
  document.body.appendChild(overlay);
  const close = () => overlay.remove();
  overlay.querySelector('#coming-soon-close-btn').addEventListener('click', close);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
}

// Shown when a signed-out guest clicks Save in the Deck Builder — the save
// itself still goes through (to the guest's single local slot, see
// GUEST_DECK_NAME in showDeckBuilder()), this is just a nudge toward
// creating an account. Reuses the Discord sign-in button styling from the
// main menu/Edit Profile (.profile-discord-signin-btn) rather than
// introducing a third variant of that button.
function showGuestSaveSignupModal() {
  const overlay = document.createElement('div');
  overlay.className = 'mk-overlay';
  overlay.style.zIndex = '13500';
  overlay.innerHTML = `
    <div class="mk-modal">
      <div class="mk-title">Save Your Deck</div>
      <div class="igm-body">Sign-up now to save your deck, edit your profile, change resource images, use alt-arts and more!</div>
      <button type="button" class="profile-discord-signin-btn" id="guest-save-signin-btn">
        <img src="assets/social/discord_icon.webp" alt="" />
        Sign Up / Login
      </button>
      <button class="btn-primary" id="guest-save-signup-close-btn" type="button">Maybe Later</button>
    </div>
  `;
  document.body.appendChild(overlay);
  const close = () => overlay.remove();
  overlay.querySelector('#guest-save-signin-btn').addEventListener('click', () => {
    window.Supa && window.Supa.signInWithDiscord();
  });
  overlay.querySelector('#guest-save-signup-close-btn').addEventListener('click', close);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
}

// Minimal settings popup — houses the light/dark theme toggle and the game
// SFX volume slider. Reuses the .mk-overlay/.mk-modal glassmorphic styling
// shared by the matchmaking/mulligan modals so it matches whichever theme is
// active.
function showSettingsModal() {
  const overlay = document.createElement('div');
  overlay.className = 'mk-overlay';
  overlay.style.zIndex = '13000';
  overlay.innerHTML = `
    <div class="mk-modal">
      <div class="mk-title">Settings</div>
      <div style="display:flex; align-items:center; gap:14px;">
        <span style="font-size:14px; color:var(--text-secondary); font-weight:600;">Theme</span>
        ${renderThemeToggleHTML()}
      </div>
      <div style="display:flex; align-items:center; gap:14px; width:100%;">
        <span style="font-size:14px; color:var(--text-secondary); font-weight:600;">Game SFX</span>
        ${renderSfxVolumeControlHTML()}
      </div>
      <div style="display:flex; align-items:center; gap:14px; width:100%; justify-content:space-between;">
        <span style="font-size:14px; color:var(--text-secondary); font-weight:600;">Show Resource Cards</span>
        ${renderResourceCardsToggleHTML()}
      </div>
      <div style="display:flex; align-items:center; gap:14px; width:100%; justify-content:space-between;">
        <span style="font-size:14px; color:var(--text-secondary); font-weight:600;">Show Update Notifications</span>
        ${renderShowUpdateNoticeToggleHTML()}
      </div>
      <button class="btn-primary" id="settings-close-btn">Close</button>
    </div>
  `;
  document.body.appendChild(overlay);
  wireThemeToggle(overlay);
  wireSfxVolumeControl(overlay);
  wireShowUpdateNoticeToggle(overlay);
  wireResourceCardsToggle(overlay);

  const close = () => overlay.remove();
  overlay.querySelector('#settings-close-btn').addEventListener('click', close);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
}

// ════════════════════════════════════════════════════════════════════════════
// EDIT PROFILE MODAL
// Username, persisted to localStorage under PROFILE_LS_KEY, plus the Discord
// account link (see renderAccountSection() inside showEditProfileModal() —
// Supabase-backed, js/supabase.js). The old Download/Upload Data JSON
// import-export pair that used to live here was removed now that signing in
// with Discord is the real backup path; getAllSavedDecksForExport() below is
// kept (just currently uncalled) since it'll likely feed a future Supabase
// deck-sync/migration flow instead.
// ════════════════════════════════════════════════════════════════════════════
const PROFILE_LS_KEY = 'gprofile';
const PROFILE_USERNAME_MAX_LEN = 20;
const PROFILE_BETA_CODE_MAX_LEN = 40; // matches #profile-beta-code-input's own maxlength

// Profile picture choices — the same bot_face artwork used for "Play Against
// Bot" opponent faces (js/bot_logic/decks/*.js's faceIcon), repurposed here
// as selectable player avatars. Hardcoded since there's no server-side
// directory listing available to a static front-end; keep in sync with
// assets/bot_face/*.webp.
const PROFILE_AVATAR_KEYS = [
  'academy', 'cb', 'marine', 'mf', 'minerva',
  'orb', 'purple_banshee', 'purple_neozeon', 'tekkadan', 'unicorn',
];
function profileAvatarPath(key) { return `assets/bot_face/${key}.webp`; }
function profileAvatarLabel(key) {
  return key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function loadProfileData() {
  try {
    const parsed = JSON.parse(localStorage.getItem(PROFILE_LS_KEY) || '{}');
    return {
      username: typeof parsed.username === 'string' ? parsed.username.slice(0, PROFILE_USERNAME_MAX_LEN) : '',
      // Beta Tester Code — see BETA_TESTER_CODES/isValidBetaTesterCode()
      // below and showDeckBuilderSettingsModal()'s Card Appearance section.
      // Stored as the raw entered value (not a boolean) so re-validating it
      // against an updated BETA_TESTER_CODES list needs no migration.
      betaCode: typeof parsed.betaCode === 'string' ? parsed.betaCode : '',
      // Chosen bot_face key (e.g. 'marine'), or '' for the generic fallback
      // icon. Re-validated against the current PROFILE_AVATAR_KEYS list so a
      // stale/removed key never produces a broken <img>.
      avatar: typeof parsed.avatar === 'string' && PROFILE_AVATAR_KEYS.includes(parsed.avatar) ? parsed.avatar : '',
    };
  } catch {
    return { username: '', betaCode: '', avatar: '' };
  }
}

function saveProfileData(profile) {
  localStorage.setItem(PROFILE_LS_KEY, JSON.stringify(profile));
}

// ── EFFECTIVE USERNAME ──────────────────────────────────────────────────────
// A player who hasn't set a real username (in Edit Profile, or by signing in
// with Discord) still needs SOME identity for the lobby member list, match
// log pills, and the username sent to server/relay.js — generic shared
// placeholders like "Pilot"/"Player 1" read as broken/anonymous, so instead
// everyone gets a random callsign (e.g. "EFSF-AB001") the moment one is
// needed. Deliberately sessionStorage, not localStorage — it's meant to be
// disposable, not a real identity; a player who wants a stable one sets a
// real username or signs in instead, either of which takes over permanently.
const TEMP_USERNAME_SS_KEY = 'gTempUsername';
const TEMP_USERNAME_FACTIONS = ['EFSF', 'ZEON'];

function generateTempUsername() {
  const faction = TEMP_USERNAME_FACTIONS[Math.floor(Math.random() * TEMP_USERNAME_FACTIONS.length)];
  const letters = Array.from({ length: 2 }, () => String.fromCharCode(65 + Math.floor(Math.random() * 26))).join('');
  const digits = String(Math.floor(Math.random() * 1000)).padStart(3, '0');
  return `${faction}-${letters}${digits}`;
}

// The single source of truth for "what is MY username right now" — a saved
// Edit Profile username always wins; otherwise a per-session random callsign
// is generated once and reused for the rest of this browser session.
function getEffectiveUsername() {
  const saved = loadProfileData().username.trim();
  if (saved) return saved;
  try {
    let temp = sessionStorage.getItem(TEMP_USERNAME_SS_KEY);
    if (!temp) {
      temp = generateTempUsername();
      sessionStorage.setItem(TEMP_USERNAME_SS_KEY, temp);
    }
    return temp;
  } catch {
    return generateTempUsername(); // storage unavailable (e.g. private browsing) — still functional, just not stable across calls
  }
}

// ── Card Appearance (Resource / EX Base / EX Resource skins) ──────────────
// Lets the player swap the default R-001/EXB-001/EXR-001 printing for
// another printing of the same card (R-*/RP-* for Resource, EXB-*/EXBP-*
// for EX Base, EXR-*/EXRP-* for EX Resource) — see showEditProfileModal()'s
// "Card Appearance" section. Persisted as cardNo strings, not image paths,
// so cardDb stays the single source of truth: swapping EX Base's art also
// swaps in that printing's real HP/effect via makeBaseCard() (e.g. EXB-002
// "Axis" is HP 5, not the standard EX Base's HP 3) — this is a full card
// substitution, not just a reskin. A subset of printings (see
// BETA_ONLY_APPEARANCE_CARDNOS below) are hidden from the picker grid
// unless a valid Beta Tester Code is entered.
const CARD_APPEARANCE_LS_KEY = 'nu-card-appearance';
const CARD_APPEARANCE_DEFAULTS = { resource: 'R-001', exBase: 'EXB-001', exResource: 'EXR-001' };

function loadCardAppearance() {
  let parsed = {};
  try { parsed = JSON.parse(localStorage.getItem(CARD_APPEARANCE_LS_KEY) || '{}'); } catch {}
  const out = { ...CARD_APPEARANCE_DEFAULTS };
  for (const key of Object.keys(CARD_APPEARANCE_DEFAULTS)) {
    if (typeof parsed[key] === 'string' && cardDb[parsed[key]]) out[key] = parsed[key];
  }
  return out;
}

function saveCardAppearanceSelection(key, cardNo) {
  const current = loadCardAppearance();
  current[key] = cardNo;
  try { localStorage.setItem(CARD_APPEARANCE_LS_KEY, JSON.stringify(current)); } catch {}
}

// ── Beta Tester Code (Edit Profile) ────────────────────────────────────────
// A perk gate, not real access control: entering one of these codes in Edit
// Profile unlocks a handful of promo/tester-only card appearance printings
// in showCardAppearancePicker() (see BETA_ONLY_APPEARANCE_CARDNOS). This is
// checked entirely client-side — there is no server component (the app's
// old server-side access-code gate was removed entirely) — so, like every
// other casual/friends-only gate this project uses, the valid codes are
// visible to anyone who reads this file. Add real codes here.
const BETA_TESTER_CODES = new Set([
  'CHANGE-ME',
]);
function isValidBetaTesterCode(code) {
  return BETA_TESTER_CODES.has(String(code || '').trim().toUpperCase());
}
// Reads the persisted profile's saved code — used wherever the appearance
// picker is opened without a live (not-yet-saved) input value on hand.
function hasBetaTesterAccess() {
  return isValidBetaTesterCode(loadProfileData().betaCode);
}

// Printings hidden from showCardAppearancePicker()'s grid unless
// hasBetaTesterAccess() (or a live, not-yet-saved code the player just
// typed in Edit Profile) is valid. Keyed by the same slot.key
// showDeckBuilderSettingsModal()'s APPEARANCE_SLOTS uses ('resource'/
// 'exBase'/'exResource'), each a cardNo -> boolean predicate.
const BETA_ONLY_EXBP_CARDNOS = new Set(['EXBP-001', 'EXBP-002', 'EXBP-004', 'EXBP-005', 'EXBP-011', 'EXBP-019']);
const BETA_ONLY_APPEARANCE_CARDNOS = {
  resource:   (cardNo) => cardNo.startsWith('RP-'),
  exBase:     (cardNo) => BETA_ONLY_EXBP_CARDNOS.has(cardNo),
  exResource: (cardNo) => cardNo.startsWith('EXRP-'),
};

// ── Favorite Cards (Deck Builder browse panel) ─────────────────────────────
// A per-player preference, like Card Appearance above, not deck data — a
// card can be favorited independent of any deck it does/doesn't appear in.
// Persisted as an array of cardNo strings; included in the Edit Profile
// Download/Upload Data pair (see showEditProfileModal()) so it survives a
// browser wipe alongside the rest of the profile.
const FAVORITE_CARDS_LS_KEY = 'nu-favorite-cards';
function loadFavoriteCards() {
  try {
    const parsed = JSON.parse(localStorage.getItem(FAVORITE_CARDS_LS_KEY) || '[]');
    return new Set(Array.isArray(parsed) ? parsed.filter(c => typeof c === 'string') : []);
  } catch { return new Set(); }
}
function saveFavoriteCards(set) {
  try { localStorage.setItem(FAVORITE_CARDS_LS_KEY, JSON.stringify([...set])); } catch {}
}

// Per-lobby win/loss tally, keyed by lobby code. The server is authoritative
// for the live shared view broadcast to everyone in LOBBY_STATE — this is
// purely a per-player cache so a page refresh (or briefly rejoining after a
// match) doesn't reset a player's own count back to 0 before the lobby's
// creator closes it, at which point the entry is cleared.
const LOBBY_RECORD_LS_PREFIX = 'nu-lobby-record:';
function loadLobbyRecord(code) {
  try {
    const parsed = JSON.parse(localStorage.getItem(LOBBY_RECORD_LS_PREFIX + code) || '{}');
    return {
      wins: Number.isFinite(parsed.wins) ? parsed.wins : 0,
      losses: Number.isFinite(parsed.losses) ? parsed.losses : 0,
    };
  } catch { return { wins: 0, losses: 0 }; }
}
function saveLobbyRecord(code, tally) {
  try { localStorage.setItem(LOBBY_RECORD_LS_PREFIX + code, JSON.stringify(tally)); } catch {}
}
function clearLobbyRecord(code) {
  try { localStorage.removeItem(LOBBY_RECORD_LS_PREFIX + code); } catch {}
}

// The full cardDb entry for a given cardNo, falling back to the given slot's
// hardcoded default entry (and finally a bare stand-in) if cardDb isn't
// loaded yet, cardNo is null/missing, or it no longer exists in cardDb.
function resolveCardAppearanceEntry(cardNo, key) {
  return cardDb[cardNo] || cardDb[CARD_APPEARANCE_DEFAULTS[key]] || { cardNo, image: `assets/images/resource/${CARD_APPEARANCE_DEFAULTS[key]}.webp`, name: key };
}

// My own current selection for a slot.
function getCardAppearanceEntry(key) {
  return resolveCardAppearanceEntry(loadCardAppearance()[key], key);
}

// The peer's selection for a slot, as received via the CARD_APPEARANCE
// network message (js/network.js) into state.oppCardAppearance — falls back
// to the standard printing if that message hasn't arrived yet (e.g. solo
// practice, or briefly at match start).
function getOppCardAppearanceEntry(key) {
  return resolveCardAppearanceEntry(state.oppCardAppearance?.[key], key);
}

// Rebuilds a saved deck's full per-card entry shape (the same fields
// buildDeckJson() writes to localStorage — image/name/level/etc., all
// otherwise just a cached copy of cardDb[cardNo]) from nothing but a
// cardNo + copy count. Used to re-enrich a profile-imported deck (which only
// carries cardNo/copies — see getAllSavedDecksForExport) back into the
// shape the rest of the app (deck list color pills, compliance checks, the
// deck builder itself) expects a gdeck:* entry to have. Returns null if the
// card isn't in the (already-loaded) database — e.g. reprinted/renumbered
// since the file was exported.
function buildDeckEntryFromDb(cardNo, copies) {
  const e = cardDb[cardNo];
  if (!e) return null;
  return {
    cardNo:       e.cardNo       || cardNo,
    image:        e.image        || '',
    name:         e.name         || '',
    level:        e.level        || '',
    cost:         e.cost         || '',
    categoryData: e.categoryData || '',
    pilot:        e.pilot        || '',
    link:         e.link         || '',
    ap:           e.ap           || '',
    hp:           e.hp           || '',
    effectData:   e.effectData   || '',
    color:        e.color        || '',
    trait:        e.trait        || '',
    alt:          e.alt          || '',
    zone:         e.zone         || '',
    copies:       String(copies),
  };
}

// Trims each saved deck down to just cardNo/copies for the profile export
// file — everything else buildDeckEntryFromDb() re-derives from cardDb on
// import, so shipping it in the JSON is pure duplication of data the app
// already has bundled (and keeps a many-deck profile export small).
function getAllSavedDecksForExport() {
  const decks = {};
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith('gdeck:')) continue;
    try {
      const parsed = JSON.parse(localStorage.getItem(key));
      if (!Array.isArray(parsed)) continue;
      decks[key.slice('gdeck:'.length)] = parsed
        .filter(c => c && c.cardNo)
        .map(c => ({ cardNo: c.cardNo, copies: c.copies }));
    } catch {}
  }
  return decks;
}

function showEditProfileModal() {
  const overlay = document.createElement('div');
  overlay.id = 'edit-profile-overlay';
  overlay.className = 'mk-overlay';
  overlay.style.zIndex = '13000';

  const profile = loadProfileData();

  overlay.innerHTML = `
    <div class="mk-modal profile-modal">
      <div class="mk-title">Edit Profile</div>

      <div class="profile-account-section" id="profile-account-section"></div>

      <div class="profile-field">
        <label class="profile-field-label">Profile Picture</label>
        <div class="profile-avatar-row">
          <div class="profile-avatar-preview" id="profile-avatar-preview"></div>
          <button type="button" class="btn-secondary" id="profile-avatar-choose-btn">Choose</button>
        </div>
      </div>

      <div class="profile-field">
        <label class="profile-field-label" for="profile-username-input">Change Username</label>
        <input type="text" id="profile-username-input" class="db-modal-input" placeholder="${escapeHtml(getEffectiveUsername())}" maxlength="20" />
      </div>

      <div class="profile-field">
        <label class="profile-field-label" for="profile-beta-code-input">Beta Tester Code</label>
        <input type="text" id="profile-beta-code-input" class="db-modal-input" placeholder="Optional — unlocks exclusive card appearances" maxlength="40" autocomplete="off" autocapitalize="off" spellcheck="false" />
        <div class="profile-beta-code-status" id="profile-beta-code-status"></div>
      </div>

      <div class="igm-btn-row">
        <button class="btn-secondary" id="profile-close-btn" type="button">Close</button>
        <button class="btn-primary" id="profile-save-btn" type="button">Save</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  // Discord account link/unlink — separate from the local username/beta-code
  // fields below, which stay purely localStorage-backed for now. Re-renders
  // whenever auth state changes (sign-in fires it after the OAuth redirect
  // bounces back to this page; sign-out fires it immediately) — guarded by
  // overlay.isConnected so a listener from a modal the player already closed
  // doesn't keep touching detached DOM on every future auth event.
  const accountSection = overlay.querySelector('#profile-account-section');
  function renderAccountSection() {
    if (!overlay.isConnected) return;
    const session = window.Supa && window.Supa.getSession();
    const user = session && session.user;
    if (user) {
      const meta = user.user_metadata || {};
      const displayName = meta.full_name || meta.name || user.email || 'Discord Pilot';
      accountSection.innerHTML = `
        <div class="profile-account-linked">
          ${meta.avatar_url ? `<img class="profile-account-avatar" src="${meta.avatar_url}" alt="" />` : '<span class="material-symbols-outlined profile-account-avatar-fallback">account_circle</span>'}
          <div class="profile-account-info">
            <div class="profile-account-name">${escapeHtml(displayName)}</div>
            <div class="profile-account-status">Signed in with Discord</div>
          </div>
          <button type="button" class="btn-secondary" id="profile-account-signout-btn">Sign Out</button>
        </div>
      `;
      accountSection.querySelector('#profile-account-signout-btn').addEventListener('click', () => {
        window.Supa.signOut();
        close();
      });
    } else {
      accountSection.innerHTML = `
        <button type="button" class="profile-discord-signin-btn" id="profile-account-signin-btn">
          <img src="assets/social/discord_icon.webp" alt="" />
          Sign in with Discord
        </button>
        <div class="db-modal-hint">Link a Discord account to save your profile and decks to the cloud instead of just this browser.</div>
      `;
      accountSection.querySelector('#profile-account-signin-btn').addEventListener('click', () => {
        window.Supa && window.Supa.signInWithDiscord();
      });
    }
  }
  renderAccountSection();
  if (window.Supa) window.Supa.onSessionChange(renderAccountSection);

  const usernameInput = overlay.querySelector('#profile-username-input');
  usernameInput.value = profile.username;

  const betaCodeInput = overlay.querySelector('#profile-beta-code-input');
  const betaCodeStatus = overlay.querySelector('#profile-beta-code-status');
  betaCodeInput.value = profile.betaCode;

  function renderBetaCodeStatus() {
    const raw = betaCodeInput.value.trim();
    if (!raw) { betaCodeStatus.textContent = ''; betaCodeStatus.className = 'profile-beta-code-status'; return; }
    const valid = isValidBetaTesterCode(raw);
    betaCodeStatus.textContent = valid
      ? '✓ Valid code — exclusive card appearances unlocked.'
      : 'Code not recognized.';
    betaCodeStatus.className = 'profile-beta-code-status' + (valid ? ' profile-beta-code-valid' : ' profile-beta-code-invalid');
  }
  renderBetaCodeStatus();
  betaCodeInput.addEventListener('input', renderBetaCodeStatus);

  // Profile picture — deferred like username/betaCode above (only actually
  // persisted on Save, not the moment a tile is picked).
  let pendingAvatar = profile.avatar;
  const avatarPreview = overlay.querySelector('#profile-avatar-preview');
  function renderAvatarPreview() {
    avatarPreview.innerHTML = pendingAvatar
      ? `<img src="${profileAvatarPath(pendingAvatar)}" alt="" />`
      : '<span class="material-symbols-outlined">account_circle</span>';
  }
  renderAvatarPreview();
  overlay.querySelector('#profile-avatar-choose-btn').addEventListener('click', () => {
    showProfileAvatarPicker(pendingAvatar, (key) => {
      pendingAvatar = key;
      renderAvatarPreview();
    });
  });

  const close = () => overlay.remove();
  overlay.querySelector('#profile-close-btn').addEventListener('click', close);
  overlay.addEventListener('click', e => { if (e.target === overlay) close(); });

  overlay.querySelector('#profile-save-btn').addEventListener('click', () => {
    const username = usernameInput.value.trim().slice(0, PROFILE_USERNAME_MAX_LEN);
    const betaCode = betaCodeInput.value.trim();
    saveProfileData({ username, betaCode, avatar: pendingAvatar });
    // The main menu's own profile icon (#mm-profile-btn, built by
    // renderMainMenuTopLeft()) only re-renders on an auth state change —
    // an avatar change alone wouldn't trigger one, so it's patched directly
    // here using the exact same markup that function would produce.
    const mainMenuProfileBtn = document.getElementById('mm-profile-btn');
    if (mainMenuProfileBtn) {
      mainMenuProfileBtn.innerHTML = pendingAvatar
        ? `<img class="mm-profile-avatar-img" src="${profileAvatarPath(pendingAvatar)}" alt="" />`
        : '<span class="material-symbols-outlined">account_circle</span>';
    }
    // Cloud push — so this username/avatar follows the account to another
    // browser/device (pulled back down by syncProfileFromCloud() at sign-in
    // there). Fire-and-forget past the local save, same UX convention as
    // saveDeckToStorage()'s cloud push: local save always "succeeds"
    // immediately, a failed cloud sync just surfaces as a toast instead of
    // blocking Close.
    if (window.Supa && window.Supa.getSession()) {
      window.Supa.saveCloudProfile({ username, avatar: pendingAvatar }).then(({ ok }) => {
        if (!ok) showToast('Profile saved locally, but the cloud sync failed — it may not follow you to another device.', true);
      });
    }
    showToast('Profile saved!');
    close();
  });

}

// Grid picker for showEditProfileModal()'s Profile Picture "Choose" button —
// every PROFILE_AVATAR_KEYS entry (the bot_face artwork also used for "Play
// Against Bot" opponent faces). Unlike showCardAppearancePicker below,
// selecting a tile here doesn't persist anything itself — it just reports
// back via onSelected(key) so the caller (still deferred to its own Save
// button, like username/betaCode) can update its pending value.
function showProfileAvatarPicker(currentKey, onSelected) {
  const overlay = document.createElement('div');
  overlay.className = 'mk-overlay';
  overlay.style.zIndex = '13500'; // above the Edit Profile modal (13000) it was opened from

  const modal = document.createElement('div');
  modal.className = 'mk-modal bop-modal';
  modal.innerHTML = `
    <div class="mk-title">Choose Profile Picture</div>
    <div class="bop-grid">
      ${PROFILE_AVATAR_KEYS.map(key => `
        <div class="bop-tile${key === currentKey ? ' bop-selected' : ''}" data-key="${key}">
          <div class="cp-img-wrap">
            <img src="${profileAvatarPath(key)}" alt="${profileAvatarLabel(key)}" />
          </div>
          <div class="bop-name">${profileAvatarLabel(key)}</div>
        </div>`).join('')}
    </div>
    <div class="igm-btn-row">
      <button class="btn-secondary" id="avatar-picker-close" type="button">Close</button>
    </div>
  `;
  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  modal.querySelectorAll('.bop-tile').forEach(tile => {
    tile.addEventListener('click', () => {
      onSelected(tile.dataset.key);
      overlay.remove();
    });
  });
  const close = () => overlay.remove();
  modal.querySelector('#avatar-picker-close').addEventListener('click', close);
  overlay.addEventListener('click', e => { if (e.target === overlay) close(); });
}

// Grid picker for showDeckBuilderSettingsModal()'s Card Appearance "Change"
// buttons — every cardDb entry whose cardNo starts with one of slot.prefixes (e.g.
// ['R-','RP-'] for Resource), sorted numerically. Every matching entry is
// shown — one BETA_ONLY_APPEARANCE_CARDNOS[key] flags as tester-only is
// rendered greyed out and unselectable instead of being left out of the
// grid, unless unlocked. Selecting an unlocked tile saves it via
// saveCardAppearanceSelection() and calls onSelected() so the caller can
// refresh its own preview.
// liveBetaCode: the Edit Profile modal's own Beta Tester Code input value at
// the moment "Change" was clicked, checked in addition to the saved profile
// value — so entering a code and immediately opening the picker unlocks it
// without having to Save the profile first.
function showCardAppearancePicker(slot, onSelected, liveBetaCode = '') {
  const { key, label, prefixes } = slot;
  const isBetaOnly = BETA_ONLY_APPEARANCE_CARDNOS[key] || (() => false);
  const betaUnlocked = isValidBetaTesterCode(liveBetaCode) || hasBetaTesterAccess();
  const options = Object.values(cardDb)
    .filter(e => prefixes.some(p => (e.cardNo || '').startsWith(p)))
    .sort((a, b) => (a.cardNo || '').localeCompare(b.cardNo || '', undefined, { numeric: true }));
  const lockedCount = options.filter(e => isBetaOnly(e.cardNo || '')).length;
  const currentCardNo = loadCardAppearance()[key];

  const overlay = document.createElement('div');
  overlay.className = 'mk-overlay';
  overlay.style.zIndex = '13500'; // above the Edit Profile modal (13000) it was opened from

  const modal = document.createElement('div');
  modal.className = 'mk-modal bop-modal';
  modal.innerHTML = `
    <div class="mk-title">Choose ${label} Art</div>
    ${lockedCount > 0 && !betaUnlocked ? `<div class="db-modal-hint">${lockedCount} tester-exclusive printing${lockedCount !== 1 ? 's' : ''} greyed out — enter a valid Beta Tester Code above and reopen this picker to unlock ${lockedCount !== 1 ? 'them' : 'it'}.</div>` : ''}
    <div class="bop-grid">
      ${options.map(entry => {
        const locked = !betaUnlocked && isBetaOnly(entry.cardNo || '');
        return `
        <div class="bop-tile${entry.cardNo === currentCardNo ? ' bop-selected' : ''}${locked ? ' bop-unselectable bop-tile-locked' : ''}" data-cardno="${entry.cardNo}">
          <div class="cp-img-wrap">
            ${entry.image ? `<img src="${entry.image}" alt="${entry.name}" />` : '<div class="cp-no-img">?</div>'}
            ${locked ? '<span class="material-symbols-outlined bop-lock-icon">lock</span>' : ''}
          </div>
          <div class="bop-name">${entry.name}</div>
          <div class="bop-subname">${entry.cardNo}</div>
        </div>`;
      }).join('')}
    </div>
    <div class="igm-btn-row">
      <button class="btn-secondary" id="appearance-picker-close" type="button">Close</button>
    </div>
  `;
  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  modal.querySelectorAll('.bop-tile').forEach(tile => {
    tile.addEventListener('click', () => {
      if (tile.classList.contains('bop-tile-locked')) {
        showToast('Enter a valid Beta Tester Code in your profile to unlock this printing.', true);
        return;
      }
      saveCardAppearanceSelection(key, tile.dataset.cardno);
      overlay.remove();
      onSelected();
    });
  });
  modal.querySelector('#appearance-picker-close').addEventListener('click', () => overlay.remove());
  overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });
}

// ════════════════════════════════════════════════════════════════════════════
// DECK BUILDER SETTINGS MODAL
// Opened from the deck builder header's Settings button (showDeckBuilder()).
// Distinct from the main showSettingsModal() (Theme/Game SFX/Show Resource
// Cards — reachable from the main menu and in-game menu): this one groups
// the settings a player is most likely to want without leaving the deck
// builder —
//   - Card Appearance: MOVED here from Edit Profile (showEditProfileModal())
//     — no longer shown there at all.
//   - Theme: a second copy of the same toggle showSettingsModal() has —
//     that one is kept too, this just saves a trip out of the deck builder.
//   - Auto save decks in deck builder: MOVED here from showSettingsModal()
//     — it only ever mattered inside the deck builder anyway.
// The Card Appearance picker (showCardAppearancePicker) is opened with no
// live Beta Tester Code (that input only exists in Edit Profile) — it falls
// back to the persisted profile's own code via hasBetaTesterAccess().
// `onClose` (optional): called after the modal is dismissed, however that
// happens (Close button or clicking outside) — showDeckBuilder() uses this
// to refresh #db-unsaved-warning immediately in case Auto Save was flipped
// mid-session, rather than waiting for the player's next deck edit.
// ════════════════════════════════════════════════════════════════════════════
function showDeckBuilderSettingsModal(onClose) {
  const overlay = document.createElement('div');
  overlay.className = 'mk-overlay';
  overlay.style.zIndex = '13000'; // same as showSettingsModal() — showCardAppearancePicker() stacks at 13500, above either
  overlay.innerHTML = `
    <div class="mk-modal db-settings-modal">
      <div class="mk-title">Deck Builder Settings</div>

      <div class="profile-field">
        <label class="profile-field-label">Card Appearance</label>
        <div class="profile-appearance-grid" id="dbs-appearance-grid"></div>
      </div>

      <div style="display:flex; align-items:center; gap:14px;">
        <span style="font-size:14px; color:var(--text-secondary); font-weight:600;">Theme</span>
        ${renderThemeToggleHTML()}
      </div>
      <div style="display:flex; align-items:center; gap:14px; width:100%; justify-content:space-between;">
        <span style="font-size:14px; color:var(--text-secondary); font-weight:600;">Auto-Save Deck on "Back"/"Quick Play"</span>
        ${renderAutoSaveDecksToggleHTML()}
      </div>

      <button class="btn-primary" id="dbs-close-btn" type="button">Close</button>
    </div>
  `;
  document.body.appendChild(overlay);
  wireThemeToggle(overlay);
  wireAutoSaveDecksToggle(overlay);

  // Same 3 slots/prefixes as showEditProfileModal() used to render before
  // Card Appearance moved here — see APPEARANCE_SLOTS's original comment.
  const APPEARANCE_SLOTS = [
    { key: 'resource',   label: 'Resource',    prefixes: ['R-', 'RP-'] },
    { key: 'exBase',     label: 'EX Base',     prefixes: ['EXB-', 'EXBP-'] },
    { key: 'exResource', label: 'EX Resource', prefixes: ['EXR-', 'EXRP-'] },
  ];
  function renderAppearanceSlots() {
    const grid = overlay.querySelector('#dbs-appearance-grid');
    if (!grid) return;
    grid.innerHTML = APPEARANCE_SLOTS.map(({ key, label }) => {
      const entry = getCardAppearanceEntry(key);
      return `
        <div class="profile-appearance-slot">
          <div class="profile-appearance-imgwrap">
            ${entry.image ? `<img src="${entry.image}" alt="${entry.name || label}" />` : '<div class="cp-no-img">?</div>'}
          </div>
          <div class="profile-appearance-name">${label}</div>
          <div class="profile-appearance-cardno">${entry.cardNo || ''}</div>
          <button type="button" class="btn-secondary profile-appearance-change-btn" data-key="${key}">Change</button>
        </div>`;
    }).join('');
    grid.querySelectorAll('.profile-appearance-change-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const slot = APPEARANCE_SLOTS.find(s => s.key === btn.dataset.key);
        showCardAppearancePicker(slot, () => renderAppearanceSlots());
      });
    });
  }
  renderAppearanceSlots();

  const close = () => { overlay.remove(); onClose?.(); };
  overlay.querySelector('#dbs-close-btn').addEventListener('click', close);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
}

// Discord invite link — see showReportBugModal() below.
const DISCORD_INVITE_URL = 'https://discord.gg/b6y4dsyU4j';

// "Report Bug" button (main menu, deck builder, in-game menu — all three
// wire straight to this same function) no longer collects a description/
// screenshot and uploads them to this server. Bug reports now go through the
// Discord server instead, so this just points players there — same button,
// same name, new destination.
function showReportBugModal() {
  const overlay = document.createElement('div');
  overlay.id = 'report-bug-overlay';
  overlay.className = 'mk-overlay';
  overlay.style.zIndex = '21000';
  overlay.innerHTML = `
    <div class="mk-modal rb-modal">
      <div class="mk-title">Join Our Discord</div>
      <div class="igm-body">Report bugs and get the latest updates on the game by joining our Discord community.</div>
      <a class="btn-primary rb-discord-link" href="${DISCORD_INVITE_URL}" target="_blank" rel="noopener">Join Discord</a>
      <button class="btn-secondary" id="rb-close-btn" type="button">Close</button>
    </div>
  `;
  document.body.appendChild(overlay);

  const close = () => overlay.remove();
  overlay.querySelector('#rb-close-btn').addEventListener('click', close);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
}

// Deck Builder "Help" button (header, right of Report Bug) — static
// instructions, no deck-builder state needed, so this is a plain top-level
// function rather than nested inside showDeckBuilder(). Reuses the same
// db-modal-overlay/db-modal-box pattern as Import/Export via Text (see
// showExportTextModal() below) since it's deck-builder-specific, rather than
// showReportBugModal()'s generic mk-overlay/mk-modal (shared with the main
// menu/in-game menu). The favorite and add-to-sideboard rows render the
// actual icon glyph (not a text label) ahead of their description, per the
// icon buttons they're documenting.
function showDeckBuilderHelpModal() {
  const existing = document.getElementById('db-help-modal-overlay');
  if (existing) { existing.remove(); return; }

  const textRow = (label, desc) => `<div class="db-help-row"><strong>${label}</strong> ${desc}</div>`;
  // Icon rows swap the text label for the actual button glyph, since the
  // icon itself IS what the player sees in the card pool.
  const iconRow = (icon, desc) => `
    <div class="db-help-row db-help-row-icon">
      <span class="material-symbols-outlined" data-ic="${icon}">${icon}</span> ${desc}
    </div>`;

  const ov = document.createElement('div');
  ov.id = 'db-help-modal-overlay';
  ov.className = 'db-modal-overlay';
  ov.innerHTML = `
    <div class="db-modal-box db-help-modal">
      <div class="db-modal-title">How to Use the Deck Builder</div>
      ${textRow('Left-Click:', 'In the card pool, add 1 copy of a card to your deck. On a card already in your deck or sideboard, remove 1 copy.')}
      ${textRow('Right-Click:', 'In the card pool, add up to 4 copies of a card to your deck at once. On a card already in your deck or sideboard, remove all copies at once.')}
      ${iconRow('favorite', 'Click to favorite a card, then filter down to just your favorites with the "Favorites" button.')}
      ${textRow('Hide New Button:', 'Hides cards with the NEW icon, which marks cards that haven’t been physically released yet and aren’t available for purchase.')}
      ${textRow('Drag-and-Drop:', 'Drag any card from the card pool into either your main deck or your sideboard to add 1 copy there. Drag a card between your deck and sideboard to move 1 copy across.')}
      ${iconRow('add_column_right', 'Click this button on a card in the pool to add 1 copy of it to your sideboard.')}
      <div class="db-modal-btns">
        <button class="db-modal-btn db-modal-cancel">Close</button>
      </div>
    </div>`;
  document.body.appendChild(ov);

  const close = () => ov.remove();
  ov.querySelector('.db-modal-cancel').addEventListener('click', close);
  ov.addEventListener('click', e => { if (e.target === ov) close(); });
  document.addEventListener('keydown', function onEsc(e) {
    if (e.key === 'Escape') { close(); document.removeEventListener('keydown', onEsc); }
  });
}

// Shared stacking container for all mm-toast instances (showToast/showToastHtml/
// showPersistentToast) — appending here instead of straight to <body> means
// repeated triggers stack vertically instead of rendering on top of each other.
function getToastStack() {
  let stack = document.getElementById('mm-toast-stack');
  if (!stack) {
    stack = document.createElement('div');
    stack.id = 'mm-toast-stack';
    document.body.appendChild(stack);
  }
  return stack;
}

function showToast(msg, isError = false) {
  const t = document.createElement('div');
  t.className = 'mm-toast' + (isError ? ' mm-toast-error' : '');
  t.textContent = msg;
  getToastStack().appendChild(t);
  setTimeout(() => t.classList.add('mm-toast-show'), 10);
  setTimeout(() => { t.classList.remove('mm-toast-show'); setTimeout(() => t.remove(), 300); }, 2200);
}

// Same as showToast() but accepts trusted inline HTML (e.g. a hyperlink) and
// stays up long enough to actually click it before auto-dismissing.
function showToastHtml(html, isError = false) {
  const t = document.createElement('div');
  t.className = 'mm-toast' + (isError ? ' mm-toast-error' : '');
  t.innerHTML = html;
  getToastStack().appendChild(t);
  setTimeout(() => t.classList.add('mm-toast-show'), 10);
  setTimeout(() => { t.classList.remove('mm-toast-show'); setTimeout(() => t.remove(), 300); }, 6000);
}

let _persistentToastEl = null;

function showPersistentToast(msg) {
  if (_persistentToastEl) _persistentToastEl.remove();
  const t = document.createElement('div');
  t.className = 'mm-toast';
  t.textContent = msg;
  getToastStack().appendChild(t);
  setTimeout(() => t.classList.add('mm-toast-show'), 10);
  _persistentToastEl = t;
}

// Shared by showCardToast/showEffectActivatedToast: creates the toast
// element in #card-toast-stack, runs it through the show/hide animation,
// and self-removes. `setContent(el)` fills in the toast's own body —
// textContent for the plain trash/hand toasts, innerHTML for the richer
// effect-activated toast (which needs the actor-name span + keyword badge).
function spawnToast(extraClass, setContent) {
  let stack = document.getElementById('card-toast-stack');
  if (!stack) {
    stack = document.createElement('div');
    stack.id = 'card-toast-stack';
    document.body.appendChild(stack);
  }
  const t = document.createElement('div');
  t.className = 'card-toast ' + extraClass;
  setContent(t);
  stack.appendChild(t);
  requestAnimationFrame(() => requestAnimationFrame(() => t.classList.add('ct-show')));
  setTimeout(() => {
    t.classList.remove('ct-show');
    setTimeout(() => {
      t.remove();
      if (stack.isConnected && stack.children.length === 0) stack.remove();
    }, 300);
  }, 3000);
}

// imgPath (optional): a small thumbnail of the card the toast is about —
// trash and destroy toasts pass the card's own .img so the toast shows
// which specific card left play, not just its name in text.
function showCardToast(msg, imgPath) {
  const isTrash = msg.includes('trash') || msg.includes('destroyed');
  spawnToast(isTrash ? 'ct-trash' : 'ct-hand', t => {
    if (imgPath) {
      t.classList.add('ct-has-thumb');
      t.innerHTML = `<img class="ct-thumb" src="${imgPath}" alt="" />` +
                    `<span class="ct-thumb-text">${escapeHtml(msg)}</span>`;
    } else {
      t.textContent = msg;
    }
  });
}

function broadcastCardToast(msg, imgPath) {
  showCardToast(msg, imgPath);
  if (state.networked) NET.send({ type: 'CARD_TOAST', msg, imgPath });
}

// Adds a log line locally AND mirrors it verbatim to the opponent, for
// events that are public knowledge (a card discarded as another card's
// cost/effect, effect damage dealt to a Unit/Base, a destroy) even though
// only the acting client's own code path ever computes them — without this,
// only the player whose card caused the event ever saw a log line for it at
// all. The receiving side's LOG_MIRROR handler just calls addLog(msg)
// directly; it's already wrapped with currentLogActor = 'opp' by the shared
// message listener (see network.js), so side attribution (whose name shows
// on the pill) flips correctly with no extra work needed here.
function broadcastLog(msg, opts) {
  addLog(msg, opts);
  if (state.networked) NET.send({ type: 'LOG_MIRROR', msg, opts });
}

// Converts a bare keyword (e.g. "When Paired", "Deploy") into the same
// <span class="effect-keyword" data-kw="..."> badge markup/styling used
// in card effect text throughout the app (see cleanEffect()) — kept in sync
// with that function's data-kw derivation so a keyword badge always looks
// identical whether it's on a card or in a toast.
function effectKeywordBadge(kw) {
  let dataKw = kw.toLowerCase().replace(/[\s·･・･\/]+/g, '-').replace(/[^a-z0-9-]/g, '');
  if (kw.startsWith('During Pair')) dataKw = 'during-pair';
  if (kw.startsWith('When Paired')) dataKw = 'when-paired';
  return `<span class="effect-keyword" data-kw="${dataKw}">${kw}</span>`;
}

// Toast shown whenever a registered card-effect trigger with a well-known
// bracket keyword (e.g. "[When Paired]", "[Deploy]") actually fires — see
// the trigger→keyword map in triggerCardEffect(). actorName is styled via
// .toast-actor-name (theme-aware blue); keyword reuses the exact card-text
// badge styling via effectKeywordBadge().
function showEffectActivatedToast(actorName, keyword) {
  spawnToast('ct-effect', t => {
    t.innerHTML = `<span class="toast-actor-name">${actorName}</span> ${effectKeywordBadge(keyword)} effect activated.`;
  });
}

// Deliberately does NOT call showEffectActivatedToast() locally — the
// owner already sees their own card resolving right there on their own
// board, so a toast announcing what their own card just did is redundant.
// Only the opponent's client ever shows this one, via the EFFECT_ACTIVATED_
// TOAST remote handler in network.js. (Trash/destroy toasts are the
// exception to "own-side toasts are muted" — see broadcastCardToast/
// showCardToast's destroy call sites — since those are board-state changes
// worth flagging regardless of whose card it was.)
function broadcastEffectActivatedToast(actorName, keyword) {
  if (state.networked) NET.send({ type: 'EFFECT_ACTIVATED_TOAST', actorName, keyword });
}

function clearPersistentToast() {
  if (_persistentToastEl) {
    _persistentToastEl.classList.remove('mm-toast-show');
    setTimeout(() => { if (_persistentToastEl) { _persistentToastEl.remove(); _persistentToastEl = null; } }, 300);
  }
}

// ── Hide-modal-for-field-view ─────────────────────────────────────────────
// Lets either player temporarily hide a picker/waiting modal so they can
// inspect the board state. Gameplay interactions are blocked while hidden;
// hover card-preview still works. A document-level bubble listener restores
// the modal on the player's NEXT click anywhere on the page, except a small
// denylist of controls that are meant to be usable without bringing the
// modal back first (checking either player's trash, the in-game menu, the
// log, and reporting a bug).
let _hiddenModalEl = null;
let _modalRestoreListener = null;

function hideModalForFieldView(overlayEl) {
  _hiddenModalEl = overlayEl;
  overlayEl.style.display = 'none';
  state.modalHidden = true;
  showPersistentToast('Click anywhere to restore the modal.');
  // Freeze the 90s auto-resolve countdown while the player can't even see
  // the modal to act on it — otherwise it keeps ticking in the background
  // and can auto-pick their choice for them before they come back.
  pauseSelectionTimer();

  if (_modalRestoreListener) {
    document.removeEventListener('click', _modalRestoreListener);
  }
  _modalRestoreListener = (e) => {
    // Trash/exile buttons (either side), in-game menu, log, and report-bug:
    // usable without restoring the hidden modal first.
    if (e.target.closest('#player-trash-btn, #opp-trash-btn, #player-exile-btn, #opp-exile-btn, .btn-menu, #btn-log, #btn-report-bug')) return;
    // Any other currently-visible overlay (e.g. the trash viewer or the
    // report-bug/Discord modal that one of those buttons just opened):
    // interacting there shouldn't also restore the hidden modal underneath.
    if (e.target.closest('[id$="-overlay"]')) return;
    restoreHiddenModal();
  };
  document.addEventListener('click', _modalRestoreListener);
}

function restoreHiddenModal() {
  if (_hiddenModalEl) {
    if (document.body.contains(_hiddenModalEl)) {
      _hiddenModalEl.style.display = '';
    }
    _hiddenModalEl = null;
  }
  state.modalHidden = false;
  clearPersistentToast();
  if (_modalRestoreListener) {
    document.removeEventListener('click', _modalRestoreListener);
    _modalRestoreListener = null;
  }
  resumeSelectionTimer();
}

// ════════════════════════════════════════════════════════════════════════════
// SELECTION TIMER
// Every modal that asks the LOCAL player to pick/confirm something gets a
// 90-second countdown. If it expires before the player acts, the modal
// auto-resolves itself: click the skip button if one exists, otherwise pick
// the first selectable option and confirm it. Only one selection timer is
// ever live — starting a new one (i.e. a new modal instance) cancels
// whichever one was still running, satisfying "resets on each new modal".
// Cleanup piggybacks on each modal's own `overlay.remove()` call (every
// resolution path already does this) via a MutationObserver, so individual
// button handlers don't need to know the timer exists.
// ════════════════════════════════════════════════════════════════════════════
const SELECTION_TIMER_SECONDS = 90;
let _selectionTimerState = null; // { intervalId, badge, observer }

function clearSelectionTimer() {
  if (!_selectionTimerState) return;
  clearInterval(_selectionTimerState.intervalId);
  _selectionTimerState.observer.disconnect();
  _selectionTimerState.badge.remove();
  _selectionTimerState = null;
}

// overlay: the modal's root DOM element — once removed from the document
// (by any resolution path, including this timer's own auto-resolve click),
// the timer tears itself down.
// onExpire(overlay): performs the DOM interaction(s) that auto-resolve the
// modal (clicking real buttons so the modal's own handlers run normally).
function startSelectionTimer(overlay, onExpire) {
  clearSelectionTimer(); // only one selection prompt is ever pending at a time

  // Every call site follows the same shape — build the visible modal "box",
  // `overlay.appendChild(box)`, then `document.body.appendChild(overlay)` —
  // so the box is always overlay's first (only) element child at this point.
  // Anchoring the badge there (each box already sets position:relative, the
  // same precondition .modal-hide-btn relies on) pins the countdown to the
  // modal card itself instead of floating separately at the top of the
  // whole screen.
  const box = overlay.firstElementChild || overlay;
  const badge = document.createElement('div');
  badge.className = 'selection-timer-badge';
  box.appendChild(badge);

  let remaining = SELECTION_TIMER_SECONDS;
  const renderBadge = () => {
    badge.textContent = `⏱ ${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, '0')}`;
    badge.classList.toggle('selection-timer-warn', remaining <= 10);
  };
  renderBadge();

  // Named (not inline in setInterval) so pauseSelectionTimer/
  // resumeSelectionTimer can stop and restart the interval from wherever
  // `remaining` left off, instead of only being able to tear the whole
  // timer down via clearSelectionTimer.
  const tick = () => {
    remaining--;
    if (remaining <= 0) {
      clearInterval(_selectionTimerState.intervalId);
      try {
        onExpire(overlay);
      } catch (e) {
        console.error('[selectionTimer] auto-resolve failed', e);
      }
      clearSelectionTimer();
      return;
    }
    renderBadge();
  };

  const intervalId = setInterval(tick, 1000);

  const observer = new MutationObserver(() => {
    if (!document.body.contains(overlay)) clearSelectionTimer();
  });
  observer.observe(document.body, { childList: true });

  _selectionTimerState = { intervalId, badge, observer, tick, paused: false };
}

// Freezes the countdown (stops decrementing `remaining`, keeps the badge and
// its last-shown value) without tearing down the timer state — used while
// the modal is hidden via hideModalForFieldView, so a player who steps away
// to check the field doesn't come back to find their choice was already
// auto-picked. No-ops if no selection timer is currently running (e.g. a
// hidden overlay that isn't a timed picker modal at all).
function pauseSelectionTimer() {
  if (!_selectionTimerState || _selectionTimerState.paused) return;
  clearInterval(_selectionTimerState.intervalId);
  _selectionTimerState.intervalId = null;
  _selectionTimerState.paused = true;
}

// Resumes a paused countdown from wherever it left off.
function resumeSelectionTimer() {
  if (!_selectionTimerState || !_selectionTimerState.paused) return;
  _selectionTimerState.paused = false;
  _selectionTimerState.intervalId = setInterval(_selectionTimerState.tick, 1000);
}

// Shared auto-resolve for the `.igm-skip`/`.igm-cancel` + `.bop-tile` /
// `.igm-confirm-base` family (showBaseReplacePrompt, showTopDeckPeekPrompt,
// showBaseOrderPrompt, showBlockPromptModal, showCardPickerModal,
// showCardPickerModalMulti, showActionStepModal, showHandDiscardModal).
// Prefers the skip/cancel button; if there isn't one (a mandatory pick),
// keeps clicking the first selectable, not-yet-picked tile — which
// naturally satisfies min-count and ordered pickers too — until Confirm is
// enabled, then clicks Confirm.
function autoResolveIgmModal(overlay) {
  // .igm-return-top: the "leave the deck undisturbed" choice offered by the
  // various "look at the top card, return it to the top or bottom (or trash/
  // hand)" modals (showLookTopCardPeekModal, showTopOrBottomModal,
  // showTopOrTrashModal, Shuji's Hideout's inline modal). Checked BEFORE
  // .igm-skip/.igm-cancel — in THESE modals "Return to Bottom"/"Place in
  // Trash" is a meaningful alternate choice reusing the .igm-cancel class,
  // not a true decline/skip, so falling into that branch first silently
  // sent the top card to the bottom/trash on every timeout instead of the
  // least-disruptive "leave it on top" default (same philosophy as
  // autoResolveMulligan defaulting to Keep Hand).
  const returnTop = overlay.querySelector('.igm-return-top');
  if (returnTop) { returnTop.click(); return; }
  const skip = overlay.querySelector('.igm-skip, .igm-cancel');
  if (skip) { skip.click(); return; }
  let guard = 0;
  while (guard++ < 10) {
    const confirmBtn = overlay.querySelector('.igm-confirm-base');
    if (confirmBtn && !confirmBtn.disabled) { confirmBtn.click(); return; }
    const tile = overlay.querySelector('.bop-tile[data-idx]:not(.bop-selected):not(.bop-unselectable):not(.bop-attacker-tile)');
    if (!tile) break;
    tile.click();
  }
  const confirmBtn = overlay.querySelector('.igm-confirm-base');
  if (confirmBtn && !confirmBtn.disabled) confirmBtn.click();
}

// Shared auto-resolve for the binary Yes/No `.mk-overlay` (and bespoke
// but identically-shaped) confirm modals (showActivationChoiceModal,
// showUnitActivateModal, showPilotActivateModal, showBaseRestSubstituteModal,
// showCommandActivateModal, showBaseActivateModal, showPairConfirmModal,
// showBaseReplaceModal). Declining the optional action is the closest thing
// these have to a "skip", so Cancel/No is preferred over Confirm.
function autoResolveCancelModal(overlay) {
  const cancel = overlay.querySelector('.btn-secondary, .pair-btn.cancel, .brm-cancel');
  if (cancel) { cancel.click(); return; }
  const confirm = overlay.querySelector('.btn-primary, .pair-btn.confirm, .brm-confirm');
  if (confirm) confirm.click();
}

// showMulliganModal has no skip — both choices are equally valid — so the
// least-disruptive default (no hand change) is Keep Hand.
function autoResolveMulligan(overlay) {
  const keepBtn = overlay.querySelector('.mulligan-btn.no');
  if (keepBtn) keepBtn.click();
}

// ════════════════════════════════════════════════════════════════════════════
// DECK BUILDER
// ════════════════════════════════════════════════════════════════════════════
const DB_COLOR_MAP = {
  blue:'#3b82f6', red:'#ef4444', green:'#22c55e', white:'#cbd5e1',
  yellow:'#eab308', purple:'#a855f7', black:'#6b7280', pink:'#ec4899',
};
const DB_EXCLUDED = new Set(['unit-token','unit-base-token','exbase-token','resource']);

// Set code is the cardNo prefix before the first dash, e.g. "GD01-013" →
// "GD01", "ST12-006" → "ST12". Module-scope (not just showDeckBuilder-local)
// so both the Deck Builder and the Private Games lobby's "Allow New Cards"
// setting can share one definition of what counts as a new-release card.
function cardSetCode(cardNo) {
  const m = (cardNo || '').match(/^([A-Za-z]+\d+)-/);
  return m ? m[1] : null;
}

// Sets flagged as recently released — cards from these get the gold "NEW"
// badge in the Deck Builder's browse list, can be hidden there via its Hide
// New button, and are gated by the Private Games lobby's "Allow New Cards"
// setting (see the lobby settings panel in showPrivateGamesLobby() and
// getDeckComplianceIssues() below).
const NEW_RELEASE_SETS = new Set(['GD06']);
function isNewReleaseCard(card) { return NEW_RELEASE_SETS.has(cardSetCode(card && card.cardNo)); }

// Derives a deck's color pips from its own saved card list (each card entry
// carries its own `color`, set at save time — see buildDeckJson()) rather
// than any separately-tracked list, so an imported deck's colors "just work"
// even if it was built on a different device/after localStorage was cleared.
function deckColorPillsHtml(cards) {
  const colors = [...new Set((Array.isArray(cards) ? cards : [])
    .map(c => (c.color || '').toLowerCase())
    .filter(Boolean))].sort();
  return colors.map(c =>
    `<span class="db-color-pill" style="background:${DB_COLOR_MAP[c]||'#888'};color:${c==='white'?'#1e293b':'#fff'}">${c}</span>`
  ).join('');
}

// Cards restricted below the normal 4-copy deckbuilding limit. Shown in the
// browser as a numbered badge next to the color dot; enforced in canAdd()
// and when importing/loading a saved deck.
const DB_COPY_LIMITS = {
  'ST02-016': 2, // Corsica Base
};
function getCardCopyLimit(cardNo) { return DB_COPY_LIMITS[cardNo] || 4; }

// Deck names are free-text (Save Deck, Import via Text's naming step, and
// the inline rename in the deck builder's own name bar) but must never be
// treated as anything other than plain text. Every place a saved deck name
// is later displayed already renders it via textContent/.value (a DOM
// property/content assignment, never raw HTML string interpolation) rather
// than trusting this filter alone — but stripping out characters that have
// no place in a deck name, and are exactly the building blocks of markup
// injection (< > " ; { } backslash, control characters, ...), is a second,
// independent line of defense, applied both live as the player types and
// again right before anything is persisted.
const SAFE_DECK_NAME_CHARS_RE = /[^\p{L}\p{N}\s\-_().,!']/gu;
function sanitizeDeckName(raw, maxLength = 100) {
  return String(raw || '').replace(SAFE_DECK_NAME_CHARS_RE, '').trim().slice(0, maxLength);
}

// Restricted-format rules effective July 24, 2026 (gundam-gcg.com banlist).
// Enforced going-forward in canAdd() (blocks new violations while editing);
// existing decks that predate the banlist (saved/imported before this patch)
// are instead flagged via getDeckComplianceIssues() everywhere a deck is
// listed or picked, rather than being silently mutated.
const DB_RESTRICTIONS_URL = 'https://www.gundam-gcg.com/en/news/01_277.html';
const DB_BANNED_CARDS = new Set([
  'GD01-020', // Anksha
]);
const DB_BANNED_PAIRS = [
  ['ST01-010', 'ST05-010'], // Amuro Ray / Mikazuki Augus
  ['GD01-008', 'GD05-015'], // Guntank / M1 Astray Shrike
];
// "Basic Lv.2 Unit": a Lv.2, cost-1, 2AP/2HP Unit with no printed ability, no
// native keyword, and no Link Requirement — a pure vanilla stat-stick. A
// deck may include at most one SUCH CARD (by cardNo), up to its normal
// 4-copy limit — i.e. two different vanilla Lv.2/cost-1/2AP/2HP units can't
// both be in the same deck just because they're different cardNos.
// Computed live from cardDb instead of a hardcoded cardNo allowlist —
// CONFIRMED BUG: the old hardcoded set was never updated past GD05, so
// GD06's own vanilla Lv.2/cost-1/2AP/2HP units (e.g. GD06-028 Gaza D,
// GD06-047 Windam, GD06-063 GFreD) silently dodged the restriction
// entirely. This version catches every current AND future set's matching
// Units automatically, with no per-release maintenance needed.
function isBasicLv2Unit(cardNo) {
  const e = cardDb[cardNo];
  if (!e) return false;
  if ((e.categoryData || '').toLowerCase().trim() !== 'unit') return false;
  if ((parseInt(e.level) || 0) !== 2) return false;
  if ((parseInt(e.cost)  || 0) !== 1) return false;
  if ((parseInt(e.ap)    || 0) !== 2) return false;
  if ((parseInt(e.hp)    || 0) !== 2) return false;
  if ((e.effectData    || '').trim() !== '') return false;
  if ((e.nativeKeyword || '').trim() !== '') return false;
  if ((e.link          || '').trim() !== '') return false;
  return true;
}

// Normalizes a saved-deck JSON array ([{cardNo, copies, ...}]) into a
// cardNo → count Map. The live deck builder's own `deck` Map can be passed
// straight into getDeckComplianceIssues() without going through this.
function getDeckCardCounts(cards) {
  const counts = new Map();
  (Array.isArray(cards) ? cards : []).forEach(c => {
    if (!c || !c.cardNo) return;
    counts.set(c.cardNo, (counts.get(c.cardNo) || 0) + (parseInt(c.copies, 10) || 0));
  });
  return counts;
}

// Returns a list of human-readable compliance issues (card names to remove)
// for a cardNo → count Map, per the July 24, 2026 restricted-format rules.
// Empty array means the deck is fully compliant.
//
// opts.disallowNewCards additionally flags any card from NEW_RELEASE_SETS —
// passed by showDeckSelectScreen() only when it's launched from a Private
// Games lobby with "Allow New Cards" turned off (see the lobby settings
// panel and NET.onChallengeAccepted() in showPrivateGamesLobby()). The public
// matchmaking queue and the Deck Builder's own compliance checks never pass
// this, so an ordinary deck is completely unaffected.
function getDeckComplianceIssues(counts, opts = {}) {
  const issues = [];
  counts.forEach((count, cardNo) => {
    if (count > 0 && DB_BANNED_CARDS.has(cardNo)) {
      issues.push(`${cardDb[cardNo]?.name || cardNo} is banned — remove it.`);
    }
  });
  DB_BANNED_PAIRS.forEach(([a, b]) => {
    if ((counts.get(a) || 0) > 0 && (counts.get(b) || 0) > 0) {
      const nameA = cardDb[a]?.name || a, nameB = cardDb[b]?.name || b;
      issues.push(`${nameA} and ${nameB} cannot be in the same deck — remove one.`);
    }
  });
  const lv2Types = [...counts.keys()].filter(no => isBasicLv2Unit(no) && (counts.get(no) || 0) > 0);
  if (lv2Types.length > 1) {
    const names = lv2Types.map(no => cardDb[no]?.name || no).join(', ');
    issues.push(`Only one type of Basic Lv.2 Unit is allowed per deck — remove all but one of: ${names}.`);
  }
  if (opts.disallowNewCards) {
    counts.forEach((count, cardNo) => {
      if (count > 0 && NEW_RELEASE_SETS.has(cardSetCode(cardNo))) {
        issues.push(`${cardDb[cardNo]?.name || cardNo} is a new-release card, which this lobby doesn't allow — remove it.`);
      }
    });
  }
  return issues;
}

// Cached File System Access handle for the saveddecks folder (session-scoped).
let _savedDecksHandle = null;
async function getSavedDecksHandle() {
  if (_savedDecksHandle) {
    try {
      const perm = await _savedDecksHandle.queryPermission({ mode: 'readwrite' });
      if (perm === 'granted') return _savedDecksHandle;
    } catch {}
  }
  if (!('showDirectoryPicker' in window)) return null;
  try {
    _savedDecksHandle = await window.showDirectoryPicker({ mode: 'readwrite', startIn: 'documents' });
    return _savedDecksHandle;
  } catch { return null; }
}

// ════════════════════════════════════════════════════════════════════════════
// CARD DATABASE
// Read-only, full-page set-by-set reference browser for the main menu — one
// section per released set (box art from assets/sets/<code>.webp, name,
// release date, Unit/Pilot/Command/Base counts with icons, per-color
// counts), then every card in that set as an image grid in cardNo order,
// with the same hover preview field/hand cards use. Deliberately NOT the
// Deck Builder's browse panel: that one is deeply tied to an in-progress
// deck (add/remove counts, sideboard, drag-and-drop, favorites, save/load)
// via closures over its own `deck`/`sideboard` Maps, so it can't be reused
// here without dragging all of that along.
//
// SET_INFO's names/release dates aren't derivable from nuCardDatabase.json
// itself — its own per-card "source" field is noisy (mixes the real set
// name with "Edition Beta", "Store Tournament Participant/Winner Pack",
// "... Bonus Pack", etc., and even disagrees on which is most common for a
// few early sets) — so they're hardcoded here, sourced from the official
// gundam-gcg.com product list (English/Asia-English). A set code with no
// entry here (e.g. an announced-but-unreleased set) simply gets no section.
// ════════════════════════════════════════════════════════════════════════════
const SET_INFO = {
  ST01: { name: 'Heroic Beginnings', release: '2025-07-11' },
  ST02: { name: 'Wings of Advance',  release: '2025-07-11' },
  ST03: { name: "Zeon's Rush",       release: '2025-07-11' },
  ST04: { name: 'SEED Strike',       release: '2025-07-11' },
  GD01: { name: 'Newtype Rising',    release: '2025-07-25' },
  ST05: { name: 'Iron Bloom',        release: '2025-09-26' },
  GD02: { name: 'Dual Impact',       release: '2025-10-24' },
  ST06: { name: 'Clan Unity',        release: '2025-10-24' },
  ST07: { name: 'Celestial Drive',   release: '2026-01-16' },
  ST08: { name: 'Flash of Radiance', release: '2026-01-16' },
  GD03: { name: 'Steel Requiem',     release: '2026-01-30' },
  ST09: { name: 'Destiny Ignition',  release: '2026-03-27' },
  GD04: { name: 'Phantom Aria',      release: '2026-04-24' },
  EB01: { name: 'Eternal Nexus',     release: '2026-06-26' },
  ST10: { name: 'Generation Pulse',  release: '2026-06-26' },
  GD05: { name: 'Freedom Ascension', release: '2026-07-24' },
  ST11: { name: 'Aquatic Assault',   release: '2026-09-25' },
  ST12: { name: 'Raging Onslaught',  release: '2026-09-25' },
  ST13: { name: 'Silent Barrage',    release: '2026-09-25' },
  ST14: { name: 'Heavy Dominion',    release: '2026-09-25' },
  GD06: { name: 'Stardust Trails',   release: '2026-10-30' },
};

// Hardcoded cardNo → originating set code for every T-XXX Unit/Base token
// (T-001..T-037, confirmed the full range — no gaps). Like SET_INFO, this
// isn't reliably derivable from cardDb at runtime: nuCardDatabase.json has
// duplicate array entries per token (promo reprints — "Premium Card
// Collection..." packs — and plain "Edition Beta" catalog noise), and
// cardDb only ever keeps the LAST one loaded under that cardNo. For several
// tokens (T-001–003, T-006, T-015–020) that happens to be a non-bracketed
// or differently-set-bracketed source string, not the token's real
// originating set — hand-verified against the correct source value per
// token instead (T-012 Daughtress has no source field at all in either of
// its two entries; inferred from GD02-043/GD02-044 being the lowest-numbered
// cards that deploy it, one set before GD03-044 reuses the same token).
const TOKEN_SET = {
  'T-001': 'ST01', 'T-002': 'ST01', 'T-003': 'ST01',
  'T-004': 'ST02', 'T-005': 'ST02',
  'T-006': 'ST03', 'T-007': 'ST03',
  'T-008': 'ST04', 'T-009': 'ST04', 'T-010': 'ST04',
  'T-011': 'GD01',
  'T-012': 'GD02',
  'T-013': 'GD03', 'T-014': 'GD03', 'T-015': 'GD03', 'T-016': 'GD03',
  'T-017': 'GD03', 'T-018': 'GD03', 'T-019': 'GD03', 'T-020': 'GD03',
  'T-021': 'GD04', 'T-022': 'GD04', 'T-023': 'GD04', 'T-024': 'GD04',
  'T-025': 'EB01',
  'T-026': 'GD05', 'T-027': 'GD05',
  'T-028': 'ST11',
  'T-029': 'GD06', 'T-030': 'GD06', 'T-031': 'GD06', 'T-032': 'GD06',
  'T-033': 'GD06', 'T-034': 'GD06', 'T-035': 'GD06', 'T-036': 'GD06', 'T-037': 'GD06',
};

// 'T00:00:00' (no 'Z') forces local-time parsing — plain 'YYYY-MM-DD' parses
// as UTC midnight, which toLocaleDateString() can then roll back a day in
// any negative-UTC-offset timezone (most of the Americas).
function formatSetReleaseDate(iso) {
  if (!iso) return '';
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

const CDB_SET_TYPE_LABELS = { ST: 'Structure Deck', GD: 'Booster Set', EB: 'Extra Booster Set' };
function cdbSetType(code) { return (code || '').slice(0, 2); }

function showCardDatabaseModal() {
  const existing = document.getElementById('card-database-overlay');
  if (existing) existing.remove();

  // One entry per cardNo (cardDb is already deduped — the raw DB array's
  // alt-art/reprint duplicates all collapse onto the same cardNo key), minus
  // the resource/EX-Base-skin categories DB_EXCLUDED also keeps out of the
  // Deck Builder's own browse list — not real collectible cards, and never
  // tied to one specific set the way a numbered card or a Unit/Base token
  // is. Unit/Base tokens (unit-token/unit-base-token) are collected
  // separately below via TOKEN_SET, since their cardNo (T-030, ...) carries
  // no set prefix for cardSetCode() to group by.
  const allEntries = Object.values(cardDb).filter(e => e && e.cardNo && !DB_EXCLUDED.has((e.categoryData || '').toLowerCase().trim()));
  const tokenEntries = Object.values(cardDb).filter(e => {
    const cat = (e?.categoryData || '').toLowerCase().trim();
    return e && e.cardNo && (cat === 'unit-token' || cat === 'unit-base-token') && TOKEN_SET[e.cardNo];
  });

  const bySet = new Map(); // setCode → entries[]
  allEntries.forEach(e => {
    const code = cardSetCode(e.cardNo);
    if (!code || !SET_INFO[code]) return; // unknown/not-yet-released set — no section to show it in yet
    if (!bySet.has(code)) bySet.set(code, []);
    bySet.get(code).push(e);
  });
  tokenEntries.forEach(e => {
    const code = TOKEN_SET[e.cardNo];
    if (!bySet.has(code)) bySet.set(code, []);
    bySet.get(code).push(e);
  });
  // Sort each set's cards once up front (cardNo order never changes) — a
  // plain string sort naturally lands every "T-0xx" token after that set's
  // own "GD0x-"/"ST0x-"/"EB01-" numbered cards (G/S/E < T), so tokens show
  // up as a trailing group in the grid without any special-casing here.
  bySet.forEach(list => list.sort((a, b) => (a.cardNo || '').localeCompare(b.cardNo || '', undefined, { numeric: true })));

  let sortDesc = true;              // true = newest set first
  const activeSetTypes = new Set(); // 'ST'/'GD'/'EB' — empty = show all
  let expandedCode = null;          // the one set section currently open (accordion — at most one)

  function orderedCodes() {
    return [...bySet.keys()]
      .filter(code => activeSetTypes.size === 0 || activeSetTypes.has(cdbSetType(code)))
      .sort((a, b) => {
        const cmp = SET_INFO[a].release.localeCompare(SET_INFO[b].release) || a.localeCompare(b);
        return sortDesc ? -cmp : cmp;
      });
  }

  function countByCategory(entries) {
    let unitCount = 0, pilotCount = 0, commandCount = 0, baseCount = 0, tokenCount = 0;
    const colorCounts = new Map();
    entries.forEach(e => {
      // Same categoryData → stat-bucket convention as renderDeckTypeSummary()/
      // the bot-deck-preview type rows — 'command pilot' counts as Command,
      // not Pilot (see those for the established house rule).
      const cat = (e.categoryData || '').toLowerCase().trim();
      const isToken = cat === 'unit-token' || cat === 'unit-base-token';
      if (isToken) tokenCount++;
      else if (cat === 'unit') unitCount++;
      else if (cat === 'pilot') pilotCount++;
      else if (cat === 'command' || cat === 'command pilot') commandCount++;
      else if (cat === 'base') baseCount++;
      // Tokens don't carry a real deck color (color is blank or the
      // 'resource' pseudo-color) — excluded from the per-color breakdown,
      // which is meant to read as that set's actual color distribution.
      if (!isToken) {
        const color = (e.color || '').toLowerCase();
        if (color) colorCounts.set(color, (colorCounts.get(color) || 0) + 1);
      }
    });
    return { unitCount, pilotCount, commandCount, baseCount, tokenCount, colorCounts };
  }

  // Builds a section's card grid the first time it's expanded — not before.
  // Every card image (and the hover-preview listener on each tile) is
  // otherwise dead weight for a set the player never opens.
  function populateGrid(section) {
    if (section.dataset.loaded === '1') return;
    section.dataset.loaded = '1';
    const code = section.dataset.code;
    const grid = section.querySelector('.cdb-set-card-grid');
    const frag = document.createDocumentFragment();
    bySet.get(code).forEach(entry => {
      const tile = document.createElement('div');
      tile.className = 'cdb-card-tile';
      tile.innerHTML = `
        <div class="cdb-card-tile-no">${entry.cardNo}</div>
        <img src="${entry.image}" alt="${entry.name}" loading="lazy" />
      `;
      // showCardPreview() expects a built game-card object (.img/.type/.bp —
      // not the raw DB entry's .image/.categoryData/.ap), same shape
      // drawCardsFromTempDeck() etc. already build via buildGameCardFromDb().
      // Passing the raw cardDb entry directly here was the CONFIRMED BUG
      // behind the broken "?" placeholder/blank TYPE in the preview popup.
      const previewCard = buildGameCardFromDb(entry, 'cdb-preview');
      tile.addEventListener('mouseenter', () => showCardPreview(previewCard, tile, { gameUi: false }));
      tile.addEventListener('mouseleave', hideCardPreview);
      frag.appendChild(tile);
    });
    grid.appendChild(frag);
  }

  // Collapsing a section releases its images (and their decoded-bitmap
  // memory) instead of just hiding them — populateGrid() rebuilds the grid
  // fresh the next time it's expanded. With 21 sets and 1000+ cards total,
  // keeping every previously-opened section's images alive forever adds up.
  function unloadGrid(section) {
    if (section.dataset.loaded !== '1') return;
    section.dataset.loaded = '0';
    section.querySelector('.cdb-set-card-grid').innerHTML = '';
  }

  function buildSetSection(code) {
    const entries = bySet.get(code);
    const info = SET_INFO[code];
    const { unitCount, pilotCount, commandCount, baseCount, tokenCount, colorCounts } = countByCategory(entries);

    const colorPillsHtml = [...colorCounts.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([color, count]) => `
        <div class="cdb-color-pill">
          <span class="db-color-dot-inline" style="background:${DB_COLOR_MAP[color] || '#888'}"></span>
          <span class="cdb-color-pill-label">${color.charAt(0).toUpperCase() + color.slice(1)}</span>
          <strong>${count}</strong>
        </div>`).join('');

    const section = document.createElement('section');
    section.className = 'cdb-set-section';
    section.dataset.code = code;
    section.innerHTML = `
      <button class="cdb-set-banner" type="button">
        <img class="cdb-set-art" src="assets/sets/${code}.webp" alt="${info.name}" loading="lazy" />
        <div class="cdb-set-info">
          <div class="cdb-set-title">${info.name} <span class="cdb-set-code">${code}</span></div>
          <div class="cdb-set-subtitle">Release Date: ${formatSetReleaseDate(info.release)}</div>
          <div class="cdb-set-type-counts">
            <div class="cdb-type-item"><span class="material-symbols-outlined">smart_toy</span><span>Units</span><strong>${unitCount}</strong></div>
            <div class="cdb-type-item"><span class="material-symbols-outlined">person</span><span>Pilots</span><strong>${pilotCount}</strong></div>
            <div class="cdb-type-item"><span class="material-symbols-outlined">sd_card_alert</span><span>Command Cards</span><strong>${commandCount}</strong></div>
            <div class="cdb-type-item"><span class="material-symbols-outlined">castle</span><span>Bases</span><strong>${baseCount}</strong></div>
            <div class="cdb-type-item"><span class="material-symbols-outlined">resize</span><span>Tokens</span><strong>${tokenCount}</strong></div>
            <div class="cdb-type-item cdb-type-total"><span>Total</span><strong>${entries.length}</strong></div>
          </div>
          <div class="cdb-set-color-counts">${colorPillsHtml}</div>
        </div>
        <span class="material-symbols-outlined cdb-set-chevron">expand_more</span>
      </button>
      <div class="cdb-set-card-grid"></div>
    `;
    section.querySelector('.cdb-set-banner').addEventListener('click', () => toggleSection(code));
    return section;
  }

  const overlay = document.createElement('div');
  overlay.id = 'card-database-overlay';
  overlay.innerHTML = `
    <div class="cdb-header">
      <button class="ds-btn ds-cancel cdb-back-btn" type="button">← Back</button>
      <div class="cdb-title">Card Database</div>
    </div>
    <div class="cdb-toolbar">
      <button class="cdb-sort-btn" type="button" id="cdb-sort-btn">
        <span class="material-symbols-outlined">swap_vert</span>
        <span id="cdb-sort-label"></span>
      </button>
      <div class="cdb-type-filters" id="cdb-type-filters"></div>
      <div class="cdb-set-pills" id="cdb-set-pills"></div>
    </div>
    <div class="cdb-body" id="cdb-body"></div>
  `;
  document.body.appendChild(overlay);

  const body = overlay.querySelector('#cdb-body');
  const pillsEl = overlay.querySelector('#cdb-set-pills');
  const typeFiltersEl = overlay.querySelector('#cdb-type-filters');
  const sortBtn = overlay.querySelector('#cdb-sort-btn');
  const sortLabel = overlay.querySelector('#cdb-sort-label');

  // Expands `code` and collapses every other section — never toggles off.
  // Used by the nav pills ("reveals and collapses other sets").
  function applyExpanded(code) {
    expandedCode = code;
    body.querySelectorAll('.cdb-set-section').forEach(sec => {
      const isOpen = sec.dataset.code === expandedCode;
      sec.classList.toggle('cdb-expanded', isOpen);
      if (isOpen) populateGrid(sec); else unloadGrid(sec);
    });
    renderPills();
  }

  function expandAndScrollTo(code) {
    applyExpanded(code);
    const sec = body.querySelector(`.cdb-set-section[data-code="${code}"]`);
    if (sec) sec.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // Clicking a section's OWN banner toggles it — closing it if it's already
  // the open one (unlike a pill click, which only ever opens).
  function toggleSection(code) {
    applyExpanded(expandedCode === code ? null : code);
  }

  function renderPills() {
    pillsEl.innerHTML = '';
    orderedCodes().forEach(code => {
      const btn = document.createElement('button');
      btn.className = 'cdb-set-pill' + (code === expandedCode ? ' active' : '');
      btn.textContent = code;
      btn.title = SET_INFO[code].name;
      btn.addEventListener('click', () => expandAndScrollTo(code));
      pillsEl.appendChild(btn);
    });
  }

  function renderTypeFilters() {
    typeFiltersEl.innerHTML = '';
    Object.keys(CDB_SET_TYPE_LABELS).forEach(t => {
      const btn = document.createElement('button');
      btn.className = 'cdb-set-type-toggle' + (activeSetTypes.has(t) ? ' active' : '');
      btn.textContent = CDB_SET_TYPE_LABELS[t];
      btn.title = `${CDB_SET_TYPE_LABELS[t]} (${t})`;
      btn.addEventListener('click', () => {
        if (activeSetTypes.has(t)) activeSetTypes.delete(t); else activeSetTypes.add(t);
        renderTypeFilters();
        renderBody();
      });
      typeFiltersEl.appendChild(btn);
    });
  }

  // Full rebuild — only happens on a sort/filter change (rare), and even
  // then only the section that ends up expanded gets its grid populated.
  function renderBody() {
    const codes = orderedCodes();
    if (!codes.includes(expandedCode)) expandedCode = codes[0] || null;
    body.innerHTML = '';
    body.scrollTop = 0;
    const frag = document.createDocumentFragment();
    codes.forEach(code => {
      const section = buildSetSection(code);
      if (code === expandedCode) {
        section.classList.add('cdb-expanded');
        populateGrid(section);
      }
      frag.appendChild(section);
    });
    body.appendChild(frag);
    renderPills();
  }

  sortBtn.addEventListener('click', () => {
    sortDesc = !sortDesc;
    sortLabel.textContent = sortDesc ? 'Newest → Oldest' : 'Oldest → Newest';
    renderBody();
  });
  sortLabel.textContent = sortDesc ? 'Newest → Oldest' : 'Oldest → Newest';

  renderTypeFilters();
  renderBody();

  overlay.querySelector('.cdb-back-btn').addEventListener('click', () => {
    hideCardPreview();
    overlay.remove();
    showMainMenu(Promise.resolve());
  });
}

function showDeckBuilder(onBack) {
  const deck = new Map(); // cardNo → count
  // Sideboard: only matters for a future Best of 3 mode (not yet
  // implemented) — Quick Play is best-of-1 and never reads it (see
  // db-play-game's handler, which still builds off `deck` alone). Saved
  // under a companion localStorage key per deck name (SIDEBOARD_LS_PREFIX)
  // rather than folded into the main `gdeck:` entry's array shape, so every
  // existing reader of that key (showDeckSelectScreen, the Load Deck list,
  // etc.) keeps working unchanged against decks saved before this existed.
  const sideboard = new Map(); // cardNo → count, max 10 total
  const activeColors    = new Set(); // empty = show all colors
  const activeTypes     = new Set(); // empty = show all types
  const activeKeywords  = new Set(); // empty = show all native keywords
  const activeEffectKws = new Set(); // empty = show all effect keywords
  const activeSets      = new Set(); // empty = show all sets
  let hideNewRelease = false; // true = filter out NEW_RELEASE_SETS cards (Hide New button)
  let showFavoritesOnly = false; // true = only show favorited cards (Show Favorite button)
  let favoriteCards = loadFavoriteCards(); // Set<cardNo> — in-memory cache, written through to localStorage on every toggle
  let levelSortDir = 'asc'; // null = default order, 'desc' = high→low Level, 'asc' = low→high Level
  let deckSortMode = 'asc'; // 'asc' = low→high Level (default), 'desc' = high→low Level, 'color' = by color, low→high within each color
  let filterCost  = '';
  let filterLevel = '';
  const searchTags = [];
  let currentDeckName = null; // name of the last saved/loaded deck
  // Reserved localStorage save-name for a deck that's never been named at
  // all when it needs to be auto-saved (see closeBuilder() below) — a
  // loaded/previously-saved deck reuses its own currentDeckName instead.
  const AUTO_SAVE_DRAFT_NAME = 'Autosaved Draft';
  // Signed-out guests don't get the free-form named multi-deck flow at all —
  // New Deck/Load Deck/Settings are disabled outright (wired below, right
  // after overlay markup is appended) and Save always targets this one fixed
  // slot instead of prompting a name, which is what actually keeps a guest
  // capped at a single saved deck (disabling the other entry points alone
  // wouldn't stop Save from piling up differently-named decks). Logged-in
  // accounts keep the existing named flow, capped at 10 — see
  // wouldExceedDeckCap() below.
  const GUEST_DECK_NAME = 'My Deck';
  const MAX_SAVED_DECKS_LOGGED_IN = 10;
  function isLoggedIn() { return !!(window.Supa && window.Supa.getSession()); }
  // True if saving under `name` would create a new (not overwriting) deck
  // entry beyond the logged-in cap. Guests never hit this — their single
  // slot is always an overwrite of itself.
  function wouldExceedDeckCap(name) {
    if (!isLoggedIn()) return false;
    if (localStorage.getItem(LS_PREFIX + name) !== null) return false;
    return getSavedDeckNames().length >= MAX_SAVED_DECKS_LOGGED_IN;
  }
  // Snapshot-diff dirty tracking, rather than a flag flipped at every one of
  // the many deck.set()/delete()/clear() call sites scattered across this
  // function (easy to miss one) — hasUnsavedChanges() just compares the
  // deck's current contents against its state as of the last save/load, so
  // every mutation is automatically covered with no per-call-site bookkeeping.
  let lastSavedSnapshot = null; // set below, once computeDeckSnapshot() exists
  let unsavedWarningDismissed = false; // reset whenever the deck becomes clean again (save/load/new)

  function computeDeckSnapshot() {
    // Sorted by cardNo so insertion order never causes a false "dirty" read.
    // Includes the sideboard so editing it alone also flags unsaved changes.
    return JSON.stringify([
      [...deck.entries()].sort((a, b) => a[0].localeCompare(b[0])),
      [...sideboard.entries()].sort((a, b) => a[0].localeCompare(b[0])),
    ]);
  }
  function hasUnsavedChanges() {
    return computeDeckSnapshot() !== lastSavedSnapshot;
  }
  // Marks the current deck contents as the new "clean" baseline — call this
  // any time the deck becomes in sync with storage (after a save, a load, or
  // resetting to a fresh empty deck).
  function markDeckClean() {
    lastSavedSnapshot = computeDeckSnapshot();
    unsavedWarningDismissed = false;
    updateUnsavedWarning();
  }
  lastSavedSnapshot = computeDeckSnapshot(); // baseline: starts empty, so a fresh deck builder isn't "dirty" until actually edited

  // #db-unsaved-warning lives under the deck panel's .db-instructions (see
  // the overlay markup below) — only shown while Auto Save is OFF (see the
  // Settings modal's "Auto save decks in deck builder" toggle,
  // getAutoSaveDecks()) and the deck has changes not yet reflected in
  // storage. Dismissible; re-appears on its own once the deck becomes dirty
  // again after being saved/loaded/reset (see markDeckClean() above).
  function updateUnsavedWarning() {
    const el = document.getElementById('db-unsaved-warning');
    if (!el) return;
    const shouldShow = !getAutoSaveDecks() && hasUnsavedChanges() && !unsavedWarningDismissed;
    if (!shouldShow) {
      el.className = 'db-unsaved-warning';
      el.innerHTML = '';
      return;
    }
    el.className = 'db-unsaved-warning db-unsaved-warning-show';
    el.innerHTML = `
      <span class="material-symbols-outlined">warning</span>
      <span class="db-unsaved-warning-text">This deck is not saved. Exiting the deck builder, refreshing the page, or playing a quick game will not save the deck.</span>
      <button class="db-unsaved-warning-close" type="button" title="Dismiss">
        <span class="material-symbols-outlined">close</span>
      </button>
    `;
    el.querySelector('.db-unsaved-warning-close').addEventListener('click', () => {
      unsavedWarningDismissed = true;
      updateUnsavedWarning();
    });
  }
  const PAGE_SIZE = 50;
  let currentFiltered = [];
  let currentPage = 0;

  const allCards = Object.values(cardDb)
    .filter(c => !DB_EXCLUDED.has((c.categoryData||'').toLowerCase()))
    .sort((a,b) => (a.color||'').localeCompare(b.color||'') || (a.name||'').localeCompare(b.name||''));

  const allColors = [...new Set(allCards.map(c=>(c.color||'').toLowerCase()).filter(Boolean))].sort();

  // Set code is the cardNo prefix before the first dash, e.g. "GD01-013" →
  // "GD01", "ST12-006" → "ST12". allCards is already filtered down to real,
  // ownable cards (see DB_EXCLUDED above), so this naturally excludes the
  // resource/token/EX-base card numbers (R-001, EXB-001, T-025, ...) that
  // aren't part of any set a deck is actually built from. Thin wrapper around
  // the module-scope cardSetCode() (see NEW_RELEASE_SETS near DB_EXCLUDED)
  // since every call site here already has the full card object in hand.
  function cardSet(card) { return cardSetCode(card.cardNo); }
  const allSets = [...new Set(allCards.map(cardSet).filter(Boolean))].sort();

  // Native combat keywords (Blocker, Suppression, Repair, Breach, ...) live in
  // entry.nativeKeyword as one or more bracketed, comma-joined tags (e.g.
  // "[Repair 1],[Blocker]"); numbered variants ("[Repair 2]") are folded down
  // to their base name ("Repair") so the filter list and matching both ignore
  // the level number.
  function cardKeywords(card) {
    return (card.nativeKeyword || '')
      .split(',')
      .map(k => k.replace(/[\[\]]/g, '').replace(/\s*\d+\s*$/, '').trim())
      .filter(Boolean);
  }
  const allKeywords = [...new Set(allCards.flatMap(cardKeywords))].sort();

  const overlay = document.createElement('div');
  overlay.id = 'deck-builder-overlay';
  overlay.innerHTML = `
    <div class="db-header">
      <button class="db-btn" id="db-back">← Back</button>
      <span class="db-title">Deck Builder</span>
      <span class="db-stats" id="db-stats">0 / 50</span>
      <div id="db-deck-name-bar-mobile" class="db-deck-name-bar db-deck-name-bar-header"></div>
      <div class="db-color-pills" id="db-color-pills"></div>
      <button class="db-header-menu-toggle" id="db-header-menu-toggle" type="button" title="Menu">
        <span class="material-symbols-outlined">menu</span>
      </button>
      <div class="db-header-actions" id="db-header-actions">
        <button class="db-btn db-btn-new" id="db-new">New Deck</button>
        <button class="db-btn db-btn-clear" id="db-clear">Clear Deck</button>
        <button class="db-btn db-btn-load" id="db-load">Load Deck</button>
        <button class="db-btn db-btn-save" id="db-save">Save Deck</button>
        <button class="db-btn db-btn-import-text" id="db-import-text">Import via Text</button>
        <button class="db-btn db-btn-export-text" id="db-export-text">Export via Text</button>
        <button class="db-btn db-btn-play" id="db-play-game">Quick Play</button>
        <button class="db-btn db-btn-settings" id="db-settings">
          <span class="material-symbols-outlined">settings</span>
          Settings
        </button>
        <button class="db-btn db-btn-report-bug" id="db-report-bug">
          <span class="material-symbols-outlined">bug_report</span>
          Report Bug
        </button>
        <button class="db-btn db-btn-help" id="db-help" title="How to use the Deck Builder">
          <span class="material-symbols-outlined">help</span>
        </button>
      </div>
      <div class="db-header-backdrop" id="db-header-backdrop"></div>
    </div>
    <div class="db-body">
      <div class="db-deck-panel" id="db-deck-panel">
        <div id="db-deck-name-bar" class="db-deck-name-bar"></div>
        <div class="db-deck-summary-grid" id="db-deck-summary-grid">
          <div class="db-panel-label">Your Deck — <span id="db-deck-count">0</span> / 50 cards</div>
          <div class="db-deck-sort-toggles" id="db-deck-sort-toggles">
            <button class="db-sort-btn" id="db-deck-sort-asc" data-mode="asc" title="Sort deck by Level: Low to High">
              <span class="db-sort-label">Low → High</span>
            </button>
            <button class="db-sort-btn" id="db-deck-sort-desc" data-mode="desc" title="Sort deck by Level: High to Low">
              <span class="db-sort-label">High → Low</span>
            </button>
            <button class="db-sort-btn" id="db-deck-sort-color" data-mode="color" title="Sort deck by Color">
              <span class="db-sort-label">By Color</span>
            </button>
          </div>
          <div class="db-deck-type-summary" id="db-deck-type-summary"></div>
        </div>
        <div class="db-instructions">Left-click a deck card to remove 1 copy · Right-click to remove all copies</div>
        <div class="db-unsaved-warning" id="db-unsaved-warning"></div>
        <div class="db-compliance-warning" id="db-compliance-warning"></div>
        <div class="db-deck-grid" id="db-deck-grid">
          <div class="db-deck-empty">No cards yet.<br>Left-click to add 1 · Right-click to add 4</div>
        </div>
        <div class="db-sideboard-section" id="db-sideboard-section">
          <div class="db-panel-label">Sideboard — <span id="db-sideboard-count">0</span> / 10 cards</div>
          <div class="db-instructions">Only matters in a Best of 3 match (coming later) — ignored during Quick Play. Left-click to remove 1 copy · Right-click to remove all copies.</div>
          <div class="db-compliance-warning" id="db-sideboard-warning"></div>
          <div class="db-deck-grid db-sideboard-grid" id="db-sideboard-grid">
            <div class="db-deck-empty">No sideboard cards yet.</div>
          </div>
        </div>
      </div>
      <div class="db-drag-handle" id="db-drag-handle"></div>
      <div class="db-browse-panel" id="db-browse-panel">
        <button class="db-filter-toggle db-search-filter-toggle" id="db-search-filter-toggle" type="button">
          <span class="material-symbols-outlined db-filter-toggle-icon">chevron_right</span>
          Search &amp; Filters
        </button>
        <div class="db-search-wrap db-filter-collapsed" id="db-search-wrap">
          <div class="db-search-row">
            <div class="db-search-field" id="db-search-field">
              <input class="db-search-inner" id="db-search" type="text"
                placeholder="Search name, trait, link req., card no., effect… (comma to tag)" autocomplete="off" />
            </div>
          </div>
          <div class="db-filter-row">
            <label class="db-num-label">Level<input class="db-num-input" id="db-filter-level" type="number" min="0" max="99" placeholder="—" /></label>
            <label class="db-num-label">Cost<input class="db-num-input" id="db-filter-cost" type="number" min="0" max="99" placeholder="—" /></label>
            <label class="db-num-label">AP<input class="db-num-input" id="db-filter-ap" type="number" min="0" max="99" placeholder="—" /></label>
            <label class="db-num-label">HP<input class="db-num-input" id="db-filter-hp" type="number" min="0" max="99" placeholder="—" /></label>
            <div class="db-sort-toggles" id="db-sort-toggles">
              <button class="db-sort-btn" id="db-sort-asc" data-dir="asc" title="Sort by Level: Low to High">
                <span class="db-sort-label">Low → High</span>
                <span class="material-symbols-outlined db-sort-icon db-sort-icon-flip">filter_list</span>
              </button>
              <button class="db-sort-btn" id="db-sort-desc" data-dir="desc" title="Sort by Level: High to Low">
                <span class="db-sort-label">High → Low</span>
                <span class="material-symbols-outlined db-sort-icon">filter_list</span>
              </button>
            </div>
            <button class="db-btn db-btn-reset db-btn-stack" id="db-filter-reset" title="Clear all filters">
              <span class="db-sort-label">Reset All</span>
              <span class="material-symbols-outlined db-sort-icon">restart_alt</span>
            </button>
            <button class="db-btn db-btn-hide-new db-btn-stack" id="db-hide-new-toggle" type="button" title="Hide cards from recently released sets">
              <span class="db-sort-label">Hide New</span>
              <span class="material-symbols-outlined db-sort-icon">visibility_off</span>
            </button>
            <button class="db-btn db-btn-show-favorite db-btn-stack" id="db-show-favorite-toggle" type="button" title="Show only favorited cards">
              <span class="db-sort-label">Favorites</span>
              <span class="material-symbols-outlined db-sort-icon">favorite</span>
            </button>
          </div>
        </div>
        <div class="db-instructions" id="db-browse-instructions">Left-click a card to add 1 copy to your deck · Right-click to add up to 4 · Use <span class="material-symbols-outlined db-instructions-icon">add_column_right</span> to add to your sideboard · Drag a card into either grid</div>
        <button class="db-filter-toggle db-color-filter-toggle" id="db-color-filter-toggle" type="button">
          <span class="material-symbols-outlined db-filter-toggle-icon">chevron_right</span>
          Color Filters
          <span class="db-filter-toggle-count" id="db-color-filter-count"></span>
        </button>
        <div class="db-color-filters db-filter-collapsed" id="db-color-filters"></div>
        <button class="db-filter-toggle db-type-filter-toggle" id="db-type-filter-toggle" type="button">
          <span class="material-symbols-outlined db-filter-toggle-icon">chevron_right</span>
          Type Filters
          <span class="db-filter-toggle-count" id="db-type-filter-count"></span>
        </button>
        <div class="db-type-filters db-filter-collapsed" id="db-type-filters"></div>
        <div class="db-filter-header-row">
          <button class="db-filter-row-refresh" id="db-keyword-filter-row-refresh" type="button" title="Clear native keyword filters">
            <span class="material-symbols-outlined">refresh</span>
          </button>
          <button class="db-filter-toggle" id="db-keyword-filter-toggle" type="button">
            <span class="material-symbols-outlined db-filter-toggle-icon">chevron_right</span>
            Native Keyword Filters
            <span class="db-filter-toggle-count" id="db-keyword-filter-count"></span>
          </button>
        </div>
        <div class="db-keyword-filters db-filter-collapsed" id="db-keyword-filters"></div>
        <div class="db-filter-header-row">
          <button class="db-filter-row-refresh" id="db-effect-kw-filter-row-refresh" type="button" title="Clear other keyword filters">
            <span class="material-symbols-outlined">refresh</span>
          </button>
          <button class="db-filter-toggle" id="db-effect-kw-filter-toggle" type="button">
            <span class="material-symbols-outlined db-filter-toggle-icon">chevron_right</span>
            Other Keyword Filters
            <span class="db-filter-toggle-count" id="db-effect-kw-filter-count"></span>
          </button>
        </div>
        <div class="db-effect-kw-filters db-filter-collapsed" id="db-effect-kw-filters"></div>
        <div class="db-filter-header-row">
          <button class="db-filter-row-refresh" id="db-set-filter-row-refresh" type="button" title="Clear set filters">
            <span class="material-symbols-outlined">refresh</span>
          </button>
          <button class="db-filter-toggle" id="db-set-filter-toggle" type="button">
            <span class="material-symbols-outlined db-filter-toggle-icon">chevron_right</span>
            Filter by Set
            <span class="db-filter-toggle-count" id="db-set-filter-count"></span>
          </button>
        </div>
        <div class="db-set-filters db-filter-collapsed" id="db-set-filters"></div>
        <div class="db-results-count" id="db-results-count"></div>
        <div class="db-card-list" id="db-card-list"></div>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  overlay.querySelector('#db-report-bug').addEventListener('click', showReportBugModal);
  overlay.querySelector('#db-help').addEventListener('click', showDeckBuilderHelpModal);
  // onClose refreshes the unsaved-changes warning immediately in case Auto
  // Save was toggled mid-session, rather than waiting for the next edit to
  // pick it up via updateStats()'s own updateUnsavedWarning() call.
  overlay.querySelector('#db-settings').addEventListener('click', () => {
    showDeckBuilderSettingsModal(() => updateUnsavedWarning());
  });

  // Guests get a single auto-managed deck slot instead of the full named
  // multi-deck flow, so New/Load/Settings — which only make sense once
  // there's an account's worth of decks to manage — are disabled outright.
  // Import via Text, Export via Text, Quick Play, and Report Bug need no
  // account and stay fully usable (see db-save's handler and
  // showImportNameModal() below for how Save/Import route guests to their
  // single slot instead of the named-deck flow).
  if (!isLoggedIn()) {
    ['db-new', 'db-load', 'db-settings'].forEach(id => {
      const btn = document.getElementById(id);
      if (!btn) return;
      btn.disabled = true;
      btn.title = 'Sign in with Discord to use this feature';
    });
  }

  // ── Hover preview ────────────────────────────────────────────────────────
  const dbPreview = document.createElement('div');
  dbPreview.id = 'db-preview';
  document.body.appendChild(dbPreview);

  function showDbPreview(entry, anchorEl) {
    const effect = cleanEffect(entry.effectData);
    const link   = entry.link ? entry.link.replace(/[[\]]/g, '') : null;
    const pilot  = (entry.pilot && (entry.categoryData || '').toLowerCase().trim() === 'command pilot')
      ? entry.pilot.replace(/[[\]]/g, '') : null;
    const mkRow  = (label, val, cls='') => val != null && val !== ''
      ? `<div class="cp-row"><span class="cp-label">${label}</span><span class="cp-val ${cls}">${val}</span></div>` : '';
    dbPreview.innerHTML = `
      <div class="cp-img-wrap"><img src="${entry.image}" alt="${entry.name}" /></div>
      <div class="cp-stats">
        <div class="cp-name">${entry.name}</div>
        ${entry.trait ? `<div class="cp-trait">${entry.trait}</div>` : ''}
        <div class="cp-divider"></div>
        <div class="cp-stat-grid">
          ${mkRow('Level', entry.level)}
          ${mkRow('Cost',  entry.cost)}
          ${mkRow('AP', entry.ap, 'cp-atk')}
          ${mkRow('HP',  entry.hp, 'cp-hp')}
          ${mkRow('Type',  displayCardType(entry.categoryData))}
          ${(entry.categoryData || '').toLowerCase().trim() === 'unit' ? `<div class="cp-row cp-row-full"><span class="cp-label">Link Requirement</span><span class="cp-val">${link || 'None'}</span></div>` : ''}
        </div>
        ${pilot ? `<div class="cp-pilot-section"><span class="cp-label">Pilot</span><span class="cp-pilot-name">${pilot}</span></div>` : ''}
        ${effect ? `<div class="cp-effect">${effect}</div>` : ''}
      </div>`;
    dbPreview.style.display = 'flex';
    const rect = anchorEl.getBoundingClientRect();
    const pw = dbPreview.offsetWidth || 640;
    const ph = dbPreview.offsetHeight || 320;
    const vw = window.innerWidth, vh = window.innerHeight;
    let left = rect.right + 12, top = rect.top;
    if (left + pw > vw - 8) left = rect.left - pw - 12;
    if (left < 8) left = 8;
    if (top + ph > vh - 8) top = vh - ph - 8;
    if (top < 8) top = 8;
    dbPreview.style.left = left + 'px';
    dbPreview.style.top  = top  + 'px';
  }

  function hideDbPreview() { dbPreview.style.display = 'none'; }

  // ── Deck helpers ─────────────────────────────────────────────────────────
  function getDeckTotal() { let n=0; deck.forEach(c=>n+=c); return n; }
  function getSideboardTotal() { let n=0; sideboard.forEach(c=>n+=c); return n; }

  function getDeckColors() {
    const colors = new Set();
    deck.forEach((_,cardNo) => { const col=(cardDb[cardNo]?.color||'').toLowerCase(); if(col) colors.add(col); });
    return colors;
  }

  // The 2-color limit is shared between the main deck and sideboard — a
  // sideboard card's color counts against the deck's limit and vice versa,
  // so both canAdd() and canAddSideboard() check this combined set rather
  // than each Map's own colors in isolation.
  function getCombinedColors() {
    const colors = getDeckColors();
    sideboard.forEach((_,cardNo) => { const col=(cardDb[cardNo]?.color||'').toLowerCase(); if(col) colors.add(col); });
    return colors;
  }

  // Copies of a card are also shared between deck and sideboard (max 4
  // total combined, e.g. 1 in deck + 3 in sideboard is fine, but not 1 + 4).
  function getCombinedCopies(cardNo) {
    return (deck.get(cardNo) || 0) + (sideboard.get(cardNo) || 0);
  }

  function updateStats() {
    const total = getDeckTotal();
    const colors = getDeckColors();
    document.getElementById('db-stats').textContent = `${total} / 50`;
    document.getElementById('db-deck-count').textContent = total;
    document.getElementById('db-color-pills').innerHTML = [...colors].map(c =>
      `<span class="db-color-pill" style="background:${DB_COLOR_MAP[c]||'#888'};color:${c==='white'?'#1e293b':'#fff'}">${c}</span>`
    ).join('');
    renderDeckTypeSummary();
    renderComplianceWarning();
    updateUnsavedWarning();
    updateEmptyDeckButtonStates(total);
  }

  // Quick Play, Export via Text, Save Deck, and Clear Deck are all no-ops
  // (or worse — Save/Clear would silently persist/wipe an empty deck) with
  // nothing in the deck yet, so disable them outright rather than letting
  // the click through to a toast/confirm explaining why nothing happened.
  // Load Deck, New Deck, and Import via Text stay enabled — those are
  // exactly how an empty deck builder is meant to get its first cards.
  function updateEmptyDeckButtonStates(total) {
    const isEmpty = total === 0;
    ['db-play-game', 'db-export-text', 'db-save', 'db-clear'].forEach(id => {
      const btn = document.getElementById(id);
      if (btn) btn.disabled = isEmpty;
    });
  }

  // Same icon+count-per-type summary as the trash modal (showTrashModal),
  // but tallying the deck's own contents so the player can see the unit/
  // pilot/command/base breakdown of their deck at a glance.
  function renderDeckTypeSummary() {
    const el = document.getElementById('db-deck-type-summary');
    if (!el) return;
    let unitCount = 0, pilotCount = 0, commandCount = 0, baseCount = 0;
    deck.forEach((count, cardNo) => {
      const cat = (cardDb[cardNo]?.categoryData || '').toLowerCase().trim();
      if (cat === 'unit') unitCount += count;
      else if (cat === 'pilot') pilotCount += count;
      else if (cat === 'command' || cat === 'command pilot') commandCount += count;
      else if (cat === 'base' || cat === 'ex base') baseCount += count;
    });
    el.innerHTML = `
      <div class="db-deck-type-item">
        <span class="material-symbols-outlined">smart_toy</span>
        <span>Unit</span><strong>${unitCount}</strong>
      </div>
      <div class="db-deck-type-item">
        <span class="material-symbols-outlined">person</span>
        <span>Pilot</span><strong>${pilotCount}</strong>
      </div>
      <div class="db-deck-type-item">
        <span class="material-symbols-outlined">sd_card_alert</span>
        <span>Command</span><strong>${commandCount}</strong>
      </div>
      <div class="db-deck-type-item">
        <span class="material-symbols-outlined">castle</span>
        <span>Base</span><strong>${baseCount}</strong>
      </div>`;
  }

  // Flags decks that predate (or were imported/loaded despite) the July 24,
  // 2026 restricted-format rules — lists exactly which cards must be removed
  // to comply, right under the deck panel's instructions row.
  function renderComplianceWarning() {
    const el = document.getElementById('db-compliance-warning');
    if (!el) return;
    const issues = getDeckComplianceIssues(deck);
    if (issues.length === 0) {
      el.className = 'db-compliance-warning';
      el.innerHTML = '';
      return;
    }
    el.className = 'db-compliance-warning db-compliance-warning-show';
    el.innerHTML = `
      <div class="db-compliance-title">
        <span class="material-symbols-outlined">warning</span>
        This deck does not meet the current restricted format:
      </div>
      <ul>${issues.map(i => `<li>${i}</li>`).join('')}</ul>
      <div>Visit <a href="${DB_RESTRICTIONS_URL}" target="_blank" rel="noopener">HERE</a> for more information.</div>`;
  }

  // Renders into every .db-deck-name-bar element found — normally just the
  // one inside the deck panel, but mobile also has a compact copy inline in
  // the header (#db-deck-name-bar-mobile); each keeps its own edit state
  // since only one is ever visible at a given viewport width.
  function renderDeckNameBar() {
    document.querySelectorAll('.db-deck-name-bar').forEach(renderDeckNameBarInto);
  }

  function renderDeckNameBarInto(bar) {
    if (!bar) return;
    if (!currentDeckName) { bar.innerHTML = ''; return; }

    const isDraft = getDeckTotal() < 50;
    // Strip _DRAFT suffix for display; badge conveys draft status separately
    const displayName = currentDeckName.replace(/_DRAFT$/, '');

    // Built without displayName inside the markup string, then set via
    // .textContent right after — a deck name is untrusted free text (see
    // sanitizeDeckName()), and interpolating it straight into an innerHTML
    // template string would let it be parsed as markup.
    bar.innerHTML = `
      ${isLoggedIn() ? '<span class="material-symbols-outlined db-name-edit-icon">edit</span>' : ''}
      <span class="db-current-deck-name"></span>
      ${isDraft ? '<span class="db-draft-name-badge">Draft</span>' : ''}
    `;
    bar.querySelector('.db-current-deck-name').textContent = displayName;

    const startEdit = () => {
      const wasDraft = getDeckTotal() < 50;
      // Same reasoning as above, doubly so here: interpolating displayName
      // into a value="..." HTML attribute string (the previous approach)
      // lets a stray " in the name break out of the attribute entirely —
      // .value is set as a DOM property below instead, which is immune to
      // that regardless of content.
      bar.innerHTML = `
        <span class="material-symbols-outlined db-name-edit-icon">edit</span>
        <input class="db-name-input" maxlength="100" autocomplete="off" />
        ${wasDraft ? '<span class="db-draft-name-badge">Draft</span>' : ''}
      `;
      const input = bar.querySelector('.db-name-input');
      input.value = displayName;
      input.focus(); input.select();
      input.addEventListener('input', () => { input.value = sanitizeDeckName(input.value); });

      const commitRename = () => {
        let newBase = sanitizeDeckName(input.value);
        if (!newBase) { renderDeckNameBar(); return; }
        const newName = getDeckTotal() < 50 ? newBase + '_DRAFT' : newBase;
        if (newName !== currentDeckName) {
          const data = localStorage.getItem(LS_PREFIX + currentDeckName);
          if (data !== null) {
            localStorage.removeItem(LS_PREFIX + currentDeckName);
            localStorage.setItem(LS_PREFIX + newName, data);
          }
          const sbData = localStorage.getItem(SIDEBOARD_LS_PREFIX + currentDeckName);
          if (sbData !== null) {
            localStorage.removeItem(SIDEBOARD_LS_PREFIX + currentDeckName);
            localStorage.setItem(SIDEBOARD_LS_PREFIX + newName, sbData);
          }
          currentDeckName = newName;
        }
        renderDeckNameBar();
      };

      input.addEventListener('blur', commitRename);
      input.addEventListener('keydown', e => {
        if (e.key === 'Enter')  { input.blur(); }
        if (e.key === 'Escape') { renderDeckNameBar(); }
      });
    };

    // Guests' single deck slot has a fixed name (GUEST_DECK_NAME) — no
    // rename UI is rendered above, so nothing to wire here either.
    if (isLoggedIn()) {
      bar.querySelector('.db-name-edit-icon').addEventListener('click', startEdit);
      bar.querySelector('.db-current-deck-name').addEventListener('click', startEdit);
    }
  }

  function renderDeck() {
    const grid = document.getElementById('db-deck-grid');
    grid.innerHTML = '';
    if (deck.size === 0) {
      grid.innerHTML = '<div class="db-deck-empty">No cards yet.<br>Left-click to add 1 · Right-click to add 4</div>';
      updateStats(); return;
    }
    const TYPE_ORDER = { unit: 0, pilot: 1, 'command pilot': 1.5, command: 2, base: 3 };
    const deckLevel = c => parseInt(c.level) || parseInt(c.cost) || 0;
    const deckEntries = [...deck.entries()]
      .map(([cardNo, count]) => ({ cardNo, count, entry: cardDb[cardNo] }))
      .filter(d => d.entry)
      .sort((a, b) => {
        if (deckSortMode === 'color') {
          const colA = (a.entry.color || '').toLowerCase();
          const colB = (b.entry.color || '').toLowerCase();
          if (colA !== colB) return colA.localeCompare(colB);
          return deckLevel(a.entry) - deckLevel(b.entry);
        }
        const cat = c => (c.categoryData || '').toLowerCase().trim();
        const typeA = TYPE_ORDER[cat(a.entry)] ?? 99;
        const typeB = TYPE_ORDER[cat(b.entry)] ?? 99;
        if (typeA !== typeB) return typeA - typeB;
        return deckSortMode === 'desc'
          ? deckLevel(b.entry) - deckLevel(a.entry)
          : deckLevel(a.entry) - deckLevel(b.entry);
      });

    const frag = document.createDocumentFragment();
    deckEntries.forEach(({ cardNo, count, entry }) => {
      const cell = document.createElement('div');
      cell.className = 'db-deck-cell';

      const wrap = document.createElement('div');
      wrap.className = 'db-deck-card';
      wrap.innerHTML = `<img src="${entry.image}" alt="${entry.name}" loading="lazy" />`
        + (count > 1 ? `<div class="db-deck-count">×${count}</div>` : '');
      // Left-click: remove 1 copy
      wrap.addEventListener('click', () => {
        hideDbPreview();
        if (count <= 1) deck.delete(cardNo); else deck.set(cardNo, count - 1);
        renderDeck(); resetList();
      });
      // Right-click: remove all copies (up to 4) at once
      wrap.addEventListener('contextmenu', e => {
        e.preventDefault();
        hideDbPreview();
        deck.delete(cardNo);
        renderDeck(); resetList();
      });
      wrap.addEventListener('mouseenter', () => showDbPreview(entry, wrap));
      wrap.addEventListener('mouseleave', hideDbPreview);
      // Draggable to the sideboard grid — moves 1 copy there (see
      // wireGridDropTarget's 'application/x-db-source' handling below).
      wrap.draggable = true;
      wrap.addEventListener('dragstart', e => {
        e.dataTransfer.setData('text/plain', cardNo);
        e.dataTransfer.setData('application/x-db-source', 'deck');
        e.dataTransfer.effectAllowed = 'move';
        hideDbPreview();
      });

      const btnRow = document.createElement('div');
      btnRow.className = 'db-deck-btn-row';

      const addBtn = document.createElement('button');
      addBtn.type = 'button';
      addBtn.className = 'db-deck-add-btn';
      addBtn.textContent = 'Add +';
      addBtn.title = `Add 1 more copy of ${entry.name}`;
      addBtn.addEventListener('click', e => {
        e.stopPropagation();
        const check = canAdd(cardNo);
        if (check.ok) {
          deck.set(cardNo, (deck.get(cardNo) || 0) + 1);
          showToast(`${entry.name} added to deck.`);
          renderDeck(); resetList();
        } else {
          showToast(check.reason, true);
        }
      });

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'db-deck-remove-btn';
      removeBtn.textContent = 'Remove −';
      removeBtn.title = `Remove 1 copy of ${entry.name}`;
      removeBtn.addEventListener('click', e => {
        e.stopPropagation();
        hideDbPreview();
        if (count <= 1) deck.delete(cardNo); else deck.set(cardNo, count - 1);
        showToast(`${entry.name} removed from deck.`);
        renderDeck(); resetList();
      });

      btnRow.appendChild(addBtn);
      btnRow.appendChild(removeBtn);

      cell.appendChild(wrap);
      cell.appendChild(btnRow);
      frag.appendChild(cell);
    });
    grid.appendChild(frag);
    updateStats();
    renderDeckNameBar();
  }

  // Sideboard grid — same look/interactions as the main deck grid (click to
  // remove 1, right-click to remove all copies), just against `sideboard`
  // instead of `deck`, no sort toggles (10 cards never needs one), and no
  // type-summary/compliance row (see canAddSideboard's comment for why).
  function renderSideboard() {
    const grid = document.getElementById('db-sideboard-grid');
    if (!grid) return;
    grid.innerHTML = '';
    if (sideboard.size === 0) {
      grid.innerHTML = '<div class="db-deck-empty">No sideboard cards yet.</div>';
      updateSideboardStats();
      return;
    }
    const TYPE_ORDER = { unit: 0, pilot: 1, 'command pilot': 1.5, command: 2, base: 3 };
    const deckLevel = c => parseInt(c.level) || parseInt(c.cost) || 0;
    const sbEntries = [...sideboard.entries()]
      .map(([cardNo, count]) => ({ cardNo, count, entry: cardDb[cardNo] }))
      .filter(d => d.entry)
      .sort((a, b) => {
        const cat = c => (c.categoryData || '').toLowerCase().trim();
        const typeA = TYPE_ORDER[cat(a.entry)] ?? 99;
        const typeB = TYPE_ORDER[cat(b.entry)] ?? 99;
        if (typeA !== typeB) return typeA - typeB;
        return deckLevel(a.entry) - deckLevel(b.entry);
      });

    const frag = document.createDocumentFragment();
    sbEntries.forEach(({ cardNo, count, entry }) => {
      const cell = document.createElement('div');
      cell.className = 'db-deck-cell';

      const wrap = document.createElement('div');
      wrap.className = 'db-deck-card';
      wrap.innerHTML = `<img src="${entry.image}" alt="${entry.name}" loading="lazy" />`
        + (count > 1 ? `<div class="db-deck-count">×${count}</div>` : '');
      wrap.addEventListener('click', () => {
        hideDbPreview();
        if (count <= 1) sideboard.delete(cardNo); else sideboard.set(cardNo, count - 1);
        renderSideboard(); resetList();
      });
      wrap.addEventListener('contextmenu', e => {
        e.preventDefault();
        hideDbPreview();
        sideboard.delete(cardNo);
        renderSideboard(); resetList();
      });
      wrap.addEventListener('mouseenter', () => showDbPreview(entry, wrap));
      wrap.addEventListener('mouseleave', hideDbPreview);
      // Draggable to the main deck grid — moves 1 copy there (see
      // wireGridDropTarget's 'application/x-db-source' handling below).
      wrap.draggable = true;
      wrap.addEventListener('dragstart', e => {
        e.dataTransfer.setData('text/plain', cardNo);
        e.dataTransfer.setData('application/x-db-source', 'sideboard');
        e.dataTransfer.effectAllowed = 'move';
        hideDbPreview();
      });

      const btnRow = document.createElement('div');
      btnRow.className = 'db-deck-btn-row';

      const addBtn = document.createElement('button');
      addBtn.type = 'button';
      addBtn.className = 'db-deck-add-btn';
      addBtn.textContent = 'Add +';
      addBtn.title = `Add 1 more copy of ${entry.name}`;
      addBtn.addEventListener('click', e => {
        e.stopPropagation();
        const check = canAddSideboard(cardNo);
        if (check.ok) {
          sideboard.set(cardNo, (sideboard.get(cardNo) || 0) + 1);
          showToast(`${entry.name} added to sideboard.`);
          renderSideboard(); resetList();
        } else {
          showToast(check.reason, true);
        }
      });

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'db-deck-remove-btn';
      removeBtn.textContent = 'Remove −';
      removeBtn.title = `Remove 1 copy of ${entry.name}`;
      removeBtn.addEventListener('click', e => {
        e.stopPropagation();
        hideDbPreview();
        if (count <= 1) sideboard.delete(cardNo); else sideboard.set(cardNo, count - 1);
        showToast(`${entry.name} removed from sideboard.`);
        renderSideboard(); resetList();
      });

      btnRow.appendChild(addBtn);
      btnRow.appendChild(removeBtn);

      cell.appendChild(wrap);
      cell.appendChild(btnRow);
      frag.appendChild(cell);
    });
    grid.appendChild(frag);
    updateSideboardStats();
  }

  function updateSideboardStats() {
    const el = document.getElementById('db-sideboard-count');
    if (el) el.textContent = getSideboardTotal();
    renderSideboardWarning();
    updateUnsavedWarning();
  }

  // A legal sideboard is exactly 10 cards — not "up to 10". The add-side cap
  // (canAddSideboard) already stops it from going over; this just surfaces
  // "not yet at 10" as a warning, the same way the main deck shows a "Draft"
  // state under 50 — informational only, since Quick Play (best-of-1) never
  // reads the sideboard and Best of 3 (where this would actually be
  // enforced) doesn't exist yet.
  function renderSideboardWarning() {
    const el = document.getElementById('db-sideboard-warning');
    if (!el) return;
    const total = getSideboardTotal();
    if (total === 10) {
      el.className = 'db-compliance-warning';
      el.innerHTML = '';
      return;
    }
    el.className = 'db-compliance-warning db-compliance-warning-show';
    el.innerHTML = `
      <div class="db-compliance-title">
        <span class="material-symbols-outlined">warning</span>
        Sideboard must contain exactly 10 cards (currently ${total}/10).
      </div>`;
  }

  function renderDeckSortToggles() {
    ['asc', 'desc', 'color'].forEach(mode => {
      const btn = document.getElementById(
        mode === 'asc' ? 'db-deck-sort-asc' : mode === 'desc' ? 'db-deck-sort-desc' : 'db-deck-sort-color'
      );
      if (btn) btn.classList.toggle('active', deckSortMode === mode);
    });
  }

  function canAdd(cardNo) {
    const entry = cardDb[cardNo]; if (!entry) return { ok:false, reason:'Unknown card' };
    if (DB_BANNED_CARDS.has(cardNo)) return { ok:false, reason:`${entry.name} is banned — cannot be added` };
    if (getDeckTotal() >= 50)          return { ok:false, reason:'Deck is full (50/50)' };
    // Copy limit is shared with the sideboard (max 4 total between the two —
    // e.g. 1 in deck + 3 in sideboard is fine, 1 + 4 is not).
    const copyLimit = getCardCopyLimit(cardNo);
    if (getCombinedCopies(cardNo) >= copyLimit) return { ok:false, reason:`Max ${copyLimit} cop${copyLimit === 1 ? 'y' : 'ies'} per card (deck + sideboard combined)` };
    for (const pair of DB_BANNED_PAIRS) {
      if (!pair.includes(cardNo)) continue;
      const other = pair[0] === cardNo ? pair[1] : pair[0];
      if ((deck.get(other) || 0) > 0) {
        return { ok:false, reason:`Cannot include with ${cardDb[other]?.name || other} (banned pair)` };
      }
    }
    if (isBasicLv2Unit(cardNo)) {
      for (const [no, count] of deck) {
        if (count > 0 && no !== cardNo && isBasicLv2Unit(no)) {
          return { ok:false, reason:'Only one type of Basic Lv.2 Unit allowed per deck' };
        }
      }
    }
    // 2-color limit is shared with the sideboard — see getCombinedColors().
    const colors = getCombinedColors();
    const cardColor = (entry.color||'').toLowerCase();
    if (cardColor && !colors.has(cardColor) && colors.size >= 2)
      return { ok:false, reason:'Max 2 colors per deck (deck + sideboard combined)' };
    return { ok:true };
  }

  // Sideboard legality mirrors the main deck's color-limit and combined
  // copy-limit rules (both shared between the two — see getCombinedColors/
  // getCombinedCopies), but not the banned-pair or Basic-Lv.2-uniqueness
  // rules, which are about a single deck's own archetype construction and
  // were never requested to extend to the sideboard.
  function canAddSideboard(cardNo) {
    const entry = cardDb[cardNo]; if (!entry) return { ok:false, reason:'Unknown card' };
    if (DB_BANNED_CARDS.has(cardNo)) return { ok:false, reason:`${entry.name} is banned — cannot be added` };
    if (getSideboardTotal() >= 10) return { ok:false, reason:'Sideboard is full (10/10)' };
    const copyLimit = getCardCopyLimit(cardNo);
    if (getCombinedCopies(cardNo) >= copyLimit) return { ok:false, reason:`Max ${copyLimit} cop${copyLimit === 1 ? 'y' : 'ies'} per card (deck + sideboard combined)` };
    const colors = getCombinedColors();
    const cardColor = (entry.color||'').toLowerCase();
    if (cardColor && !colors.has(cardColor) && colors.size >= 2)
      return { ok:false, reason:'Max 2 colors per deck (deck + sideboard combined)' };
    return { ok:true };
  }

  // Small "clear this row" icon button, prepended inline into a pill row
  // (Color/Type Filters — unlike the Keyword filter sections, these have no
  // always-visible header row to hang a refresh icon off of on desktop, so
  // it lives inside the same flex-wrap row as the pills instead of above
  // them). onClear should mutate the relevant Set and re-render.
  function buildFilterRowRefreshBtn(title, onClear) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'db-filter-row-refresh';
    btn.title = title;
    btn.innerHTML = '<span class="material-symbols-outlined">refresh</span>';
    btn.addEventListener('click', onClear);
    return btn;
  }

  // ── Color toggle filters ──────────────────────────────────────────────────
  // Renders both a full color name (shown on desktop) and a single-letter
  // abbreviation (shown on mobile, where .db-color-toggle collapses to a
  // circle) — CSS media queries pick which span is visible, no JS branching
  // on viewport needed. First letters are unique across all colors (Purple,
  // Blue, White, Red, Green), so no collision table is needed.
  function renderColorToggles() {
    const container = document.getElementById('db-color-filters');
    container.innerHTML = '';
    container.appendChild(buildFilterRowRefreshBtn('Clear color filters', () => {
      if (activeColors.size === 0) return;
      activeColors.clear();
      renderColorToggles();
      resetList();
    }));
    allColors.forEach(color => {
      const btn = document.createElement('button');
      btn.className = 'db-color-toggle' + (activeColors.has(color) ? ' active' : '');
      btn.style.setProperty('--cc', DB_COLOR_MAP[color] || '#888');
      btn.title = color.charAt(0).toUpperCase() + color.slice(1);
      btn.innerHTML = `<span class="db-color-toggle-full">${color}</span><span class="db-color-toggle-abbr">${color.charAt(0).toUpperCase()}</span>`;
      btn.addEventListener('click', () => {
        if (activeColors.has(color)) activeColors.delete(color);
        else activeColors.add(color);
        renderColorToggles();
        resetList();
      });
      container.appendChild(btn);
    });
    updateFilterToggleCount('db-color-filter-count', activeColors.size);
  }

  // ── Type toggle filters ───────────────────────────────────────────────────
  // Icons match the ones used in renderDeckTypeSummary() for the same types.
  const TYPE_FILTERS = [
    { key: 'unit',    label: 'Unit',    color: '#3b82f6', icon: 'smart_toy' },
    { key: 'pilot',   label: 'Pilot',   color: '#f59e0b', icon: 'person' },
    { key: 'command', label: 'Command', color: '#a855f7', icon: 'sd_card_alert' },
    { key: 'base',    label: 'Base',    color: '#10b981', icon: 'castle' },
  ];

  function matchesActiveTypes(card) {
    if (activeTypes.size === 0) return true;
    const cat = (card.categoryData || '').toLowerCase().trim();
    return [...activeTypes].some(type => {
      if (type === 'pilot')   return cat === 'pilot'   || cat === 'command pilot';
      if (type === 'command') return cat === 'command' || cat === 'command pilot';
      return cat === type;
    });
  }

  // Renders an icon (always visible — used as the mobile, circle-button
  // form) alongside the text label (hidden on mobile via CSS, shown on
  // desktop) so the same markup serves both layouts.
  function renderTypeToggles() {
    const container = document.getElementById('db-type-filters');
    container.innerHTML = '';
    container.appendChild(buildFilterRowRefreshBtn('Clear type filters', () => {
      if (activeTypes.size === 0) return;
      activeTypes.clear();
      renderTypeToggles();
      resetList();
    }));
    TYPE_FILTERS.forEach(({ key, label, color, icon }) => {
      const btn = document.createElement('button');
      btn.className = 'db-type-toggle' + (activeTypes.has(key) ? ' active' : '');
      btn.style.setProperty('--tc', color);
      btn.title = label;
      btn.innerHTML = `<span class="material-symbols-outlined db-type-toggle-icon">${icon}</span><span class="db-type-toggle-label">${label}</span>`;
      btn.addEventListener('click', () => {
        if (activeTypes.has(key)) activeTypes.delete(key);
        else activeTypes.add(key);
        renderTypeToggles();
        resetList();
      });
      container.appendChild(btn);
    });
    updateFilterToggleCount('db-type-filter-count', activeTypes.size);
  }

  // ── Keyword toggle filters (Blocker, Suppression, Repair, ...) ────────────
  // Same per-pill colored-outline treatment as TYPE_FILTERS/.db-type-toggle —
  // each keyword gets its own accent so the row reads as one family with the
  // type filters above it rather than a flat single-color block.
  const KEYWORD_COLORS = {
    'Blocker':       '#3b82f6',
    'Suppression':   '#ef4444',
    'Repair':        '#10b981',
    'Breach':        '#f97316',
    'Support':       '#06b6d4',
    'First Strike':  '#eab308',
    'High-Maneuver': '#a855f7',
  };
  function keywordColor(keyword) { return KEYWORD_COLORS[keyword] || '#888'; }

  function matchesActiveKeywords(card) {
    if (activeKeywords.size === 0) return true;
    const keywords = cardKeywords(card);
    return [...activeKeywords].some(k => keywords.includes(k));
  }

  // Wires a collapsed-by-default filter section's header toggle button.
  // Collapsed state lives purely in the DOM (a class on the panel) so it
  // survives renderKeywordToggles()/renderEffectKwToggles() rebuilding the
  // panel's contents without resetting open/closed state.
  //
  // Also auto-collapses the panel 10s after it's expanded, to keep an
  // opened-and-forgotten section from permanently eating vertical space.
  // The countdown resets on any click inside the panel (selecting/
  // deselecting a pill), so it only fires after 10s of no activity — never
  // mid-selection. Listened for via delegation on the panel container
  // itself (not the individual pill buttons), since renderColorToggles()/
  // renderTypeToggles()/renderKeywordToggles()/renderEffectKwToggles()/
  // renderSetToggles() all rebuild their buttons from scratch on every
  // selection, which would otherwise drop a listener attached directly to
  // a pill.
  const FILTER_AUTO_COLLAPSE_MS = 10000;
  function wireFilterToggle(toggleId, panelId) {
    const toggle = document.getElementById(toggleId);
    const panel = document.getElementById(panelId);
    if (!toggle || !panel) return;

    let autoCollapseTimer = null;
    function clearAutoCollapse() {
      clearTimeout(autoCollapseTimer);
      autoCollapseTimer = null;
    }
    function scheduleAutoCollapse() {
      clearAutoCollapse();
      autoCollapseTimer = setTimeout(() => {
        panel.classList.add('db-filter-collapsed');
        toggle.classList.remove('db-filter-toggle-open');
        autoCollapseTimer = null;
      }, FILTER_AUTO_COLLAPSE_MS);
    }

    toggle.addEventListener('click', () => {
      const collapsed = panel.classList.toggle('db-filter-collapsed');
      toggle.classList.toggle('db-filter-toggle-open', !collapsed);
      if (collapsed) clearAutoCollapse();
      else scheduleAutoCollapse();
    });
    panel.addEventListener('click', () => {
      if (!panel.classList.contains('db-filter-collapsed')) scheduleAutoCollapse();
    });
  }

  function updateFilterToggleCount(countId, count) {
    const el = document.getElementById(countId);
    if (el) el.textContent = count > 0 ? String(count) : '';
  }

  function renderKeywordToggles() {
    const container = document.getElementById('db-keyword-filters');
    container.innerHTML = '';
    allKeywords.forEach(keyword => {
      const btn = document.createElement('button');
      btn.className = 'db-keyword-toggle' + (activeKeywords.has(keyword) ? ' active' : '');
      btn.style.setProperty('--kc', keywordColor(keyword));
      btn.textContent = keyword;
      btn.addEventListener('click', () => {
        if (activeKeywords.has(keyword)) activeKeywords.delete(keyword);
        else activeKeywords.add(keyword);
        renderKeywordToggles();
        resetList();
      });
      container.appendChild(btn);
    });
    updateFilterToggleCount('db-keyword-filter-count', activeKeywords.size);
  }

  // ── Effect keyword toggle filters ────────────────────────────────────────
  // Matches against the bracketed trigger tags in effectData, e.g. [Burst],
  // [Deploy], [Activate･Main]. Each entry maps a display label to the exact
  // substring to search for (including brackets). LINK_REQ_TAG and
  // COST_EFFECT_TAG are sentinels handled specially in
  // matchesActiveEffectKws rather than as a plain substring search —
  // LINK_REQ_TAG checks card.link, COST_EFFECT_TAG checks for a circled-
  // number cost badge (①②③... — see cleanEffect()'s [①-⑳] handling)
  // anywhere in the raw effect text.
  const LINK_REQ_TAG = '__HAS_LINK_REQ__';
  const COST_EFFECT_TAG = '__HAS_COST_EFFECT__';
  const CIRCLED_NUM_RE = /[①-⑳]/;
  const EFFECT_KW_FILTERS = [
    { label: 'Burst',           tag: '[Burst]' },
    { label: 'When Linked',     tag: '[When Linked]' },
    { label: 'During Link',     tag: '[During Link]' },
    { label: 'When Paired',     tag: '[When Paired]' },
    { label: 'During Pair',     tag: '[During Pair]' },
    { label: 'Deploy',          tag: '[Deploy]' },
    { label: 'Attack',          tag: '[Attack]' },
    { label: 'Activate Main',   tag: '[Activate･Main]' },
    { label: 'Activate Action', tag: '[Activate･Action]' },
    { label: 'Destroyed',       tag: '[Destroyed]' },
    { label: 'Main',            tag: '[Main]' },
    { label: 'Action',          tag: '[Action]' },
    { label: 'Development',     tag: 'Development' },
    { label: 'Has a link requirement', tag: LINK_REQ_TAG },
    { label: 'Cost Effect',     tag: COST_EFFECT_TAG },
  ];
  const EFFECT_KW_COLOR = '#64748b';

  function matchesActiveEffectKws(card) {
    if (activeEffectKws.size === 0) return true;
    const effect = card.effectData || '';
    return [...activeEffectKws].some(tag => {
      if (tag === LINK_REQ_TAG) return !!card.link;
      if (tag === COST_EFFECT_TAG) return CIRCLED_NUM_RE.test(effect);
      return effect.includes(tag);
    });
  }

  function renderEffectKwToggles() {
    const container = document.getElementById('db-effect-kw-filters');
    container.innerHTML = '';
    EFFECT_KW_FILTERS.forEach(({ label, tag }) => {
      const btn = document.createElement('button');
      btn.className = 'db-effect-kw-toggle' + (activeEffectKws.has(tag) ? ' active' : '');
      btn.textContent = label;
      btn.addEventListener('click', () => {
        if (activeEffectKws.has(tag)) activeEffectKws.delete(tag);
        else activeEffectKws.add(tag);
        renderEffectKwToggles();
        resetList();
      });
      container.appendChild(btn);
    });
    updateFilterToggleCount('db-effect-kw-filter-count', activeEffectKws.size);
  }

  // ── Set toggle filters (ST01, GD01, EB01, ...) ───────────────────────────
  function matchesActiveSets(card) {
    if (activeSets.size === 0) return true;
    return activeSets.has(cardSet(card));
  }

  function renderSetToggles() {
    const container = document.getElementById('db-set-filters');
    container.innerHTML = '';
    allSets.forEach(set => {
      const btn = document.createElement('button');
      btn.className = 'db-set-toggle' + (activeSets.has(set) ? ' active' : '');
      btn.textContent = set;
      btn.addEventListener('click', () => {
        if (activeSets.has(set)) activeSets.delete(set);
        else activeSets.add(set);
        renderSetToggles();
        resetList();
      });
      container.appendChild(btn);
    });
    updateFilterToggleCount('db-set-filter-count', activeSets.size);
  }

  // ── Tag-based search ────────────────────────────────────────────────────
  function cardMatchesToken(c, token) {
    const t = token.toLowerCase();
    return (c.name         || '').toLowerCase().includes(t) ||
           (c.cardNo       || '').toLowerCase().includes(t) ||
           (c.trait        || '').toLowerCase().includes(t) ||
           (c.effectData   || '').toLowerCase().includes(t) ||
           (c.categoryData || '').toLowerCase().includes(t) ||
           (c.color        || '').toLowerCase().includes(t) ||
           (c.nativeKeyword|| '').toLowerCase().includes(t) ||
           (c.link         || '').toLowerCase().includes(t);
  }

  function renderSearchTags() {
    const field = document.getElementById('db-search-field');
    const input = document.getElementById('db-search');
    if (!field || !input) return;
    field.querySelectorAll('.db-search-tag').forEach(el => el.remove());
    searchTags.forEach((tag, i) => {
      const pill = document.createElement('span');
      pill.className = 'db-search-tag';
      const label = document.createTextNode(tag);
      const x = document.createElement('button');
      x.className = 'db-search-tag-x';
      x.textContent = '×';
      x.addEventListener('click', e => {
        e.stopPropagation();
        searchTags.splice(i, 1);
        renderSearchTags();
        resetList();
      });
      pill.appendChild(label);
      pill.appendChild(x);
      field.insertBefore(pill, input);
    });
    input.placeholder = searchTags.length === 0
      ? 'Search name, trait, card no., effect… (comma to tag)'
      : 'Add tag…';
  }

  // ── Level sort toggles ──────────────────────────────────────────────────
  // Clicking the already-active direction toggles it back off (default order).
  function cardSortLevel(c) { return parseInt(c.level) || parseInt(c.cost) || 0; }

  function renderSortToggles() {
    ['desc', 'asc'].forEach(dir => {
      const btn = document.getElementById(dir === 'desc' ? 'db-sort-desc' : 'db-sort-asc');
      btn.classList.toggle('active', levelSortDir === dir);
    });
  }
  document.getElementById('db-sort-desc').addEventListener('click', () => {
    levelSortDir = levelSortDir === 'desc' ? null : 'desc';
    renderSortToggles();
    resetList();
  });
  document.getElementById('db-sort-asc').addEventListener('click', () => {
    levelSortDir = levelSortDir === 'asc' ? null : 'asc';
    renderSortToggles();
    resetList();
  });
  renderSortToggles(); // reflect default 'asc' on open

  // ── Deck sort toggles (Low→High / High→Low / By Color) ────────────────────
  ['db-deck-sort-asc', 'db-deck-sort-desc', 'db-deck-sort-color'].forEach(id => {
    document.getElementById(id).addEventListener('click', () => {
      deckSortMode = document.getElementById(id).dataset.mode;
      renderDeckSortToggles();
      renderDeck();
    });
  });
  renderDeckSortToggles(); // reflect default 'asc' on open

  // ── Paginated card list with IntersectionObserver ─────────────────────────
  const scrollObserver = new IntersectionObserver(entries => {
    if (entries[0].isIntersecting) { entries[0].target.remove(); appendPage(); }
  }, { root: document.getElementById('db-card-list'), rootMargin: '120px' });

  function getFiltered() {
    const q = (document.getElementById('db-search')?.value || '').toLowerCase().trim();
    const costVal  = (document.getElementById('db-filter-cost')?.value  || '').trim();
    const levelVal = (document.getElementById('db-filter-level')?.value || '').trim();
    const hpVal    = (document.getElementById('db-filter-hp')?.value    || '').trim();
    const apVal    = (document.getElementById('db-filter-ap')?.value    || '').trim();
    const filtered = allCards.filter(c => {
      const matchColor     = activeColors.size === 0 || activeColors.has((c.color||'').toLowerCase());
      const matchType      = matchesActiveTypes(c);
      const matchKeyword   = matchesActiveKeywords(c);
      const matchEffectKw  = matchesActiveEffectKws(c);
      const matchSet       = matchesActiveSets(c);
      const matchHideNew   = !hideNewRelease || !isNewReleaseCard(c);
      const matchFavorite  = !showFavoritesOnly || favoriteCards.has(c.cardNo);
      const matchTags  = searchTags.length === 0 || searchTags.some(tag => cardMatchesToken(c, tag));
      const matchText  = !q || cardMatchesToken(c, q);
      const matchCost  = !costVal  || String(c.cost  ?? '') === costVal;
      const matchLevel = !levelVal || String(c.level ?? '') === levelVal;
      const matchHp    = !hpVal    || String(c.hp    ?? '') === hpVal;
      const matchAp    = !apVal    || String(c.ap    ?? '') === apVal;
      return matchColor && matchType && matchKeyword && matchEffectKw && matchSet && matchHideNew && matchFavorite && matchTags && matchText && matchCost && matchLevel && matchHp && matchAp;
    });
    if (levelSortDir) {
      filtered.sort((a, b) => levelSortDir === 'desc'
        ? cardSortLevel(b) - cardSortLevel(a)
        : cardSortLevel(a) - cardSortLevel(b));
    }
    return filtered;
  }

  // Updates a browse item's badge and highlight class in-place after the deck
  // changes, without rebuilding the list (which would reset scroll position).
  function refreshBrowseItem(item, entry) {
    const inDeck = deck.get(entry.cardNo) || 0;
    const inSideboard = sideboard.get(entry.cardNo) || 0;
    item.className = 'db-browse-item' + (inDeck > 0 ? ' db-in-deck' : '') + (DB_BANNED_CARDS.has(entry.cardNo) ? ' db-banned-item' : '');
    const badge = item.querySelector('.db-browse-badge, .db-browse-badge-empty');
    if (badge) {
      badge.className = inDeck > 0 ? 'db-browse-badge' : 'db-browse-badge-empty';
      badge.textContent = inDeck > 0 ? `MD ×${inDeck}` : '';
    }
    const sbBadge = item.querySelector('.db-browse-sb-badge');
    if (inSideboard > 0) {
      if (sbBadge) {
        sbBadge.textContent = `SB ×${inSideboard}`;
      } else {
        const el = document.createElement('div');
        el.className = 'db-browse-sb-badge';
        el.title = 'Copies in sideboard';
        el.textContent = `SB ×${inSideboard}`;
        badge?.insertAdjacentElement('beforebegin', el);
      }
    } else if (sbBadge) {
      sbBadge.remove();
    }
  }

  function makeBrowseItem(entry) {
    const inDeck = deck.get(entry.cardNo) || 0;
    const inSideboard = sideboard.get(entry.cardNo) || 0;
    const isBanned = DB_BANNED_CARDS.has(entry.cardNo);
    const item = document.createElement('div');
    item.className = 'db-browse-item' + (inDeck > 0 ? ' db-in-deck' : '') + (isBanned ? ' db-banned-item' : '');
    item.dataset.cardno = entry.cardNo; // looked up by the grid drop handlers below to refresh this exact row
    const color = (entry.color||'').toLowerCase();
    const dot   = color ? `<span class="db-color-dot" style="background:${DB_COLOR_MAP[color]||'#888'}"></span>` : '';
    const copyLimit = getCardCopyLimit(entry.cardNo);
    const limitBadge = isBanned
      ? `<span class="db-banned-badge" title="Banned — cannot be used in decks or sideboards"><span class="material-symbols-outlined">block</span></span>`
      : copyLimit < 4
        ? `<span class="db-copy-limit-badge" title="Limited to ${copyLimit} cop${copyLimit === 1 ? 'y' : 'ies'} per deck">${copyLimit}</span>`
        : '';
    const meta  = [displayCardType(entry.categoryData), entry.color, entry.level?`Lv.${entry.level}`:'', entry.ap?`AP ${entry.ap}`:'', entry.hp?`HP ${entry.hp}`:''].filter(Boolean).join(' · ');
    const newBadge = isNewReleaseCard(entry)
      ? `<span class="db-new-badge" title="From a recently released set"><span class="material-symbols-outlined">release_alert</span>NEW</span>`
      : '';
    const sbBadge = inSideboard > 0
      ? `<div class="db-browse-sb-badge" title="Copies in sideboard">SB ×${inSideboard}</div>`
      : '';
    const isFavorited = favoriteCards.has(entry.cardNo);
    item.draggable = true;
    item.innerHTML = `
      <img class="db-browse-img" src="${entry.image}" alt="${entry.name}" loading="lazy" />
      <div class="db-browse-info">
        <div class="db-browse-name">${entry.name}</div>
        <div class="db-browse-meta">${meta}</div>
      </div>
      ${dot}
      ${newBadge}
      ${limitBadge}
      ${sbBadge}
      ${inDeck>0 ? `<div class="db-browse-badge">MD ×${inDeck}</div>` : '<div class="db-browse-badge-empty"></div>'}
      <button class="db-sideboard-add-btn" type="button" title="Add 1 copy to sideboard">
        <span class="material-symbols-outlined">add_column_right</span>
      </button>
      <button class="db-favorite-btn${isFavorited ? ' db-favorited' : ''}" type="button" title="${isFavorited ? 'Remove from favorites' : 'Add to favorites'}">
        <span class="material-symbols-outlined">favorite</span>
      </button>`;
    // Left-click: add 1 copy to the main deck
    item.addEventListener('click', e => {
      const check = canAdd(entry.cardNo);
      if (check.ok) {
        deck.set(entry.cardNo, (deck.get(entry.cardNo) || 0) + 1);
        showToast(`${entry.name} added to deck.`);
        renderDeck(); refreshBrowseItem(item, entry);
      } else { showToast(check.reason, true); }
    });
    // Right-click: add up to 4 copies to the main deck at once (naturally
    // stops early against the 50-card cap / per-card copy limit too)
    item.addEventListener('contextmenu', e => {
      e.preventDefault();
      let added = 0;
      for (let i = 0; i < 4; i++) {
        const check = canAdd(entry.cardNo);
        if (!check.ok) break;
        deck.set(entry.cardNo, (deck.get(entry.cardNo) || 0) + 1);
        added++;
      }
      if (added > 0) {
        showToast(`${entry.name} ×${added} added to deck.`);
        renderDeck(); refreshBrowseItem(item, entry);
      } else {
        showToast(canAdd(entry.cardNo).reason || 'Cannot add more copies', true);
      }
    });
    // Dragging a browse item carries its cardNo — dropped on either the deck
    // grid or sideboard grid to add 1 copy there (see the dragover/drop
    // wiring on #db-deck-grid/#db-sideboard-grid below).
    item.addEventListener('dragstart', e => {
      e.dataTransfer.setData('text/plain', entry.cardNo);
      e.dataTransfer.effectAllowed = 'copy';
      hideDbPreview();
    });
    item.addEventListener('mouseenter', () => showDbPreview(entry, item));
    item.addEventListener('mouseleave', hideDbPreview);
    // Favorite toggle — stopPropagation on both events so it never also
    // triggers the item's own click (add 1)/contextmenu (add 4) handlers
    // above, which would otherwise fire too since this button sits inside it.
    const favoriteBtn = item.querySelector('.db-favorite-btn');
    favoriteBtn.addEventListener('click', e => {
      e.stopPropagation();
      const nowFavorited = !favoriteCards.has(entry.cardNo);
      if (nowFavorited) favoriteCards.add(entry.cardNo); else favoriteCards.delete(entry.cardNo);
      saveFavoriteCards(favoriteCards);
      favoriteBtn.classList.toggle('db-favorited', nowFavorited);
      favoriteBtn.title = nowFavorited ? 'Remove from favorites' : 'Add to favorites';
      showToast(nowFavorited ? `${entry.name} added to favorites.` : `${entry.name} removed from favorites.`);
      // Show Favorite is currently filtering the list to just favorites —
      // un-favoriting a card while that's active should drop it immediately.
      if (showFavoritesOnly && !nowFavorited) resetList();
    });
    favoriteBtn.addEventListener('contextmenu', e => e.stopPropagation());
    // Add-to-sideboard icon — same stopPropagation convention as the
    // favorite button above, 1 copy per click (right-click does nothing
    // special here, just suppressed so it doesn't fall through to the
    // item's own add-4-to-deck handler).
    const sideboardBtn = item.querySelector('.db-sideboard-add-btn');
    sideboardBtn.addEventListener('click', e => {
      e.stopPropagation();
      const check = canAddSideboard(entry.cardNo);
      if (check.ok) {
        sideboard.set(entry.cardNo, (sideboard.get(entry.cardNo) || 0) + 1);
        showToast(`${entry.name} added to sideboard.`);
        renderSideboard(); refreshBrowseItem(item, entry);
      } else { showToast(check.reason, true); }
    });
    sideboardBtn.addEventListener('contextmenu', e => e.stopPropagation());
    return item;
  }

  function appendPage() {
    const list = document.getElementById('db-card-list');
    const start = currentPage * PAGE_SIZE;
    const end   = Math.min(start + PAGE_SIZE, currentFiltered.length);
    if (start >= currentFiltered.length) return;
    const frag = document.createDocumentFragment();
    for (let i = start; i < end; i++) frag.appendChild(makeBrowseItem(currentFiltered[i]));
    list.appendChild(frag);
    currentPage++;
    if (currentPage * PAGE_SIZE < currentFiltered.length) {
      const sentinel = document.createElement('div');
      sentinel.className = 'db-sentinel';
      list.appendChild(sentinel);
      scrollObserver.observe(sentinel);
    }
  }

  function resetList() {
    hideDbPreview();
    currentFiltered = getFiltered();
    currentPage = 0;
    document.getElementById('db-results-count').textContent =
      `${currentFiltered.length.toLocaleString()} card${currentFiltered.length!==1?'s':''} — right-click to add`;
    document.getElementById('db-card-list').innerHTML = '';
    appendPage();
  }

  // ── Wire up events ────────────────────────────────────────────────────────
  const _dbSearch = document.getElementById('db-search');
  _dbSearch.addEventListener('keydown', e => {
    if (e.key === ',') {
      e.preventDefault();
      const val = _dbSearch.value.trim();
      if (val) { searchTags.push(val); _dbSearch.value = ''; renderSearchTags(); resetList(); }
    } else if (e.key === 'Backspace' && _dbSearch.value === '' && searchTags.length > 0) {
      searchTags.pop(); renderSearchTags(); resetList();
    }
  });
  _dbSearch.addEventListener('input', resetList);
  document.getElementById('db-search-field').addEventListener('click', () => _dbSearch.focus());
  document.getElementById('db-filter-cost').addEventListener('input', resetList);
  document.getElementById('db-filter-level').addEventListener('input', resetList);
  document.getElementById('db-filter-hp').addEventListener('input', resetList);
  document.getElementById('db-filter-ap').addEventListener('input', resetList);
  renderSearchTags();

  document.getElementById('db-filter-reset').addEventListener('click', () => {
    activeColors.clear(); activeTypes.clear(); activeKeywords.clear(); activeEffectKws.clear(); activeSets.clear();
    hideNewRelease = false;
    document.getElementById('db-hide-new-toggle').classList.remove('active');
    showFavoritesOnly = false;
    document.getElementById('db-show-favorite-toggle').classList.remove('active');
    searchTags.length = 0;
    _dbSearch.value = '';
    document.getElementById('db-filter-cost').value = '';
    document.getElementById('db-filter-level').value = '';
    document.getElementById('db-filter-hp').value = '';
    document.getElementById('db-filter-ap').value = '';
    levelSortDir = 'asc';
    renderColorToggles();
    renderTypeToggles();
    renderKeywordToggles();
    renderEffectKwToggles();
    renderSetToggles();
    renderSortToggles();
    renderSearchTags();
    resetList();
    showToast('Filters reset.');
  });

  document.getElementById('db-hide-new-toggle').addEventListener('click', (e) => {
    hideNewRelease = !hideNewRelease;
    e.currentTarget.classList.toggle('active', hideNewRelease);
    resetList();
  });

  document.getElementById('db-show-favorite-toggle').addEventListener('click', (e) => {
    showFavoritesOnly = !showFavoritesOnly;
    e.currentTarget.classList.toggle('active', showFavoritesOnly);
    resetList();
  });

  // Drag-and-drop: dropping a browse item (see its 'dragstart' handler in
  // makeBrowseItem(), which carries the cardNo as plain text) onto either
  // grid adds 1 copy there — same legality checks and toast/render calls as
  // a left-click add. db-drag-over is purely a visual affordance (CSS).
  // `source` carried on the drag (see the dragstart handlers in
  // makeBrowseItem()/renderDeck()/renderSideboard()) is '' for a card
  // dragged from the browse panel (add a new copy), or 'deck'/'sideboard'
  // for a card dragged off the OTHER grid (move 1 copy across — decrement
  // the other side first so canAddFn's combined-copy/color checks correctly
  // see the state as it'll be AFTER the move, not before; rolled back if the
  // target rejects it, e.g. target already full).
  function wireGridDropTarget(gridId, ownName, ownMap, canAddFn, ownRenderFn, otherMap, otherRenderFn) {
    const grid = document.getElementById(gridId);
    if (!grid) return;
    grid.addEventListener('dragover', e => {
      e.preventDefault();
      e.dataTransfer.dropEffect = e.dataTransfer.types.includes('application/x-db-source') ? 'move' : 'copy';
      grid.classList.add('db-drag-over');
    });
    grid.addEventListener('dragleave', () => grid.classList.remove('db-drag-over'));
    grid.addEventListener('drop', e => {
      e.preventDefault();
      grid.classList.remove('db-drag-over');
      const cardNo = e.dataTransfer.getData('text/plain');
      const source = e.dataTransfer.getData('application/x-db-source');
      const entry = cardDb[cardNo];
      if (!cardNo || !entry) return;
      if (source === ownName) return; // dropped back onto the grid it came from — no-op

      if (source) {
        // Moving a card off the OTHER grid.
        const otherCount = otherMap.get(cardNo) || 0;
        if (otherCount <= 0) return;
        if (otherCount <= 1) otherMap.delete(cardNo); else otherMap.set(cardNo, otherCount - 1);
        const check = canAddFn(cardNo);
        if (!check.ok) {
          otherMap.set(cardNo, otherCount); // roll back — leave it where it was
          showToast(check.reason, true);
          return;
        }
        ownMap.set(cardNo, (ownMap.get(cardNo) || 0) + 1);
        showToast(`${entry.name} moved to ${ownName}.`);
        ownRenderFn();
        otherRenderFn();
      } else {
        // From the browse panel — add a new copy.
        const check = canAddFn(cardNo);
        if (!check.ok) { showToast(check.reason, true); return; }
        ownMap.set(cardNo, (ownMap.get(cardNo) || 0) + 1);
        showToast(`${entry.name} added to ${ownName}.`);
        ownRenderFn();
      }
      const row = document.querySelector(`.db-browse-item[data-cardno="${cardNo}"]`);
      if (row) refreshBrowseItem(row, entry); else resetList();
    });
  }
  wireGridDropTarget('db-deck-grid', 'deck', deck, canAdd, renderDeck, sideboard, renderSideboard);
  wireGridDropTarget('db-sideboard-grid', 'sideboard', sideboard, canAddSideboard, renderSideboard, deck, renderDeck);

  // Per-row refresh buttons for Native/Other Keyword Filters — each clears
  // only its own filter group's selections, unlike "Reset All" above which
  // clears everything at once. Color/Type Filters get the same treatment,
  // but via buildFilterRowRefreshBtn() inside renderColorToggles()/
  // renderTypeToggles() instead of a static button here — those two rows
  // have no always-visible header on desktop to hang a button off of, so
  // their refresh icon renders inline inside the pill row itself.
  document.getElementById('db-keyword-filter-row-refresh').addEventListener('click', () => {
    if (activeKeywords.size === 0) return;
    activeKeywords.clear();
    renderKeywordToggles();
    resetList();
  });
  document.getElementById('db-effect-kw-filter-row-refresh').addEventListener('click', () => {
    if (activeEffectKws.size === 0) return;
    activeEffectKws.clear();
    renderEffectKwToggles();
    resetList();
  });
  document.getElementById('db-set-filter-row-refresh').addEventListener('click', () => {
    if (activeSets.size === 0) return;
    activeSets.clear();
    renderSetToggles();
    resetList();
  });

  // ── Panel resize (drag handle between deck panel and browse panel) ────────
  const dragHandle = document.getElementById('db-drag-handle');
  const dbBody = overlay.querySelector('.db-body');
  const deckPanelEl = document.getElementById('db-deck-panel');
  let dragging = false;
  function onDragMove(e) {
    if (!dragging) return;
    const rect = dbBody.getBoundingClientRect();
    let pct = ((e.clientX - rect.left) / rect.width) * 100;
    pct = Math.max(30, Math.min(85, pct));
    deckPanelEl.style.width = pct + '%';
  }
  function onDragEnd() {
    if (!dragging) return;
    dragging = false;
    dragHandle.classList.remove('dragging');
    document.body.style.userSelect = '';
  }
  dragHandle.addEventListener('mousedown', e => {
    dragging = true;
    dragHandle.classList.add('dragging');
    document.body.style.userSelect = 'none';
    e.preventDefault();
  });
  window.addEventListener('mousemove', onDragMove);
  window.addEventListener('mouseup', onDragEnd);

  // Both exit paths — the Back button and Play A Game — funnel through here,
  // so this is the one place that needs to auto-save an unsaved deck before
  // it's gone. Only when the "Auto save decks in deck builder" Settings
  // toggle is on (getAutoSaveDecks(), default true); when it's off, leaving
  // with unsaved changes intentionally does NOT save (the player already saw
  // #db-unsaved-warning while building).
  function closeBuilder(callback) {
    if (hasUnsavedChanges() && getAutoSaveDecks()) {
      // Guests always auto-save to their one fixed slot rather than
      // AUTO_SAVE_DRAFT_NAME, which would be a second guest deck.
      const autoSaveName = isLoggedIn() ? (currentDeckName || AUTO_SAVE_DRAFT_NAME) : GUEST_DECK_NAME;
      if (!wouldExceedDeckCap(autoSaveName)) {
        saveDeckToStorage(autoSaveName, { auto: true });
      }
    }
    hideDbPreview();
    dbPreview.remove();
    window.removeEventListener('mousemove', onDragMove);
    window.removeEventListener('mouseup', onDragEnd);
    overlay.classList.add('mm-fade-out');
    callback(); // mount the destination screen before removing this overlay
    setTimeout(() => overlay.remove(), 400);
  }

  // ── Deck JSON helpers ─────────────────────────────────────────────────────
  function buildDeckJson() {
    const result = [];
    deck.forEach((count, cardNo) => {
      const entry = buildDeckEntryFromDb(cardNo, count);
      if (entry) result.push(entry);
    });
    return result;
  }

  function buildSideboardJson() {
    const result = [];
    sideboard.forEach((count, cardNo) => {
      const entry = buildDeckEntryFromDb(cardNo, count);
      if (entry) result.push(entry);
    });
    return result;
  }

  // Used by showExportTextModal() (copy-to-clipboard) — one
  // "<count>x <cardNo>" line per card, sorted Unit → Pilot → Command Pilot
  // → Command → Base, then by level/cost.
  function buildDeckTxtLines() {
    const TYPE_ORDER = { unit: 0, pilot: 1, 'command pilot': 1.5, command: 2, base: 3 };
    return [...deck.entries()]
      .map(([cardNo, count]) => ({ cardNo, count, entry: cardDb[cardNo] }))
      .filter(d => d.entry)
      .sort((a, b) => {
        const cat = c => (c.categoryData || '').toLowerCase().trim();
        const typeA = TYPE_ORDER[cat(a.entry)] ?? 99;
        const typeB = TYPE_ORDER[cat(b.entry)] ?? 99;
        if (typeA !== typeB) return typeA - typeB;
        const lvA = parseInt(a.entry.level) || parseInt(a.entry.cost) || 0;
        const lvB = parseInt(b.entry.level) || parseInt(b.entry.cost) || 0;
        return lvA - lvB;
      })
      .map(({ cardNo, count }) => `${count}x ${cardNo}`);
  }

  // Shows the deck's "<count>x <cardNo>" list in a modal textbox with a Copy
  // to Clipboard button (the file-download Export button was removed —
  // Export via Text is the only export path now).
  function showExportTextModal() {
    if (deck.size === 0) { showToast('Deck is empty — nothing to export.', true); return; }
    const existing = document.getElementById('db-name-modal-overlay');
    if (existing) existing.remove();

    const lines = buildDeckTxtLines();
    const text = lines.join('\n') + '\n';

    const ov = document.createElement('div');
    ov.id = 'db-name-modal-overlay';
    ov.className = 'db-modal-overlay';
    ov.innerHTML = `
      <div class="db-modal-box db-text-import-modal">
        <div class="db-modal-title">Export via Text</div>
        <div class="db-modal-hint">${lines.length} card${lines.length !== 1 ? 's' : ''} — copy the list below.</div>
        <textarea class="db-modal-textarea" id="db-export-text-output" readonly autocomplete="off" spellcheck="false"></textarea>
        <div class="db-modal-btns">
          <button class="db-modal-btn db-modal-cancel">Close</button>
          <button class="db-modal-btn db-modal-confirm" id="db-export-text-copy">Copy to Clipboard</button>
        </div>
      </div>`;
    document.body.appendChild(ov);

    const textarea = ov.querySelector('#db-export-text-output');
    textarea.value = text;
    textarea.focus();
    textarea.select();

    const copyBtn = ov.querySelector('#db-export-text-copy');
    copyBtn.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(text);
      } catch (e) {
        // Clipboard API can be unavailable (permissions, insecure context) —
        // fall back to the older select+execCommand copy path.
        textarea.select();
        document.execCommand('copy');
      }
      const originalLabel = copyBtn.textContent;
      copyBtn.textContent = 'Copied!';
      setTimeout(() => { copyBtn.textContent = originalLabel; }, 1500);
      showToast('Decklist copied to clipboard!');
    });
    ov.querySelector('.db-modal-cancel').addEventListener('click', () => ov.remove());
    textarea.addEventListener('keydown', e => {
      if (e.key === 'Escape') ov.remove();
    });
    ov.addEventListener('click', e => { if (e.target === ov) ov.remove(); });
  }

  // ── Import via Text (pasted) ──────────────────────────────────────────────
  // Mirrors showExportTextModal()'s own output format: one "<count>x <cardNo>"
  // line per card (e.g. "4x GD04-038"), so a deck exported from here can be
  // re-imported losslessly. Also accepts other common decklist line shapes
  // so lists copied from elsewhere (e.g. Limitless-style exports) paste in
  // directly:
  //   "4x EB01-011"              — count + "x" + cardNo, no space
  //   "4 x EB01-011"             — count + "x" + cardNo, spaced
  //   "4 GD04-016 Zoloat (League Militaire)" — count + cardNo + card name
  //                                   (name is ignored; the cardNo is
  //                                   recognised by shape — letters/digits,
  //                                   dash, digits — as the token right
  //                                   after the count)
  //   "4 Beginning Gundam EB01-011" — count + card name + cardNo (name is
  //                                   ignored; the cardNo is the last
  //                                   whitespace-separated token, used when
  //                                   the token right after the count isn't
  //                                   itself card-code shaped)
  // "// ..." comment/section-header lines (e.g. "// Main Deck") are skipped
  // silently rather than reported, since they're a normal structural part
  // of some export formats, not a mistake. Lines that don't match any shape,
  // or whose resolved cardNo isn't in cardDb, are skipped and reported
  // rather than aborting the whole import.
  //
  // Security: this is untrusted pasted (or file-loaded) text, so before any
  // shape matching runs, SAFE_IMPORT_LINE_RE rejects a line outright if it
  // contains anything outside letters (any language/diacritic — real card
  // names include Ü/ä/ö/ō/Ξ/’/∀), digits, whitespace, and the specific
  // punctuation that actually appears in this game's own card names (see
  // the "name" tokens above) — no angle brackets, quotes, semicolons,
  // braces, backslashes, or control/zero-width characters, which have no
  // legitimate place in a decklist line and are exactly the building blocks
  // of markup/script injection. The resolved cardNo is separately re-checked
  // against cardDb regardless (only a real, known card code is ever
  // accepted), and the "name" portion of a line is always discarded, never
  // stored or displayed — but rejecting the disallowed characters outright,
  // rather than trying to strip them, is a second, independent line of
  // defense. A hard cap on total lines and per-line length guards against a
  // pathologically large paste as well.
  const CARD_CODE_RE = /^[A-Za-z0-9]+-\d+$/;
  const SAFE_IMPORT_LINE_RE = /^[\p{L}\p{N}\s!"&'(),\-./:?[\]’∀Ξ]*$/u;
  const MAX_IMPORT_LINES = 500;
  const MAX_IMPORT_LINE_LENGTH = 200;
  function parseDeckImportText(text) {
    const deckMap = new Map();
    const skipped = [];
    String(text).split(/\r?\n/).slice(0, MAX_IMPORT_LINES).forEach(line => {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('//')) return;
      if (trimmed.length > MAX_IMPORT_LINE_LENGTH || !SAFE_IMPORT_LINE_RE.test(trimmed)) {
        skipped.push(trimmed.slice(0, MAX_IMPORT_LINE_LENGTH));
        return;
      }
      let count = null, cardNo = null;
      const xMatch = trimmed.match(/^(\d+)\s*x\s*(\S+)$/i);
      if (xMatch) {
        count = parseInt(xMatch[1], 10);
        cardNo = xMatch[2].toUpperCase();
      } else {
        const codeFirstMatch = trimmed.match(/^(\d+)\s+(\S+)(?:\s+.+)?$/);
        if (codeFirstMatch && CARD_CODE_RE.test(codeFirstMatch[2])) {
          count = parseInt(codeFirstMatch[1], 10);
          cardNo = codeFirstMatch[2].toUpperCase();
        } else {
          const nameMatch = trimmed.match(/^(\d+)\s+(.+)$/);
          if (nameMatch) {
            count = parseInt(nameMatch[1], 10);
            const tokens = nameMatch[2].trim().split(/\s+/);
            cardNo = tokens[tokens.length - 1].toUpperCase();
          }
        }
      }
      if (!count || !cardNo || !CARD_CODE_RE.test(cardNo) || !cardDb[cardNo]) { skipped.push(trimmed); return; }
      const copyLimit = getCardCopyLimit(cardNo);
      const finalCount = Math.min(count, copyLimit);
      deckMap.set(cardNo, Math.min((deckMap.get(cardNo) || 0) + finalCount, copyLimit));
    });
    return { deckMap, skipped };
  }

  // Paste-a-decklist counterpart to the file-based Import button — same
  // parseDeckImportText()/showImportNameModal() pipeline, just sourced from
  // a textarea instead of FileReader.
  function showImportTextModal() {
    const existing = document.getElementById('db-name-modal-overlay');
    if (existing) existing.remove();

    const ov = document.createElement('div');
    ov.id = 'db-name-modal-overlay';
    ov.className = 'db-modal-overlay';
    ov.innerHTML = `
      <div class="db-modal-box db-text-import-modal">
        <div class="db-modal-title">Import via Text</div>
        <div class="db-modal-hint">Paste a decklist below. Accepted line formats: "4 EB01-011 Beginning Gundam", "4 Beginning Gundam EB01-011", "4 x EB01-011", or "4x EB01-011".</div>
        <textarea class="db-modal-textarea" id="db-import-text-input"
                  placeholder="4 GD04-016 Zoloat (League Militaire)&#10;4 Beginning Gundam EB01-011&#10;4x GD04-038&#10;4 x GD01-124&#10;…" autocomplete="off" spellcheck="false"></textarea>
        <div class="db-modal-btns">
          <button class="db-modal-btn db-modal-cancel">Cancel</button>
          <button class="db-modal-btn db-modal-confirm">Import</button>
        </div>
      </div>`;
    document.body.appendChild(ov);

    const textarea = ov.querySelector('#db-import-text-input');
    textarea.focus();

    const doImport = () => {
      const text = textarea.value;
      if (!text.trim()) { textarea.classList.add('db-input-error'); return; }
      const { deckMap, skipped } = parseDeckImportText(text);
      if (deckMap.size === 0) { showToast('No valid cards found in that text.', true); return; }
      ov.remove();
      showImportNameModal(deckMap, skipped);
    };
    ov.querySelector('.db-modal-cancel').addEventListener('click', () => ov.remove());
    ov.querySelector('.db-modal-confirm').addEventListener('click', doImport);
    textarea.addEventListener('keydown', e => {
      if (e.key === 'Escape') ov.remove();
    });
    ov.addEventListener('click', e => { if (e.target === ov) ov.remove(); });
  }

  function showImportNameModal(deckMap, skipped) {
    const total = [...deckMap.values()].reduce((s, c) => s + c, 0);

    // Guests have no naming flow to go through — the import lands straight
    // in their one fixed slot, same as a guest's Save button. Mirrors the
    // logged-in doSave() body below minus the name prompt/overwrite confirm.
    if (!isLoggedIn()) {
      deck.clear();
      deckMap.forEach((count, cardNo) => deck.set(cardNo, count));
      deckSortMode = 'asc';
      renderDeckSortToggles();
      renderDeck(); renderDeckNameBar(); resetList();
      saveDeckToStorage(GUEST_DECK_NAME);
      return;
    }

    const isDraft = total < 50;
    const existing = document.getElementById('db-name-modal-overlay');
    if (existing) existing.remove();

    const ov = document.createElement('div');
    ov.id = 'db-name-modal-overlay';
    ov.className = 'db-modal-overlay';
    ov.innerHTML = `
      <div class="db-modal-box">
        <div class="db-modal-title">Name Imported Deck</div>
        <div class="db-modal-hint">${total} card${total !== 1 ? 's' : ''} imported${skipped.length ? `, ${skipped.length} line${skipped.length !== 1 ? 's' : ''} skipped (unrecognised)` : ''}.${isDraft ? ' ⚠ Fewer than 50 cards — will be saved as <strong>_DRAFT</strong>.' : ''}</div>
        <input class="db-modal-input" id="db-import-name-input" type="text"
               placeholder="Enter deck name…" maxlength="100" autocomplete="off" />
        <div class="db-modal-btns">
          <button class="db-modal-btn db-modal-cancel">Cancel</button>
          <button class="db-modal-btn db-modal-confirm">Save</button>
        </div>
      </div>`;
    document.body.appendChild(ov);

    const input = ov.querySelector('#db-import-name-input');
    input.focus();
    input.addEventListener('input', () => { input.value = sanitizeDeckName(input.value); });

    const doSave = () => {
      let name = sanitizeDeckName(input.value);
      if (!name) { input.classList.add('db-input-error'); return; }
      if (isDraft) name += '_DRAFT';
      if (localStorage.getItem(LS_PREFIX + name) !== null) {
        if (!confirm(`"${name}" already exists. Overwrite?`)) return;
      } else if (wouldExceedDeckCap(name)) {
        showToast(`You already have ${MAX_SAVED_DECKS_LOGGED_IN} saved decks — delete one first or overwrite an existing deck.`, true);
        return;
      }
      ov.remove();
      deck.clear();
      deckMap.forEach((count, cardNo) => deck.set(cardNo, count));
      deckSortMode = 'asc';
      renderDeckSortToggles();
      renderDeck(); renderDeckNameBar(); resetList();
      saveDeckToStorage(name);
    };
    ov.querySelector('.db-modal-cancel').addEventListener('click', () => ov.remove());
    ov.querySelector('.db-modal-confirm').addEventListener('click', doSave);
    input.addEventListener('keydown', e => {
      if (e.key === 'Enter') doSave();
      if (e.key === 'Escape') ov.remove();
    });
    ov.addEventListener('click', e => { if (e.target === ov) ov.remove(); });
  }

  function loadDeckFromJson(data, deckName = null) {
    deck.clear();
    (Array.isArray(data) ? data : []).forEach(item => {
      const count = Math.min(parseInt(item.copies) || 1, getCardCopyLimit(item.cardNo));
      if (item.cardNo && count > 0) deck.set(item.cardNo, count);
    });
    // Sideboard lives under its own companion key (SIDEBOARD_LS_PREFIX +
    // name) — a deck saved before the sideboard existed simply has no such
    // key, which parses to [] below and just leaves the sideboard empty.
    sideboard.clear();
    if (deckName !== null) {
      try {
        const sbRaw = localStorage.getItem(SIDEBOARD_LS_PREFIX + deckName);
        const sbData = sbRaw ? JSON.parse(sbRaw) : [];
        (Array.isArray(sbData) ? sbData : []).forEach(item => {
          const count = Math.min(parseInt(item.copies) || 1, getCardCopyLimit(item.cardNo));
          if (item.cardNo && count > 0) sideboard.set(item.cardNo, count);
        });
      } catch {}
    }
    if (deckName !== null) currentDeckName = deckName;
    deckSortMode = 'asc';
    renderDeckSortToggles();
    renderDeck(); renderSideboard(); renderDeckNameBar(); resetList();
    markDeckClean(); // just loaded from storage — nothing unsaved yet
  }

  // ── Save ──────────────────────────────────────────────────────────────────
  function showSaveNameModal() {
    const total = getDeckTotal();
    const isDraft = total < 50;
    const existing = document.getElementById('db-name-modal-overlay');
    if (existing) existing.remove();

    const ov = document.createElement('div');
    ov.id = 'db-name-modal-overlay';
    ov.className = 'db-modal-overlay';
    ov.innerHTML = `
      <div class="db-modal-box">
        <div class="db-modal-title">Save Deck</div>
        ${isDraft ? `<div class="db-modal-hint">⚠ Fewer than 50 cards — will be saved as <strong>_DRAFT</strong>.</div>` : ''}
        <input class="db-modal-input" id="db-save-name-input" type="text"
               placeholder="Enter deck name…" maxlength="100" autocomplete="off" />
        <div class="db-modal-btns">
          <button class="db-modal-btn db-modal-cancel">Cancel</button>
          <button class="db-modal-btn db-modal-confirm">Save</button>
        </div>
      </div>`;
    document.body.appendChild(ov);

    const input = ov.querySelector('#db-save-name-input');
    input.focus();
    input.addEventListener('input', () => { input.value = sanitizeDeckName(input.value); });

    const doSave = () => {
      let name = sanitizeDeckName(input.value);
      if (!name) { input.classList.add('db-input-error'); return; }
      if (isDraft) name += '_DRAFT';
      if (localStorage.getItem(LS_PREFIX + name) !== null) {
        if (!confirm(`"${name}" already exists. Overwrite?`)) return;
      } else if (wouldExceedDeckCap(name)) {
        showToast(`You already have ${MAX_SAVED_DECKS_LOGGED_IN} saved decks — delete one first or overwrite an existing deck.`, true);
        return;
      }
      ov.remove();
      saveDeckToStorage(name);
    };
    ov.querySelector('.db-modal-cancel').addEventListener('click', () => ov.remove());
    ov.querySelector('.db-modal-confirm').addEventListener('click', doSave);
    input.addEventListener('keydown', e => {
      if (e.key === 'Enter') doSave();
      if (e.key === 'Escape') ov.remove();
    });
    ov.addEventListener('click', e => { if (e.target === ov) ov.remove(); });
  }

  const LS_PREFIX = 'gdeck:';
  // Companion key for the sideboard, keyed by the same deck name — kept
  // entirely separate from LS_PREFIX's array-of-cards shape so every
  // existing reader of a `gdeck:` entry (showDeckSelectScreen, the Load Deck
  // list preview, etc.) keeps working unchanged against decks saved before
  // the sideboard existed.
  const SIDEBOARD_LS_PREFIX = 'gdeck-sb:';

  // `auto: true` is used by closeBuilder()'s auto-save-on-exit path — same
  // storage write, just a toast that makes clear the game did this rather
  // than the player clicking Save themselves.
  //
  // localStorage stays the mechanism every existing reader (this deck
  // builder's own Load list, showDeckSelectScreen, Best of 3 sideboarding,
  // getAllSavedDecksForExport, ...) keeps reading synchronously — nothing
  // about that changes. For a signed-in account, Supabase's public.decks
  // table (see supabase/schema.sql) becomes the actual durable copy: this
  // also pushes the save there (fire-and-forget — the local save has
  // already succeeded and updated the UI by the time this resolves, so a
  // slow/failed network write only surfaces as a toast, never blocks Save).
  // Guests never reach this branch at all (isLoggedIn() is false for them),
  // so a deck only ever created pre-login stays local-only, exactly as
  // asked — see syncDecksFromCloud() below for the sign-in-time backfill of
  // any OTHER already-logged-in-era local decks.
  function saveDeckToStorage(deckName, { auto = false } = {}) {
    try {
      const deckJson = buildDeckJson();
      const sideboardJson = buildSideboardJson();
      localStorage.setItem(LS_PREFIX + deckName, JSON.stringify(deckJson));
      // Always written (even []), so saving over a deck whose sideboard was
      // just emptied out correctly clears its previously-saved sideboard too.
      localStorage.setItem(SIDEBOARD_LS_PREFIX + deckName, JSON.stringify(sideboardJson));
      currentDeckName = deckName;
      showToast(auto ? `Deck auto-saved as "${deckName}".` : `"${deckName}" saved!`);
      renderDeckNameBar();
      markDeckClean();
      if (isLoggedIn()) {
        window.Supa.saveDeck(deckName, deckJson, sideboardJson).then(({ ok }) => {
          if (!ok) showToast(`"${deckName}" saved locally, but the cloud sync failed — it may not appear on other devices.`, true);
        });
      }
    } catch (e) {
      showToast('Save failed — storage may be full.', true);
    }
  }

  function getSavedDeckNames() {
    const names = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(LS_PREFIX)) names.push(key.slice(LS_PREFIX.length));
    }
    return names.sort((a, b) => a.localeCompare(b));
  }

  function handleLoadDeck() {
    const names = getSavedDeckNames();
    if (!names.length) { showToast('No saved decks found.', true); return; }
    showLoadListModal(names);
  }

  function showLoadListModal(names) {
    const existing = document.getElementById('db-load-modal-overlay');
    if (existing) existing.remove();

    const ov = document.createElement('div');
    ov.id = 'db-load-modal-overlay';
    ov.className = 'db-modal-overlay';
    ov.innerHTML = `
      <div class="db-modal-box db-load-modal">
        <div class="db-modal-title">Load Deck</div>
        <div class="db-deck-file-list" id="db-deck-file-list"></div>
        <div class="db-modal-btns">
          <button class="db-modal-btn db-modal-cancel">Cancel</button>
        </div>
      </div>`;
    document.body.appendChild(ov);

    function rebuildList(currentNames) {
      const list = ov.querySelector('#db-deck-file-list');
      list.innerHTML = '';
      if (!currentNames.length) {
        list.innerHTML = '<div style="color:#94a3b8;font-size:13px;padding:8px 4px;">No saved decks.</div>';
        return;
      }
      currentNames.forEach(name => {
        const raw = localStorage.getItem(LS_PREFIX + name);
        let cardTotal = 0;
        let parsed = [];
        try {
          parsed = JSON.parse(raw || '[]');
          if (!Array.isArray(parsed)) parsed = [];
          cardTotal = parsed.reduce((s, c) => s + (parseInt(c.copies) || 0), 0);
        } catch {}
        const isDraft = cardTotal < 50;
        const issues = getDeckComplianceIssues(getDeckCardCounts(parsed));
        const row = document.createElement('div');
        row.className = 'db-deck-file-item';
        const displayName = name.replace(/_DRAFT$/, '');
        // Built without displayName inside the markup string, then set via
        // .textContent right after — see sanitizeDeckName()'s comment: a
        // deck name is untrusted free text and must never be parsed as markup.
        row.innerHTML = `
          <span class="db-deck-pills">${deckColorPillsHtml(parsed)}</span>
          <span class="db-file-name"></span>
          ${issues.length ? `<span class="material-symbols-outlined db-compliance-warning-icon" title="${issues.join(' ').replace(/"/g, '&quot;')}">warning</span>` : ''}
          ${isDraft ? '<span class="db-draft-tag">Draft</span>' : ''}
          <button class="db-deck-delete-btn" title="Delete deck">
            <span class="material-symbols-outlined">delete</span>
          </button>`;
        row.querySelector('.db-file-name').textContent = displayName;

        // Bound to the whole row, not just the name text — the row's hover
        // highlight and pointer cursor make its full width look clickable,
        // so a listener on just .db-file-name silently ate clicks that
        // landed on the pills/padding instead (felt like "needs two clicks").
        row.addEventListener('click', () => {
          try {
            const raw = localStorage.getItem(LS_PREFIX + name);
            if (!raw) { showToast('Deck not found.', true); return; }
            loadDeckFromJson(JSON.parse(raw), name);
            ov.remove();
            showToast(`"${name}" loaded!`);
          } catch { showToast('Failed to load deck.', true); }
        });

        row.querySelector('.db-deck-delete-btn').addEventListener('click', e => {
          e.stopPropagation();
          // Inline confirmation — replace row content with confirm prompt.
          // <strong> is left empty in the markup string and filled via
          // .textContent right after, same reasoning as displayName above.
          row.innerHTML = `
            <span class="db-delete-confirm-text">Delete "<strong></strong>"?</span>
            <div class="db-delete-confirm-btns">
              <button class="db-delete-no">Cancel</button>
              <button class="db-delete-yes">Delete</button>
            </div>`;
          row.querySelector('.db-delete-confirm-text strong').textContent = name;
          row.querySelector('.db-delete-no').addEventListener('click', ev => {
            ev.stopPropagation();
            rebuildList(getSavedDeckNames());
          });
          row.querySelector('.db-delete-yes').addEventListener('click', ev => {
            ev.stopPropagation();
            localStorage.removeItem(LS_PREFIX + name);
            localStorage.removeItem(SIDEBOARD_LS_PREFIX + name);
            showToast(`"${name}" deleted.`);
            rebuildList(getSavedDeckNames());
            if (isLoggedIn()) window.Supa.deleteDeck(name);
          });
        });

        list.appendChild(row);
      });
    }

    rebuildList(names);
    ov.querySelector('.db-modal-cancel').addEventListener('click', () => ov.remove());
    ov.addEventListener('click', e => { if (e.target === ov) ov.remove(); });
  }

  // ── Button wiring ─────────────────────────────────────────────────────────
  document.getElementById('db-new').addEventListener('click', () => {
    deck.clear();
    sideboard.clear();
    currentDeckName = null;
    renderDeck(); renderSideboard(); renderDeckNameBar(); resetList();
    markDeckClean(); // fresh empty deck — nothing to warn about until it's actually edited
    showSaveNameModal();
  });

  document.getElementById('db-clear').addEventListener('click', () => {
    if (!confirm('Clear all cards from the deck and sideboard?')) return;
    deck.clear();
    sideboard.clear();
    renderDeck(); renderSideboard(); resetList();
  });

  document.getElementById('db-save').addEventListener('click', () => {
    if (!isLoggedIn()) {
      saveDeckToStorage(GUEST_DECK_NAME);
      showGuestSaveSignupModal();
      return;
    }
    if (currentDeckName) {
      saveDeckToStorage(currentDeckName);
    } else {
      showSaveNameModal();
    }
  });
  document.getElementById('db-load').addEventListener('click', handleLoadDeck);
  document.getElementById('db-export-text').addEventListener('click', showExportTextModal);
  document.getElementById('db-import-text').addEventListener('click', showImportTextModal);

  document.getElementById('db-play-game').addEventListener('click', () => {
    if (getDeckTotal() < 50) {
      showToast(`Deck needs 50 cards to play (currently ${getDeckTotal()}).`, true);
      return;
    }
    if (getDeckComplianceIssues(deck).length > 0) {
      showToastHtml(`Deck cannot contain ban cards and must meet the new restrictions. Visit <a href="${DB_RESTRICTIONS_URL}" target="_blank" rel="noopener">HERE</a> for more information.`, true);
      return;
    }
    const deckData = buildDeckJson();
    closeBuilder(() => showMatchmakingScreen(deckData));
  });

  document.getElementById('db-back').addEventListener('click', () => closeBuilder(onBack || showMainMenu));

  // ── Mobile header drawer (New/Clear/Load/Save Deck, Import/Export via
  // Text, Play, Settings, Report Bug) — on desktop .db-header-actions renders inline as always;
  // on mobile it becomes an off-canvas panel toggled by the hamburger button.
  const headerActions = document.getElementById('db-header-actions');
  const headerBackdrop = document.getElementById('db-header-backdrop');
  const headerMenuToggle = document.getElementById('db-header-menu-toggle');
  function closeHeaderDrawer() {
    headerActions.classList.remove('db-drawer-open');
    headerBackdrop.classList.remove('db-drawer-open');
  }
  headerMenuToggle.addEventListener('click', () => {
    headerActions.classList.toggle('db-drawer-open');
    headerBackdrop.classList.toggle('db-drawer-open');
  });
  headerBackdrop.addEventListener('click', closeHeaderDrawer);
  headerActions.querySelectorAll('.db-btn').forEach(btn => btn.addEventListener('click', closeHeaderDrawer));

  // Guests' single deck slot auto-loads on open since Load Deck is disabled
  // for them — logged-in accounts keep manually choosing via Load Deck.
  if (!isLoggedIn()) {
    const guestRaw = localStorage.getItem(LS_PREFIX + GUEST_DECK_NAME);
    if (guestRaw) {
      try { loadDeckFromJson(JSON.parse(guestRaw), GUEST_DECK_NAME); } catch {}
    }
  }

  // ── Initial render ────────────────────────────────────────────────────────
  wireFilterToggle('db-search-filter-toggle', 'db-search-wrap');
  wireFilterToggle('db-color-filter-toggle', 'db-color-filters');
  wireFilterToggle('db-type-filter-toggle', 'db-type-filters');
  wireFilterToggle('db-keyword-filter-toggle', 'db-keyword-filters');
  wireFilterToggle('db-effect-kw-filter-toggle', 'db-effect-kw-filters');
  wireFilterToggle('db-set-filter-toggle', 'db-set-filters');
  renderColorToggles();
  renderTypeToggles();
  renderKeywordToggles();
  renderEffectKwToggles();
  renderSetToggles();
  renderSortToggles();
  renderDeck(); // also populates the type-summary/compliance-warning rows on first paint
  renderSideboard();
  resetList();
}

function showBattleStart(callback) {
  const overlay = document.createElement('div');
  overlay.id = 'battle-start-overlay';
  overlay.innerHTML = `
    <div class="bs-words">
      <span class="bs-battle">BATTLE</span>
      <span class="bs-start">START</span>
    </div>`;
  document.body.appendChild(overlay);
  // 2.6s matches CSS: 0.65s slide-in + ~1.4s hold + 0.55s fade-out
  setTimeout(() => { overlay.remove(); callback(); }, 2600);
}

// ════════════════════════════════════════════════════════════════════════════
// MULLIGAN MODAL
function showBaseReplaceModal(incoming, current, onConfirm) {
  const modal = document.createElement('div');
  modal.id = 'base-replace-modal';
  modal.innerHTML = `
    <div class="brm-box">
      <button class="modal-hide-btn" title="Hide to view field"><span class="modal-hide-label">View Field</span><span class="material-symbols-outlined">ad_off</span></button>
      <div class="brm-title">Replace Base?</div>
      <div class="brm-cards">
        <div class="brm-card">
          <img class="brm-img" src="${incoming.img}" alt="${incoming.name}">
          <div class="brm-name">${incoming.name}</div>
          <div class="brm-stats">Lv.${incoming.level ?? '—'} &middot; Cost ${incoming.cost ?? '—'} &middot; HP ${incoming.hp ?? '—'}</div>
          <div class="brm-label">Incoming</div>
        </div>
        <span class="material-symbols-outlined brm-arrow">double_arrow</span>
        <div class="brm-card">
          <img class="brm-img" src="${current.img}" alt="${current.name}">
          <div class="brm-name">${current.name}</div>
          <div class="brm-stats">Lv.${current.level ?? '—'} &middot; Cost ${current.cost ?? '—'} &middot; HP ${current.hp ?? '—'}</div>
          <div class="brm-label">Current</div>
        </div>
      </div>
      <div class="brm-btns">
        <button class="brm-btn brm-cancel">Cancel</button>
        <button class="brm-btn brm-confirm">Replace</button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
  modal.querySelector('.brm-cancel').addEventListener('click', () => modal.remove());
  modal.querySelector('.brm-confirm').addEventListener('click', () => { modal.remove(); onConfirm(); });
  modal.querySelector('.modal-hide-btn').addEventListener('click', (e) => {
    e.stopPropagation();
    hideModalForFieldView(modal);
  });
  startSelectionTimer(modal, () => autoResolveCancelModal(modal));
}

// Shown at game start. Player sees their opening hand and chooses to keep or
// mulligan (replace all hand cards with a random new set of the same size).
// After the choice the game auto-advances: Start → Draw (draw 1) → Resource → Main.
// ════════════════════════════════════════════════════════════════════════════
function showMulliganModal() {
  console.log('[showMulliganModal] opening mulligan modal');

  // Build overlay
  const overlay = document.createElement('div');
  overlay.id = 'mulligan-overlay';

  const modal = document.createElement('div');
  modal.id = 'mulligan-modal';

  const hideBtn = document.createElement('button');
  hideBtn.className = 'modal-hide-btn';
  hideBtn.title = 'Hide to view field';
  hideBtn.innerHTML = '<span class="modal-hide-label">View Field</span><span class="material-symbols-outlined">ad_off</span>';
  hideBtn.addEventListener('click', (e) => { e.stopPropagation(); hideModalForFieldView(overlay); });
  modal.appendChild(hideBtn);

  // Title
  const title = document.createElement('div');
  title.className = 'mulligan-title';
  title.textContent = 'Opening Hand';
  modal.appendChild(title);

  const turnPill = document.createElement('div');
  turnPill.className = 'mulligan-turn-pill ' + (state.myTurn ? 'going-first' : 'going-second');
  turnPill.textContent = state.myTurn ? 'Going First' : 'Going Second';
  modal.appendChild(turnPill);

  const sub = document.createElement('div');
  sub.className = 'mulligan-sub';
  sub.textContent = 'Would you like to mulligan?';
  modal.appendChild(sub);

  // Hand preview
  const handRow = document.createElement('div');
  handRow.className = 'mulligan-hand';
  state.hand.forEach(card => {
    const wrap = document.createElement('div');
    wrap.className = 'mulligan-card';
    if (card.img) {
      const img = document.createElement('img');
      img.src = card.img;
      img.alt = card.name;
      wrap.appendChild(img);
    }
    // Show card preview popup on hover (positioned above the card)
    wrap.addEventListener('mouseenter', () => showCardPreview(card, wrap));
    wrap.addEventListener('mouseleave', hideCardPreview);
    handRow.appendChild(wrap);
  });
  modal.appendChild(handRow);

  // Buttons
  const btns = document.createElement('div');
  btns.className = 'mulligan-btns';

  const btnYes = document.createElement('button');
  btnYes.className = 'mulligan-btn yes';
  btnYes.textContent = 'Mulligan';
  btnYes.addEventListener('click', () => {
    console.log('[showMulliganModal] player chose MULLIGAN');
    closeMulliganModal();
    dispatch('MULLIGAN');
    render();
    finishMulligan();
  });

  const btnNo = document.createElement('button');
  btnNo.className = 'mulligan-btn no';
  btnNo.textContent = 'Keep Hand';
  btnNo.addEventListener('click', () => {
    console.log('[showMulliganModal] player chose KEEP');
    closeMulliganModal();
    dispatch('KEEP_HAND');
    render();
    finishMulligan();
  });

  btns.appendChild(btnYes);
  btns.appendChild(btnNo);
  modal.appendChild(btns);

  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  overlay.classList.add('visible');
  startSelectionTimer(overlay, () => autoResolveMulligan(overlay));
}

registerAction('MULLIGAN', () => {
  const count = state.hand.length;
  if (state.tempDeck) {
    // 1. Old hand goes to the BOTTOM of the deck (not mixed in and
    //    reshuffled yet — order among these 5 doesn't matter since they get
    //    shuffled along with everything else in step 3).
    state.hand.forEach(c => {
      const entry = state.tempDeck.find(e => e.cardNo === c.cardNo && e.copyVer === c.copyVer);
      if (entry) sendDeckEntryToBottom(entry);
    });
    // 2. Draw a fresh 5 from the top (the OLD top of deck — the mulliganed
    //    cards are safely out of the way at the bottom and can't be redrawn).
    state.hand = drawCardsFromTempDeck(count);
    // 3. Reshuffle the whole deck once more, now that the mulliganed cards
    //    are back in the pool, before shields are dealt off the new top.
    shuffleTempDeck();
  } else {
    state.hand = [];
  }
  addLog(`Mulligan — drew ${count} new cards.`);
  dealPlayerShields(6);
});

registerAction('KEEP_HAND', () => {
  // If no tempDeck, manually deduct from counter
  if (!state.tempDeck) state.deckCount = Math.max(0, state.deckCount - state.hand.length);
  dealPlayerShields(6);
});

function closeMulliganModal() {
  const overlay = document.getElementById('mulligan-overlay');
  if (!overlay) return;
  overlay.remove();
}

// ════════════════════════════════════════════════════════════════════════════
// MULLIGAN HANDSHAKE
// Neither player should start the post-mulligan phase sequence until both
// have made their mulligan choice. Whoever finishes first waits; the phase
// sequence only ever starts once both sides have signaled done.
// ════════════════════════════════════════════════════════════════════════════
let mulliganLocalDone  = false;
let mulliganRemoteDone = false;

function finishMulligan() {
  mulliganLocalDone = true;
  if (state.networked) {
    NET.send({ type: 'MULLIGAN_DONE' });
  } else {
    mulliganRemoteDone = true;
  }
  tryProceedPastMulligan();
}

function tryProceedPastMulligan() {
  if (!mulliganLocalDone) return;
  if (!mulliganRemoteDone) { showMulliganWaitingOverlay(); return; }

  hideMulliganWaitingOverlay();
  showTurnIndicator();
  // Both sides now have a final hand/shield/deck count (and the going-second
  // EX RES catch-up bonus, if any) — push an immediate snapshot so the peer
  // doesn't have to wait for my first turn's phase steps to see it.
  broadcastCounters();
  if (state.myTurn) {
    advanceThroughPhases();
  } else {
    lockUI();
    render();
    setupDropZones();
    // No second client to wait on a PHASE_SYNC from in a bot match — if the
    // coin flip gave the bot the first turn, drive it directly (mirrors
    // handOffTurn()'s own `if (state.isBotMatch) BotEngine.startBotTurn()`
    // for every later turn; this is specifically turn 1, which skips the
    // Start Phase/turn-increment every other turn has — see
    // startBotFirstTurn()'s own header comment in bot-engine.js for why
    // startBotTurn() itself isn't reused here).
    if (state.isBotMatch) BotEngine.startBotFirstTurn();
  }
}

NET.onMulliganDone(() => {
  mulliganRemoteDone = true;
  tryProceedPastMulligan();
});

function showMulliganWaitingOverlay() {
  if (document.getElementById('mulligan-waiting-overlay')) return;
  const overlay = document.createElement('div');
  overlay.id = 'mulligan-waiting-overlay';
  overlay.innerHTML = `
    <div class="mk-modal">
      <button class="modal-hide-btn" title="Hide to view field"><span class="modal-hide-label">View Field</span><span class="material-symbols-outlined">ad_off</span></button>
      <div class="mk-spinner"></div>
      <div class="mk-title">Waiting on opponent…</div>
    </div>`;
  overlay.querySelector('.modal-hide-btn').addEventListener('click', (e) => {
    e.stopPropagation();
    hideModalForFieldView(overlay);
  });
  document.body.appendChild(overlay);
}

function hideMulliganWaitingOverlay() {
  const overlay = document.getElementById('mulligan-waiting-overlay');
  if (overlay) overlay.remove();
}

// Disable field interaction — hand stays hoverable (for the card preview modal)
// but its cards are individually dimmed/blocked by canPlayCard()'s state.myTurn check.
function lockUI() {
  document.getElementById('arena').classList.add('ui-locked');
  console.log('[lockUI] UI locked — field non-interactive');
}

// Re-enable field interaction
function unlockUI() {
  document.getElementById('arena').classList.remove('ui-locked');
  console.log('[unlockUI] UI unlocked — Main Phase active');
}

// Clears the rested flag from every player field card at the start of a new
// turn, along with any "gains <Keyword> during this turn" temp grants (see
// hasNativeKeyword), "during this turn" AP modifiers (see effectiveBp),
// "can't attack during this turn" locks, and any still-pending turn-long
// destroy watchers (see notifyUnitDestroyedByBattle) — none of these persist
// past the turn they were granted/registered on. battleApMod is also reset
// here as a defensive backstop even though runDamageStep already clears it
// at the end of every battle.
//
// CLEAR_TURN_EFFECTS fires at End Phase (advanceNewTurn) so "during this turn"
// modifiers are removed as soon as the turn ends, before the opponent acts.
// UNREST_ALL fires at the start of the NEXT turn to un-rest cards; it keeps
// the temp-field clears below as a defensive backstop in case a rare path
// (e.g. reconnect) bypasses CLEAR_TURN_EFFECTS.
registerAction('CLEAR_TURN_EFFECTS', () => {
  const clear = c => {
    if (!c) return;
    c.tempKeywords = [];
    c.tempApMod = 0;
    c.battleApMod = 0;
    c.tempCantAttack = false;
    c.tempCantReceiveEffectDamage = false;
    c.tempCantAttackNonUnit = false;
    c.tempCantDirectAttack = false;
    c.tempCantBlock = false;
    c.tempImmuneToEffectDestruction = false;
    c.linkedThisTurn = false;
    c.nextDamageReduction = 0;
    c.tempGrantedAbilities = [];
    c.tempAttackDespiteSickness = false;
    c.tempIncomingDamageReduction = 0;
    c.tempBattleDamageReduction = 0;
  };
  state.playerField.forEach(clear);
  state.oppField.forEach(clear);   // also clear opponent mirror (e.g. debuffs we applied)
  turnDestroyWatchers = [];
  state.superpowerDeployedThisTurn = false;
  state.neoZeonEffectDestroyedOwnUnitThisTurn = false;
  state.specialMoveCommandActivatedThisTurn = false;
  state.oppSpecialMoveCommandActivatedThisTurn = false;
  state.causedEnemyDiscardThisTurn = false;
  state.militiaOrDiannaEffectPaidThisTurn = false;
  state.playerUnitEffectResSpentThisTurn = 0;
  state.forcedAttackTargetUid = null;
  state.attackerBdProxyUid = null;
  // GD06-102 "A Miracle": "[Action] If it is your opponent's turn and no
  // enemy Units have attacked this turn, draw 2." Reset alongside every
  // other per-turn flag here and in UNREST_ALL below (same dual-reset
  // convention as causedEnemyDiscardThisTurn) — set unconditionally inside
  // registerAction('ATTACK', ...) regardless of which side is attacking.
  state.attackOccurredThisTurn = false;
  state.effectDrawOccurredThisTurn = false;
  state.playerField.forEach(c => { if (c?.pilot) c.pilot.pilotEffectUsedThisTurn = false; });
});

registerAction('UNREST_ALL', () => {
  // ST14-001 The-O: "Each enemy player's rested Units with the lowest Lv.
  // won't be set as active during the start phase of their turn." Re-checked
  // every Start Phase (not a one-shot keepRestedNextTurn grant) for as long
  // as the ENEMY controls it — same generic "check a hook on the opponent's
  // mirrored field" pattern as dealDamageToBase's reduceBaseEffectDamage.
  const blockedByEnemy = state.oppField.some(c => c && window.CardEffects?.[c.cardNo]?.preventsLowestLevelUnrest?.(c));
  let lowestRestedLevel = Infinity;
  if (blockedByEnemy) {
    state.playerField.forEach(c => {
      if (c && c.rested) lowestRestedLevel = Math.min(lowestRestedLevel, parseInt(c.level) || 0);
    });
  }
  state.playerField.forEach(c => {
    if (c) {
      // cantBeSetActive: a permanent restriction printed on the card itself
      // (e.g. T-014 Ad Balloon: "This Unit can't be set as active") — see
      // registry.js for the hook contract. Checked alongside the enemy-driven
      // preventsLowestLevelUnrest block above; either can keep a card rested.
      const blockedFromUnresting = (blockedByEnemy && c.rested && (parseInt(c.level) || 0) === lowestRestedLevel)
        || !!window.CardEffects?.[c.cardNo]?.cantBeSetActive?.(c);
      if (!c.keepRestedNextTurn && !blockedFromUnresting) c.rested = false;
      c.keepRestedNextTurn = false;
      c.tempKeywords = []; c.tempApMod = 0; c.battleApMod = 0;
      c.tempCantAttack = false; c.tempCantReceiveEffectDamage = false; c.tempCantAttackNonUnit = false;
      c.tempCantDirectAttack = false;
      c.tempCantBlock = false;
      c.tempImmuneToEffectDestruction = false;
      c.battleDefMod = 0; c.tempDestroyOnHit = null; c.nextDamageReduction = 0;
      c.tempGrantedAbilities = [];
      c.tempAttackDespiteSickness = false;
      c.tempIncomingDamageReduction = 0;
      c.tempBattleDamageReduction = 0;
      c.passiveUsedThisTurn = false; c.activateMainUsedThisTurn = false; c.activateActionUsedThisTurn = false;
      if (c.pilot) { c.pilot.passiveUsedThisTurn = false; c.pilot.activateMainUsedThisTurn = false; c.pilot.pilotEffectUsedThisTurn = false; }
    }
  });
  if (state.playerBase) {
    state.playerBase.rested = false;
    state.playerBase.baseAbilityUsedThisTurn = false;
    state.playerBase.baseActionUsedThisTurn = false;
    state.playerBase.passiveUsedThisTurn = false;
  }
  turnDestroyWatchers = [];
  state.superpowerDeployedThisTurn = false;
  state.neoZeonEffectDestroyedOwnUnitThisTurn = false;
  state.specialMoveCommandActivatedThisTurn = false;
  state.oppSpecialMoveCommandActivatedThisTurn = false;
  state.causedEnemyDiscardThisTurn = false;
  state.forcedAttackTargetUid = null;
  state.attackOccurredThisTurn = false;
  state.effectDrawOccurredThisTurn = false;
  console.log('[unrestAllCards] all player units and base are now active');
});

// Sets the active phase index/name. Used by every phase-advance step.
registerAction('SET_PHASE', ({ index }) => {
  state.phaseIndex = index;
  state.phase = state.phases[index];
});

// Increments the turn counter at the start of startNewTurn().
registerAction('NEW_TURN', () => {
  state.turn++;
  addLog(`─── Turn ${state.turn} begins ───`, { turnMarker: true });
});

// Shows a large bold phase name over the field that fades out automatically.
function showPhaseAnnouncement(phaseName) {
  const existing = document.getElementById('phase-announcement');
  if (existing) existing.remove();
  const el = document.createElement('div');
  el.id = 'phase-announcement';
  el.textContent = phaseName;
  document.body.appendChild(el);
  // Pin to just below the phase bar using its real position
  const bar = document.getElementById('phase-bar');
  if (bar) {
    const rect = bar.getBoundingClientRect();
    el.style.top = (rect.bottom + 8) + 'px';
  }
  setTimeout(() => { if (el.parentNode) el.remove(); }, 1900);
  console.log(`[showPhaseAnnouncement] "${phaseName}"`);
}

// Small badge near the phase bar showing whose turn it is in a networked
// match. Updated whenever state.myTurn flips.
function showTurnIndicator() {
  let el = document.getElementById('turn-indicator');
  if (!el) {
    el = document.createElement('div');
    el.id = 'turn-indicator';
    document.body.appendChild(el);
  }
  el.textContent = state.myTurn ? 'Your Turn' : "Opponent's Turn";
  el.className = state.myTurn ? 'mine' : 'theirs';
  // Pin just above the phase bar using its real position. Anchored via
  // `bottom` (not `top`) so it grows upward from the bar's top edge without
  // needing to know the indicator's own height in advance — same reasoning
  // as showCardPreview()'s hand-card anchoring. top is forced to 'auto'
  // (not just cleared to '') so it can never end up specified alongside
  // bottom at the same time — that over-constrained combination is what
  // rendered inconsistently between Chrome and Firefox.
  const bar = document.getElementById('phase-bar');
  if (bar) {
    const rect = bar.getBoundingClientRect();
    el.style.top = 'auto';
    el.style.bottom = (window.innerHeight - rect.top + 8) + 'px';
  }
}

// Set by showPrivateGamesLobby()'s NET.onMatchReady right before a
// lobby-originated match starts, consumed exactly once by leaveLobbyMatch()
// when that match ends — { code, resume() }.
let _lobbyResumeState = null;

// Set by showPrivateGamesLobby()'s NET.onMatchReady (alongside
// _lobbyResumeState above) only when that lobby has Best of 3 enabled —
// { code, myDeckData, mySideboardData, gamesWonByMe, gamesWonByOpp,
// gameNumber, iWasLoser }. Null for every ordinary (non-Bo3) match, which
// keeps showVictoryModal()/leaveLobbyMatch() behaving exactly as before.
let _bo3MatchState = null;

// BO3_SIDEBOARD_READY/BO3_TURN_CHOICE are pure barrier signals that can
// arrive before or after this client reaches the matching wait point (e.g.
// my opponent finishes sideboarding while I'm still moving cards around) —
// each pair of latch/callback vars below resolves that race regardless of
// arrival order. Registered exactly once, permanently, for the page's whole
// lifetime (not reset by NET.disconnect() — see network.js's hooks object),
// since a Bo3 series' socket connection survives across all of its games.
let _bo3OpponentSideboardReady = false;
let _bo3PendingSideboardReadyCb = null;
NET.onBo3SideboardReady(() => {
  if (_bo3PendingSideboardReadyCb) {
    const cb = _bo3PendingSideboardReadyCb;
    _bo3PendingSideboardReadyCb = null;
    cb();
  } else {
    _bo3OpponentSideboardReady = true;
  }
});
let _bo3TurnChoiceReceived = null;
let _bo3PendingTurnChoiceCb = null;
NET.onBo3TurnChoice((payload) => {
  if (_bo3PendingTurnChoiceCb) {
    const cb = _bo3PendingTurnChoiceCb;
    _bo3PendingTurnChoiceCb = null;
    cb(payload);
  } else {
    _bo3TurnChoiceReceived = payload;
  }
});

// Called from every place a networked match can end (Victory/Defeat's
// Return to Menu, Surrender confirm, and the opponent-left/surrendered
// auto-return below). Returns false immediately (no-op) for a non-lobby
// match, so every caller falls back to its existing behavior unchanged.
// For a lobby match: records the result into this player's own per-lobby
// localStorage tally, rejoins as an idle member with the updated tally, and
// hands the view back to the lobby page. Applies the same way whether or
// not this player created the lobby — finishing a match never closes the
// lobby by itself; only the explicit Close Lobby button, or the creator
// actually disconnecting/leaving while idle, does that (see
// cleanupLobbyMembership in server/relay.js).
function leaveLobbyMatch(result) {
  if (!_lobbyResumeState) return false;
  const { code, resume } = _lobbyResumeState;
  _lobbyResumeState = null;
  // Abandons an in-progress Bo3 series on any early exit (surrender,
  // opponent-left) the same way a normal single-game match ends — every
  // existing call site that already calls leaveLobbyMatch() needs no
  // Bo3-specific change of its own.
  _bo3MatchState = null;
  state.networked = false;
  clearTempDeck();
  const tally = loadLobbyRecord(code);
  if (result === 'win') tally.wins++;
  else if (result === 'loss') tally.losses++;
  saveLobbyRecord(code, tally);
  NET.send({ type: 'LOBBY_RETURN', wins: tally.wins, losses: tally.losses });
  resume();
  return true;
}

// Sums a gdeck-style array's copies into a cardNo → count Map — the shape
// showSideboardingModal() manipulates while the player freely moves cards
// between deck and sideboard.
function bo3ArrayToCountMap(arr) {
  const map = new Map();
  (Array.isArray(arr) ? arr : []).forEach(c => {
    if (!c || !c.cardNo) return;
    const n = parseInt(c.copies) || 0;
    if (n > 0) map.set(c.cardNo, (map.get(c.cardNo) || 0) + n);
  });
  return map;
}

// Called from showVictoryModal() (via its Continue button) once a Bo3 game
// ends without deciding the series. Walks: sideboarding (skipped entirely if
// this player's sideboard is empty) → the two-sided BO3_SIDEBOARD_READY
// barrier → loser-picks-turn-order → proceedToNextBo3Game(). The socket
// stays open and state.networked stays true throughout — a Bo3 series never
// touches _lobbyResumeState/LOBBY_RETURN until the whole series ends (see
// showVictoryModal's seriesOver branch).
function handleBo3GameEnd(won) {
  _bo3MatchState.iWasLoser = !won;
  _bo3MatchState.gameNumber++; // now refers to the upcoming game

  const afterSideboarding = () => {
    NET.send({ type: 'BO3_SIDEBOARD_READY' });
    if (_bo3OpponentSideboardReady) {
      _bo3OpponentSideboardReady = false;
      proceedToBo3TurnOrder();
    } else {
      showDecisionWaitingOverlay('Waiting for opponent to finish sideboarding…');
      _bo3PendingSideboardReadyCb = () => {
        hideDecisionWaitingOverlay();
        proceedToBo3TurnOrder();
      };
    }
  };

  if (!_bo3MatchState.mySideboardData || _bo3MatchState.mySideboardData.length === 0) {
    afterSideboarding();
  } else {
    showSideboardingModal(({ deckData, sideboardData }) => {
      _bo3MatchState.myDeckData = deckData;
      _bo3MatchState.mySideboardData = sideboardData;
      afterSideboarding();
    });
  }
}

// Loser of the just-finished game picks who goes first next — the winner
// just waits for that choice (BO3_TURN_CHOICE), mirroring the shape of the
// sideboard-ready barrier above.
function proceedToBo3TurnOrder() {
  if (_bo3MatchState.iWasLoser) {
    showBo3TurnChoiceModal(_bo3MatchState.gameNumber, (chooseFirst) => {
      NET.send({ type: 'BO3_TURN_CHOICE', chooserGoesFirst: chooseFirst });
      proceedToNextBo3Game(chooseFirst);
    });
  } else {
    const onChoiceKnown = ({ chooserGoesFirst }) => {
      hideDecisionWaitingOverlay();
      proceedToNextBo3Game(!chooserGoesFirst);
    };
    if (_bo3TurnChoiceReceived) {
      const payload = _bo3TurnChoiceReceived;
      _bo3TurnChoiceReceived = null;
      onChoiceKnown(payload);
    } else {
      showDecisionWaitingOverlay('Waiting for opponent to choose turn order…');
      _bo3PendingTurnChoiceCb = onChoiceKnown;
    }
  }
}

// Starts the next game of the series with the freshly-sideboarded deck and
// the turn order the previous game's loser picked. forceMyTurn is passed
// straight into initGame() (rather than patched onto state afterward) so the
// "going second" EX Resource catch-up bonus — computed inside initGame()
// itself, before this call returns — sees the right value too.
function proceedToNextBo3Game(myTurnNext) {
  hideDecisionWaitingOverlay();
  initGame(_bo3MatchState.myDeckData, { networked: true, forceMyTurn: myTurnNext });
  setupDropZones();
}

// Lets the player freely reshuffle cards between their (temporary, series-
// only — never touches the saved gdeck:/gdeck-sb: localStorage keys) deck
// and sideboard between Bo3 games, with no 50/10 cap enforced while editing —
// only Finish is gated on hitting exactly 50/10. onFinish receives
// { deckData, sideboardData } in the same gdeck-entry array shape
// _bo3MatchState already stores.
function showSideboardingModal(onFinish) {
  const deckMap = bo3ArrayToCountMap(_bo3MatchState.myDeckData);
  const sbMap = bo3ArrayToCountMap(_bo3MatchState.mySideboardData);

  const overlay = document.createElement('div');
  overlay.id = 'bo3-sideboard-overlay';
  overlay.className = 'mk-overlay';
  overlay.innerHTML = `
    <div class="mk-modal bo3-sideboard-modal">
      <div class="mk-title">Sideboard for Game ${_bo3MatchState.gameNumber}</div>
      <div class="bo3-sideboard-warning" id="bo3-sideboard-warning">Deck must be exactly 50 cards and Sideboard exactly 10 cards to finish.</div>
      <div class="bo3-sideboard-columns">
        <div class="bo3-sideboard-col">
          <div class="db-panel-label">Main Deck — <span id="bo3-deck-count">0</span> / 50</div>
          <div class="db-deck-grid" id="bo3-deck-grid"></div>
        </div>
        <div class="bo3-sideboard-col">
          <div class="db-panel-label">Sideboard — <span id="bo3-sb-count">0</span> / 10</div>
          <div class="db-deck-grid" id="bo3-sb-grid"></div>
        </div>
      </div>
      <button class="btn-primary" id="bo3-finish-btn" disabled>Finish</button>
    </div>
  `;
  document.body.appendChild(overlay);

  const deckGrid = overlay.querySelector('#bo3-deck-grid');
  const sbGrid = overlay.querySelector('#bo3-sb-grid');
  const deckCountEl = overlay.querySelector('#bo3-deck-count');
  const sbCountEl = overlay.querySelector('#bo3-sb-count');
  const warningEl = overlay.querySelector('#bo3-sideboard-warning');
  const finishBtn = overlay.querySelector('#bo3-finish-btn');

  function mapTotal(map) {
    let sum = 0;
    map.forEach(n => { sum += n; });
    return sum;
  }

  function renderGrid(grid, map, otherMap, moveLabel) {
    grid.innerHTML = '';
    const entries = [...map.entries()]
      .map(([cardNo, count]) => ({ cardNo, count, entry: cardDb[cardNo] }))
      .filter(d => d.entry);
    if (entries.length === 0) {
      grid.innerHTML = '<div class="db-deck-empty">No cards.</div>';
      return;
    }
    const frag = document.createDocumentFragment();
    entries.forEach(({ cardNo, count, entry }) => {
      const cell = document.createElement('div');
      cell.className = 'db-deck-cell';

      const wrap = document.createElement('div');
      wrap.className = 'db-deck-card';
      wrap.innerHTML = `<img src="${entry.image}" alt="${entry.name}" loading="lazy" />`
        + (count > 1 ? `<div class="db-deck-count">×${count}</div>` : '');

      const btnRow = document.createElement('div');
      btnRow.className = 'db-deck-btn-row';
      const moveBtn = document.createElement('button');
      moveBtn.type = 'button';
      moveBtn.className = 'bo3-move-btn';
      moveBtn.textContent = moveLabel;
      moveBtn.title = `Move 1 copy of ${entry.name}`;
      moveBtn.addEventListener('click', () => {
        if (count <= 1) map.delete(cardNo); else map.set(cardNo, count - 1);
        otherMap.set(cardNo, (otherMap.get(cardNo) || 0) + 1);
        renderAll();
      });
      btnRow.appendChild(moveBtn);

      cell.appendChild(wrap);
      cell.appendChild(btnRow);
      frag.appendChild(cell);
    });
    grid.appendChild(frag);
  }

  function renderAll() {
    renderGrid(deckGrid, deckMap, sbMap, '→ Sideboard');
    renderGrid(sbGrid, sbMap, deckMap, '→ Main Deck');
    const deckTotal = mapTotal(deckMap);
    const sbTotal = mapTotal(sbMap);
    deckCountEl.textContent = deckTotal;
    sbCountEl.textContent = sbTotal;
    const ready = deckTotal === 50 && sbTotal === 10;
    finishBtn.disabled = !ready;
    warningEl.classList.toggle('bo3-sideboard-warning-show', !ready);
  }
  renderAll();

  finishBtn.addEventListener('click', () => {
    if (finishBtn.disabled) return;
    const deckData = [...deckMap.entries()].map(([cardNo, count]) => buildDeckEntryFromDb(cardNo, count)).filter(Boolean);
    const sideboardData = [...sbMap.entries()].map(([cardNo, count]) => buildDeckEntryFromDb(cardNo, count)).filter(Boolean);
    overlay.remove();
    onFinish({ deckData, sideboardData });
  });
}

// Simple two-button modal for the loser of the previous game to pick turn
// order for the next one (see proceedToBo3TurnOrder above).
function showBo3TurnChoiceModal(nextGameNumber, onChoose) {
  const overlay = document.createElement('div');
  overlay.id = 'bo3-turnchoice-overlay';
  overlay.className = 'mk-overlay';
  overlay.innerHTML = `
    <div class="mk-modal">
      <div class="mk-title">You lost Game ${nextGameNumber - 1}</div>
      <div class="igm-body">Choose who goes first in Game ${nextGameNumber}.</div>
      <div class="igm-btn-row">
        <button class="btn-primary" id="bo3-go-first-btn">Go First</button>
        <button class="btn-secondary" id="bo3-go-second-btn">Go Second</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  const close = () => overlay.remove();
  overlay.querySelector('#bo3-go-first-btn').addEventListener('click', () => { close(); onChoose(true); });
  overlay.querySelector('#bo3-go-second-btn').addEventListener('click', () => { close(); onChoose(false); });
}

// Shown when the match ends because the opponent left — either by surrendering
// or by disconnecting outright. Stays up briefly, then returns to the main menu
// on its own (no button — the match is already over, there's nothing to wait on).
// If this was a lobby-originated match, the opponent leaving/surrendering is
// always a win-by-forfeit for the player still here.
function showMatchEndOverlay(message) {
  if (document.getElementById('opponent-left-overlay')) return;
  // Guards a race unique to lobby matches: if the peer's socket closes
  // ungracefully (crash/tab-kill) shortly after I've already gracefully
  // LOBBY_RETURN'd, the relay's own OPPONENT_LEFT can still arrive here a
  // moment later even though I'm no longer in a match — state.networked is
  // already false by then (leaveLobbyMatch sets it synchronously), so
  // there's nothing left to end.
  if (!state.networked) return;
  const overlay = document.createElement('div');
  overlay.id = 'opponent-left-overlay';
  overlay.innerHTML = `
    <div class="mk-modal">
      <div class="mk-title">${message}</div>
    </div>`;
  document.body.appendChild(overlay);
  setTimeout(() => {
    overlay.remove();
    // Re-checked here (not just at function entry above): a lobby's
    // LOBBY_CLOSED (creator closed it, or their connection dropped while
    // idle) can arrive and already conclude this session (setting
    // state.networked false) during the 3s this overlay was sitting up. If
    // so, that arrival already put the player back on the (correct)
    // pre-lobby screen — this timer firing anyway must do nothing further,
    // not re-trigger a return or fall back to disconnect/main-menu on top
    // of it.
    if (!state.networked) return;
    if (!leaveLobbyMatch('win')) {
      NET.disconnect();
      clearTempDeck();
      showMainMenu(Promise.resolve());
    }
  }, 3000);
}

// Shown for ~3s when shields are hit, revealing the real card(s) to both
// players before they resolve (trashed, or returned to hand if [Burst]).
// `cards`/`hasBursts` are parallel arrays — length 2 when the attacker has
// the native [Suppression] keyword, otherwise length 1. Purely visual — the
// actual hand/trash mutation happens in the caller's onDone.
function showShieldRevealOverlay(cards, hasBursts, onDone) {
  if (document.getElementById('shield-reveal-overlay')) { onDone(); return; }
  const overlay = document.createElement('div');
  overlay.id = 'shield-reveal-overlay';
  const cardsHtml = cards.map((card, i) => `
    <div class="sr-card-slot">
      <div class="sr-img-wrap">
        <img class="sr-card-img" src="${card.img || ''}" alt="${card.name}">
        ${hasBursts[i] ? '<div class="sr-burst">BURST!</div>' : ''}
      </div>
      <div class="sr-subtitle">${card.type === 'base' ? card.name : `${card.name}${hasBursts[i] ? ' — returned to hand' : ' — sent to trash'}`}</div>
    </div>`).join('');
  overlay.innerHTML = `
    <div class="mk-modal sr-modal${cards.length > 1 ? ' sr-modal-multi' : ''}">
      <div class="mk-title">${cards.length > 1 ? 'Shields Hit! (Suppression)' : 'Shield Hit!'}</div>
      <div class="sr-cards-row">${cardsHtml}</div>
    </div>`;
  document.body.appendChild(overlay);

  let resolved = false;
  const finish = () => {
    if (resolved) return;
    resolved = true;
    clearTimeout(timer);
    overlay.remove();
    onDone();
  };
  const timer = setTimeout(finish, 3000);
  // Deferred by a tick — see the identical comment in showOpponentPlayModal.
  // This overlay also appears unannounced (a shield hit can be triggered by
  // the opponent's attack), so an in-flight click from just before it
  // appeared shouldn't be able to instantly dismiss it.
  setTimeout(() => overlay.addEventListener('click', finish), 0);
}

// Called by the side whose real shield card(s) are being hit — that's
// defenderSide === 'player' in resolveAttack(), OR defenderSide === 'opp'
// when state.isBotMatch (the bot has a real oppTempDeck, same as a human
// deck, just with no second client to keep it hidden from — see
// state.isBotMatch's comment in initGame()). `count` is 2 when the attacker
// has native [Suppression], otherwise 1 (clamped to however many shields are
// actually left). Picks the card(s), reveals them (still shown to the human
// either way — shields are revealed when hit in real play), then resolves
// each (hand if [Burst], else trash; bases prompt for placement — bot-owned
// bases skip the prompt, see the side==='opp' branch below) and broadcasts
// the corrected counters.
//
// A bot-owned shield card with [Burst] ability text beyond the generic
// hand/trash placement (e.g. "Choose 1 ... from your trash", or "[Burst]
// Activate this card's [Main]") redirects to the active deck's own onBurst
// hook (bot_logic) instead of the real registered 'burst' handler — that
// handler assumes a human decision-maker on the activating side's own
// client and isn't safely side-agnostic (see the ST11-001 deploy-handler
// example in js/bot_logic/bot-engine.js's header). No onBurst entry = no
// effect, same convention as every other bot interception point. A revealed
// Base card IS always deployed (BotEngine.deployBaseFromShield) regardless,
// since that one has no such per-card choice to make.
function resolveShieldHit(side = 'player', count = 1) {
  const isBot = side === 'opp';
  const tempDeck = isBot ? state.oppTempDeck : state.tempDeck;
  const shieldsAvailable = tempDeck ? tempDeck.filter(e => e.state === 'shield') : [];
  if (shieldsAvailable.length === 0) { render(); setupDropZones(); return; }

  const n = Math.min(count, shieldsAvailable.length);
  const pool = [...shieldsAvailable];
  const pickedEntries = [];
  for (let i = 0; i < n; i++) {
    const idx = Math.floor(Math.random() * pool.length);
    pickedEntries.push(pool.splice(idx, 1)[0]);
  }

  const entries = pickedEntries
    .map(picked => {
      const dbEntry = cardDb[picked.cardNo];
      if (!dbEntry) return null;
      const card = buildGameCardFromDb(dbEntry, picked.copyVer);
      // Only "[Burst]Add this card to your hand." sends it to hand — other
      // [Burst] effects (place resource, activate Main, deploy, etc.) aren't
      // programmatically resolved here, so those cards fall through to the
      // normal trash/base handling below.
      const hasBurst = /\[Burst\]\s*Add this card to your hand\./i.test(card.effectRaw || '');
      return { picked, card, hasBurst };
    })
    .filter(Boolean);

  if (entries.length === 0) { render(); setupDropZones(); return; }

  if (!state.isBotMatch) {
    NET.send({
      type: 'SHIELD_REVEAL',
      cards: entries.map(e => e.card),
      hasBursts: entries.map(e => e.hasBurst),
    });
  }

  const setEntryState = (cardNo, copyVer, newState) => {
    const entry = tempDeck && tempDeck.find(e => e.cardNo === cardNo && e.copyVer === copyVer);
    if (entry) entry.state = newState;
    if (isBot) { state.oppDeckCount = tempDeck.filter(isInDeck).length; }
    else { state.deckCount = tempDeck.filter(isInDeck).length; }
  };

  showShieldRevealOverlay(entries.map(e => e.card), entries.map(e => e.hasBurst), () => {
    const baseEntries  = entries.filter(e => e.card.type === 'base');
    const otherEntries = entries.filter(e => e.card.type !== 'base');

    // Non-base cards resolve immediately: hand on Burst, else trash.
    otherEntries.forEach(({ card, picked, hasBurst }) => {
      if (hasBurst) {
        setEntryState(picked.cardNo, picked.copyVer, 'hand');
        if (isBot) { state.oppHand.push(card); state.oppHandCount = state.oppHand.length; }
        else { state.hand.push(card); glowHandCard(card.uid); }
        addLog(`${card.name} had [Burst] — added to hand.`);
        broadcastCardToast(`${card.name} was added to hand.`);
      } else {
        setEntryState(picked.cardNo, picked.copyVer, 'trash');
        if (isBot) { state.oppTrashCount += 1; state.oppTrash.push(toTrashCard(card)); }
        else { state.trashCount += 1; state.playerTrash.push(toTrashCard(card)); }
        addLog(`${card.name} sent to trash.`);
        broadcastCardToast(`${card.name} was sent to trash.`, card.img);
        if (!isBot && !state.isBotMatch) NET.send({ type: 'SHIELD_RESOLVE', card });
      }
      // Cards with extra [Burst] ability text beyond the generic hand/trash
      // placement above opt into a registered 'burst' handler — see
      // js/effects/. The real registered handler is player-hardcoded (same
      // landmine as every other active effect — see bot-engine.js's header
      // comment), so a bot-owned shield redirects to its own deck's onBurst
      // hook instead, rather than silently doing nothing (the previous V1
      // limitation this comment used to document).
      if (!isBot && /\[Burst\]/i.test(card.effectRaw || '')) {
        // Pass onDone so burst handlers that "Activate this card's [Main/Action]"
        // can call it to trigger fireCommandEffectPassives (e.g. GD04-066 reactor).
        // Standalone burst effects ignore ctx.onDone and passives are not fired.
        triggerCardEffect(card, 'burst', { picked, onDone: () => fireCommandEffectPassives(card) });
      } else if (isBot && /\[Burst\]/i.test(card.effectRaw || '')) {
        window.BotDecks?.[state.botDeckId]?.onBurst?.[card.cardNo]?.(card);
      }
    });

    if (isBot) {
      // Bot-owned Base revealed from shield ("[Burst]Deploy this card."):
      // always deployed, same as the bot always deploys a Base it draws
      // into hand — see BotEngine.deployBaseFromShield.
      baseEntries.forEach(({ card, picked }) => BotEngine.deployBaseFromShield(card, picked));
    } else if (baseEntries.length === 2) {
      // Base cards get an extra placement decision. Two revealed bases need
      // an explicit deploy-order choice; a single revealed base reuses the
      // existing replace-or-not prompt.
      showBaseOrderPrompt(baseEntries);
    } else if (baseEntries.length === 1) {
      showBaseReplacePrompt(baseEntries[0].card, baseEntries[0].picked);
    }

    broadcastCounters();
    render();
    setupDropZones();
  });
}

// Shared by showBaseReplacePrompt and showBaseOrderPrompt: deploys a single
// revealed base card, trashing the current base first (unless it's an EX
// base), then applying the normal base-deploy shield-to-hand effect and
// mirroring the new base to the peer exactly like a normal DEPLOY_BASE play.
function deployBaseFromShield(card, picked, logSuffix) {
  const oldBase = state.playerBase;
  if (oldBase && !isRemovedFromGameBase(oldBase)) {
    state.trashCount++;
    state.playerTrash.push(toTrashCard(oldBase));
    updateTempState(oldBase.cardNo, oldBase.copyVer, 'trash');
    addLog(`${oldBase.name} sent to trash.`);
    broadcastCardToast(`${oldBase.name} was sent to trash.`, oldBase.img);
  }
  updateTempState(picked.cardNo, picked.copyVer, 'base');
  state.playerBase = card;
  const drawnUid = applyBaseDeployEffect();
  addLog(oldBase
    ? `${card.name} replaced ${oldBase.name} as base${logSuffix || ''}.`
    : `${card.name} deployed as base${logSuffix || ''}.`);
  // Mirror before triggering the deploy effect — see the identical comment in
  // the DEPLOY_BASE/REPLACE_BASE action handlers for why ordering matters here.
  NET.send({ type: 'DEPLOY_BASE', card: state.playerBase, counters: myCounters() });
  triggerCardEffect(card, 'deploy', {});
  if (drawnUid) glowHandCard(drawnUid);
}

// Asks the shield owner whether to deploy a revealed Base card as their new
// base. Declining sends it to trash (same as any other non-Burst shield).
// Accepting reuses REPLACE_BASE's exact logic (old base to trash unless EX,
// applyBaseDeployEffect() returns 1 shield to hand) and mirrors the new base
// to the peer the same way a normal DEPLOY_BASE play does.
function showBaseReplacePrompt(card, picked) {
  const overlay = document.createElement('div');
  overlay.id = 'ingame-menu-overlay';

  const hasBase = !!state.playerBase;
  const promptText = hasBase
    ? `Your shield revealed a Base card: ${card.name}. Replace your current base with it?`
    : `Your shield revealed a Base card: ${card.name}. Would you like to deploy it as your base?`;
  const confirmLabel = hasBase ? 'Yes, Replace' : 'Yes, Deploy';

  const row = (label, val) =>
    `<div class="cp-row">
       <span class="cp-label">${label}</span>
       <span class="cp-val">${val ?? '—'}</span>
     </div>`;

  const modal = document.createElement('div');
  modal.id = 'ingame-menu-modal';
  modal.innerHTML = `
    <button class="modal-hide-btn" title="Hide to view field"><span class="modal-hide-label">View Field</span><span class="material-symbols-outlined">ad_off</span></button>
    <div class="igm-title">Base Revealed!</div>
    <div class="srb-content">
      <div class="cp-img-wrap">
        ${card.img ? `<img src="${card.img}" alt="${card.name}" />` : '<div class="cp-no-img">?</div>'}
      </div>
      <div class="cp-stats">
        <div class="cp-name">${card.name}</div>
        ${card.trait ? `<div class="cp-trait">${card.trait}</div>` : ''}
        <div class="cp-divider"></div>
        <div class="cp-stat-grid">
          ${row('Level', card.level)}
          ${row('Cost',  card.cost)}
          ${row('AP',    card.bp)}
          ${row('HP',    card.hp)}
          ${row('Type',  'Base')}
        </div>
        <div class="cp-effect">${card.effect ?? '—'}</div>
      </div>
    </div>
    <div class="igm-body">${promptText}</div>
    <div class="igm-btn-row">
      <button class="igm-btn igm-cancel">No</button>
      <button class="igm-btn igm-confirm-base">${confirmLabel}</button>
    </div>
  `;
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  startSelectionTimer(overlay, () => autoResolveIgmModal(overlay));
  modal.querySelector('.modal-hide-btn').addEventListener('click', (e) => {
    e.stopPropagation();
    hideModalForFieldView(overlay);
  });

  modal.querySelector('.igm-cancel').addEventListener('click', () => {
    overlay.remove();
    updateTempState(picked.cardNo, picked.copyVer, 'trash');
    state.trashCount += 1;
    state.playerTrash.push(toTrashCard(card));
    addLog(`${card.name} sent to trash.`);
    broadcastCardToast(`${card.name} was sent to trash.`, card.img);
    NET.send({ type: 'SHIELD_RESOLVE', card });
    broadcastCounters();
    render();
    setupDropZones();
  });

  modal.querySelector('.igm-confirm-base').addEventListener('click', () => {
    overlay.remove();
    deployBaseFromShield(card, picked, ' (from shield)');
    broadcastCounters();
    render();
    setupDropZones();
  });
}

// Generic Yes/No confirmation modal — no card involved, just a text prompt
// and two buttons. Used by effects that offer the LOCAL player a plain
// binary choice with no candidate to select (e.g. EB01-062 Jona Basta's
// enemy-offered "you may draw 1"). onChoice receives a bool.
function showYesNoPrompt(title, body, onChoice, { yesLabel = 'Yes', noLabel = 'No' } = {}) {
  const overlay = document.createElement('div');
  overlay.id = 'ingame-menu-overlay';

  const modal = document.createElement('div');
  modal.id = 'ingame-menu-modal';
  modal.innerHTML = `
    <button class="modal-hide-btn" title="Hide to view field"><span class="modal-hide-label">View Field</span><span class="material-symbols-outlined">ad_off</span></button>
    <div class="igm-title">${title}</div>
    <div class="igm-body">${body}</div>
    <div class="igm-btn-row">
      <button class="igm-btn igm-cancel">${noLabel}</button>
      <button class="igm-btn igm-confirm-base">${yesLabel}</button>
    </div>
  `;
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  startSelectionTimer(overlay, () => autoResolveIgmModal(overlay));
  modal.querySelector('.modal-hide-btn').addEventListener('click', (e) => {
    e.stopPropagation();
    hideModalForFieldView(overlay);
  });
  modal.querySelector('.igm-cancel').addEventListener('click', () => {
    overlay.remove();
    onChoice(false);
  });
  modal.querySelector('.igm-confirm-base').addEventListener('click', () => {
    overlay.remove();
    onChoice(true);
  });
}

// Privately shows the local player the top card of their own deck (e.g.
// ST06-009 "[When Linked] Look at the top card of your deck..."), with no
// network traffic of its own — this view is for the local player only, and
// nothing is sent to the peer unless `onResolve(true)` causes the caller to
// actually add the card to hand. If `canTake` is false (the looked-at card
// doesn't meet whatever condition gates taking it), only a single
// acknowledgement button is shown and `onResolve` always reports false.
function showTopDeckPeekPrompt(pilotCard, peekedCard, canTake, onResolve) {
  const overlay = document.createElement('div');
  overlay.id = 'ingame-menu-overlay';

  const row = (label, val) =>
    `<div class="cp-row">
       <span class="cp-label">${label}</span>
       <span class="cp-val">${val ?? '—'}</span>
     </div>`;

  const promptText = canTake
    ? `${pilotCard.name} — you may reveal ${peekedCard.name} and add it to your hand, or return it to the bottom of your deck.`
    : `${pilotCard.name} — ${peekedCard.name} isn't a (Clan) card. Return it to the bottom of your deck.`;

  const modal = document.createElement('div');
  modal.id = 'ingame-menu-modal';
  modal.innerHTML = `
    <button class="modal-hide-btn" title="Hide to view field"><span class="modal-hide-label">View Field</span><span class="material-symbols-outlined">ad_off</span></button>
    <div class="igm-title">Top of Deck</div>
    <div class="srb-content">
      <div class="cp-img-wrap">
        ${peekedCard.img ? `<img src="${peekedCard.img}" alt="${peekedCard.name}" />` : '<div class="cp-no-img">?</div>'}
      </div>
      <div class="cp-stats">
        <div class="cp-name">${peekedCard.name}</div>
        ${peekedCard.trait ? `<div class="cp-trait">${peekedCard.trait}</div>` : ''}
        <div class="cp-divider"></div>
        <div class="cp-stat-grid">
          ${row('Level', peekedCard.level)}
          ${row('Cost',  peekedCard.cost)}
          ${row('AP',    peekedCard.bp)}
          ${row('HP',    peekedCard.hp)}
          ${row('Type',  displayCardType(peekedCard.type))}
        </div>
        <div class="cp-effect">${peekedCard.effect ?? '—'}</div>
      </div>
    </div>
    <div class="igm-body">${promptText}</div>
    <div class="igm-btn-row">
      <button class="igm-btn igm-cancel">${canTake ? 'Return to Bottom' : 'OK'}</button>
      ${canTake ? '<button class="igm-btn igm-confirm-base">Add to Hand</button>' : ''}
    </div>
  `;
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  startSelectionTimer(overlay, () => autoResolveIgmModal(overlay));
  modal.querySelector('.modal-hide-btn').addEventListener('click', (e) => {
    e.stopPropagation();
    hideModalForFieldView(overlay);
  });

  modal.querySelector('.igm-cancel').addEventListener('click', () => {
    overlay.remove();
    onResolve(false);
  });

  const confirmBtn = modal.querySelector('.igm-confirm-base');
  if (confirmBtn) {
    confirmBtn.addEventListener('click', () => {
      overlay.remove();
      onResolve(true);
    });
  }
}

// Shown when a [Suppression] attack reveals two shield cards and both are
// Base cards. The owner picks which deploys first; the second deployment
// immediately replaces the first (only one base can be active at a time),
// so the final base on the field is whichever was selected second — but
// both deployments still trigger the normal shield-to-hand draw, once each,
// in the chosen order, matching the user-facing "Deploying First/Second"
// labels shown above each card while picking.
function showBaseOrderPrompt(baseEntries) {
  const overlay = document.createElement('div');
  overlay.id = 'ingame-menu-overlay';

  const modal = document.createElement('div');
  modal.id = 'ingame-menu-modal';
  modal.classList.add('bop-modal');

  const order = []; // indices into baseEntries, in pick order

  function renderTiles() {
    modal.innerHTML = `
      <button class="modal-hide-btn" title="Hide to view field"><span class="modal-hide-label">View Field</span><span class="material-symbols-outlined">ad_off</span></button>
      <div class="igm-title">Two Bases Revealed!</div>
      <div class="igm-body">Both shields revealed Base cards. Choose the order to deploy them — the second deployment will replace the first.</div>
      <div class="bop-row">
        ${baseEntries.map(({ card }, i) => {
          const pos = order.indexOf(i);
          const label = pos === 0 ? 'Deploying First' : pos === 1 ? 'Deploying Second' : '';
          return `
            <div class="bop-tile${pos !== -1 ? ' bop-selected' : ''}" data-idx="${i}">
              <div class="bop-order-label">${label}</div>
              <div class="cp-img-wrap">
                ${statTileHtml(card)}
              </div>
              <div class="bop-name">${card.name}</div>
            </div>`;
        }).join('')}
      </div>
      <div class="igm-btn-row">
        <button class="igm-btn igm-confirm-base"${order.length < 2 ? ' disabled' : ''}>Confirm Order</button>
      </div>
    `;
    modal.querySelector('.modal-hide-btn').addEventListener('click', (e) => {
      e.stopPropagation();
      hideModalForFieldView(overlay);
    });
    modal.querySelectorAll('.bop-tile').forEach(tile => {
      tile.addEventListener('click', () => {
        const idx = parseInt(tile.dataset.idx, 10);
        const existingPos = order.indexOf(idx);
        if (existingPos !== -1) order.splice(existingPos, 1);
        else if (order.length < 2) order.push(idx);
        renderTiles();
      });
    });
    modal.querySelector('.igm-confirm-base').addEventListener('click', () => {
      if (order.length < 2) return;
      overlay.remove();
      deployBaseFromShield(baseEntries[order[0]].card, baseEntries[order[0]].picked, ' (deployed first from shield)');
      deployBaseFromShield(baseEntries[order[1]].card, baseEntries[order[1]].picked, ' (deployed second from shield)');
      broadcastCounters();
      render();
      setupDropZones();
    });
  }

  renderTiles();
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  startSelectionTimer(overlay, () => autoResolveIgmModal(overlay));
}

// Snapshot of every counter the opponent's client needs to mirror as "opp*".
// level is recomputed here rather than read from the cached state.playerLevel
// — that cache is only refreshed inside render(), which may not have run yet
// when this is called from a just-dispatched action's network mirror.
function myCounters() {
  return {
    deckCount:  state.deckCount,
    trashCount: state.trashCount,
    res:        state.res,
    resBase:    state.resBase,
    exRes:      state.exRes,
    exResRested: state.exResRested,
    level:      state.resBase + state.exRes + state.exResRested,
    shield:     state.playerShield,
    handCount:  state.hand.length,
  };
}

// Writes a peer's counters snapshot into my "opp*" state fields.
function applyOppCounters(counters) {
  if (!counters) return;
  state.oppDeckCount  = counters.deckCount;
  state.oppTrashCount = counters.trashCount;
  state.oppRes        = counters.res;
  state.oppResBase    = counters.resBase;
  state.oppExRes      = counters.exRes;
  state.oppExResRested = counters.exResRested || 0;
  state.oppLevel      = counters.level;
  state.oppShield     = counters.shield;
  if (counters.handCount !== undefined) state.oppHandCount = counters.handCount;
}

// Sends the current turn/phase (plus a snapshot of my own counters) to the
// peer so their tab mirrors the same phase pill and turn number without
// running its own timer loop. Only ever called by whoever owns the turn.
function broadcastPhaseSync() {
  NET.send({
    type: 'PHASE_SYNC',
    turn: state.turn,
    phaseIndex: state.phaseIndex,
    oppCounters: myCounters(),
  });
}

// Sends just my own counters snapshot, independent of turn/phase — used
// whenever my counters change outside the turn-owner's phase-step cadence
// (e.g. the post-mulligan EX RES catch-up bonus, dealt shields), so the peer
// never has to wait for my next turn to see an accurate value.
function broadcastCounters() {
  NET.send({ type: 'COUNTER_SYNC', counters: myCounters() });
}

NET.onPhaseSync(({ turn, phaseIndex, oppCounters }) => {
  if (typeof turn === 'number') state.turn = turn;
  // The turn owner sends a PHASE_SYNC for every step in their phase sequence,
  // including mid-phase counter updates (e.g. the draw-card step right after
  // the Draw Phase announcement) that reuse the same phaseIndex. Only
  // announce when the phase actually changed, or it shows up twice here.
  const phaseChanged = phaseIndex !== state.phaseIndex;
  dispatch('SET_PHASE', { index: phaseIndex });
  applyOppCounters(oppCounters);
  if (phaseChanged) {
    addLog(`Opponent's Phase → ${state.phase}`);
    showPhaseAnnouncement(state.phase);
  }
  render();
  setupDropZones();
});

NET.onCounterSync(counters => {
  applyOppCounters(counters);
  render();
});

NET.onEndTurn(() => {
  addLog(`${actorDisplayName('opp')}'s turn has ended.`, { noPill: true });
  state.myTurn = true;
  showTurnIndicator();
  render();
  setupDropZones();
  startNewTurn();
});

NET.onOpponentLeft(() => {
  showMatchEndOverlay('Opponent disconnected');
});

// Sent by the peer right before they disconnect via the surrender flow —
// arrives slightly ahead of the OPPONENT_LEFT the relay sends on socket close,
// so the dedup guard in showMatchEndOverlay() keeps the "disconnected" version
// from ever overwriting this one.
NET.onSurrender(() => {
  showMatchEndOverlay('Opponent surrendered');
});

// A dropped/errored connection (crashed relay, timed-out socket, etc.) never
// produces an OPPONENT_LEFT message — that only arrives if the relay's own
// 'close' handler fires, which an ungraceful disconnect can skip entirely.
// Without this, a mid-match connection failure just leaves whichever
// "waiting on opponent" overlay is up (e.g. showMulliganWaitingOverlay)
// spinning forever with no feedback. End the match immediately instead of
// waiting on a message that may never come.
function handleNetworkDisconnectError() {
  if (!state.networked) return;
  if (document.getElementById('main-menu-overlay')) return; // already idle — ignore a stray late event
  state.networked = false;
  document.getElementById('mulligan-waiting-overlay')?.remove();
  document.getElementById('decision-waiting-overlay')?.remove();
  addLog('Connection error — network disconnected.');
  showToast('ERROR: Connection disconnected', true);
  NET.disconnect();
  clearTempDeck();
  showMainMenu(Promise.resolve());
}
NET.onSocketError(handleNetworkDisconnectError);
NET.onSocketClosedUnexpectedly(handleNetworkDisconnectError);

// The peer who owns the hit shield reveals it to me — I just display the
// same overlay; the owner's resolveMyShieldHit() handles the real mutation
// and a following broadcastCounters() corrects my oppTrashCount/oppShield.
// If a revealed card is a Base, the owner gets an open-ended deploy/replace
// prompt with no timeout — show a waiting overlay for the rest of that
// decision so I'm not left staring at an idle screen. It's dismissed by
// whichever resolution message arrives (SHIELD_RESOLVE if they decline,
// DEPLOY_BASE if they deploy it).
// Race guard: DEPLOY_BASE or SHIELD_RESOLVE can arrive while the 3s reveal
// animation is still playing, making hideDecisionWaitingOverlay() a no-op
// (the overlay doesn't exist yet). Check state after the animation completes
// to avoid creating an overlay that will never be dismissed.
NET.onShieldReveal((cards, hasBursts) => {
  showShieldRevealOverlay(cards, hasBursts, () => {
    render();
    setupDropZones();
    const revealedBases = cards.filter(c => c.type === 'base');
    if (revealedBases.length > 0) {
      const alreadyResolved = revealedBases.every(bc =>
        state.oppBase?.uid === bc.uid ||
        state.oppTrash.some(t => t.uid === bc.uid)
      );
      if (!alreadyResolved) {
        showDecisionWaitingOverlay("Waiting on opponent's base decision…", revealedBases[0]);
      }
    }
  });
});

// The owner tells me explicitly when their revealed shield card ends up in
// their trash (vs. hand or becoming their new base) — COUNTER_SYNC only
// carries the trash count, not card identity, so without this the opponent
// trash modal would never show shield-originated cards.
NET.onShieldResolve(card => {
  hideDecisionWaitingOverlay();
  state.oppTrash.push(toTrashCard(card));
  // resolveMyShieldHit() (the owner's side) already logs this locally — this
  // mirrors the same line onto MY log, since without it only the shield's
  // owner ever saw what card it actually was; the other player (who caused
  // the hit) saw only the shield-removed count, never the card identity.
  addLog(`${card.name} sent to trash.`);
  render();
});

// A peer effect privately looked at one of their own cards (e.g. the top of
// their deck) and chose to reveal it to me (e.g. ST06-009 adding a (Clan)
// card to hand) — no state to apply on my side, just surface what happened.
NET.onCardReveal((card, pilotName) => {
  addLog(`Opponent's ${pilotName} revealed ${card.name}.`);
});

// A peer effect unconditionally milled the top card of their own deck into
// their trash (e.g. GD03-092 Nyaan) — always public, unlike a private peek,
// so mirror it into oppTrash the same way onShieldResolve does for
// shield-originated trash cards.
NET.onDeckMill(card => {
  state.oppTrash.push(toTrashCard(card));
  addLog(`Opponent milled ${card.name} to their trash.`);
  render();
});

// Block Step / Action Step (see registerAction('ATTACK', ...)) pause and wait
// on the peer's real decision when the peer owns it. Only one attack is ever
// in flight at a time (gated by state.attackInProgress), so a single pending
// slot per message type is enough — no per-attack correlation id needed.
let pendingBlockResolve = null;
let pendingActionResolve = null;
let pendingEndPhaseActionResolve = null;
// ST12-013 The Final Victor's [Main] — set while waiting on the opponent's
// MUTUAL_BATTLE_UNIT_RESPONSE reply to our own REQUEST_MUTUAL_BATTLE_UNIT.
let pendingMutualBattleResolve = null;
// ST13-009 Gundam Pharact / ST14-003 Palace Athene — set while waiting on the
// opponent's TRASH_UNIT_EXILE_DONE reply to our own REQUEST_TRASH_UNIT_EXILE.
let pendingTrashExileResolve = null;

// Buffer for an ATTACK_ACTION message that arrives before pendingActionResolve
// is set. This happens when the acting side's action-card handler is async
// (shows a modal) — the peer auto-skips and sends ATTACK_ACTION before the
// modal closes and onDone() fires to advance to the waiting branch.
let bufferedActionPayload = null;
// Buffer for an ATTACK_BLOCK message that arrives before pendingBlockResolve
// is set. This happens when a pilot [Attack] trigger picker is open on the
// attacker's client — the defender has already sent ATTACK_BLOCK before the
// attacker's trigger modal closes and runBlockStep runs to set up the wait.
let bufferedBlockPayload = null;
let pendingDestroyedEffectResolve = null;
// Buffer for a DESTROYED_EFFECT_DONE message that arrives before
// pendingDestroyedEffectResolve is set. This is the same race as
// bufferedBlockPayload above, just for the destroyed-Base round trip: the
// owner's client can resolve its (possibly zero-candidate, immediate) picker
// and reply before this client's own combat resolution — which itself has to
// get through its own Block Step / Action Step round trips first — ever
// reaches the "waiting on their destroyed effect" line below.
let bufferedDestroyedEffectDone = false;
// Single-slot pending callback for an OFFER_DISCARD round-trip (e.g.
// GD05-034 Gaia Gundam: "the enemy player may discard 1. If they don't,
// you may deploy..."). Same one-pending-slot convention as
// pendingBlockResolve — only one such exchange is ever in flight at a time.
let pendingDiscardResolve = null;
// Single-slot pending callback for an OFFER_DRAW round trip (e.g. EB01-062
// Jona Basta: "Choose 1 enemy player. They may draw 1. If they draw with
// this effect, draw 1."). Same convention as pendingDiscardResolve above.
let pendingDrawResolve = null;
// Gates the defender's Block Step on the attacker's own Attack Step triggers
// (unit [Attack] then pilot [During Link][Attack]) having fully resolved —
// e.g. GD05-049 Sazabi: "[Attack] You may choose 1 of your Units. Destroy
// it..." opens a picker on the attacker's own client, and without this gate
// the defender's client (which never runs the attacker's own trigger — see
// fireAttackerUnitAttackTrigger's attackerSide-gate) reaches runBlockStep and
// pops the Block prompt immediately upon receiving ATTACK, racing the
// attacker's still-open picker instead of waiting for it to finish first.
// bufferedAttackTriggersDone is the same early-arrival race protection as
// bufferedBlockPayload/bufferedDestroyedEffectDone above, for the case where
// the attacker has no [Attack] trigger at all and ATTACK_TRIGGERS_DONE
// arrives before runBlockStep ever registers the wait.
let pendingAttackTriggersResolve = null;
let bufferedAttackTriggersDone = false;

// ── Stuck-wait watchdog ─────────────────────────────────────────────────
// Every "Waiting on opponent's ___…" overlay above (Block Step, Action
// Step, a destroyed Base's effect, the attacker's own [Attack] triggers,
// ...) is ultimately just this client sitting on one of the pendingXResolve
// callbacks above until a specific network message arrives. A message lost
// in transit, or an ordering bug like the ones already found and fixed this
// session, strands BOTH clients staring at a "waiting" overlay forever with
// no way to recover short of reloading.
//
// armStuckWaitWatchdog(getPending, resolveStranded) arms a one-shot timer;
// if `getPending()` is still truthy (nobody has resolved it normally) once
// it fires, it asks the peer over the wire whether they're actually still
// mid-decision — busy:true means they currently have a decision modal open
// (checked via #ingame-menu-overlay's presence — see NET.onStuckCheck below;
// every picker/prompt/action-step modal in the game shares that one overlay
// id), busy:false means they're just idle. A "still busy" reply just re-arms
// the watchdog (a legitimately slow human shouldn't get their choice yanked
// away — this covers someone who's taken 30+ seconds just choosing WHICH
// card/action to play, before any specific target-picker even opens); "not
// busy" — or no reply at all, e.g. they disconnected — means whatever
// message we were expecting never arrived, so we resolve locally with the
// same fallback value a real "declined/skipped/nothing to do" reply would
// have carried, instead of hanging forever.
const STUCK_WAIT_TIMEOUT_MS = 30000;
const STUCK_CHECK_GRACE_MS = 4000;
let pendingStuckCheckResolve = null; // single-slot — only one STUCK_CHECK is ever in flight

function armStuckWaitWatchdog(getPending, resolveStranded) {
  if (!state.networked) return;
  setTimeout(function check() {
    if (!getPending()) return; // resolved normally in the meantime — nothing to do
    pendingStuckCheckResolve = (busy) => {
      if (!getPending()) return; // resolved while the check was in flight
      if (busy) {
        setTimeout(check, STUCK_WAIT_TIMEOUT_MS);
      } else {
        console.warn('[stuck-wait watchdog] peer reports idle — self-resolving a stranded wait');
        const fn = getPending();
        resolveStranded(fn);
      }
    };
    NET.send({ type: 'STUCK_CHECK' });
    setTimeout(() => {
      if (pendingStuckCheckResolve) {
        pendingStuckCheckResolve = null;
        if (getPending()) {
          console.warn('[stuck-wait watchdog] no STUCK_CHECK reply — self-resolving a stranded wait');
          const fn = getPending();
          resolveStranded(fn);
        }
      }
    }, STUCK_CHECK_GRACE_MS);
  }, STUCK_WAIT_TIMEOUT_MS);
}

NET.onStuckCheck(() => {
  // _pickerModalBusy alone used to be the busy signal here, but it's only
  // ever set by showCardPickerModal/showCardPickerModalMulti — it says
  // nothing about showActionStepModal (choosing WHICH card/pilot/unit/base
  // to activate in the first place) or the other #ingame-menu-overlay
  // prompts (showBaseReplacePrompt, showTopDeckPeekPrompt, showBlockPromptModal,
  // showHandDiscardModal, ...). A player who spent the 30s watchdog window
  // sitting on any of THOSE screens got wrongly reported as idle, letting
  // the opponent's watchdog self-resolve their End Phase Action Step out
  // from under them — including flipping the turn via END_TURN while that
  // player's own (now-orphaned) target picker was still open. Checking the
  // shared overlay's presence covers every one of these modals uniformly.
  NET.send({ type: 'STUCK_CHECK_RESPONSE', busy: !!document.getElementById('ingame-menu-overlay') });
});

NET.onStuckCheckResponse((busy) => {
  if (pendingStuckCheckResolve) {
    const fn = pendingStuckCheckResolve;
    pendingStuckCheckResolve = null;
    fn(busy);
  }
});

// ST12-013 The Final Victor's [Main] — the opponent already chose their own
// Unit and is asking me to choose one of MY OWN Units for the mutual battle.
// Only I have authoritative field data, so I resolve the picker locally and
// reply with my choice (or null if I have no Units at all).
NET.onRequestMutualBattleUnit(() => {
  const candidates = state.playerField.filter(c => c);
  if (candidates.length === 0) {
    NET.send({ type: 'MUTUAL_BATTLE_UNIT_RESPONSE', uid: null });
    return;
  }
  showCardPickerModal('The Final Victor', 'Choose 1 of your Units for the mutual battle.', candidates, (target) => {
    NET.send({ type: 'MUTUAL_BATTLE_UNIT_RESPONSE', uid: target ? target.uid : null });
  }, null, { mandatory: true });
});

NET.onMutualBattleUnitResponse((uid) => {
  if (pendingMutualBattleResolve) {
    const fn = pendingMutualBattleResolve;
    pendingMutualBattleResolve = null;
    fn(uid);
  }
});

// ST13-009 Gundam Pharact / ST14-003 Palace Athene — the opponent chose me as
// the enemy player and is asking me to choose `count` Unit cards from MY OWN
// trash to exile (only I have authoritative trash data). Reuses the existing
// exileFromTrash()/exileCardsFromTrash() helpers, which already send their
// own TRASH_EXILE per exiled card — TRASH_UNIT_EXILE_DONE is just a
// lightweight "I'm finished" acknowledgment so the requester's wait resolves.
// If I have fewer than `count` Unit cards in trash, I choose as many as I
// have (down to 0, at which point nothing to pick — just acknowledge).
NET.onRequestTrashUnitExile((count) => {
  const candidates = state.playerTrash.filter(c => c && c.type === 'unit');
  const take = Math.min(count, candidates.length);
  if (take <= 0) {
    addLog('No Unit cards in trash to exile.');
    NET.send({ type: 'TRASH_UNIT_EXILE_DONE' });
    return;
  }
  showCardPickerModalMulti('Choose Unit cards to exile', `Choose ${take} Unit card${take > 1 ? 's' : ''} from your trash. They will be exiled from the game.`, candidates,
    { min: take, max: take, mandatory: true },
    (chosen) => {
      if (chosen && chosen.length) {
        exileCardsFromTrash(chosen);
        addLog(`Exiled ${chosen.map(c => c.name).join(', ')} from trash (enemy effect).`);
        broadcastCounters(); render();
      }
      NET.send({ type: 'TRASH_UNIT_EXILE_DONE' });
    }, null);
});

NET.onTrashUnitExileDone(() => {
  if (pendingTrashExileResolve) {
    const fn = pendingTrashExileResolve;
    pendingTrashExileResolve = null;
    fn();
  }
});

// Shared helper for ST13-009/ST14-003-style "choose 1 enemy player, that
// player chooses N Unit cards from their own trash to exile" effects. Since
// this app is a strict 1v1 match, "choose 1 enemy player" always resolves to
// the opponent (see enemyPlayerCount()) — no player-choice picker needed.
// Sends the request, waits on TRASH_UNIT_EXILE_DONE (or the stuck-wait
// watchdog if the opponent's client never responds), then calls onDone().
function requestEnemyTrashUnitExile(count, onDone) {
  if (!state.networked) {
    addLog('Requires an opponent to choose from their own trash; unavailable in solo practice.');
    onDone();
    return;
  }
  pendingTrashExileResolve = () => {
    pendingTrashExileResolve = null;
    hideDecisionWaitingOverlay();
    onDone();
  };
  armStuckWaitWatchdog(() => pendingTrashExileResolve, (fn) => { pendingTrashExileResolve = null; fn(); });
  setTimeout(() => {
    if (pendingTrashExileResolve !== null) {
      showDecisionWaitingOverlay('Waiting on opponent to choose cards from their trash…');
    }
  }, 400);
  NET.send({ type: 'REQUEST_TRASH_UNIT_EXILE', count });
}

NET.onAttackBlock(blockerUid => {
  if (pendingBlockResolve) {
    const fn = pendingBlockResolve;
    pendingBlockResolve = null;
    fn(blockerUid);
  } else if (state.attackInProgress) {
    // [Attack] trigger is still open — buffer until runBlockStep registers its wait.
    bufferedBlockPayload = { blockerUid };
  }
});

NET.onAttackAction((side, played) => {
  if (pendingActionResolve) {
    const fn = pendingActionResolve;
    pendingActionResolve = null;
    fn(played);
  } else {
    // Peer responded before we entered the waiting branch — buffer it so
    // runActionStep can drain it immediately when it gets there.
    bufferedActionPayload = { played };
  }
});

NET.onEndPhaseAction((played) => {
  if (pendingEndPhaseActionResolve) {
    const fn = pendingEndPhaseActionResolve;
    pendingEndPhaseActionResolve = null;
    fn(played);
  }
  // If no pending resolve, we're the side that just finished our step — the
  // peer is notifying us what the current turn player played; just log it.
  else {
    applyPeerActionPlay(played);
  }
});

NET.onDestroyedEffectDone(() => {
  if (pendingDestroyedEffectResolve) {
    const fn = pendingDestroyedEffectResolve;
    pendingDestroyedEffectResolve = null;
    fn();
  } else {
    // Wait not registered yet — buffer until fireBaseDestroyedEffect sets it up.
    bufferedDestroyedEffectDone = true;
  }
});

NET.onAttackTriggersDone(() => {
  if (pendingAttackTriggersResolve) {
    const fn = pendingAttackTriggersResolve;
    pendingAttackTriggersResolve = null;
    fn();
  } else {
    bufferedAttackTriggersDone = true;
  }
});

// Opponent (current turn player) is asking me to take my End Phase action step.
// I act as 'player' (attacker side) so I see the action step modal; when done
// my resolve function sends END_PHASE_ACTION back so the turn player can continue.
NET.onEndPhaseOppRequest(() => {
  state.endPhaseStep = true;
  const ctx = { attackerSide: 'player', defenderSide: 'opp', battle: null };
  runActionStep(ctx, 'attacker', () => {
    state.endPhaseStep = false;
    // Turn player will receive our END_PHASE_ACTION and continue their sequence.
  });
});

// A card effect on the opponent's side (e.g. GD03-118) targeted one of MY
// real units and returned it to MY hand — only I have authoritative hand
// data, so I rebuild a clean copy from the DB rather than trusting whatever
// the opponent's (stripped/approximate) oppField mirror looked like.
NET.onUnitBounce(uid => {
  const idx = state.playerField.findIndex(c => c && c.uid === uid);
  if (idx === -1) return;
  const card = state.playerField[idx];
  state.playerField[idx] = null;
  if (isUnitToken(card)) {
    // Tokens never go to hand — bouncing one just removes it from the game.
    // A paired Pilot is a real deck card and returns to hand.
    if (card.pilot) {
      const dbPilot = cardDb[card.pilot.cardNo];
      const pilotHandCard = dbPilot
        ? buildGameCardFromDb(dbPilot, card.pilot.copyVer)
        : { ...card.pilot, rested: false, damaged: false };
      updateTempState(card.pilot.cardNo, card.pilot.copyVer, 'hand');
      state.hand.push(pilotHandCard);
      glowHandCard(pilotHandCard.uid);
    }
    addLog(`${card.name} (token) is removed from the game.${card.pilot ? ` ${card.pilot.name} returned to your hand.` : ''}`);
  } else {
    if (card.pilot) {
      const dbPilot = cardDb[card.pilot.cardNo];
      const pilotHandCard = dbPilot
        ? buildGameCardFromDb(dbPilot, card.pilot.copyVer)
        : { ...card.pilot, rested: false, damaged: false };
      updateTempState(card.pilot.cardNo, card.pilot.copyVer, 'hand');
      state.hand.push(pilotHandCard);
      glowHandCard(pilotHandCard.uid);
    }
    const dbEntry = cardDb[card.cardNo];
    const handCard = dbEntry ? buildGameCardFromDb(dbEntry, card.copyVer) : { ...card, pilot: null, rested: false, damaged: false };
    updateTempState(handCard.cardNo, handCard.copyVer, 'hand');
    state.hand.push(handCard);
    addLog(`${card.name}${card.pilot ? ` and ${card.pilot.name} were` : ' was'} returned to your hand.`);
    glowHandCard(handCard.uid);
  }
  broadcastCounters();
  render();
  setupDropZones();
});

// Mirrors bounceEnemyUnit's structure but for "return to the bottom of its
// owner's deck" effects (e.g. GD05-002 Strike Freedom Gundam) instead of
// "return to hand" — a paired Pilot still returns to hand (same convention
// as UNIT_BOUNCE), but the Unit itself rejoins the bottom of MY tempDeck via
// its already-tracked entry rather than becoming a fresh hand card.
NET.onUnitToDeckBottom(uid => {
  const idx = state.playerField.findIndex(c => c && c.uid === uid);
  if (idx === -1) return;
  const card = state.playerField[idx];
  state.playerField[idx] = null;
  if (card.pilot) {
    const dbPilot = cardDb[card.pilot.cardNo];
    const pilotHandCard = dbPilot
      ? buildGameCardFromDb(dbPilot, card.pilot.copyVer)
      : { ...card.pilot, rested: false, damaged: false };
    updateTempState(card.pilot.cardNo, card.pilot.copyVer, 'hand');
    state.hand.push(pilotHandCard);
    glowHandCard(pilotHandCard.uid);
  }
  if (!isUnitToken(card) && state.tempDeck) {
    const entry = state.tempDeck.find(e => e.cardNo === card.cardNo && e.copyVer === card.copyVer);
    if (entry) { entry.state = 'deck'; sendDeckEntryToBottom(entry); }
  }
  addLog(`${card.name}${card.pilot ? ` was returned to the bottom of your deck; ${card.pilot.name} was returned to your hand.` : ' was returned to the bottom of your deck.'}`);
  broadcastCounters();
  render();
  setupDropZones();
});

// Sent by a non-combat effect on the opponent's side (e.g. GD05-107
// Interwoven Blessings: "Destroy the first 2 cards in that player's shield
// area") — resolveShieldHit() already does exactly this for combat, so
// just reuse it directly; only MY client has the authoritative private
// identities of MY own shields.
NET.onForceShieldHit(count => {
  addLog(`${count} of your shields were destroyed simultaneously.`);
  resolveShieldHit('player', count);
});

// Sent by a card effect (e.g. GD05-049 Sazabi: "[Attack]...all enemy players
// each choose 1 of their non-battling Units. Destroy them.") that forces THIS
// client's own controller to choose and destroy one of their own non-battling
// Units — only I have the authoritative field/effect data for my own Units.
// sourceCard is null (not attributed to one of MY own (Neo Zeon) card's
// effects) since the destroying card belongs to my opponent.
NET.onForceDestroyNonBattling(excludeUid => {
  const candidates = state.playerField.filter(c => c && c.uid !== excludeUid);
  if (candidates.length === 0) {
    addLog('Enemy effect: no non-battling Units to destroy.');
    return;
  }
  showCardPickerModal(
    'Enemy Effect',
    'Choose 1 of your non-battling Units. Destroy it.',
    candidates,
    (target) => {
      if (!target) return;
      effectDestroyOwnUnit(target, null);
    },
    null,
    { mandatory: true }
  );
});

// Sent by a card effect (e.g. GD05-034 Gaia Gundam) offering THIS client's
// own controller an OPTIONAL 1-card discard from their own hand — only I
// have authoritative hand data. Always replies with DISCARD_RESPONSE so the
// sender's effect (which may branch on "if they don't discard...") knows
// the outcome.
NET.onOfferDiscard(() => {
  if (state.hand.length === 0) {
    NET.send({ type: 'DISCARD_RESPONSE', discarded: false });
    return;
  }
  showCardPickerModal(
    'Enemy Effect',
    'You may discard 1 card from your hand.',
    [...state.hand],
    (target) => {
      if (target) {
        state.hand = state.hand.filter(c => c.uid !== target.uid);
        updateTempState(target.cardNo, target.copyVer, 'trash');
        state.trashCount++;
        state.playerTrash.push(toTrashCard(target));
        addLog(`Discarded ${target.name} (enemy effect).`);
        broadcastCardToast(`${target.name} was sent to trash.`, target.img);
        broadcastCounters(); render(); setupDropZones();
      }
      NET.send({ type: 'DISCARD_RESPONSE', discarded: !!target });
    },
    null,
    { mandatory: false, skipLabel: 'Decline' }
  );
});

// Consumed by whichever card effect just called offerDiscardToOpponent() —
// see the shared helper below. Single-pending-slot convention (see
// pendingBlockResolve above).
NET.onDiscardResponse(discarded => {
  if (pendingDiscardResolve) {
    const fn = pendingDiscardResolve;
    pendingDiscardResolve = null;
    fn(discarded);
  }
});

// Sends an OFFER_DISCARD to the opponent and waits for their DISCARD_RESPONSE
// before calling onResult(discarded: bool). Shared by any red-card effect
// that needs to branch on whether the enemy actually discarded (e.g.
// GD05-034 Gaia Gundam). Also sets state.causedEnemyDiscardThisTurn when the
// discard actually happened (GD05-041 Gaia Gundam MA Mode's hand-cost hook).
function offerDiscardToOpponent(onResult) {
  // Waiting overlay stays up for as long as the opponent is actually
  // deciding — only hidden once their real DISCARD_RESPONSE arrives (or the
  // stuck-wait watchdog below gives up on a lost message after 30s+), not on
  // any fixed timer.
  showDecisionWaitingOverlay("Waiting on opponent's discard decision…");
  pendingDiscardResolve = (discarded) => {
    hideDecisionWaitingOverlay();
    if (discarded) state.causedEnemyDiscardThisTurn = true;
    onResult(discarded);
  };
  armStuckWaitWatchdog(() => pendingDiscardResolve, (fn) => { pendingDiscardResolve = null; fn(false); });
  NET.send({ type: 'OFFER_DISCARD' });
}

// Sends a FORCE_DISCARD to the opponent (mandatory, no response awaited).
// Callers are expected to have already checked the opponent has a qualifying
// hand size, so the discard is assumed to succeed — sets
// state.causedEnemyDiscardThisTurn immediately (GD05-041's hand-cost hook).
function forceDiscardOnOpponent() {
  state.causedEnemyDiscardThisTurn = true;
  NET.send({ type: 'FORCE_DISCARD' });
}

// EB01-062 Jona Basta's [Attack] — the activating player is offering ME an
// OPTIONAL draw from MY OWN deck (only I have authoritative deck data).
// Same shape as onOfferDiscard just above, for a draw instead of a discard.
NET.onOfferDraw(() => {
  showYesNoPrompt('Enemy Effect', 'You may draw 1 card.', (accepted) => {
    let drew = false;
    if (accepted) {
      const drawn = drawCardsFromTempDeck(1, true);
      if (drawn.length) {
        state.hand.push(drawn[0]);
        drew = true;
        addLog(`Drew ${drawn[0].name} (enemy effect).`);
        broadcastCounters(); render(); setupDropZones();
      }
    }
    NET.send({ type: 'DRAW_RESPONSE', drew });
  }, { yesLabel: 'Draw 1', noLabel: 'Decline' });
});

// Consumed by whichever card effect just called offerDrawToOpponent() — see
// the shared helper below. Single-pending-slot convention (see
// pendingDiscardResolve above).
NET.onDrawResponse(drew => {
  if (pendingDrawResolve) {
    const fn = pendingDrawResolve;
    pendingDrawResolve = null;
    fn(drew);
  }
});

// Sends an OFFER_DRAW to the opponent and waits for their DRAW_RESPONSE
// before calling onResult(drew: bool). Shared by any card effect that needs
// to branch on whether the enemy actually drew (e.g. EB01-062 Jona Basta:
// "if they draw with this effect, draw 1").
function offerDrawToOpponent(onResult) {
  showDecisionWaitingOverlay("Waiting on opponent's draw decision…");
  pendingDrawResolve = (drew) => {
    hideDecisionWaitingOverlay();
    onResult(drew);
  };
  armStuckWaitWatchdog(() => pendingDrawResolve, (fn) => { pendingDrawResolve = null; fn(false); });
  NET.send({ type: 'OFFER_DRAW' });
}

// Mirrors the opponent's own specialMoveCommandActivatedThisTurn flag (see
// triggerCardEffect) so a live passiveKeywords check on their copy of a card
// (e.g. GD05-068 Shining Gundam (Super Mode)) reads correctly on my client.
NET.onSpecialMoveCommandActivated(() => {
  state.oppSpecialMoveCommandActivatedThisTurn = true;
});

// Sent by a card effect (e.g. GD05-046 Abyss Gundam (MA Mode)) that forces
// THIS client's own controller to discard 1 card from their own hand —
// mandatory, no response expected.
NET.onForceDiscard(() => {
  if (state.hand.length === 0) {
    addLog('Enemy effect: no cards in hand to discard.');
    return;
  }
  showCardPickerModal(
    'Enemy Effect',
    'Choose 1 card from your hand to discard.',
    [...state.hand],
    (target) => {
      if (!target) return;
      state.hand = state.hand.filter(c => c.uid !== target.uid);
      updateTempState(target.cardNo, target.copyVer, 'trash');
      state.trashCount++;
      state.playerTrash.push(toTrashCard(target));
      addLog(`Discarded ${target.name} (enemy effect).`);
      broadcastCardToast(`${target.name} was sent to trash.`, target.img);
      broadcastCounters(); render(); setupDropZones();
    },
    null,
    { mandatory: true }
  );
});

// Sent directly by a card effect (e.g. GD03-119) that modifies AP on EITHER
// side's units — both playerField and oppField are only approximate mirrors
// on the non-controlling client, so the real controller of each affected uid
// applies the modifier to their own authoritative field card so their
// effectiveBp()/combat math agrees with the other client's. `field` is
// 'tempApMod' ("during this turn") or 'battleApMod' ("during this battle");
// defaults to 'tempApMod' for callers that predate this parameter.
NET.onFieldApMod((uids, delta, field) => {
  const key = field || 'tempApMod';
  uids.forEach(uid => {
    const card = state.playerField.find(c => c && c.uid === uid) ||
                 state.oppField.find(c => c && c.uid === uid);
    if (!card) return;
    // passiveApReduceImmune: pilot grants immunity to negative AP mods (e.g. GD01-090 Duo Maxwell)
    if (delta < 0 && (key === 'tempApMod' || key === 'battleApMod') && card.pilot) {
      const h = window.CardEffects?.[card.pilot.cardNo];
      if (h?.passiveApReduceImmune?.(card.pilot, card)) {
        addLog(`${card.name} — AP reduction blocked (linked pilot grants immunity).`);
        return;
      }
    }
    if (key === 'battleImmuneAp') {
      card.battleImmuneAp = Math.max(card.battleImmuneAp || 0, delta);
    } else if (key === 'battleImmuneAll') {
      card.battleImmuneAll = true;
    } else {
      card[key] = (card[key] || 0) + delta;
    }
    // onEnemyApReduced: fires on player's own unit when an enemy effect reduces its AP (e.g. GD02-009 Calamity Gundam)
    if (delta < 0 && (key === 'tempApMod' || key === 'battleApMod')) {
      if (state.playerField.some(c => c && c.uid === uid)) {
        window.CardEffects?.[card.cardNo]?.onEnemyApReduced?.(card);
      }
    }
  });
  render();
  setupDropZones();
});

// Sent directly by a card effect that rests/un-rests a Unit or Base outside
// the normal combat-driven ATTACK flow (e.g. GD03-119 setting a Base active)
// — searches both fields and both bases since either client may own the
// affected uid.
NET.onUnitRest((uid, rested) => {
  const playerUnit = state.playerField.find(c => c && c.uid === uid);
  const card = playerUnit ||
               state.oppField.find(c => c && c.uid === uid) ||
               (state.playerBase && state.playerBase.uid === uid ? state.playerBase : null) ||
               (state.oppBase && state.oppBase.uid === uid ? state.oppBase : null);
  if (!card) return;
  const wasRested = card.rested;
  card.rested = rested;
  // Fire 'unrest' pilot trigger when an opponent's effect un-rests one of our
  // units. The same trigger fires from setRested() for player-initiated
  // un-rests; this covers the network-driven path (opponent's turn effects).
  if (wasRested && !rested && playerUnit?.pilot) {
    triggerCardEffect(playerUnit.pilot, 'unrest', { unit: playerUnit });
  }
  // Base passive hook: fires when the opponent's effect rests a player unit
  // during the opponent's turn (e.g. GD03-128 Doritea). Once-per-turn gate
  // is enforced via base.passiveUsedThisTurn, cleared by UNREST_ALL.
  if (playerUnit && rested && !state.myTurn && state.playerBase) {
    const baseHandlers = window.CardEffects?.[state.playerBase.cardNo];
    if (baseHandlers?.onOpponentRestPlayerUnit && !state.playerBase.passiveUsedThisTurn) {
      baseHandlers.onOpponentRestPlayerUnit(state.playerBase, playerUnit);
    }
  }
  render();
  setupDropZones();
});

// Auto-advances Draw → Resource → Main after mulligan, 2s between each phase.
// Announces each phase name over the field, locks UI until Main Phase.
// Only the turn owner runs this — the other tab mirrors via PHASE_SYNC.
function advanceThroughPhases() {
  lockUI();
  const steps = [
    { index: 1, delay: 2000, announce: true,  action: null },        // Draw Phase
    { index: 1, delay: 2500, announce: false, action: () => dispatch('DRAW_CARD') }, // draw card mid-phase
    { index: 2, delay: 4000, announce: true,  action: () => dispatch('GAIN_RESOURCE') }, // Resource Phase
    { index: 3, delay: 6000, announce: true,  action: () => { unlockUI(); maybeAutoEndMainPhase(); } }, // Main Phase — unlock
  ];
  steps.forEach(({ index, delay, announce, action }) => {
    setTimeout(() => {
      dispatch('SET_PHASE', { index });
      if (action) action();
      broadcastPhaseSync();
      render();
      setupDropZones();
      if (announce) {
        addLog(`Phase → ${state.phase}`);
        showPhaseAnnouncement(state.phase);
      }
    }, delay / PHASE_SPEED);
  });
}

// Triggered by clicking End Phase pill. Only the turn owner can do this —
// ending a turn hands control to the opponent rather than starting my own
// next turn locally.
// Runs the End Phase action step for both players (opponent first, then current
// player), then calls onAllDone to continue with REPAIR_FIELD and CLEAR_TURN_EFFECTS.
// In solo (non-networked) games only the current player acts.
function runEndPhaseStepSequence(onAllDone) {
  const ctx = { attackerSide: 'player', defenderSide: 'opp', battle: null };

  function runMyStep() {
    // Current player's own end phase action step
    state.endPhaseStep = true;
    runActionStep(ctx, 'attacker', () => {
      state.endPhaseStep = false;
      onAllDone();
    });
  }

  if (!state.networked) {
    // Solo practice: only current player acts
    runMyStep();
    return;
  }

  // Networked: tell the opponent to take their step first, then take mine
  NET.send({ type: 'END_PHASE_OPP_REQUEST' });
  state.endPhaseStep = true;
  runActionStep(ctx, 'defender', () => {
    // Opponent done — now take my own step
    state.endPhaseStep = false;
    runMyStep();
  });
}

function advanceNewTurn() {
  if (!state.myTurn) return;     // not my turn — End Phase pill shouldn't be reachable anyway
  if (state.phaseLocked) return; // guard against double-clicks during transition
  lockUI();

  dispatch('SET_PHASE', { index: 4 }); // 'End Phase'
  addLog('Phase → End Phase');
  showPhaseAnnouncement('End Phase');
  broadcastPhaseSync();
  render();
  setupDropZones();

  function finishEndPhase() {
    // ── Repair first (synchronous) ──
    dispatch('REPAIR_FIELD');

    // Build the ordered list of end-of-turn hook calls. Both endOfTurnLinked and
    // endOfTurn hooks may open picker modals, so they run sequentially via an
    // async chain — each hook receives an `onDone` callback and must call it when
    // its own flow (including any open pickers) has fully resolved. Hooks with
    // only one parameter are synchronous; onDone is called immediately after them.
    const hookCalls = [];
    state.playerField.forEach(card => {
      if (!card || !card.linkedThisTurn || !card.pilot) return;
      const h = window.CardEffects?.[card.cardNo]?.endOfTurnLinked;
      if (h) hookCalls.push((onDone) => { h(card, onDone); if (h.length < 2) onDone(); });
    });
    state.playerField.forEach(card => {
      if (!card) return;
      const h = window.CardEffects?.[card.cardNo]?.endOfTurn;
      if (h) hookCalls.push((onDone) => { h(card, onDone); if (h.length < 2) onDone(); });
    });

    let hookIdx = 0;
    function runNextHook() {
      if (hookIdx >= hookCalls.length) {
        afterHooks();
        return;
      }
      hookCalls[hookIdx++](runNextHook);
    }

    function afterHooks() {
      dispatch('CLEAR_TURN_EFFECTS');
      render();
      setupDropZones();

      function handOffTurn() {
        // Safety: if a picker modal is still open, defer until it closes so the
        // phase never hands off while the player is mid-selection. Also wait
        // out any effect-damage destroy still in flight (pendingEffectDestroys)
        // — e.g. a Unit destroyed during the End Phase Action Step whose
        // [Destroyed] trigger (dealDamageToUnit's doDestroy) hasn't run yet:
        // the Action Step's own onDone() fires right after dealDamageToUnit()
        // returns, well before that 500ms animation delay elapses, so without
        // this the turn could hand off — and the trigger silently never
        // fire — before the destroy (and any modal it opens) even happens.
        // Polled rather than MutationObserver+flag-check split in two, since
        // pendingEffectDestroys clearing isn't itself a DOM mutation (the
        // 500ms timeout can resolve with no modal ever opening at all).
        if (document.getElementById('ingame-menu-overlay') || pendingEffectDestroys > 0) {
          const poll = setInterval(() => {
            if (pendingEffectDestroys === 0 && !document.getElementById('ingame-menu-overlay')) {
              clearInterval(poll);
              handOffTurn();
            }
          }, 100);
          return;
        }
        state.myTurn = false;
        // Reset per-turn action gates so [Activate･Action] effects can be used
        // during the opponent's attack step (each player's turn is independent).
        state.playerField.forEach(c => {
          if (c) c.activateActionUsedThisTurn = false;
        });
        addLog(`${actorDisplayName('player')}'s turn has ended.`, { noPill: true });
        NET.send({ type: 'END_TURN' });
        showTurnIndicator();
        render();
        setupDropZones();
        // No second client to send END_TURN to in a bot match — drive the
        // bot's own turn directly instead (see js/bot_logic/bot-engine.js).
        if (state.isBotMatch) BotEngine.startBotTurn();
      }

      // ── Hand limit check — player must discard down to 10 before new turn ──
      if (state.hand.length > 10) {
        showHandDiscardModal(handOffTurn);
        return;
      }
      handOffTurn();
    }

    runNextHook();
  }

  // ── End Phase Action Step (opponent first, then player) → then repair/clear ──
  runEndPhaseStepSequence(finishEndPhase);
}

// Skips straight to End Phase if the turn owner has no actions left when
// Main Phase opens — no playable card in hand and no active (non-rested)
// field unit to attack with — instead of waiting on a manual click.
// Re-checks after the delay in case state changed in the brief window
// before firing.
function hasMainPhaseActionsLeft() {
  return state.hand.some(canPlayCard) ||
    state.playerField.some(c => c && !c.rested) ||
    canActivateBase() ||
    state.playerField.some(c => c && canActivatePilot(c)) ||
    state.playerField.some(c => c && canActivateUnitEffect(c));
}

// Also called after every action that could exhaust the player's options
// mid-phase (playing/discarding a hand card, finishing an attack) — not just
// once when Main Phase opens — so the auto-skip kicks in the moment the last
// action is spent, not only at the start of the phase.
function maybeAutoEndMainPhase() {
  if (!state.myTurn || state.phase !== 'Main Phase' || state.attackInProgress) return;
  if (hasMainPhaseActionsLeft()) return;
  setTimeout(() => {
    if (state.myTurn && state.phase === 'Main Phase' && !state.attackInProgress && !hasMainPhaseActionsLeft()) {
      if (document.getElementById('ingame-menu-overlay') || document.getElementById('decision-waiting-overlay')) return;
      addLog('No actions left — automatically proceeding to End Phase.');
      advanceNewTurn();
    }
  }, 1200);
}

function startNewTurn() {
  dispatch('NEW_TURN');
  console.log(`[startNewTurn] starting turn ${state.turn}`);
  broadcastPhaseSync();

  const steps = [
    { index: 0, delay: 2000, announce: true,  action: () => { dispatch('UNREST_ALL'); dispatch('RESET_RESOURCES'); } }, // Start Phase
    { index: 1, delay: 4000, announce: true,  action: null },            // Draw Phase
    { index: 1, delay: 4500, announce: false, action: () => dispatch('DRAW_CARD') },     // draw card (no re-announce)
    { index: 2, delay: 6000, announce: true,  action: () => dispatch('GAIN_RESOURCE') },    // Resource Phase
    { index: 3, delay: 8000, announce: true,  action: () => { unlockUI(); maybeAutoEndMainPhase(); } }, // Main Phase — unlock
  ];
  steps.forEach(({ index, delay, announce, action }) => {
    setTimeout(() => {
      dispatch('SET_PHASE', { index });
      if (action) action();
      broadcastPhaseSync();
      render();
      setupDropZones();
      if (announce) {
        addLog(`Phase → ${state.phase}`);
        showPhaseAnnouncement(state.phase);
      }
    }, delay / PHASE_SPEED);
  });
}

registerAction('DISCARD_HAND_LIMIT', ({ uids }) => {
  const uidSet = new Set(uids);
  const discarded = state.hand.filter(c => uidSet.has(c.uid));
  state.hand = state.hand.filter(c => !uidSet.has(c.uid));
  state.playerTrash.push(...discarded.map(toTrashCard));
  const gain = discarded.length;
  addLog(`Discarded ${gain} card${gain !== 1 ? 's' : ''} to trash (hand limit).`);
  discarded.forEach(c => broadcastCardToast(`${c.name} was sent to trash.`, c.img));
  return gain;
});

// `opts.needed` defaults to the hand-limit overflow (hand.length - 10).
// `opts.exact` (default false) controls validation: false = legacy "discard
// down to 10" mode (selecting more than needed is fine, as long as the
// remaining hand is <= 10); true = must select exactly `needed` cards
// (used by card effects like GD01-118's "discard 1"). Built on the same
// #ingame-menu-modal/.bop-tile/.igm-btn-row shell as every other picker
// (showCardPickerModal, showCardPickerModalMulti, ...) rather than its own
// bespoke markup, so it looks and behaves like the rest of that family —
// min/max just collapse the two validation modes onto the same
// disable-until-valid Confirm button + selection-cap pattern they use.
function showHandDiscardModal(onConfirm, opts = {}) {
  const existing = document.getElementById('ingame-menu-overlay');
  if (existing) existing.remove();

  const needed = opts.needed != null ? opts.needed : (state.hand.length - 10);
  const exact = !!opts.exact;
  const min = needed;
  const max = exact ? needed : state.hand.length;
  const cards = state.hand;
  const title = opts.title || 'Hand Limit Exceeded';
  const body = opts.question ||
    `You have ${state.hand.length} cards in hand. Select ${needed} card${needed !== 1 ? 's' : ''} to discard.`;
  const selected = new Set();

  const overlay = document.createElement('div');
  overlay.id = 'ingame-menu-overlay';

  const modal = document.createElement('div');
  modal.id = 'ingame-menu-modal';
  modal.classList.add('bop-modal');

  const rowClass = cards.length > 5 ? 'bop-grid' : 'bop-row';

  function renderTiles() {
    modal.innerHTML = `
      <button class="modal-hide-btn" title="Hide to view field"><span class="modal-hide-label">View Field</span><span class="material-symbols-outlined">ad_off</span></button>
      <div class="igm-title">${title}</div>
      <div class="igm-body">${body}</div>
      <div class="${rowClass}">
        ${cards.map((card, i) => `
          <div class="bop-tile${selected.has(i) ? ' bop-selected' : ''}" data-idx="${i}">
            <div class="bop-order-label">${selected.has(i) ? 'Selected' : ''}</div>
            <div class="cp-img-wrap">
              ${(card.type === 'unit' || card.type === 'unit-token' || card.type === 'base') ? statTileHtml(card) : (card.img ? `<img src="${card.img}" alt="${card.name}" />` : '<div class="cp-no-img">?</div>')}
            </div>
            <div class="bop-name">${card.name}</div>
          </div>`).join('')}
      </div>
      <div class="igm-btn-row">
        <button class="igm-btn igm-confirm-base"${selected.size < min ? ' disabled' : ''}>Confirm</button>
      </div>
    `;
    modal.querySelector('.modal-hide-btn').addEventListener('click', (e) => {
      e.stopPropagation();
      hideModalForFieldView(overlay);
    });
    modal.querySelectorAll('.bop-tile').forEach(tile => {
      const idx = parseInt(tile.dataset.idx, 10);
      const card = cards[idx];
      tile.addEventListener('mouseenter', e => showCardPreview(card, e.currentTarget));
      tile.addEventListener('mouseleave', hideCardPreview);
      tile.addEventListener('click', () => {
        if (selected.has(idx)) {
          selected.delete(idx);
        } else if (selected.size < max) {
          selected.add(idx);
        }
        modal.querySelectorAll('.bop-tile[data-idx]').forEach(t => {
          const i = parseInt(t.dataset.idx, 10);
          const isSel = selected.has(i);
          t.classList.toggle('bop-selected', isSel);
          t.querySelector('.bop-order-label').textContent = isSel ? 'Selected' : '';
        });
        const confirmBtn = modal.querySelector('.igm-confirm-base');
        if (confirmBtn) confirmBtn.disabled = selected.size < min;
      });
    });
    modal.querySelector('.igm-confirm-base').addEventListener('click', () => {
      if (selected.size < min) return;
      overlay.remove();
      hideCardPreview();
      const uids = [...selected].map(i => cards[i].uid);
      const gain = dispatch('DISCARD_HAND_LIMIT', { uids });
      animateTrashDestroy('trash-count-2', gain, () => { state.trashCount += gain; render(); });
      render();
      setupDropZones();
      onConfirm();
    });
  }

  renderTiles();
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  startSelectionTimer(overlay, () => autoResolveIgmModal(overlay));
}

registerAction('RESET_RESOURCES', () => {
  state.res = state.resBase;  // refill spendable pool back to permanent accumulated level
  state.resourceSetActiveThisTurn = false;
  addLog(`Start Phase — RES restored to ${state.res}.`);
  // A "rested" EX Resource (e.g. GD04-085 Suletta Mercury's "place 1 rested
  // EX Resource") counts toward level immediately but isn't spendable until
  // the controller's next turn — same one-turn delay as a normal Resource
  // gained via GAIN_RESOURCE, which becomes fully spendable every Start
  // Phase via the state.res = state.resBase line above. Previously nothing
  // ever moved exResRested back into exRes, so a rested EX Resource stayed
  // permanently unspendable for the rest of the game.
  if (state.exResRested > 0) {
    state.exRes += state.exResRested;
    addLog(`Start Phase — ${state.exResRested} EX Resource(s) now active (total active: ${state.exRes}).`);
    state.exResRested = 0;
  }
});

registerAction('GAIN_RESOURCE', () => {
  if (state.resBase >= 10) {
    addLog('Resource Phase — RES already at max (10). No gain.');
    return;
  }
  state.res     = Math.min(state.res + 1, 10);
  state.resBase = Math.min(state.resBase + 1, 10);
  addLog(`Resource Phase — gained 1 RES (total: ${state.res}).`);
});

function animateTrashDestroy(elId, amount, onComplete) {
  onComplete(); // update value and re-render first
  requestAnimationFrame(() => {
    const el = document.getElementById(elId);
    if (!el) return;
    el.classList.remove('trash-destroy-anim'); // reset in case already animating
    void el.offsetWidth; // force reflow so re-adding the class restarts the animation
    el.classList.add('trash-destroy-anim');
    setTimeout(() => el.classList.remove('trash-destroy-anim'), 650);
  });
}

// Same glow-bounce pulse as animateTrashDestroy, but for the EXILED counter.
// Exile is append-only (see state.playerExile/oppExile), and every path that
// exiles a card funnels through exileFromTrash (js/effects/purple.js), so
// this is called from there right after the push — unlike trash there's no
// separate "amount"/onComplete state-update step to sequence around, since
// render() (called by whichever effect handler triggered the exile) already
// picks up the new state.playerExile.length on its own.
function flashExileCounter(elId) {
  requestAnimationFrame(() => {
    const el = document.getElementById(elId);
    if (!el) return;
    el.classList.remove('trash-destroy-anim');
    void el.offsetWidth;
    el.classList.add('trash-destroy-anim');
    setTimeout(() => el.classList.remove('trash-destroy-anim'), 650);
  });
}

// Returns any permanent AP bonus contributed by a paired pilot's
// `passiveApBonus` hook (e.g. "while there is a friendly white Base in play,
// this Unit gets AP+2", or "while no enemy Base is in play, this Unit gets
// AP+1"). The hook receives (pilot, unit, ownerBase, enemyBase) — both the
// base card belonging to the unit's controlling player and the base card
// belonging to the opposing player (either may be null), determined
// dynamically by searching playerField vs oppField so the right sides are
// checked on both clients. Returns 0 when no hook is registered or the
// pilot's condition is not met.
function getPilotPassiveApBonus(card) {
  if (!card || !card.pilot) return 0;
  const handlers = window.CardEffects?.[card.pilot.cardNo];
  if (!handlers?.passiveApBonus) return 0;
  // Not a genuine field Unit (e.g. called on a Base during effectiveBp()'s
  // combat math) — CONFIRMED BUG: falling through to the old unconditional
  // "found on playerField ? player : opp" below silently misattributed
  // EVERY non-field card (a Base isn't in playerField/oppField at all) to
  // 'opp', scanning the WRONG side's field for bonus-granting cards. See
  // getGlobalUnitApBonus's matching fix for the concrete symptom this
  // caused: a paired Gundam's own [During Pair] AP+1-to-all-your-Units aura
  // leaking onto the ENEMY's base's effective AP when Gundam attacked it,
  // then getting applied back as real counter-damage to Gundam itself.
  if (!state.playerField.some(c => c && c.uid === card.uid) && !state.oppField.some(c => c && c.uid === card.uid)) return 0;
  const ownerSide = state.playerField.some(c => c && c.uid === card.uid) ? 'player' : 'opp';
  const enemySide = ownerSide === 'player' ? 'opp' : 'player';
  const base = state[`${ownerSide}Base`] || null;
  const enemyBase = state[`${enemySide}Base`] || null;
  return handlers.passiveApBonus(card.pilot, card, base, enemyBase) || 0;
}

// Unit-level conditional AP bonus (e.g. GD01-081 M1 Astray: +1 while another
// TSA unit is in play). Hook signature: passiveUnitApBonus(card, ownerSide).
function getUnitPassiveApBonus(card) {
  if (!card) return 0;
  const handlers = window.CardEffects?.[card.cardNo];
  if (!handlers?.passiveUnitApBonus) return 0;
  // Not a field Unit (e.g. a Base) — see getPilotPassiveApBonus's matching
  // guard above for why this must return early instead of defaulting to 'opp'.
  if (!state.playerField.some(c => c && c.uid === card.uid) && !state.oppField.some(c => c && c.uid === card.uid)) return 0;
  const ownerSide = state.playerField.some(c => c && c.uid === card.uid) ? 'player' : 'opp';
  return handlers.passiveUnitApBonus(card, ownerSide) || 0;
}

// Unit-level conditional HP bonus (e.g. GD01-076 Michaelis: HP+1 while 4+
// commands in owner's trash). Hook signature: passiveUnitHpBonus(card,
// ownerSide) → number. Folded into effectiveHp(), effectiveMaxHp(), and all
// three HP display sites so the live value always matches combat math.
function getUnitPassiveHpBonus(card) {
  if (!card) return 0;
  const handlers = window.CardEffects?.[card.cardNo];
  if (!handlers?.passiveUnitHpBonus) return 0;
  // Not a field Unit (e.g. a Base) — see getPilotPassiveApBonus's matching
  // guard above for why this must return early instead of defaulting to 'opp'.
  if (!state.playerField.some(c => c && c.uid === card.uid) && !state.oppField.some(c => c && c.uid === card.uid)) return 0;
  const ownerSide = state.playerField.some(c => c && c.uid === card.uid) ? 'player' : 'opp';
  return handlers.passiveUnitHpBonus(card, ownerSide) || 0;
}

// Pilot-granted conditional HP bonus to its paired Unit — the HP counterpart
// to getPilotPassiveApBonus above (same signature: passiveHpBonus(pilot,
// unit, ownerBase, enemyBase) → number). Example: GD05-091 Sting Oakley:
// "While an enemy player has 7 or more cards in their trash, this Unit gets
// AP+1 and HP+1" (paired with passiveApBonus for the AP half).
function getPilotPassiveHpBonus(card) {
  if (!card || !card.pilot) return 0;
  const handlers = window.CardEffects?.[card.pilot.cardNo];
  if (!handlers?.passiveHpBonus) return 0;
  // Not a field Unit (e.g. a Base) — see getPilotPassiveApBonus's matching
  // guard above for why this must return early instead of defaulting to 'opp'.
  if (!state.playerField.some(c => c && c.uid === card.uid) && !state.oppField.some(c => c && c.uid === card.uid)) return 0;
  const ownerSide = state.playerField.some(c => c && c.uid === card.uid) ? 'player' : 'opp';
  const enemySide = ownerSide === 'player' ? 'opp' : 'player';
  const base = state[`${ownerSide}Base`] || null;
  const enemyBase = state[`${enemySide}Base`] || null;
  return handlers.passiveHpBonus(card.pilot, card, base, enemyBase) || 0;
}

// Unit-level conditional keyword grants (e.g. GD01-081 M1 Astray: Blocker while
// another TSA unit is in play). Hook signature: passiveKeywords(card, ownerSide)
// → string[]. Checked live by hasNativeKeyword so the keyword appears/disappears
// automatically as field state changes without any per-turn state mutation.
function getPassiveKeywords(card) {
  if (!card) return [];
  const handlers = window.CardEffects?.[card.cardNo];
  if (!handlers?.passiveKeywords) return [];
  // Not a field Unit (e.g. a Base) — see getPilotPassiveApBonus's matching
  // guard above for why this must return early instead of defaulting to 'opp'.
  if (!state.playerField.some(c => c && c.uid === card.uid) && !state.oppField.some(c => c && c.uid === card.uid)) return [];
  const ownerSide = state.playerField.some(c => c && c.uid === card.uid) ? 'player' : 'opp';
  return handlers.passiveKeywords(card, ownerSide) || [];
}

// Field-wide AP bonus granted by one card to other units on the same side
// (e.g. GD03-033 Providence Gundam: "[During Pair·(ZAFT) Pilot] all ZAFT Units
// get AP+2"). Hook signature: globalUnitApBonus(targetCard, sourceCard, ownerSide)
// → number. Called once per other field card per effectiveBp evaluation; safe
// to call effectiveBp/effectiveHp inside the hook because it doesn't recurse
// back through getGlobalUnitApBonus.
function getGlobalUnitApBonus(card) {
  if (!card) return 0;
  // Not a field Unit — CONFIRMED BUG (the one that actually surfaced as a
  // visible gameplay issue): effectiveBp() calls this unconditionally on
  // ANY card, including a defending Base during combat (defBp =
  // effectiveBp(defender)). A Base is never present in playerField/oppField,
  // so the old unconditional "found on playerField ? player : opp" below
  // always fell through to 'opp' for a Base — including the HUMAN's OWN
  // base, which got misread as opp-owned, scanning the BOT's field instead
  // and picking up the bot's own paired Gundam's "[During Pair] AP+1 to all
  // your Units" aura as if the enemy's base were one of Gundam's own units.
  // That bogus +1 became the base's "counter-damage" AP in runDamageStep,
  // dealing 1 damage back to Gundam for simply attacking a base — see
  // getPilotPassiveApBonus's matching guard above for the other functions
  // with this same bug.
  if (!state.playerField.some(c => c && c.uid === card.uid) && !state.oppField.some(c => c && c.uid === card.uid)) return 0;
  const ownerSide = state.playerField.some(c => c && c.uid === card.uid) ? 'player' : 'opp';
  const field = ownerSide === 'player' ? state.playerField : state.oppField;
  let bonus = 0;
  for (const c of field) {
    if (c) {
      const h = window.CardEffects?.[c.cardNo];
      if (h?.globalUnitApBonus) bonus += h.globalUnitApBonus(card, c, ownerSide) || 0;
      // Pilot-level global AP bonuses (e.g. GD04-083 Marbet Fingerhat: all LM tokens +1)
      if (c.pilot) {
        const ph = window.CardEffects?.[c.pilot.cardNo];
        if (ph?.globalPilotApBonus) bonus += ph.globalPilotApBonus(card, c.pilot, c, ownerSide) || 0;
      }
    }
  }
  return bonus;
}
// Field-wide OUTGOING damage bonus — a card can boost the damage EVERY
// matching card on its own side deals to enemy Units (e.g. GD06-033 ZZ
// Gundam: "your (AEUG) cards deal +1"), the mirror image of
// getGlobalUnitApBonus above but for damage dealt rather than a stat.
// Applied at the point damage is dealt — dealDamageToUnit for effect
// damage (via currentEffectSourceCard, whichever card's handler is
// currently resolving), runDamageStep for battle damage (via the
// attacker/defender directly) — rather than folded into AP, so ANY damage
// source on that side gets boosted without each one having to re-derive it.
// Hook signature: globalOutgoingDamageBonus(sourceOfDamageCard,
// providingCard, ownerSide) → number. `ownerSide` here is the DEALING
// side (the side whose (Trait) card is dealing damage), not the target's.
function getGlobalOutgoingDamageBonus(sourceCard, ownerSide) {
  if (!sourceCard) return 0;
  const field = ownerSide === 'player' ? state.playerField : state.oppField;
  let bonus = 0;
  for (const c of field) {
    if (c) {
      const h = window.CardEffects?.[c.cardNo];
      if (h?.globalOutgoingDamageBonus) bonus += h.globalOutgoingDamageBonus(sourceCard, c, ownerSide) || 0;
    }
  }
  return bonus;
}
// Field-wide keyword grants from one card to other units on the same side
// (e.g. GD01-001 Gundam: all WBT units gain Repair 1; GD04-013 Core Fighter:
// LM tokens gain Blocker while rested). Hook signature:
// globalUnitKeyword(targetCard, sourceCard, ownerSide) → string[].
function getGlobalUnitKeywords(card) {
  if (!card) return [];
  // Not a field Unit (e.g. a Base) — see getPilotPassiveApBonus's matching
  // guard above for why this must return early instead of defaulting to 'opp'.
  if (!state.playerField.some(c => c && c.uid === card.uid) && !state.oppField.some(c => c && c.uid === card.uid)) return [];
  const ownerSide = state.playerField.some(c => c && c.uid === card.uid) ? 'player' : 'opp';
  const field = ownerSide === 'player' ? state.playerField : state.oppField;
  let kws = [];
  for (const c of field) {
    if (!c || c.uid === card.uid) continue;
    const h = window.CardEffects?.[c.cardNo];
    if (h?.globalUnitKeyword) kws = kws.concat(h.globalUnitKeyword(card, c, ownerSide) || []);
  }
  return kws;
}
// Field-wide keyword grants that apply to ALL Units regardless of side (e.g.
// EB01-042 Psycho Haro: "While this Unit is rested, all Units gain
// <Blocker>"). Unlike getGlobalUnitKeywords (same-side sources only), this
// scans both fields for a source. Hook signature:
// globalKeywordAllSides(targetCard, sourceCard) → string[].
function getGlobalKeywordsAllSides(card) {
  if (!card) return [];
  let kws = [];
  const allUnits = [...state.playerField, ...state.oppField];
  for (const c of allUnits) {
    if (!c || c.uid === card.uid) continue;
    const h = window.CardEffects?.[c.cardNo];
    if (h?.globalKeywordAllSides) kws = kws.concat(h.globalKeywordAllSides(card, c) || []);
  }
  return kws;
}
function getBaseUnitApBonus(card) {
  if (!card) return 0;
  // Not a field Unit — e.g. called on a Base itself during effectiveBp()'s
  // combat math (defBp = effectiveBp(defender)). A Base granting an AP bonus
  // to "this card" is meaningless when "this card" IS a Base; see
  // getPilotPassiveApBonus's matching guard above for why this must return
  // early instead of defaulting to 'opp' and reading the wrong side's base.
  if (!state.playerField.some(c => c && c.uid === card.uid) && !state.oppField.some(c => c && c.uid === card.uid)) return 0;
  const ownerSide = state.playerField.some(c => c && c.uid === card.uid) ? 'player' : 'opp';
  const base = ownerSide === 'player' ? state.playerBase : state.oppBase;
  if (!base) return 0;
  const h = window.CardEffects?.[base.cardNo];
  return h?.baseUnitApBonus ? (h.baseUnitApBonus(card, ownerSide) || 0) : 0;
}
function effectiveBp(card) {
  return Math.max(0, (parseInt(card.bp) || 0) + (card.pilot ? (parseInt(card.pilot.bp) || 0) : 0) + (card.tempApMod || 0) + (card.battleApMod || 0) + getPilotPassiveApBonus(card) + getUnitPassiveApBonus(card) + getGlobalUnitApBonus(card) + getBaseUnitApBonus(card));
}
function effectiveHp(card) {
  return (parseInt(card.hp) || 0) + (card.pilot ? (parseInt(card.pilot.hp) || 0) : 0) + getUnitPassiveHpBonus(card) + getPilotPassiveHpBonus(card);
}
// Maximum HP a card can reach — DB base HP + pilot bonus + passive unit HP
// bonus. card.hp starts at the DB value and decreases with damage; it can
// recover back to this ceiling but never beyond.
function effectiveMaxHp(card) {
  if (!card) return 0;
  const entry = getCardDbEntry(card.img);
  const baseMax = entry ? (parseStatValue(entry.hp) ?? 0) : (parseInt(card.hp) || 0);
  const pilotBonus = card.pilot ? (parseInt(card.pilot.hp) || 0) : 0;
  return baseMax + pilotBonus + getUnitPassiveHpBonus(card) + getPilotPassiveHpBonus(card);
}

// onExResourceSpent: fires whenever an active EX Resource is actually
// consumed to pay a cost (e.g. GD05-018 Gundam Calibarn). Mirrors the
// onExResourcePlaced hook's call pattern (playerBase + all field units).
function notifyExResourceSpent(exSpent) {
  if (exSpent <= 0) return;
  if (state.playerBase) {
    window.CardEffects?.[state.playerBase.cardNo]?.onExResourceSpent?.(state.playerBase, { amount: exSpent });
  }
  state.playerField.forEach(c => {
    if (c) window.CardEffects?.[c.cardNo]?.onExResourceSpent?.(c, { amount: exSpent });
  });
}

// preferEx: drain state.exRes BEFORE state.res instead of the normal
// res-first order (e.g. GD05-118 Incendiary Spark: "If you use an EX
// Resource to play this card..." — the player deliberately chooses to pay
// with an EX Resource via prePlayFromHand, even when normal RES alone would
// have covered it). Returns the actual amount drawn from exRes, so the
// caller can tell whether the "used an EX Resource" condition was met even
// when the opponent's mirrored view or a 0-cost edge case makes it ambiguous.
function spendResources(cost, { preferEx = false } = {}) {
  if (!cost || cost <= 0) return 0;
  if (preferEx) {
    const fromEx = Math.min(state.exRes, cost);
    state.exRes -= fromEx;
    const remaining = cost - fromEx;
    state.res = Math.max(0, state.res - remaining);
    notifyExResourceSpent(fromEx);
    return fromEx;
  }
  const fromRes = Math.min(state.res, cost);
  state.res -= fromRes;
  const remaining = cost - fromRes;
  let exSpent = 0;
  if (remaining > 0) {
    exSpent = Math.min(state.exRes, remaining);
    state.exRes = Math.max(0, state.exRes - remaining);
    notifyExResourceSpent(exSpent);
  }
  return exSpent;
}

// This app is always a strict 1v1 match — no FFA/multiplayer lobby ever puts
// more than one opponent on the field. Cards whose printed text scales or
// gates on "the number of enemy players" (e.g. ST10-008's "draw a number of
// cards equal to the number of enemy players") always resolve against this
// constant instead of inventing per-effect logic. A card gated on "if there
// are 2 or more enemy players" is therefore permanently inert in this app —
// implemented faithfully (so it's correct if the app ever adds true
// multiplayer) rather than left unregistered.
function enemyPlayerCount() { return 1; }

// Places 1 EX Resource for the player (active or rested). Active EX resources
// can be spent alongside normal RES to pay card costs; rested ones count toward
// level but are not spendable. Fires the onExResourcePlaced hook on the player's
// base and all field units so cards can react to the placement event.
function placeExResource(rested = false) {
  if (rested) {
    state.exResRested++;
    addLog(`Placed 1 rested EX Resource (total: ${state.exRes + state.exResRested}).`);
  } else {
    state.exRes++;
    addLog(`Placed 1 EX Resource (active: ${state.exRes}, total: ${state.exRes + state.exResRested}).`);
  }
  if (state.playerBase) {
    window.CardEffects?.[state.playerBase.cardNo]?.onExResourcePlaced?.(state.playerBase, { rested });
  }
  state.playerField.forEach(c => {
    if (c) window.CardEffects?.[c.cardNo]?.onExResourcePlaced?.(c, { rested });
  });
  broadcastCounters();
  render();
}

// Returns the Repair value from a card's effect — checks tempKeywords first
// so "gains <Repair N> during this turn" grants are honoured at End Phase
// before UNREST_ALL clears them on the following Start Phase.
function getRepairValue(card) {
  if (!card) return 0;
  const tempMatch = (card.tempKeywords || []).join(' ').match(/Repair (\d+)/);
  if (tempMatch) return parseInt(tempMatch[1]);
  // Condition-gated Repair (e.g. GD02-072 Hyaku-Shiki: Repair only while white
  // Base in play). The hook returns the numeric value when the condition is met,
  // 0 otherwise. Takes priority over effect-text parsing so the card's own
  // "Repair 1" effect text doesn't trigger repair unconditionally.
  const condVal = window.CardEffects?.[card.cardNo]?.conditionalRepair?.(card);
  if (typeof condVal === 'number') return condVal;
  // Pilot-level conditional Repair (e.g. GD01-087 Sayla Mass: Repair 1 while blue).
  // Only returns early when the condition IS met (pVal > 0); otherwise falls through
  // so the unit's own native Repair is not suppressed.
  if (card.pilot) {
    const pilotH = window.CardEffects?.[card.pilot.cardNo];
    if (pilotH?.pilotConditionalRepairValue) {
      const pVal = pilotH.pilotConditionalRepairValue(card.pilot, card);
      if (typeof pVal === 'number' && pVal > 0) return pVal;
    }
  }
  if (!card.effect) {
    const gkw = getGlobalUnitKeywords(card).join(' ').match(/Repair (\d+)/);
    return gkw ? parseInt(gkw[1]) : 0;
  }
  const m = card.effect.match(/Repair (\d+)/);
  if (m) return parseInt(m[1]);
  const gkw = getGlobalUnitKeywords(card).join(' ').match(/Repair (\d+)/);
  return gkw ? parseInt(gkw[1]) : 0;
}

// Called at End Phase: heals each field unit with Repair N by up to N HP,
// capped at the unit's max HP (default DB value + paired pilot bonus).
registerAction('REPAIR_FIELD', () => {
  state.playerField.forEach(card => {
    if (!card) return;
    const repairVal = getRepairValue(card);
    if (repairVal <= 0) return;

    const entry = getCardDbEntry(card.img);
    // cap is the unit's own default HP — pilot bonus is always added on top via
    // effectiveHp() and is never stored in card.hp, so it must not be included here
    const maxHp     = entry ? (parseStatValue(entry.hp) ?? 0) : 0;
    const currentHp = parseInt(card.hp) || 0;
    if (currentHp >= maxHp) return; // already at full health

    const newHp = Math.min(currentHp + repairVal, maxHp);
    card.hp = newHp;
    if (newHp >= maxHp) card.damaged = false; // clear damage flag when fully healed
    const pilotBonus = card.pilot ? (parseInt(card.pilot.hp) || 0) : 0;
    addLog(`Repair ${repairVal} — ${card.name} HP restored to ${newHp + pilotBonus}/${maxHp + pilotBonus}.`);
    // Mirror HP change to opponent so their oppField display stays in sync.
    NET.send({ type: 'FIELD_HP_MOD', uids: [card.uid], delta: newHp - currentHp });
    // Pilot passive: "when this Unit recovers HP" (e.g. GD02-085 Four Murasame)
    const _rfPilotH = card.pilot && window.CardEffects?.[card.pilot.cardNo];
    if (_rfPilotH?.onUnitHpRecovered) _rfPilotH.onUnitHpRecovered(card.pilot, card);
    // Unit-level passive: "when THIS Unit recovers HP" (e.g. EB01-004 Gundam
    // Barbatos Lupus Rex), independent of any paired pilot.
    window.CardEffects?.[card.cardNo]?.onSelfHpRecovered?.(card);
  });
});

// True when a card's nativeKeyword field carries the given bracketed tag,
// e.g. hasNativeKeyword(card, 'Blocker') checks for the literal "[Blocker]".
// Also true for a keyword temporarily granted by a card effect via
// card.tempKeywords (e.g. "gains <Blocker> during this turn") — cleared by
// the next UNREST_ALL on that card's controller — or permanently granted by
// a paired pilot via card.pilotKeywords (e.g. Cagalli Yula Athha) — persists
// across turns for as long as the pilot is paired; cleared when pilot unlinks.
function hasNativeKeyword(card, kw) {
  if (card && card.tempKeywords   && card.tempKeywords.includes(kw))   return true;
  if (card && card.pilotKeywords  && card.pilotKeywords.includes(kw))  return true;
  if (getPassiveKeywords(card).includes(kw))                           return true;
  if (getGlobalUnitKeywords(card).includes(kw))                        return true;
  if (getGlobalKeywordsAllSides(card).includes(kw))                    return true;
  // Live conditional keyword grant from a paired pilot (e.g. GD05-087 Lauda
  // Neill: "While this Unit is (Academy), it gains <High-Maneuver>") — same
  // pilotPassiveKeywords(pilot, unit) hook hasKwIcon() already reads for the
  // badge, now also consulted here so the grant actually functions in play
  // (block-step eligibility, etc.), not just the UI icon.
  if (card && card.pilot) {
    const pilotHandlers = window.CardEffects?.[card.pilot.cardNo];
    if (pilotHandlers?.pilotPassiveKeywords && (pilotHandlers.pilotPassiveKeywords(card.pilot, card) || []).includes(kw)) return true;
  }
  return ((card && card.nativeKeyword) || '').includes(`[${kw}]`);
}

// Checks whether a keyword family icon should be shown on this unit card.
// `prefix` is the bare keyword name (e.g. 'Breach', 'Support', 'Blocker').
// Covers all sources: the unit's own native/temp/pilot-granted/passive keywords,
// AND the paired pilot's own nativeKeyword (e.g. Nena Trinity's [Support 2]).
// Uses startsWith on arrays so tiered keywords ('Breach 2') match prefix 'Breach'.
function hasKwIcon(card, prefix) {
  if (!card) return false;
  if ((card.nativeKeyword || '').includes(`[${prefix}`))                    return true;
  // Conditional Breach/Repair cards (e.g. GD05-020 Nu Gundam: "[During Pair]
  // gains <Breach 3>", GD02-072 Hyaku-Shiki: "gains <Repair 1> while a white
  // Base is in play") register conditionalBreachValue/conditionalRepair
  // instead of a static nativeKeyword, since the bonus is only sometimes
  // active. getBreachValue()/getRepairValue() already read these hooks for
  // the actual mechanic, so the badge must too or the icon never appears
  // even while the bonus is live — some cards additionally duplicate the
  // grant into passiveKeywords (checked below) for the same reason, but that
  // shouldn't be required of every card, hence checking the hooks directly.
  const _condH = window.CardEffects?.[card.cardNo];
  if (prefix === 'Breach' && _condH?.conditionalBreachValue?.(card)) return true;
  if (prefix === 'Repair' && _condH?.conditionalRepair?.(card))      return true;
  if ((card.tempKeywords  || []).some(kw => kw.startsWith(prefix)))         return true;
  if ((card.pilotKeywords || []).some(kw => kw.startsWith(prefix)))         return true;
  if (getPassiveKeywords(card).some(kw => kw.startsWith(prefix)))           return true;
  if ((card.pilot?.nativeKeyword || '').includes(`[${prefix}`))             return true;
  // Pilot-level conditional keywords for display (e.g. GD01-087 Sayla Mass: Repair 1 when blue)
  if (card.pilot) {
    const _pilotKwH = window.CardEffects?.[card.pilot.cardNo];
    if (_pilotKwH?.pilotPassiveKeywords) {
      const _pKws = _pilotKwH.pilotPassiveKeywords(card.pilot, card) || [];
      if (_pKws.some(kw => kw.startsWith(prefix))) return true;
    }
    // Pilot-level conditional Breach/Repair hooks (e.g. GD05-082 Andrew
    // Waldfeld: pilotConditionalRepairValue only, no pilotPassiveKeywords;
    // GD03-088 Asemu Asuno: pilotConditionalBreachValue only) — same gap as
    // the unit-level hooks above, checked directly for the same reason.
    if (prefix === 'Breach' && _pilotKwH?.pilotConditionalBreachValue?.(card.pilot, card)) return true;
    if (prefix === 'Repair' && _pilotKwH?.pilotConditionalRepairValue?.(card.pilot, card)) return true;
  }
  // Global keyword grants from other field units (e.g. GD01-001 Gundam Repair 1 to WBT, GD04-013 Core Fighter Blocker to LM tokens)
  if (getGlobalUnitKeywords(card).some(kw => kw.startsWith(prefix))) return true;
  return false;
}

// Turn-long "if a friendly Unit destroys an enemy Unit with battle damage
// this turn, then..." watchers (e.g. GD03-120) — registered by a card's
// 'main'/'action'/'deploy' handler via turnDestroyWatchers.push(fn), invoked
// by notifyUnitDestroyedByBattle() from runDamageStep's two Unit-destroyed
// branches, AND (targetType 'shield'/'base') from the direct shield-attack,
// direct base-attack, and Breach shield/base-damage branches — a friendly
// Unit destroying an enemy Base or Shield with battle damage fires the same
// array, just with a different targetType. Cleared on UNREST_ALL since none
// of these are meant to outlive the turn they were registered on.
// Watcher signature: (destroyer, destroyed, destroyerSide, targetType) => void
// — destroyerSide is 'player' or 'opp' from THIS client's perspective,
// matching attackerSide/defenderSide elsewhere; targetType is 'unit',
// 'shield', or 'base', identifying what was actually destroyed, since most
// registered watchers (e.g. GD03-120, GD04-002, GD04-035, ST12-016) are
// worded "destroys an enemy UNIT" and must ignore a shield/base kill — only
// GD05-002 Strike Freedom Gundam ("destroys an enemy CARD") reacts to all
// three. Not removed after firing — a standing "during this turn" condition
// can fire more than once if its trigger condition recurs.
let turnDestroyWatchers = [];

// Permanent (NOT turn-cleared) watchers for "a friendly Unit's effect just
// cost 1 or more RES to resolve" — e.g. GD04-129 Willgem's Base passive.
// No code path calls notifyUnitEffectResSpent() yet: Unit-activated
// abilities ([Activate･Main]/[Activate･Action] printed on Unit cards) have
// no dispatch/UI path in this engine at all today — only Base/Command
// activation do. This is forward-proofing infra for whenever that's built;
// registered watchers simply never fire until then. Watcher signature:
// (side, amount, unit) => void — side is 'player' or 'opp' (whoever paid the
// cost), unit is the specific field Unit whose own [Activate･Main]/
// [Activate･Action] ability was just paid for (e.g. GD05-101 Gavane Goonny:
// "if THIS is a (Militia) Unit" refers to the paying unit itself, not
// necessarily Gavane's own paired unit — see white.js).
let unitEffectResSpentWatchers = [];
function notifyUnitEffectResSpent(side, amount, unit) {
  if (!amount || amount < 1) return;
  unitEffectResSpentWatchers.forEach(w => w(side, amount, unit));
}

// Returns the DOM element that visually represents a damage target on
// `side`'s board — a field slot's `.card`, or the base/shield zone div
// itself (those zones have no `.card` child of their own, unlike field
// slots). `target` may be a live unit object, a base object (matched by
// uid against state[`${side}Base`]), or a shield/direct pseudo-target
// ({ isShield: true } / { isDirect: true }) as used by showAttackConfirmModal.
// Centralizes a lookup that used to be re-derived ad hoc (and inconsistently
// — base/shield zones were never matched by the old `.card`-only selector,
// so they silently never vibrated).
function getCardElement(side, target) {
  if (!target) return null;
  if (target.isDirect) return null; // attacking the player directly — no card to vibrate
  if (target.isShield) return document.getElementById(`zone-${side}-shield`);
  const base = state[`${side}Base`];
  if (base && target.uid === base.uid) return document.getElementById(`zone-${side}-base`);
  const idx = state[`${side}Field`].findIndex(c => c && c.uid === target.uid);
  return idx !== -1 ? document.getElementById(`${side}-slot-${idx}`)?.querySelector('.card') : null;
}

// Applies the shared battle-vibrate shake/glow animation to whatever element
// represents `target` on `side`'s board, regardless of whether it's a unit,
// token, base, EX Base, or shield. Returns the element (or null) so callers
// that need to chain a destroy-after-vibrate timeout can reuse the lookup
// instead of re-querying the DOM.
function vibrateCard(side, target) {
  const el = getCardElement(side, target);
  if (el) {
    playSfx('destroyed'); // shared "took damage" shake — covers units, bases, and shields alike
    el.classList.add('battle-vibrate');
    // Remove the class once the shake completes so it doesn't linger on base/
    // shield zone elements. Without cleanup, the atk-target-glow hover rule
    // (which uses animation !important) temporarily overrides battle-vibrate
    // while the cursor is over the zone; when the cursor leaves, the computed
    // animation value switches back to battle-vibrate and the browser replays
    // the shake even though no damage was dealt. animationName filter guards
    // against false-firing on the ::after's battle-inner-glow event, which
    // also bubbles to this element.
    function _onVibrateEnd(e) {
      if (e.animationName === 'battle-vibrate' || e.animationName === 'battle-vibrate-rested') {
        el.classList.remove('battle-vibrate');
        el.removeEventListener('animationend', _onVibrateEnd);
      }
    }
    el.addEventListener('animationend', _onVibrateEnd);
  }
  return el;
}

// Deals direct "effect damage" to a Base — i.e. damage from something other
// than a Unit's normal combat attack (an explicit "Deal N damage" effect, or
// Breach's bonus damage carrying past a destroyed defender onto the Base
// behind it — see the Breach branch in runDamageStep). `side` is 'player' or
// 'opp', whichever base zone is being hit. `fromEnemy` gates a permanent
// "can't receive enemy effect damage" immunity (card.cantReceiveEffectDamage,
// e.g. GD02-129 Argama) — self-inflicted damage (e.g. Willgem's own deploy
// effect) always applies regardless of that flag, since it isn't from an
// enemy. Distinct from card.tempCantReceiveEffectDamage (GD04-119), which is
// a turn-scoped Unit-only flag for a damage source that doesn't exist yet.
function dealDamageToBase(side, amount, { fromEnemy = false } = {}) {
  const base = state[`${side}Base`];
  if (!base) return false;
  if (fromEnemy && base.cantReceiveEffectDamage) {
    addLog(`${base.name} is immune to effect damage — no damage dealt.`);
    return false;
  }
  // reduceBaseEffectDamage(base, side) → flat reduction (e.g. ST11-006
  // Shamblo: "reduce it by 5"). `side` is passed through so the hook can
  // read the correct field ('player' or 'opp' relative to THIS client) for
  // its own "friendly Unit in play" condition — dealDamageToBase is called
  // with side:'opp' whenever a LOCAL effect damages the enemy's base, so the
  // base's owner isn't always state.playerField.
  if (fromEnemy) {
    const reduction = window.CardEffects?.[base.cardNo]?.reduceBaseEffectDamage?.(base, side) || 0;
    if (reduction > 0) {
      const reduced = Math.min(reduction, amount);
      amount -= reduced;
      addLog(`${base.name} — reduces the incoming effect damage by ${reduced}.`);
      if (amount <= 0) return false;
    }
  }
  base.hp = Math.max(0, (parseInt(base.hp) || 0) - amount);
  base.damaged = true;
  // Mirror the HP change to the peer — unlike dealDamageToUnit (FIELD_HP_MOD),
  // this previously sent nothing at all, so a Base damaged/destroyed by a
  // card effect (as opposed to battle, which both clients already resolve
  // independently and identically) silently desynced: the acting client saw
  // it damaged/gone while the peer's mirror of that Base stayed at full HP.
  // uid-addressed (checked against both playerBase/oppBase — see BASE_HP_MOD
  // in network.js) exactly like FIELD_HP_MOD is uid-addressed against both
  // fields, so this works whether `side` here is 'player' or 'opp'.
  NET.send({ type: 'BASE_HP_MOD', uid: base.uid, delta: -amount });
  broadcastLog(`${base.name} takes ${amount} effect damage${currentEffectSourceCard ? ` from ${currentEffectSourceCard.name}` : ''} (HP: ${base.hp}).`);
  if (base.hp <= 0) {
    const removedFromGame = isRemovedFromGameBase(base);
    state[`${side}Base`] = null;
    if (!removedFromGame) {
      state[trashCountKey(side)] += 1;
      state[trashArrayKey(side)].push(toTrashCard(base));
    }
    broadcastLog(`${base.name} is destroyed!${removedFromGame ? ' (removed from game)' : ''}`, { destroyedCard: base });
    broadcastCardToast(`${base.name} was destroyed!`, base.img);
    triggerCardEffect(base, 'destroyed', {});
  } else {
    requestAnimationFrame(() => vibrateCard(side, base));
  }
  return true;
}
// `destroyerIsAttacker` is true only at the call site where `destroyer` won
// the exchange while in the attacker role (runDamageStep's "Defender
// destroyed?" branch); it's false at the other call site, where a defender
// destroys the attacker via counter-damage — that destroyer was NOT
// attacking. Needed by passives gated on "while this Unit is attacking"
// (e.g. GD01-094 Yzak Jule), which must not fire off a counter-kill.
function notifyUnitDestroyedByBattle(destroyer, destroyed, destroyerSide, destroyerIsAttacker) {
  turnDestroyWatchers.forEach(w => w(destroyer, destroyed, destroyerSide, 'unit'));
  // Standing (not turn-scoped) passive on a paired Pilot, e.g. GD02-093 Olba
  // Frost — checked live off destroyer.pilot at the moment of the kill,
  // mirroring the onBaseTakesBattleDamage hook pattern rather than the
  // register/clear-on-UNREST_ALL turnDestroyWatchers array, since it isn't a
  // "during this turn" effect set up by a Main/Action — it's always armed
  // for as long as the Pilot is paired with the destroying Unit.
  // Bot-owned destroyer: same "call site is side-agnostic, registered
  // handler body usually isn't" landmine as triggerCardEffect's 'destroyed'
  // redirect above (see its comment) — e.g. GD05-085 Amuro Ray's own
  // onLinkedUnitDestroysEnemy explicitly early-returns unless
  // destroyerSide === 'player', since its HP recovery touches `unit`
  // directly but was only ever written assuming that's a real player-side
  // card. Redirect to the bot deck's own onLinkedUnitDestroysEnemy hook
  // (bot_logic) instead, same "no handler = no effect" convention.
  if (state.isBotMatch && destroyerSide === 'opp' && destroyer?.pilot) {
    const hook = window.BotDecks?.[state.botDeckId]?.onLinkedUnitDestroysEnemy?.[destroyer.pilot.cardNo];
    hook?.(destroyer.pilot, destroyer, destroyed, destroyerIsAttacker);
  } else {
    const pilotHandlers = destroyer?.pilot && window.CardEffects?.[destroyer.pilot.cardNo];
    if (pilotHandlers?.onLinkedUnitDestroysEnemy) {
      pilotHandlers.onLinkedUnitDestroysEnemy(destroyer.pilot, destroyer, destroyed, destroyerSide, destroyerIsAttacker);
    }
  }
  // Standing passive on the destroying Unit itself (e.g. GD03-022 Gundam Kyrios:
  // "[During Link] when this Unit destroys an enemy Unit with battle damage").
  const unitHandlers = window.CardEffects?.[destroyer.cardNo];
  if (unitHandlers?.onUnitDestroysEnemy) {
    unitHandlers.onUnitDestroysEnemy(destroyer, destroyed, destroyerSide, destroyerIsAttacker);
  }
  // Temp per-battle grant (e.g. ST12-014 Wise Leader's Pride) — see its
  // capture/restore dance in runDamageStep for why this reads correctly
  // here despite the generic battle-mod clear earlier in the same battle.
  // Consumed (cleared) immediately so it can't also fire for a later kill
  // this same battle or persist beyond it. Only the destroyer's own owner
  // evaluates this — only their client has a real oppField to pick from.
  if (destroyerSide === 'player' && destroyer.battleOnDestroyEnemyDestroyLowAp) {
    destroyer.battleOnDestroyEnemyDestroyLowAp = false;
    const lowApTargets = state.oppField.filter(c => c && effectiveBp(c) <= 2);
    if (lowApTargets.length > 0) {
      showCardPickerModal(destroyer.name, 'Choose 1 enemy Unit with 2 or less AP. Destroy it.', lowApTargets, (target) => {
        if (target) effectDestroyEnemyUnit(target);
      }, destroyer, { mandatory: true });
    }
  }
  // Standing passive on the player's Base (e.g. GD03-125 Peacemillion):
  // fires when a player unit destroys an enemy while attacking.
  if (destroyerSide === 'player' && destroyerIsAttacker && state.playerBase) {
    const baseHandlers = window.CardEffects?.[state.playerBase.cardNo];
    if (baseHandlers?.onPlayerUnitDestroysByBattle) {
      baseHandlers.onPlayerUnitDestroysByBattle(state.playerBase, destroyer, destroyed, destroyerSide, destroyerIsAttacker);
    }
  }
  // Standing passive on other friendly field units that watch for any friendly kill (e.g. GD02-002 Gundam Epyon)
  if (destroyerSide === 'player') {
    state.playerField.forEach(watcher => {
      if (!watcher || watcher.uid === destroyer.uid) return;
      window.CardEffects?.[watcher.cardNo]?.onAnyFriendlyUnitDestroysEnemy?.(watcher, destroyer, destroyed, destroyerSide, destroyerIsAttacker);
    });
  }
}

// Effect-kill counterpart to notifyUnitDestroyedByBattle above — fires
// onFriendlyUnitDestroyedByEffect(card, destroyedUnit) on the LOCAL player's
// own base and every remaining playerField card whenever one of their own
// Units is destroyed by a card effect rather than battle damage (e.g.
// GD05-054 Alpha Azieru: "[Once per Turn] When one of your Units is
// destroyed by an effect, draw 1"). Only ever called for the true owner's
// own authoritative playerField — call sites: dealDamageToUnit's HP≤0
// branch, network.js's FIELD_HP_MOD doDestroy, and effectDestroyOwnUnit
// (purple.js). destroyedUnit has already been removed from state.playerField
// by the time this runs, so it never notifies itself.
function notifyUnitDestroyedByEffect(destroyedUnit) {
  if (state.playerBase) {
    window.CardEffects?.[state.playerBase.cardNo]?.onFriendlyUnitDestroyedByEffect?.(state.playerBase, destroyedUnit);
  }
  state.playerField.forEach(c => {
    if (c) window.CardEffects?.[c.cardNo]?.onFriendlyUnitDestroyedByEffect?.(c, destroyedUnit);
  });
}

// Counts effect-damage destroys whose doDestroy() (see dealDamageToUnit) is
// still pending — either waiting out the 500ms vibrate-animation setTimeout,
// or synchronously inside a [Destroyed] handler that opens a picker modal
// (e.g. GD01-005 Unicorn Gundam (Unicorn Mode)'s "return paired Pilot to
// hand, then discard 1"). handOffTurn() below waits for this to hit 0 before
// ending the turn — otherwise a destroy triggered during the End Phase
// Action Step could have its [Destroyed] effect silently skipped: the
// Action Step's own onDone() fires immediately after dealDamageToUnit()
// returns (well before the animation delay elapses), so the End Phase could
// finish and hand off the turn before doDestroy() — and the trigger it
// fires — ever runs.
let pendingEffectDestroys = 0;

// Deals direct "effect damage" to a Unit — i.e. HP loss that is NOT normal
// combat damage (runDamageStep). `target` is the live game-card object.
// `fromEnemy` is true when the damage originates from the TARGET unit's
// opponent (e.g. an opponent card effect targeting the player's unit). When
// true, any `effectDamageReduction` hook registered on the target's linked
// pilot is checked and the amount reduced accordingly (minimum 0). If the
// reduction brings the damage to 0 the function is a no-op; otherwise the HP
// delta is applied and mirrored to the peer via FIELD_HP_MOD. Does NOT handle
// unit destruction — check effectiveHp(target) <= 0 after calling if needed.
function dealDamageToUnit(target, amount, { fromEnemy = false } = {}) {
  if (!target || amount <= 0) return;
  // Resolve to the live field object so mutations and effectiveHp checks always
  // use current state — not a stale captured reference from a picker callback
  // opened before a PLAY_CARD update (e.g. peer pairing a pilot) replaced the slot.
  const _live = [...state.playerField, ...state.oppField].find(c => c && c.uid === target.uid);
  if (!_live) return; // unit no longer on the field (already destroyed by another effect)
  target = _live;
  let finalAmount = amount;
  // Outgoing damage bonus from the DEALING side's own field-wide auras (e.g.
  // GD06-033 ZZ Gundam: "your (AEUG) cards deal +1") — added first, before
  // every reduction hook below, so the boost is part of the base amount
  // those hooks then reduce rather than an unreducible top-up.
  // currentEffectSourceCard identifies which card's effect is dealing this
  // damage (see its declaration) — set for the full duration of the
  // triggerCardEffect() call this runs inside, including async picker
  // callbacks. fromEnemy===true here always means the target is an enemy
  // Unit relative to whoever is dealing the damage (see this function's own
  // doc comment above), so the dealing side is simply the opposite of the
  // target's own side. Self-inflicted damage (fromEnemy===false, e.g.
  // GD06-055 damaging itself) never qualifies — the card text is scoped to
  // "enemy Units" only.
  if (fromEnemy && finalAmount > 0 && currentEffectSourceCard) {
    const targetOwnerSide = state.playerField.some(c => c && c.uid === target.uid) ? 'player' : 'opp';
    const dealingSide = targetOwnerSide === 'player' ? 'opp' : 'player';
    const outgoingBonus = getGlobalOutgoingDamageBonus(currentEffectSourceCard, dealingSide);
    if (outgoingBonus > 0) {
      finalAmount += outgoingBonus;
      addLog(`${currentEffectSourceCard.name} — damage increased by ${outgoingBonus}.`);
    }
  }
  // Unit-granted conditional FULL immunity to enemy effect damage (e.g.
  // ST11-002 Acguy: "During your opponent's turn, while this Unit is
  // rested, friendly (Marine) Units with 2 or less HP can't receive enemy
  // effect damage.") — unlike effectDamageReduction/baseEffectDamageBlock
  // below (partial/threshold-on-the-damage-amount reductions), this is an
  // absolute block gated on the TARGET's own current HP, and the granting
  // Unit doesn't have to be the target itself, so every Unit on the
  // target's own side is checked. Resolved before the reduction hooks so
  // the log doesn't show a partial "reduced by N" immediately followed by
  // a contradictory full-immunity line.
  // Hook signature: passiveUnitGrantsEffectDamageImmunity(grantingCard, targetUnit) → bool
  if (fromEnemy && finalAmount > 0) {
    const ownerSide = state.playerField.some(c => c && c.uid === target.uid) ? 'player' : 'opp';
    const ownerField = ownerSide === 'player' ? state.playerField : state.oppField;
    const immuneGranter = ownerField.find(c => c && window.CardEffects?.[c.cardNo]?.passiveUnitGrantsEffectDamageImmunity?.(c, target));
    if (immuneGranter) {
      addLog(`${immuneGranter.name} — ${target.name} is immune to effect damage.`);
      finalAmount = 0;
    }
  }
  if (fromEnemy && finalAmount > 0 && target.pilot && isLinkUnit(target)) {
    const handlers = window.CardEffects?.[target.pilot.cardNo];
    const reduction = handlers?.effectDamageReduction?.(target.pilot, target) || 0;
    if (reduction > 0) {
      finalAmount = Math.max(0, finalAmount - reduction);
      const blocked = amount - finalAmount;
      addLog(`${target.pilot.name} — reduced effect damage by ${blocked}${finalAmount === 0 ? ' (fully blocked)' : ''}.`);
    }
  }
  // Unit-card-level effect damage reduction (e.g. GD04-068 Silver Bullet).
  // Applied after pilot reduction; both can stack.
  if (fromEnemy && finalAmount > 0) {
    const unitHandlers = window.CardEffects?.[target.cardNo];
    const unitReduction = unitHandlers?.effectDamageReduction?.(target) || 0;
    if (unitReduction > 0) {
      const before = finalAmount;
      finalAmount = Math.max(0, finalAmount - unitReduction);
      const blocked = before - finalAmount;
      addLog(`${target.name} — reduced effect damage by ${blocked}${finalAmount === 0 ? ' (fully blocked)' : ''}.`);
    }
  }
  // Field-wide pilot passive (e.g. GD05-084 Odelo Henrik: reduces enemy effect
  // damage to friendly (League Militaire) Unit tokens by 1, regardless of
  // which unit he's actually paired with). Checked across every friendly
  // paired pilot on the target's own side, not just target's own pilot —
  // distinct from the target-scoped effectDamageReduction hook above.
  // Hook signature: passiveFieldTokenDamageReduction(pilotCard, targetToken) → number.
  if (fromEnemy && finalAmount > 0) {
    const ownerSide = state.playerField.some(c => c && c.uid === target.uid) ? 'player' : 'opp';
    const ownerField = ownerSide === 'player' ? state.playerField : state.oppField;
    ownerField.forEach(c => {
      if (finalAmount <= 0 || !c || !c.pilot) return;
      const pilotHandlers = window.CardEffects?.[c.pilot.cardNo];
      const fieldReduction = pilotHandlers?.passiveFieldTokenDamageReduction?.(c.pilot, target) || 0;
      if (fieldReduction > 0) {
        const before = finalAmount;
        finalAmount = Math.max(0, finalAmount - fieldReduction);
        const blocked = before - finalAmount;
        addLog(`${c.pilot.name} — reduced effect damage to ${target.name} by ${blocked}${finalAmount === 0 ? ' (fully blocked)' : ''}.`);
      }
    });
  }
  // Base-level threshold damage block (e.g. GD05-123 Archangel: friendly
  // (Orb) Units can't receive 2-or-less enemy effect damage during the
  // opponent's turn — a full block below a threshold, not a flat reduction,
  // so this hook receives the live `amount` unlike effectDamageReduction.
  // Hook signature: baseEffectDamageBlock(baseCard, targetUnit, amount) → number
  // (how many of those points to block).
  if (fromEnemy && finalAmount > 0) {
    const ownerSide = state.playerField.some(c => c && c.uid === target.uid) ? 'player' : 'opp';
    const ownerBase = ownerSide === 'player' ? state.playerBase : state.oppBase;
    if (ownerBase) {
      const baseHandlers = window.CardEffects?.[ownerBase.cardNo];
      const baseBlock = baseHandlers?.baseEffectDamageBlock?.(ownerBase, target, finalAmount) || 0;
      if (baseBlock > 0) {
        const before = finalAmount;
        finalAmount = Math.max(0, finalAmount - baseBlock);
        const blocked = before - finalAmount;
        addLog(`${ownerBase.name} — blocked ${blocked} effect damage to ${target.name}${finalAmount === 0 ? ' (fully blocked)' : ''}.`);
      }
    }
  }
  // tempIncomingDamageReduction: a per-instance "reduce all damage this unit
  // receives by N until end of turn" grant (e.g. GD05-018 Gundam Calibarn,
  // GD05-125 Ra Cailum) — unlike nextDamageReduction this is NOT consumed
  // after one hit; it applies to every instance of damage for the rest of
  // the turn. Checked directly on the card instance, not via a registered
  // hook, since it's granted dynamically by another card's effect rather
  // than being an innate property of this card's own cardNo.
  if (fromEnemy && finalAmount > 0 && (target.tempIncomingDamageReduction || 0) > 0) {
    const before = finalAmount;
    finalAmount = Math.max(0, finalAmount - target.tempIncomingDamageReduction);
    const blocked = before - finalAmount;
    addLog(`${target.name} — incoming damage reduced by ${blocked}${finalAmount === 0 ? ' (fully blocked)' : ''}.`);
  }
  // battleDefMod: normally a battle-combat-only reduction (captured+cleared
  // by runDamageStep), but also honored here for effect damage that lands
  // during that same still-open battle window (e.g. GD05-022 Gundam
  // Schwarzette: "During this battle, when this Unit receives enemy damage
  // [battle OR effect], reduce it by 2" — activated via [Activate･Action]
  // during that battle's own Action Step, before runDamageStep clears it).
  if (fromEnemy && finalAmount > 0 && (target.battleDefMod || 0) > 0) {
    const before = finalAmount;
    finalAmount = Math.max(0, finalAmount - target.battleDefMod);
    const blocked = before - finalAmount;
    addLog(`${target.name} — incoming damage reduced by ${blocked} this battle${finalAmount === 0 ? ' (fully blocked)' : ''}.`);
  }
  if (finalAmount <= 0) return;
  // nextDamageReduction: one-time incoming damage reduction (e.g. GD04-093 Rey Za Burrel).
  // Consumed here for effect damage; also consumed in runDamageStep for battle damage.
  if ((target.nextDamageReduction || 0) > 0) {
    const ndr = target.nextDamageReduction;
    finalAmount = Math.max(0, finalAmount - ndr);
    target.nextDamageReduction = 0;
    NET.send({ type: 'FIELD_AP_MOD', uids: [target.uid], delta: -ndr, field: 'nextDamageReduction' });
    addLog(`${target.name} — next damage reduced by ${ndr}${finalAmount === 0 ? ' (fully blocked)' : ''}.`);
    if (finalAmount <= 0) return;
  }
  // Unit-card passive immunity to enemy effect damage (e.g. GD02-064 Gundam Leopard Lv5)
  if (fromEnemy) {
    const immune = window.CardEffects?.[target.cardNo]?.effectDamageImmune?.(target);
    if (immune) {
      addLog(`${target.name} — immune to enemy effect damage.`);
      return;
    }
  }
  target.hp = (parseInt(target.hp) || 0) - finalAmount;
  target.damaged = true;
  NET.send({ type: 'FIELD_HP_MOD', uids: [target.uid], delta: -finalAmount });
  broadcastLog(`${target.name} takes ${finalAmount} effect damage${currentEffectSourceCard ? ` from ${currentEffectSourceCard.name}` : ''} (HP: ${effectiveHp(target)}).`);
  // Only the acting client ever calls dealDamageToUnit — the peer applies
  // the mirrored delta directly above in NET.onFieldHpMod without re-running
  // this function — so this can't double-fire the toast.
  if (currentEffectSourceCard) {
    broadcastCardToast(`${currentEffectSourceCard.name} dealt ${finalAmount} damage to ${target.name}.`);
  }
  // Hook: base reacts to a friendly unit taking effect damage (e.g. GD03-129 Hotarubi)
  if (state.myTurn && state.playerBase && !state.playerBase.rested) {
    if (state.playerField.some(c => c && c.uid === target.uid)) {
      window.CardEffects?.[state.playerBase.cardNo]?.onFriendlyUnitTakesEffectDamage?.(state.playerBase, target);
    }
  }
  // Hook: pilot reacts to their own unit taking effect damage (e.g. GD03-095 Azee Gurumin)
  if (target.pilot) {
    window.CardEffects?.[target.pilot.cardNo]?.onUnitTakesEffectDamage?.(target.pilot, target);
  }
  // Hook: unit itself reacts to taking effect damage; fromEnemy=true when source is opponent (e.g. GD02-010 Raider Gundam)
  window.CardEffects?.[target.cardNo]?.onSelfTakesEffectDamage?.(target, fromEnemy);
  // Hook: any other friendly unit watches when a friendly unit takes effect damage (e.g. GD03-053)
  if (state.myTurn) {
    const targetIsPlayerUnit = state.playerField.some(c => c && c.uid === target.uid);
    if (targetIsPlayerUnit) {
      state.playerField.forEach(watcher => {
        if (watcher && watcher.uid !== target.uid) {
          window.CardEffects?.[watcher.cardNo]?.onAnyFriendlyUnitTakesEffectDamage?.(watcher, target);
        }
      });
    }
  }

  if (effectiveHp(target) <= 0) {
    const _pIdx = state.playerField.findIndex(c => c && c.uid === target.uid);
    const _oIdx = state.oppField.findIndex(c => c && c.uid === target.uid);
    const _side = _pIdx !== -1 ? 'player' : 'opp';
    const _idx  = _pIdx !== -1 ? _pIdx : _oIdx;
    // Render with the card still on the field so the element exists for the animation.
    render();
    const _cardEl = vibrateCard(_side, target);
    // Null the slot immediately so any pending runDamageStep sees this unit
    // as destroyed and fizzles the attack — doDestroy handles the rest after
    // the animation delay.
    if (_idx !== -1) state[`${_side}Field`][_idx] = null;
    pendingEffectDestroys++;
    const doDestroy = () => {
      const _isToken = isUnitToken(target);
      if (!_isToken) {
        if (_side === 'player') updateTempState(target.cardNo, target.copyVer, 'trash');
        state[trashCountKey(_side)]++;
        state[trashArrayKey(_side)].push(toTrashCard(target));
      }
      broadcastLog(`${target.name} is destroyed by effect damage!${_isToken ? ' (token — removed from the game)' : ''}`, { destroyedCard: target });
      broadcastCardToast(`${target.name} was destroyed!`, target.img);
      // pilotToTrash is staged (not committed) until after the 'destroyed'
      // triggers below, so a handler like GD01-005 Unicorn Gundam (Unicorn
      // Mode)'s "[During Link][Destroyed] Return this Unit's paired Pilot to
      // its owner's hand" can rescue it via ctx.rescuePilot() before it's
      // sent to trash — same convention as the battle-destroy paths in
      // resolveAttack and DISCARD_CARD's destroy:true. Without this the
      // pilot got trashed unconditionally here, then ALSO pushed into hand
      // by the rescuing handler, duplicating it.
      let pilotToTrash = target.pilot || null;
      const rescuePilot = () => { pilotToTrash = null; };
      if (_side === 'player') {
        triggerCardEffect(target, 'destroyed', { ownerSide: 'player', rescuePilot });
        if (target.pilot) triggerCardEffect(target.pilot, 'destroyed', { ownerSide: 'player', unit: target, rescuePilot });
        notifyUnitDestroyedByEffect(target);
      } else {
        // _side === 'opp': an ENEMY Unit was just destroyed by OUR OWN effect
        // damage. triggerCardEffect is still called for the destroyed card
        // itself (ownerSide: 'opp') — CONFIRMED BUG: this was previously
        // skipped entirely here (only the narrow onLinkedUnitEffectDamageDestroysEnemy
        // watcher below ran), on the assumption that the enemy's own client
        // resolves its own [Destroyed] trigger in real networked play. True
        // for a real peer, but a bot match has no second client — this was
        // the one remaining combat/effect-destroy path state.isBotMatch's
        // redirect (triggerCardEffect, above) never got a chance to catch,
        // so a bot-owned Unit destroyed by the HUMAN's own effect damage
        // (e.g. Gundam Exia Repair killed by a direct-damage card during the
        // human's turn — units aren't valid normal attack targets without
        // Blocker, so this is the realistic way it actually happens) never
        // milled its 2 cards. Safe for real networked matches too: every
        // registered 'destroyed' handler already self-gates on
        // ctx.ownerSide === 'player' (same convention as every combat-destroy
        // call site), so this is a harmless no-op there — the real owner's
        // own client still resolves it independently, same as before.
        triggerCardEffect(target, 'destroyed', { ownerSide: 'opp', rescuePilot });
        if (target.pilot) triggerCardEffect(target.pilot, 'destroyed', { ownerSide: 'opp', unit: target, rescuePilot });
        // onLinkedUnitEffectDamageDestroysEnemy — the effect-damage
        // counterpart to onLinkedUnitDestroysEnemy (battle damage) — fires on
        // the current attacker's paired pilot ONLY while that specific Unit
        // is the attacker of a battle in progress (currentAttackCtx, set in
        // resolveAttack/cleared in runEpilogue), for card text like "while
        // this Unit is attacking" (e.g. ST13-012 Suletta Mercury: "[Once per
        // Turn] When an enemy Unit is destroyed with effect damage while
        // this Unit is attacking, draw 1"). Not fired for every friendly
        // effect-damage kill in general — only this one narrow, currently-
        // needed shape; broaden if a future card needs a wider watcher.
        if (state.attackInProgress && currentAttackCtx?.attackerSide === 'player') {
          const attackingUnit = currentAttackCtx.attacker;
          const pilotHandlers = attackingUnit?.pilot && window.CardEffects?.[attackingUnit.pilot.cardNo];
          pilotHandlers?.onLinkedUnitEffectDamageDestroysEnemy?.(attackingUnit.pilot, attackingUnit, target);
        }
      }
      if (pilotToTrash) {
        if (_side === 'player') updateTempState(pilotToTrash.cardNo, pilotToTrash.copyVer, 'trash');
        state[trashCountKey(_side)]++;
        state[trashArrayKey(_side)].push(toTrashCard(pilotToTrash));
      }
      broadcastCounters();
      render();
      setupDropZones();
      pendingEffectDestroys--;
    };
    if (_cardEl) {
      setTimeout(doDestroy, 500);
    } else {
      doDestroy();
    }
    return;
  }

  render();
  const _eSide = state.playerField.some(c => c && c.uid === target.uid) ? 'player' : 'opp';
  vibrateCard(_eSide, target);
}

// Resolves which side owns a card, for log-attribution purposes (see
// currentLogActor above) — checks uid membership across every zone that can
// hold a real card object on either side, including a paired pilot nested
// under its unit. Returns 'player' | 'opp' | null (not found — e.g. a token
// already removed from the game, or mid-transition between zones).
function cardOwnerSide(card) {
  if (!card || !card.uid) return null;
  const mine = [
    ...state.playerField, state.playerBase, ...state.hand,
    ...state.playerTrash, ...state.playerExile,
  ].filter(Boolean);
  const mineWithPilots = mine.concat(mine.map(c => c.pilot).filter(Boolean));
  if (mineWithPilots.some(c => c.uid === card.uid)) return 'player';

  const theirs = [
    ...state.oppField, state.oppBase, ...state.oppTrash, ...state.oppExile,
    ...(state.oppHand || []),
  ].filter(Boolean);
  const theirsWithPilots = theirs.concat(theirs.map(c => c.pilot).filter(Boolean));
  if (theirsWithPilots.some(c => c.uid === card.uid)) return 'opp';

  return null;
}

// True when `card` is the bot's own card in an active bot match — used to
// intercept the generic picker-modal UI (showCardPickerModal/Multi) so a
// bot-owned effect's choice is resolved by js/bot_logic/bot-engine.js
// instead of being shown to the human. See cardOwnerSide() above (already
// side-agnostic, so no per-card ownership tag is needed).
function isBotControlledCard(card) {
  return state.isBotMatch && !!card && cardOwnerSide(card) === 'opp';
}

// trigger name → the bracket keyword shown in the effect-activated toast
// (see triggerCardEffect). Only triggers where the JS trigger name maps
// 1:1 onto a single, unconditional card-text keyword are listed here — each
// of these firing IS the real-world event happening (a deploy/destroy/
// attack/activation genuinely occurred), so it's safe to toast purely off
// the trigger name.
//
// 'pair' is deliberately excluded: it fires for ANY pairing, but a card's
// own text might be "[When Paired]" (unconditional — should toast), "[When
// Linked]"/"[During Link]" (only if isLinkUnit(card) — often not yet true
// right after pairing), or "[During Pair]" (a continuous passive, not a
// one-time trigger at all) — all four share this one JS trigger name, so
// there's no way to tell from here which the card actually means. Toasting
// unconditionally previously showed "CARD_NAME [When Paired] effect
// activated" even for [When Linked] cards whose own isLinkUnit(card) guard
// silently no-opped inside the handler — i.e. the trigger fired but the
// card's actual effect never resolved. Triggers with no natural
// bracket-keyword at all (passiveKeywords, endOfTurn, the onAny* watcher
// hooks, etc.) are likewise omitted.
const TRIGGER_KEYWORD_MAP = {
  deploy: 'Deploy',
  destroyed: 'Destroyed',
  attack: 'Attack',
  main: 'Main',
  action: 'Action',
};

// Looks up and invokes a registered per-card effect handler (see
// js/effects/registry.js), if one exists for this card+trigger. No-op for
// every card that doesn't have one registered — its effect text is still
// shown via cleanEffect() for manual adjudication, same as today. Returns
// true if a handler was found and invoked, false otherwise — callers use
// this to know whether they should fall back to default post-trigger
// behavior (e.g. 'main' effects that need to defer maybeAutoEndMainPhase()
// until their own async flow finishes).
function triggerCardEffect(card, trigger, ctx) {
  // Bot-owned card, 'destroyed' trigger: unlike deploy/pair/main/action/
  // activate (which bot-engine.js never routes through triggerCardEffect at
  // all — see that file's header comment), 'destroyed' genuinely fires here
  // for EITHER side's cards as plain combat resolution (runDamageStep and
  // friends call this unconditionally, no attackerSide/defenderSide gate).
  // But the registered HANDLER BODY itself often isn't side-agnostic even
  // though the call site is — e.g. GD05-050 Gundam Exia Repair's own
  // `destroyed` handler (js/effects/purple.js) explicitly early-returns
  // unless ctx.ownerSide === 'player', because its effect (mill 2 cards)
  // touches state.tempDeck/playerTrash directly, which only ever holds the
  // LOCAL human's real data — it was never meant to run for a mirrored
  // opponent card at all (in real P2P play, the actual owner's own client
  // resolves it and syncs the result). Redirect to the deck's own
  // onDestroyed hook (bot_logic) instead of the real registry here, same
  // "no handler = no effect" convention as every other bot interception
  // point, rather than risk silently no-op'ing (or worse, mutating the
  // wrong side's deck) by calling the real handler.
  //
  // Ownership check here deliberately does NOT use isBotControlledCard()/
  // cardOwnerSide(card) alone — CONFIRMED BUG, caught by driving an actual
  // combat kill rather than trusting the isolated hook: every combat
  // destroy path (runDamageStep) nulls the card's field slot BEFORE calling
  // this trigger and only pushes it into trash AFTER, so at the moment this
  // runs the card is discoverable in NEITHER state.oppField NOR
  // state.oppTrash yet — cardOwnerSide(card) returns null, so the bot-owned
  // check silently failed 100% of the time for every genuine combat kill
  // (deploy/pair/etc. never hit this, since those never involve a card
  // already stripped from its own zone). Every combat-related 'destroyed'
  // call site already passes ctx.ownerSide explicitly (the side was known
  // before the card was removed) — that's what's actually reliable here.
  // Falls back to cardOwnerSide(card) only for a 'destroyed' call site that
  // doesn't pass ownerSide (e.g. a Base's destroy, game.js's
  // dealDamageToBase) where the card may still be in its zone at call time.
  if (state.isBotMatch && trigger === 'destroyed' && (ctx?.ownerSide || cardOwnerSide(card)) === 'opp') {
    const hook = window.BotDecks?.[state.botDeckId]?.onDestroyed?.[card.cardNo];
    if (hook) { hook(card, ctx); return true; }
    return false;
  }
  // state.specialMoveCommandActivatedThisTurn: single choke point for "has a
  // (Special Move) Command card's [Main]/[Action] been activated this turn"
  // (e.g. GD05-089 Master Asia). Every Main/Action activation path (the hand
  // Main-activate modal, resolveActionPlay, and a paired pilot's Main
  // triggered mid-attack — see red.js's gd0XXXActivatePairedMain) funnels
  // through this one function, so hooking here covers all of them without
  // needing to instrument each call site individually. Always represents the
  // LOCAL client's own activation (main/action only ever fire for the
  // player's own hand/field cards), so no side-tracking is needed.
  if ((trigger === 'main' || trigger === 'action') && card && (card.trait || '').includes('(Special Move)')) {
    if (!state.specialMoveCommandActivatedThisTurn) {
      state.specialMoveCommandActivatedThisTurn = true;
      // Mirrors the flag to the peer so a live "gains <Suppression> while
      // active this turn" passive keyword check (e.g. GD05-068 Shining
      // Gundam (Super Mode)) reads correctly on BOTH clients, not just the
      // activating player's own — unlike Master Asia's [Attack] hook (which
      // only ever evaluates on the activating player's own client), a
      // passiveKeywords hook is checked for a card on EITHER side's field.
      if (state.networked) NET.send({ type: 'SPECIAL_MOVE_COMMAND_ACTIVATED' });
    }
    // Notifies every OTHER friendly field unit every time a (Special Move)
    // Command's Main/Action activates this turn — not just the first (that's
    // what the one-shot flag above is for) — so a reactive "when you
    // activate a (Special Move) Command's [Main]/[Action]" watcher (e.g.
    // GD06-071 Shining Gundam (Meikyo Shisui)) fires on every activation.
    // Each watcher enforces its own once-per-turn gate independently via
    // passiveUsedThisTurn, same convention as onAnyFriendlyUnitDestroysEnemy.
    state.playerField.forEach(watcher => {
      if (!watcher || watcher.uid === card.uid) return;
      window.CardEffects?.[watcher.cardNo]?.onFriendlySpecialMoveCommandActivated?.(watcher, card);
    });
  }
  const handlers = card && window.CardEffects && window.CardEffects[card.cardNo];
  const handler = handlers && handlers[trigger];
  // Temporarily granted triggered abilities (e.g. GD05-104 At the Risk of
  // One's Life: "[During Link][Destroyed] ..." granted to a chosen unit
  // until end of turn — see registerTempGrantedAbility()). Independent of
  // the card's own registered handler; both can fire for the same trigger.
  const grants = card && Array.isArray(card.tempGrantedAbilities)
    ? card.tempGrantedAbilities.filter(g => g.trigger === trigger && (!g.requiresLink || isLinkUnit(card)))
    : [];
  if (!handler && grants.length === 0) return false;
  const prevActor = currentLogActor;
  const side = cardOwnerSide(card);
  if (side) currentLogActor = side;
  // Effect-activated toast (see showEffectActivatedToast) — fires for the
  // small set of triggers with an unambiguous bracket-keyword label. Gated
  // to side === 'player': this same trigger call runs independently on BOTH
  // clients' mirrored simulation (see the Gundam Pharact fix elsewhere in
  // this file for the full rationale), so only broadcasting when the LOCAL
  // client owns the card avoids a doubled toast — the opponent's own client
  // fires (and broadcasts) it when the same card resolves as 'player' from
  // their side.
  if (side === 'player' && TRIGGER_KEYWORD_MAP[trigger]) {
    broadcastEffectActivatedToast(card.name, TRIGGER_KEYWORD_MAP[trigger]);
    // Log line companion to the toast above — unlike the toast (muted for
    // the owner, see broadcastEffectActivatedToast), this one shows on BOTH
    // players' logs: an effect activating is public knowledge, and this is
    // the only general-purpose record that it happened at all for the many
    // triggers whose handler doesn't itself addLog() anything (e.g. a
    // [Deploy] that does nothing visible until its own later effect fires).
    broadcastLog(`${card.name} ${effectKeywordBadge(TRIGGER_KEYWORD_MAP[trigger])} effect activated.`);
  }
  // See currentEffectSourceCard's declaration — covers handlers that mutate
  // state synchronously (no picker modal involved); showCardPickerModal/
  // showCardPickerModalMulti re-establish this themselves for the async
  // onChoice case, since this synchronous scope will already have unwound
  // by the time a picker's callback actually fires.
  const prevEffectSource = currentEffectSourceCard;
  currentEffectSourceCard = card;
  try {
    if (handler) handler(card, ctx);
    grants.forEach(g => g.handler(card, ctx));
  } finally {
    currentLogActor = prevActor;
    currentEffectSourceCard = prevEffectSource;
  }
  return true;
}

// Grants `card` a triggered ability (matching `trigger`, e.g. 'destroyed')
// that lasts until CLEAR_TURN_EFFECTS/UNREST_ALL clears tempGrantedAbilities
// — used for effects like GD05-104 At the Risk of One's Life that hand a
// chosen unit a temporary [During Link][Destroyed]-style ability. Fires
// alongside (not instead of) the card's own permanently registered handler
// for the same trigger, from triggerCardEffect(). `requiresLink: true` gates
// the ability on isLinkUnit(card) at the moment the trigger actually fires
// (not at grant time), so a unit with no link requirement at all can never
// satisfy it, and one that isn't yet linked when granted still can later.
function registerTempGrantedAbility(card, { trigger, requiresLink = false, handler }) {
  if (!card) return;
  card.tempGrantedAbilities = card.tempGrantedAbilities || [];
  card.tempGrantedAbilities.push({ trigger, requiresLink, handler });
}

// A registered card's optional canActivate(card, ctx) predicate reports
// whether its effect could actually do anything right now (its "If
// [condition]..." gate is met, or at least one legal target/candidate
// exists) — see js/effects/registry.js. Cards with no registered predicate
// (the large majority, whose effects always have something to do) default
// to true so existing behavior is unchanged. Used to keep a card from being
// offered as choosable/activatable at all in the Action Step picker and the
// Main Phase Command-drop flow when its precondition can't be met, instead
// of letting it be selected and then silently no-op.
function cardEffectCanActivate(card, ctx) {
  const handlers = card && window.CardEffects && window.CardEffects[card.cardNo];
  const predicate = handlers && handlers.canActivate;
  if (!predicate) return true;
  // A predicate throwing must never propagate — every call site here is
  // either synchronous UI gating (canPlayCard/canActivateBase: a thrown
  // error would just break hand/Base rendering) or, worse, the Action Step
  // eligibility filter (getActionEligibleHandCards), where an uncaught
  // throw aborts the function before its caller's "no eligible cards" branch
  // can send the auto-skip ATTACK_ACTION network message — permanently
  // hanging the *other* player's "Waiting on opponent's Action Step…"
  // overlay with no way to recover short of reloading. Treat a buggy
  // predicate as "can't activate" and log it instead.
  try {
    return !!predicate(card, ctx);
  } catch (err) {
    console.error(`[cardEffectCanActivate] ${card.cardNo} canActivate threw — treating as not activatable`, err);
    return false;
  }
}

// Same defensive wrapping as cardEffectCanActivate above, for the sibling
// canAction(card, ctx) hook used by Units/Bases with an [Activate･Action]
// (see getActionEligibleUnits/getActionEligibleBase below). A throw here
// would otherwise abort the Action Step eligibility scan before
// runActionStep's "no eligible sources" branch can send its auto-skip
// network message, permanently hanging the *other* player's "Waiting on
// opponent's Action Step…" overlay — the exact failure class this guards
// against for canActivate, just previously left unguarded for canAction.
function cardEffectCanAction(handlers, card, ctx) {
  const predicate = handlers && handlers.canAction;
  if (!predicate) return true;
  try {
    return !!predicate(card, ctx);
  } catch (err) {
    console.error(`[cardEffectCanAction] ${card.cardNo} canAction threw — treating as not eligible`, err);
    return false;
  }
}

// True when a Unit's paired pilot satisfies its Link Req. — either a
// specific pilot by name (link reads "Amuro Ray", from DB "[Amuro Ray]")
// or, when the link reads e.g. "(Zeon) Trait" (from DB "(Zeon) Trait"),
// any pilot whose own `trait` field contains that bracketed trait. A link
// may list multiple alternatives separated by "/" (e.g. DB
// "[Allelujah Haptism] / [Hallelujah Haptism]") — any one matching is enough.
// For Command Pilot cards the card's own name is the card name, not the pilot
// identity — the DB's `pilot` key holds the actual pilot name (e.g. "Carta
// Issue" on "Persistent and Fortitudinous") and is stored as card.pilotName.
// Name matching is "contains", not exact-equal: a Link Req of "Garrod Ran"
// must also match a pilot card named "Garrod Ran & Tiffa Adill", and a Link
// Req of "Elan Ceres" must match "Elan Ceres (Enhanced Person Number 4)" —
// as long as the pilot's name/alias contains the required name, it counts.
function isLinkUnit(card) {
  if (!card || !card.pilot || !card.link) return false;
  const alternatives = card.link.split('/').map(s => s.replace(/[[\]]/g, '').trim());
  return alternatives.some(alt => {
    if (!alt) return false;
    if (alt.includes('Trait')) {
      const m = alt.match(/\(([^)]+)\)/);
      return !!m && ((card.pilot.trait || '').includes(`(${m[1]})`));
    }
    return (card.pilot.name || '').includes(alt) ||
           (card.pilot.pilotName || '').includes(alt) ||
           (card.pilot.pilotAliases || []).some(a => a.includes(alt));
  });
}

// A rested Linked pilot can grant blanket base battle-damage immunity too
// (e.g. GD06-098 Duo Maxwell: "[During Link] While this Unit is rested,
// friendly Bases can't receive damage from enemy Units whose Lv. are equal
// to or lower than this Unit."), unlike isImmuneToBattleDamageFrom (checked
// at both call sites below) which only ever reads the Base's OWN registered
// handler — a pilot's grant has to be found by scanning every field unit's
// paired pilot instead. Hook signature:
// pilotBaseBattleImmunityFrom(pilot, unit, attacker) → bool.
function hasPilotBaseBattleImmunity(defenderSide, attacker) {
  return state[`${defenderSide}Field`].some(c => {
    if (!c || !c.pilot) return false;
    return !!window.CardEffects?.[c.pilot.cardNo]?.pilotBaseBattleImmunityFrom?.(c.pilot, c, attacker);
  });
}

// Unit tokens (e.g. ST04-012's deployed Strike Gundam tokens) are temporary —
// not part of any deck, so they never occupy the trash or hand. Wherever a
// Unit normally would be trashed/returned to hand, a token is instead simply
// removed from the game (just taken off the field, nothing pushed anywhere
// else). A paired Pilot is a real deck card and is unaffected by this — it
// always follows its own normal destination (trash on destroy/bounce, same
// as today) regardless of whether the Unit it was paired with was a token.
function isUnitToken(card) {
  return !!card && card.type === 'unit-token';
}

// Returns the Breach value from a card (or its paired pilot) — checks
// tempKeywords first so "gains <Breach N> during this turn" grants are
// honoured in the damage step before UNREST_ALL clears them next turn.
// Cards with a conditional Breach (e.g. "while AP ≥ N") register a
// conditionalBreachValue(card) hook that overrides the text-based parse.
function getBreachValue(card) {
  if (!card) return 0;
  const tempMatch = (card.tempKeywords || []).join(' ').match(/Breach (\d+)/);
  if (tempMatch) return parseInt(tempMatch[1]);
  const condBreach = window.CardEffects?.[card.cardNo]?.conditionalBreachValue?.(card);
  if (condBreach !== undefined) return condBreach;
  // Pilot-level conditional breach (e.g. GD03-088 Asemu Asuno: only for AGE System units).
  // Takes precedence over text-based detection so conditional pilots can return 0 to suppress it.
  if (card.pilot) {
    const pilotH = window.CardEffects?.[card.pilot.cardNo];
    if (pilotH && typeof pilotH.pilotConditionalBreachValue !== 'undefined') {
      return pilotH.pilotConditionalBreachValue(card.pilot, card);
    }
  }
  const sources = [card?.effect, card?.pilot?.effect].filter(Boolean);
  for (const e of sources) {
    const m = e.match(/Breach (\d+)/);
    if (m) return parseInt(m[1]);
  }
  // Global token Breach bonus (e.g. GD04-022 Kikeroga MS Mode — all tokens gain Breach 1)
  // Not a field Unit — see getPilotPassiveApBonus's matching guard above for
  // why this must return early instead of defaulting to 'opp'.
  if (isUnitToken(card) && (state.playerField.some(c => c && c.uid === card.uid) || state.oppField.some(c => c && c.uid === card.uid))) {
    const tokenOwnerSide = state.playerField.some(c => c && c.uid === card.uid) ? 'player' : 'opp';
    for (const fc of state[`${tokenOwnerSide}Field`]) {
      if (!fc) continue;
      const tBonus = window.CardEffects?.[fc.cardNo]?.passiveGlobalTokenBreach?.(fc) || 0;
      if (tBonus > 0) return tBonus;
    }
  }
  return 0;
}

// Returns a cost descriptor for a base's [Activate･Main] ability, or null if
// it has no such ability at all. Only a circled RES number (①②③ — cleanEffect
// converts these to <span class="effect-circle-num">N</span>) counts as a
// formal "cost" to spend; "Rest this Base:" / "Exile 1 Command card from your
// trash:" are alternative cost-payment methods recognized by their own
// literal substrings, not a RES amount (per ruling: only circled-RES-number
// cards are considered to have a spendable cost).
function getBaseActivationCost(card) {
  if (!card || !(card.effectRaw || '').includes('[Activate･Main]')) return null;
  const match = (card.effect || '').match(/<span class="effect-circle-num">(\d+)<\/span>/);
  return {
    res: match ? parseInt(match[1]) : null,
    restBase: (card.effectRaw || '').includes('Rest this Base'),
    exileCommand: (card.effectRaw || '').includes('Exile 1 Command card from your trash'),
    oncePerTurn: (card.effectRaw || '').includes('[Once per Turn]'),
  };
}

// Returns true if the field unit's paired pilot has an [Activate･Main] effect
// that is currently usable (registered activate handler + canActivate passes).
function canActivatePilot(unit) {
  if (!unit?.pilot) return false;
  if (state.phase !== 'Main Phase' || !state.myTurn) return false;
  if (state.modalHidden) return false;
  // Only [Activate･Main] pilots may be activated during the Main Phase via the
  // icon badge. [Activate･Action] pilots are exclusively gated to the combat
  // Action Step picker (getActionEligiblePilots / resolvePilotActionActivate).
  if (!(unit.pilot.effectRaw || '').includes('[Activate･Main]')) return false;
  const handlers = window.CardEffects?.[unit.pilot.cardNo];
  if (!handlers?.activate) return false;
  return cardEffectCanActivate(unit.pilot, undefined);
}

// Parses the RES cost (circled-number span) and/or "Rest N of your Units"
// cost from a unit's [Activate･Main] or [Activate･Action] effect. Returns
// { res, restUnits, oncePerTurn, isMainPhase } or null. restUnits > 0 (e.g.
// GD05-001 V2 Gundam: "Rest 2 of your Units: Set this Unit as active.") is
// paid by choosing that many OTHER active field units before the effect
// resolves — see showUnitActivateModal's Activate handler.
function getUnitActivationCost(unit) {
  if (!unit) return null;
  const raw = unit.effectRaw || '';
  if (!raw.includes('[Activate･Main]') && !raw.includes('[Activate･Action]')) return null;
  const match = (unit.effect || '').match(/<span class="effect-circle-num">(\d+)<\/span>/);
  const restUnitsMatch = raw.match(/Rest (\d+) of your Units/);
  return {
    res: match ? parseInt(match[1]) : 0,
    restUnits: restUnitsMatch ? parseInt(restUnitsMatch[1]) : 0,
    oncePerTurn: raw.includes('[Once per Turn]'),
    isMainPhase: raw.includes('[Activate･Main]'),
  };
}

// Returns true if the field unit can activate its own [Activate･Main] effect.
function canActivateUnitEffect(unit) {
  if (!unit) return false;
  if (state.phase !== 'Main Phase' || !state.myTurn) return false;
  if (state.modalHidden) return false;
  const cost = getUnitActivationCost(unit);
  if (!cost || !cost.isMainPhase) return false;
  if (cost.oncePerTurn && unit.activateMainUsedThisTurn) return false;
  if (cost.res > 0 && (state.res + state.exRes) < cost.res) return false;
  if (cost.restUnits > 0 &&
      state.playerField.filter(c => c && c.uid !== unit.uid && !c.rested).length < cost.restUnits) return false;
  // Card-specific precondition (e.g. "at least one rested Blocker exists").
  // cardEffectCanActivate defaults to true when no canActivate hook is registered.
  if (!cardEffectCanActivate(unit)) return false;
  return true;
}

// A base without the native [Activate･Main] keyword has no activatable
// ability at all — it must never be treated as a playable action (for the
// base zone's clickability or for the auto-end-Main-Phase check), even if a
// stray circled-number happens to appear elsewhere in its effect text.
function canActivateBase() {
  const card = state.playerBase;
  if (!card) return false;
  if (state.phase !== 'Main Phase' || !state.myTurn) return false;
  const cost = getBaseActivationCost(card);
  if (!cost) return false;
  if (cost.oncePerTurn && card.baseAbilityUsedThisTurn) return false;
  // Resting is only a precondition when it's actually part of the printed
  // cost — a RES-only ability (e.g. Archangel) doesn't require an active
  // base, since nothing else in this engine ever rests a base.
  if (cost.restBase && card.rested) return false;
  if (cost.exileCommand) {
    const hasCommand = state.playerTrash.some(c => c.type === 'command' || c.type === 'command pilot');
    if (!hasCommand) return false;
  }
  if (cost.res !== null && (state.res + state.exRes) < cost.res) return false;
  if (!cardEffectCanActivate(card)) return false;
  return true;
}

registerAction('DRAW_CARD', () => {
  const cards = drawCardsFromTempDeck(1);
  if (cards.length > 0) {
    state.hand.push(cards[0]);
    addLog(`Drew: ${cards[0].name}`);
  } else {
    addLog(state.tempDeck ? 'Draw Phase — deck is empty.' : 'Draw Phase — no deck loaded.');
  }
  broadcastCounters();
});

// When a base is deployed: remove 1 shield and return it to hand (no deck reduction).
function applyBaseDeployEffect() {
  if (state.playerShield <= 0) {
    addLog('Base Deploy — no shields to return to hand.');
    return null;
  }
  state.playerShield = Math.max(0, state.playerShield - 1);

  // Randomly pick one of the player's shield cards
  let pickedShield = null;
  if (state.tempDeck) {
    const shields = state.tempDeck.filter(e => e.state === 'shield');
    if (shields.length > 0) pickedShield = shields[Math.floor(Math.random() * shields.length)];
  }

  if (state.hand.length >= 10) {
    addLog('Base Deploy — hand full, shield card discarded.');
    if (pickedShield) pickedShield.state = 'trash';
    return null;
  }

  if (pickedShield) {
    pickedShield.state = 'hand';
    const dbEntry = cardDb[pickedShield.cardNo];
    if (dbEntry) {
      const card = buildGameCardFromDb(dbEntry, pickedShield.copyVer);
      state.hand.push(card);
      addLog(`Base Deploy — shield card returned to hand: ${card.name}.`);
      return card.uid;
    }
  }
  return null;
}

function glowHandCard(uid) {
  if (!uid) return;
  requestAnimationFrame(() => {
    const el = document.querySelector(`#hand-area [data-uid="${uid}"]`);
    if (!el) return;
    el.classList.add('hand-add-glow');
    setTimeout(() => el.classList.remove('hand-add-glow'), 3000);
  });
}

// Stamps a unique runtime id (uid) onto a card copy so the drag system can
// identify individual instances even if the same card template appears twice.
function clone(card) {
  return { ...card, uid: Math.random().toString(36).slice(2) };
}

// ════════════════════════════════════════════════════════════════════════════
// CARD DATABASE
// Loaded from assets/cardDatabase.json at startup. Keyed by cardNo.
// cardNo matches the image filename without extension:
//   e.g. 'assets/images/blue/unit/ST01-001.webp' → cardNo 'ST01-001'
// ════════════════════════════════════════════════════════════════════════════
let cardDb = {};

// Fetches cardDatabase.json and indexes every entry by its cardNo.
async function loadCardDatabase() {
  try {
    const res = await fetch('assets/nuCardDatabase.json');
    const arr = await res.json();
    // Base printing wins over alt-art/promo variants (P1/P2/P3, ...) of the
    // same cardNo — those share identical stats/effect text but carry a
    // remote-hosted image instead of the local one, so indexing whichever
    // happened to appear first in the array could silently pick a variant
    // that breaks offline. Otherwise first-occurrence wins, same as before.
    arr.forEach(entry => {
      if (!entry.cardNo) return;
      const existing = cardDb[entry.cardNo];
      if (!existing || (existing.alt && !entry.alt)) cardDb[entry.cardNo] = entry;
    });
    console.log(`[loadCardDatabase] loaded ${arr.length} card entries`);
  } catch (err) {
    console.warn('[loadCardDatabase] failed to load card database:', err);
  }
}

// Returns a trash-ready copy of a card: pilot stripped, stats reset to DB defaults,
// damaged flag cleared — so hovering in the trash modal shows original values.
function toTrashCard(card) {
  // A Pilot card temporarily deployed to the field as a standalone Unit
  // (e.g. GD05-089 Master Asia's "deploy as an AP3/HP3 Unit instead") is
  // treated as a Pilot again the moment it leaves the field — rebuilt fresh
  // from cardDb (immune to the runtime type/bp/hp override used while it sat
  // on the field) rather than spread from the mutated field object below.
  // The hand-return paths (RETURN_TO_HAND, UNIT_BOUNCE, UNIT_TO_DECK_BOTTOM)
  // already rebuild fresh via buildGameCardFromDb(cardDb[card.cardNo], ...)
  // instead of spreading the field object, so they're unaffected by this
  // override and need no separate fix.
  if (card._deployedAsUnitFromPilot) {
    const pilotEntry = cardDb[card.cardNo];
    if (pilotEntry) return buildGameCardFromDb(pilotEntry, card.copyVer);
  }
  const entry = getCardDbEntry(card.img);
  const origHp = entry ? (parseStatValue(entry.hp) ?? card.hp) : card.hp;
  const origBp = entry ? (parseStatValue(entry.ap) ?? card.bp) : card.bp;
  return { ...card, pilot: null, hp: origHp, bp: origBp, damaged: false, rested: false, tempApMod: 0, battleApMod: 0 };
}

// Returns the DB record for a card given its image path, or null if not found.
function getCardDbEntry(imgPath) {
  if (!imgPath) return null;
  const cardNo = imgPath.split('/').pop().replace(/\.(png|webp|jpe?g)$/i, '');
  return cardDb[cardNo] || null;
}

// Formats effect text as an HTML string for innerHTML display.
// Pipeline:
//   1. <br> → temp newline  (consumed first so they don't match the keyword step)
//   2. pipe separators → comma
//   3. <Keyword> / <Repair 2> → white badge  (angle-bracket game abilities)
//   4. 【keyword】 → coloured badge  (action keywords)
//   5. newlines → <br> for HTML line-breaks
//   6. (parenthetical text) → <em>(text)</em>  (italics only inside parens)
function cleanEffect(html) {
  if (!html) return null;
  const s = html
    .replace(/\r/g, '')
    .replace(/&lt;/g, '<').replace(/&gt;/g, '>')                                     // decode HTML entities first
    .replace(/<br\s*\/?>/gi, '\n')                                                    // real <br> → temp newline
    .replace(/\s*\|\s*/g, ', ')                                                       // pipe separator → comma
    .replace(/<([A-Za-z][^>]*)>/g,
      '<span class="effect-ability">$1</span>')                                       // <Ability> → white badge (before [keyword] so spans aren't re-matched)
    .replace(/\[([^\]]*Development\s*\d*[^\]]*)\]/g,
      (_, kw) => `<span class="effect-keyword" data-kw="development">${kw}</span>`)     // [X･Development N] → own badge, before the trigger regex below so it isn't recoloured as X's trigger
    .replace(/\[(Burst|Once per Turn|When Paired[^\]]*|During Pair[^\]]*|During Link|When Linked|Pilot|Deploy|Activate[·･・･]Main|Activate Main|Activate[·･・･]Action|Main|Attack|Action|Destroyed)\]/g,
      (_, kw) => effectKeywordBadge(kw)) // known [keyword] → coloured badge
    .replace(/(This card's name is also treated as )\[([^\]]+)\]/g,
      // &#91;/&#93; (not literal brackets) so the generic strip below — which
      // also runs as a literal-bracket regex — doesn't immediately undo this
      // by re-matching and stripping the brackets right back out.
      '$1<strong>&#91;$2&#93;</strong>')
    .replace(/\[([^\]]+)\]/g, '$1')                                                  // strip brackets from non-keyword [card name] references
    .replace(/[①-⑳]/g,
      c => `<span class="effect-circle-num">${c.charCodeAt(0) - 0x245F}</span>`)     // ①②③ → white circle badge
    .replace(/\n/g, '<br>')                                                           // newlines → HTML line-breaks
    .replace(/\(([^)]+)\)/g, '<em>($1)</em>')                                         // (text) → italic
    .trim();
  return s || null;
}

// Parses a DB stat string. Returns the string unchanged if it's a modifier
// like '+1' or '-1'; otherwise parses to integer. Returns null for missing values.
function parseStatValue(val) {
  if (val == null) return null;
  const s = String(val).trim();
  if (s === '' || s === 'null') return null;
  if (/^[+\-]\d+$/.test(s)) return s; // modifier string — keep as-is
  const n = parseInt(s, 10);
  return isNaN(n) ? null : n;
}

// No-op — practice dummy units are now built directly from cardDb in initGame().
function enrichCardsFromDb() {}

// Shared by makeBaseCard() (looks entry up by image path) and
// makeBaseCardFromEntry() (already has the entry — used by the Card
// Appearance feature, see below) so both build the exact same shape.
function buildBaseCardFromEntry(entry, fallbackName, fallbackImg) {
  return {
    img:    entry?.image                             || fallbackImg,
    name:   entry?.name                              || fallbackName,
    bp:     (entry ? parseStatValue(entry.ap)        : null) ?? 0,
    hp:     (entry ? parseStatValue(entry.hp)       : null) ?? 0,
    level:  (entry ? parseStatValue(entry.level)    : null) ?? null,
    cost:   (entry ? parseStatValue(entry.cost)     : null) ?? null,
    trait:  entry?.trait                             || null,
    effect: entry ? cleanEffect(entry.effectData)   : null,
    type:   entry?.categoryData                      || 'BASE',
    color:  entry?.color ? entry.color.toLowerCase(): null,
    cardNo: entry?.cardNo                            || null,
  };
}

// Builds a base-card state object from the DB for a given image path.
// Used for the initial EXB base and any non-CARDS base lookups.
function makeBaseCard(imgPath, fallbackName = 'Base') {
  return buildBaseCardFromEntry(getCardDbEntry(imgPath), fallbackName, imgPath);
}

// Builds a base-card state object from an explicit cardNo instead of an
// image path — needed because looking a card up FROM its image path (as
// makeBaseCard() does) is lossy when multiple cardNos share one image file,
// which EXB-002 "Axis" (the alt-art EX Base skin, not the GD06-129 5th Luna
// Base Token of the same name) does: its DB entry's own `image` field points
// at EXB-001.webp, the standard EX Base's art. Round-tripping Axis's image
// path back through makeBaseCard() would silently resolve to the standard EX
// Base's stats (HP 3) instead of Axis's own (HP 5). Used by the Card
// Appearance feature (Edit Profile), where the exact cardNo is already known
// and shouldn't be thrown away.
function makeBaseCardFromEntry(entry, fallbackName = 'Base') {
  return buildBaseCardFromEntry(entry, fallbackName, `assets/images/resource/${entry?.cardNo || fallbackName}.webp`);
}

// ════════════════════════════════════════════════════════════════════════════
// PHASE BAR
// Renders the glassmorphic phase pill bar between the two field areas.
// ════════════════════════════════════════════════════════════════════════════
function renderPhaseBar() {
  const bar = document.getElementById('phase-bar');
  if (!bar) return;
  // Red accent while it's the opponent's turn — the clearest "not your move"
  // signal is right where the player's eyes already are (the phase pills).
  bar.classList.toggle('opponent-turn', !state.myTurn);
  bar.innerHTML = '';
  const container = document.createElement('div');
  container.className = 'phase-pill-container';
  state.phases.forEach((p, i) => {
    const pill = document.createElement('div');
    const isEndPhase = (p === 'End Phase');
    const endBlocked = isEndPhase && (!state.myTurn || state.modalHidden);
    pill.className = 'phase-pill'
      + (i === state.phaseIndex ? ' active' : '')
      + (isEndPhase ? ' end-phase' : '')
      + (endBlocked ? ' disabled' : '');
    pill.textContent = p.toUpperCase();
    if (isEndPhase && state.myTurn) {
      pill.title = state.modalHidden ? 'Waiting for opponent…' : 'End your turn';
      pill.addEventListener('click', () => {
        if (state.modalHidden) return;
        if (document.getElementById('ingame-menu-overlay') || document.getElementById('decision-waiting-overlay')) return;
        console.log('[renderPhaseBar] End Phase clicked');
        advanceNewTurn();
      });
    }
    container.appendChild(pill);
  });
  bar.appendChild(container);
}

// Builds the "current/original" markup for a RES or EX RES counter — the
// current spendable amount at normal size, a slash, then the turn's starting
// amount (snapshotted each Start Phase) in a smaller font.
function resFractionHtml(current, original) {
  return `${current}<span class="di-val-den">/${original}</span>`;
}

// ════════════════════════════════════════════════════════════════════════════
// RENDER
// The single function that syncs the entire DOM to the current state object.
// Call render() after any state mutation; never mutate DOM directly elsewhere.
// ════════════════════════════════════════════════════════════════════════════
function render() {
  // ── Top bar counters ──
  document.getElementById('turn-num').textContent = state.turn;
  document.getElementById('phase-name').textContent = state.phase;
  document.getElementById('opp-level').textContent        = state.oppLevel;
  document.getElementById('opp-shield-count').textContent  = state.oppShield;
  document.getElementById('opp-deck-count').textContent    = state.oppDeckCount;
  document.getElementById('opp-hand-count').textContent    = state.oppHandCount;
  document.getElementById('opp-res-count').innerHTML     = resFractionHtml(state.oppRes, state.oppResBase);
  { const oppExResTotal = state.oppExRes + state.oppExResRested;
    document.getElementById('opp-ex-res-count').innerHTML = state.oppExResRested > 0
      ? resFractionHtml(state.oppExRes, oppExResTotal) : String(state.oppExRes); }

  // ── Bottom bar counters ──
  state.playerLevel = state.resBase + state.exRes + state.exResRested;
  document.getElementById('player-shield-count').textContent = state.playerShield;
  document.getElementById('player-level').textContent = state.playerLevel;
  document.getElementById('deck-count-2').textContent  = state.deckCount;
  const trashEl    = document.getElementById('trash-count-2');
  const oppTrashEl = document.getElementById('opp-trash-count');
  trashEl.textContent    = state.trashCount;
  oppTrashEl.textContent = state.oppTrashCount;
  document.getElementById('exile-count').textContent     = state.playerExile.length;
  document.getElementById('opp-exile-count').textContent = state.oppExile.length;
  document.getElementById('res-count').innerHTML    = resFractionHtml(state.res, state.resBase);
  { const exResTotal = state.exRes + state.exResRested;
    document.getElementById('ex-res-count').innerHTML = state.exResRested > 0
      ? resFractionHtml(state.exRes, exResTotal) : String(state.exRes); }
  document.getElementById('hand-count-num').textContent = state.hand.length;
  renderResourceTray();

  // ── Phase bar ──
  renderPhaseBar();

  // ── Shield pips — rebuild both shield zones from their state values ──
  renderShields('zone-player-shield', state.playerShield);
  renderShields('zone-opp-shield', state.oppShield);

  // ── Opponent field: 6 slots (display-only) ──
  for (let i = 0; i < 6; i++) {
    renderSlot(`opp-slot-${i}`, state.oppField[i], true); // isOpp=true → not draggable
  }
  // ── Player field: 6 slots (front row 0-2, back row 3-5) ──
  for (let i = 0; i < 6; i++) {
    renderSlot(`player-slot-${i}`, state.playerField[i], false);
  }

  // ── Player base zone ──
  renderBase();

  // ── Opponent profile panel (bot info for now) ──
  renderBotProfilePanel();

  // ── Hand — rebuild the hand row from the state.hand array ──
  renderHand();

}

// Opponent profile panel, right sidebar below the opponent's Base zone.
// Bot-match-only for now (empties/hides otherwise) — the same panel slot is
// meant to show a real peer's profile for quick play/private games later,
// but that's not implemented yet, so a non-bot match just sees it empty.
// Just the face + name + a "View Profile" button here — the full
// name/deck/pills/playstyle/decklist-source breakdown lives in
// showBotProfileModal(), opened by that button.
function renderBotProfilePanel() {
  const panel = document.getElementById('bot-profile-panel');
  if (!panel) return;
  if (!state.isBotMatch) { panel.innerHTML = ''; panel.classList.remove('has-profile'); return; }
  const deck = window.BotDecks?.[state.botDeckId];
  if (!deck) { panel.innerHTML = ''; panel.classList.remove('has-profile'); return; }
  panel.classList.add('has-profile');
  panel.innerHTML = `
    <img class="bpp-face" src="${deck.faceIcon || ''}" alt="${deck.personaName || deck.name}">
    <div class="bpp-name">${deck.personaName || deck.name}</div>
    <button class="bpp-view-btn" id="bpp-view-profile-btn" type="button">View Profile</button>
  `;
  panel.querySelector('#bpp-view-profile-btn').addEventListener('click', () => showBotProfileModal(deck));
}

// Renders both base zones — shows the base card image if one is set,
// otherwise falls back to the "BASE" label.
function renderBase() {
  renderBaseZone('zone-player-base', state.playerBase);
  renderBaseZone('zone-opp-base',    state.oppBase);
}

function renderBaseZone(zoneId, baseCard) {
  const zone = document.getElementById(zoneId);
  if (!zone) return;

  zone.classList.remove('base-rested', 'has-base-card');

  if (baseCard) {
    if (zoneId === 'zone-opp-base') zone.classList.add('has-base-card');
    // Base cards have no printed AP stat in the physical game — only show HP.
    const _baseHp = parseInt(baseCard.hp) || 0;
    zone.innerHTML = `
      <img class="base-img" src="${baseCard.img}" alt="${baseCard.name}" />
      <div class="card-stats">
        <div class="stat${baseCard.damaged ? ' damaged' : ''}">${_baseHp}</div>
      </div>
    `;
    zone.onmouseenter = () => showCardPreview(baseCard, zone);
    zone.onmouseleave = hideCardPreview;

    // Applies to both zones — a rested base is visible to both players (it's
    // not hidden information), so the enemy player must see the same rested
    // state the owner does, not just the local player's own base.
    if (baseCard.rested) zone.classList.add('base-rested');

    if (zoneId === 'zone-player-base') {
      const canActivate = canActivateBase();

      zone.style.cursor = '';
      zone.onclick = null;
      // Activation button — same pulsing icon used on field Units
      // (.unit-activate-icon), instead of the whole zone glowing. Floats
      // above the zone's top edge (position: relative on .base-zone).
      if (canActivate) {
        const activateIcon = document.createElement('span');
        activateIcon.className = 'material-symbols-outlined unit-activate-icon';
        activateIcon.textContent = 'fmd_bad';
        activateIcon.title = 'Activate Base Effect';
        activateIcon.addEventListener('click', (e) => {
          e.stopPropagation();
          if (state.modalHidden) return;
          showBaseActivateModal(baseCard);
        });
        zone.appendChild(activateIcon);
      }
    } else if (zoneId === 'zone-opp-base') {
      zone.style.cursor = '';
      zone.onclick = null;
    }
  } else {
    zone.textContent = 'BASE';
    zone.style.cursor = '';
    zone.onclick = null;
    zone.onmouseenter = null;
    zone.onmouseleave = null;
  }
}

// Rebuilds a shield zone as a vertical stack of rotated card backs.
// Each card is a portrait image rotated 90° inside a landscape wrapper so it
// displays sideways (wider than tall). Cards fan downward — card 0 is on top
// (fully visible); each subsequent card peeks below the previous by OFFSET px.
// Called by render() for both player and opponent shield zones.
// Renders the R-001/EXR-001 token tray that peeks out from behind the
// bottom bar (see #res-token-tray in index.html) — rebuilt fresh on every
// render() call, same "clear and recreate" convention as renderShields().
// RES: renders state.resBase total tokens; the first (resBase - res) of
// them — i.e. however many have been spent this turn — show rotated
// (rested), left to right, so resting "fills in" starting from the left.
// EX RES: renders state.exRes active tokens followed by state.exResRested
// rested tokens, so spending an EX Resource removes from the left of the
// active group, and a newly-placed rested EX Resource appends at the right
// (see placeExResource()). Both counts naturally read as "0 rested" right
// after Start Phase, since RESET_RESOURCES resets state.res to state.resBase
// and folds exResRested back into exRes — no extra reset logic needed here.
// Builds one token as a small clipping wrapper (.res-token-wrap) containing
// the full card image absolutely positioned inside it (.res-token-img) — see
// the CSS comment above these classes for why rested tokens need the
// rotate-around-top-left trick instead of a plain center-pivot rotate().
// Settings-modal "Show Resource Cards" toggle — hides the R-001/EXR-001
// token images in #res-token-tray and #opp-res-token-tray while leaving the
// numeric RES/EX RES counters in #deck-info/#opp-deck-info untouched. Purely
// a local display preference (like the theme toggle) — not broadcast to the
// peer, so each player's own toggle governs what THEY see of both trays.
// Defaults to shown (true) when unset.
const SHOW_RESOURCE_CARDS_STORAGE_KEY = 'nu-show-resource-cards';

function getShowResourceCards() {
  try {
    const raw = localStorage.getItem(SHOW_RESOURCE_CARDS_STORAGE_KEY);
    return raw === null ? true : raw === 'true';
  } catch {
    return true;
  }
}

function setShowResourceCards(show) {
  try { localStorage.setItem(SHOW_RESOURCE_CARDS_STORAGE_KEY, String(show)); } catch {}
  applyResourceCardsVisibility();
}

function applyResourceCardsVisibility() {
  const hidden = !getShowResourceCards();
  const tray = document.getElementById('res-token-tray');
  if (tray) tray.classList.toggle('res-token-tray-hidden', hidden);
  const oppTray = document.getElementById('opp-res-token-tray');
  if (oppTray) oppTray.classList.toggle('res-token-tray-hidden', hidden);
}

function renderResourceCardsToggleHTML() {
  const checked = getShowResourceCards();
  return `
    <label class="switch-toggle">
      <input type="checkbox" class="switch-toggle-input" ${checked ? 'checked' : ''} />
      <span class="switch-toggle-track"><span class="switch-toggle-thumb"></span></span>
    </label>
  `;
}

function wireResourceCardsToggle(containerEl) {
  const input = containerEl.querySelector('.switch-toggle-input');
  if (!input) return;
  input.addEventListener('change', () => setShowResourceCards(input.checked));
}

// Settings-modal "Show Update Notifications" toggle — governs the main
// menu's #mm-update-toast (see startUpdateNoticeToast() near the top of this
// file). Enabled (shown) by default. Toggling it off while the main menu's
// toast is already mounted hides it immediately and stops its cycle
// interval; toggling it back on re-fetches and resumes it right away,
// rather than waiting for the next visit to the main menu — see
// setShowUpdateNotice() below, which just re-invokes
// startUpdateNoticeToast() either way since that function already gates on
// this same setting at its own entry point.
const SHOW_UPDATE_NOTICE_STORAGE_KEY = 'nu-show-update-notice';

function getShowUpdateNotice() {
  try {
    const raw = localStorage.getItem(SHOW_UPDATE_NOTICE_STORAGE_KEY);
    return raw === null ? true : raw === 'true';
  } catch {
    return true;
  }
}

function setShowUpdateNotice(show) {
  try { localStorage.setItem(SHOW_UPDATE_NOTICE_STORAGE_KEY, String(show)); } catch {}
  const toastEl = document.getElementById('mm-update-toast');
  if (toastEl) startUpdateNoticeToast(toastEl);
}

// Wrapped in its own #settings-show-update-notice-toggle span (same
// convention as #settings-auto-save-decks-toggle below) so wiring this
// toggle can't accidentally grab a different switch already in the same
// settings modal.
function renderShowUpdateNoticeToggleHTML() {
  const checked = getShowUpdateNotice();
  return `
    <span id="settings-show-update-notice-toggle">
      <label class="switch-toggle">
        <input type="checkbox" class="switch-toggle-input" ${checked ? 'checked' : ''} />
        <span class="switch-toggle-track"><span class="switch-toggle-thumb"></span></span>
      </label>
    </span>
  `;
}

function wireShowUpdateNoticeToggle(containerEl) {
  const input = containerEl.querySelector('#settings-show-update-notice-toggle .switch-toggle-input');
  if (!input) return;
  input.addEventListener('change', () => setShowUpdateNotice(input.checked));
}

// Settings-modal "Auto save decks in deck builder" toggle — when enabled
// (the default), leaving the deck builder with unsaved changes (via the
// Back button or Play A Game) automatically saves the deck instead of
// discarding the work — see closeBuilder() in showDeckBuilder(). Named
// decks are saved back under their own name; a deck that was never named
// at all is saved under AUTO_SAVE_DRAFT_NAME. When disabled, unsaved
// changes are never auto-saved and showDeckBuilder() shows a dismissible
// "this deck is not saved" warning instead (#db-unsaved-warning).
const AUTO_SAVE_DECKS_STORAGE_KEY = 'nu-auto-save-decks';

function getAutoSaveDecks() {
  try {
    const raw = localStorage.getItem(AUTO_SAVE_DECKS_STORAGE_KEY);
    return raw === null ? true : raw === 'true';
  } catch {
    return true;
  }
}

function setAutoSaveDecks(enabled) {
  try { localStorage.setItem(AUTO_SAVE_DECKS_STORAGE_KEY, String(enabled)); } catch {}
}

// Wrapped in its own #settings-auto-save-decks-toggle span (rather than the
// bare `.switch-toggle-input` selector wireResourceCardsToggle() uses) so
// wiring this toggle can't accidentally grab the Show Resource Cards
// checkbox instead now that the settings modal has more than one of these
// switches — see wireAutoSaveDecksToggle() below.
function renderAutoSaveDecksToggleHTML() {
  const checked = getAutoSaveDecks();
  return `
    <span id="settings-auto-save-decks-toggle">
      <label class="switch-toggle">
        <input type="checkbox" class="switch-toggle-input" ${checked ? 'checked' : ''} />
        <span class="switch-toggle-track"><span class="switch-toggle-thumb"></span></span>
      </label>
    </span>
  `;
}

function wireAutoSaveDecksToggle(containerEl) {
  const input = containerEl.querySelector('#settings-auto-save-decks-toggle .switch-toggle-input');
  if (!input) return;
  input.addEventListener('change', () => setAutoSaveDecks(input.checked));
}

function buildResToken(src, alt, rested) {
  const wrap = document.createElement('div');
  wrap.className = 'res-token-wrap' + (rested ? ' res-token-wrap-rested' : '');
  const img = document.createElement('img');
  img.src = src;
  img.alt = alt;
  img.className = 'res-token-img' + (rested ? ' res-token-img-rested' : '');
  wrap.appendChild(img);
  return wrap;
}

// Shared by both my own tray and the opponent's mirrored one below — takes
// the row elements plus a {resTotal, res, exRes, exResRested} counters shape
// (myCounters()'s fields, or the oppRes*/oppExRes* state built from the
// peer's broadcast) and fills the rows with tokens the same way for either.
// isOpp picks whose Card Appearance choice (see Deck Builder Settings) the
// art comes from: my own for my tray, the peer's (via the CARD_APPEARANCE network
// message — js/network.js) for their mirrored one, so each side's tray
// always reflects whoever actually owns those resources' own pick.
function fillResourceTrayRows(resRow, exResRow, resTotal, res, exRes, exResRested, isOpp) {
  const getEntry = isOpp ? getOppCardAppearanceEntry : getCardAppearanceEntry;
  const resEntry = getEntry('resource');
  const resImg = resEntry.image || `assets/images/resource/${CARD_APPEARANCE_DEFAULTS.resource}.webp`;
  const resName = resEntry.name || 'Resource';
  const exResEntry = getEntry('exResource');
  const exResImg = exResEntry.image || `assets/images/resource/${CARD_APPEARANCE_DEFAULTS.exResource}.webp`;
  const exResName = exResEntry.name || 'EX Resource';

  resTotal = Math.max(0, resTotal);
  const resRestedCount = Math.max(0, resTotal - res);
  resRow.innerHTML = '';
  for (let i = 0; i < resTotal; i++) {
    resRow.appendChild(buildResToken(resImg, resName, i < resRestedCount));
  }

  const exActive = Math.max(0, exRes);
  const exRested = Math.max(0, exResRested);
  exResRow.innerHTML = '';
  for (let i = 0; i < exActive + exRested; i++) {
    exResRow.appendChild(buildResToken(exResImg, exResName, i >= exActive));
  }
}

function renderResourceTray() {
  applyResourceCardsVisibility();

  const resRow = document.getElementById('res-token-row');
  const exResRow = document.getElementById('ex-res-token-row');
  if (resRow && exResRow) {
    fillResourceTrayRows(resRow, exResRow, state.resBase, state.res, state.exRes, state.exResRested, false);
  }

  // Opponent's tray mirrors state.oppRes/oppResBase/oppExRes/oppExResRested,
  // already kept in sync by applyOppCounters() whenever the peer broadcasts
  // — no extra network message needed for the counts. Its art comes from
  // state.oppCardAppearance (set by the CARD_APPEARANCE message) instead.
  const oppResRow = document.getElementById('opp-res-token-row');
  const oppExResRow = document.getElementById('opp-ex-res-token-row');
  if (oppResRow && oppExResRow) {
    fillResourceTrayRows(oppResRow, oppExResRow, state.oppResBase, state.oppRes, state.oppExRes, state.oppExResRested, true);
  }
}

function renderShields(zoneId, count) {
  const PW     = 100; // portrait card width  (becomes landscape height after rotation)
  const PH     = 140; // portrait card height (becomes landscape width  after rotation)
  const LW     = PH;  // landscape display width  = 140px
  const LH     = PW;  // landscape display height = 100px
  const OFFSET = 16;  // px each card peeks below the one above it

  const zone = document.getElementById(zoneId);
  zone.innerHTML = '<div class="stack-label">SHIELD</div>';

  if (zoneId === 'zone-opp-shield') {
    // Shield is only a valid attack target when the opponent has no base card.
    zone.classList.toggle('has-shield-cards', count > 0 && !state.oppBase);
  }

  if (count <= 0) {
    if (zoneId === 'zone-opp-shield') {
      const direct = document.createElement('div');
      direct.className = 'shield-direct-target';
      direct.innerHTML = '<span class="material-symbols-outlined">person</span>';
      direct.title = state.oppBase ? 'Destroy the opponent\'s base first!' : 'No shields — opponent is open to a direct attack!';
      zone.appendChild(direct);
    } else {
      const msg = document.createElement('div');
      msg.className = 'shield-empty';
      msg.textContent = '—';
      zone.appendChild(msg);
    }
    return;
  }

  const stackH = LH + (count - 1) * OFFSET;
  const stack  = document.createElement('div');
  stack.className    = 'shield-stack';
  stack.style.width  = LW + 'px';
  stack.style.height = stackH + 'px';

  for (let i = 0; i < count; i++) {
    // Wrapper sized to the landscape footprint; clips the rotated portrait image
    const wrap = document.createElement('div');
    wrap.className      = 'shield-card-wrap';
    wrap.style.position = 'absolute';
    wrap.style.width    = LW + 'px';
    wrap.style.height   = LH + 'px';
    wrap.style.top      = (i * OFFSET) + 'px';
    wrap.style.left     = '0px';
    wrap.style.zIndex   = count - i;

    // Portrait image rotated 90° clockwise around its top-left corner.
    // After rotation a PW×PH (72×101) element visually occupies PH×PW (101×72)
    // which exactly fills the LW×LH wrapper.
    const img = document.createElement('img');
    img.src                  = 'assets/images/back.webp';
    img.alt                  = 'Shield card (face-down)';
    img.style.position        = 'absolute';
    img.style.width           = PW + 'px';
    img.style.height          = PH + 'px';
    img.style.top             = '0px';
    img.style.left            = PH + 'px'; // offset so rotation lands inside wrapper
    img.style.transform       = 'rotate(90deg)';
    img.style.transformOrigin = 'top left';

    wrap.appendChild(img);
    stack.appendChild(wrap);
  }

  zone.appendChild(stack);

}

// Returns true if the card can't attack right now — either from the standard
// tempCantAttack turn-flag or from a card-specific cantAttack hook in the
// effect registry (e.g. GD03-081 which needs a qualifying deploy this turn).
function cardCantAttackNow(card) {
  if (!card) return false;
  if (card.rested) return true;
  // tempAttackDespiteSickness (e.g. GD05-030 Michaelis: may attack a rested
  // enemy Unit the turn it's deployed) overrides the deploy-sickness block
  // specifically — showAttackTargetPicker still restricts *which* targets
  // are offered via tempCantAttackNonUnit + the normal rested-only default.
  if (card.tempCantAttack && !card.tempAttackDespiteSickness) return true;
  return !!(window.CardEffects?.[card.cardNo]?.cantAttack?.(card));
}

// Returns the UID of the forced attack target on defenderSide's field, or null.
// A unit with forcedAttackTarget(card, ownerSide) → bool in its handler forces
// the attacker to declare against it instead of any other target.
// Only the first matching active forced target is returned.
// Runs on the ATTACKER's client: they see the defender's field as 'opp'.
// `attacker` (optional) is the unit declaring the attack — passed through to
// forcedAttackTarget/pilotForcedAttackTarget hooks so they can apply
// attacker-side exclusions (e.g. GD05-086 Kayra Su: "Enemy Units OTHER THAN
// Link Units" must choose the forced target).
function getForcedAttackTargetUid(defenderSide, attacker) {
  // State-level forced target from a command card (e.g. GD04-107 Destined Battle)
  if (state.forcedAttackTargetUid) {
    const forceTarget = state[`${defenderSide}Field`].find(c => c && c.uid === state.forcedAttackTargetUid);
    if (forceTarget) return forceTarget.uid;
  }
  for (const card of state[`${defenderSide}Field`]) {
    if (!card) continue;
    const handlers = window.CardEffects?.[card.cardNo];
    if (handlers?.forcedAttackTarget && handlers.forcedAttackTarget(card, defenderSide, attacker)) return card.uid;
    // Pilot-granted forced-target ability (e.g. GD05-086 Kayra Su's [During
    // Link] "enemy Units choose this rested Unit as their attack target"),
    // checked via the paired pilot's own handler rather than the unit's.
    const pilotHandlers = card.pilot && window.CardEffects?.[card.pilot.cardNo];
    if (pilotHandlers?.pilotForcedAttackTarget && pilotHandlers.pilotForcedAttackTarget(card, card.pilot, defenderSide, attacker)) return card.uid;
  }
  return null;
}

// Returns an array of valid forced-target candidates if any unit on defenderSide
// has a forcedAttackTargetPool(card, defenderSide) hook that returns non-empty results.
// When a pool is present the attacker may only choose among those units.
// Returns null if no pool hook fires.
function getForcedAttackTargetPool(defenderSide) {
  for (const card of state[`${defenderSide}Field`]) {
    if (!card) continue;
    const handlers = window.CardEffects?.[card.cardNo];
    if (!handlers?.forcedAttackTargetPool) continue;
    const pool = handlers.forcedAttackTargetPool(card, defenderSide);
    if (pool && pool.length > 0) return pool;
  }
  return null;
}

// Returns true if the card is restricted to attacking enemy Units only —
// it cannot declare attacks against the base, shield, or direct target.
// Hook signature: cantAttackNonUnit(card) → bool. Example: ST01-009 Zowort.
// Also reads card.tempCantAttackNonUnit for turn-scoped restrictions
// (e.g. GD02-069 Zeta Gundam after its activate effect fires).
function cardCantAttackNonUnit(card) {
  if (!card) return false;
  if (card.tempCantAttackNonUnit) return true;
  return !!(window.CardEffects?.[card.cardNo]?.cantAttackNonUnit?.(card));
}

// Returns true if the attacker may target active (non-rested) enemy units.
// By default units can only attack rested units; card effects hook in via
// canAttackActive(card) → bool in the effect registry.
function canAttackActiveUnits(card) {
  if (!card) return false;
  if ((card.tempKeywords || []).includes('CanAttackActiveAll')) return true;
  return !!(window.CardEffects?.[card.cardNo]?.canAttackActive?.(card));
}

// Renders a single field slot: empty placeholder, unit card, or unit + pilot.
// Pilot is appended first (behind in DOM stacking order) so the unit renders on top.
function renderSlot(slotId, cardData, isOpp) {
  const slot = document.getElementById(slotId);
  let slotClasses = 'field-slot drop-zone';
  if (isOpp) slotClasses += ' opp-slot';
  if (!cardData) {
    slot.className = slotClasses;
    slot.innerHTML = isOpp ? '' : '+';
    return;
  }
  if (cardData.pilot)      slotClasses += ' has-pilot';
  if (cardData.rested)     slotClasses += ' rested';
  if (!isOpp ? cardCantAttackNow(cardData) : cardData.tempCantAttack) slotClasses += ' cant-attack';
  slot.className = slotClasses;
  slot.innerHTML = '';
  if (cardData.pilot) slot.appendChild(buildPilotEl(cardData.pilot));
  const cardEl = buildCardEl(cardData, isOpp);
  slot.appendChild(cardEl);

  // Activation indicator: pulsing fmd_bad icon floated above the slot top edge
  // when the unit or its paired pilot has an activatable [Activate･Main] effect.
  // Positioned on the slot so it never rotates with a rested card.
  // Covers both unit and pilot activations — a choice modal appears when both
  // are available simultaneously.
  if (!isOpp && state.phase === 'Main Phase' && state.myTurn) {
    const canUnit  = canActivateUnitEffect(cardData);
    const canPilot = canActivatePilot(cardData);
    if (canUnit || canPilot) {
      const activateIcon = document.createElement('span');
      activateIcon.className = 'material-symbols-outlined unit-activate-icon';
      activateIcon.textContent = 'fmd_bad';
      activateIcon.title = canUnit && canPilot ? 'Activate Unit or Pilot Effect'
                         : canUnit             ? 'Activate Unit Effect'
                         :                       'Activate Pilot Effect';
      activateIcon.addEventListener('click', (e) => {
        e.stopPropagation();
        if (canActivateUnitEffect(cardData) && canActivatePilot(cardData)) {
          showActivationChoiceModal(cardData);
        } else if (canActivateUnitEffect(cardData)) {
          showUnitActivateModal(cardData);
        } else {
          showPilotActivateModal(cardData);
        }
      });
      slot.appendChild(activateIcon);
    }
  }

  // Attack button: red sword badge at the top-right of the slot, shown when
  // the unit can declare an attack this turn. Clicking it opens the target
  // picker directly — no need to click the unit card first. Also requires
  // an actual valid target to exist (getValidAttackTargets, same function
  // showAttackTargetPicker itself uses) — cardCantAttackNow alone only
  // covers rested/deploy-sickness/a cantAttack hook, not "restricted to
  // Units only (tempCantAttackNonUnit) AND no rested enemy Unit to hit
  // either" — e.g. GD04-065 Banshee Norn (Destroy Mode) after activating
  // its own "can't attack the enemy player this turn" ability, with no
  // rested enemy Units on the field, previously still showed the icon for
  // an attack it could never actually declare.
  if (!isOpp && state.myTurn && !state.attackInProgress && state.phase === 'Main Phase' && !cardCantAttackNow(cardData) && getValidAttackTargets(cardData).length > 0) {
    const attackBtn = document.createElement('span');
    attackBtn.className = 'material-symbols-outlined unit-attack-btn';
    attackBtn.textContent = 'swords';
    attackBtn.title = 'Declare attack';
    attackBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      showAttackTargetPicker(cardData);
    });
    slot.appendChild(attackBtn);
  }
}

// Builds the pilot card element that peeks below its parent unit card.
function buildPilotEl(pilotData) {
  const el = document.createElement('div');
  el.className = `pilot-card ${pilotData.color || ''}`;
  el.innerHTML = `
    ${pilotData.img ? `<img class="pilot-img" src="${pilotData.img}" alt="${pilotData.name}" />` : ''}
  `;
  return el;
}

// ════════════════════════════════════════════════════════════════════════════
// BUILD CARD ELEMENT
// Constructs and returns a .card div from a card data object.
// isOpp=true produces a display-only card (draggable=false, no drag listeners).
// Attaches a click listener for selection regardless of ownership.
// "command pilot" reads as plain "Command" in the TYPE stat everywhere it's
// shown to the player — display-only shorthand. The underlying categoryData/
// card.type value is left completely alone, so every game-logic check that
// compares against 'command pilot' (canPilot, TYPE_ORDER, action-type
// filters, ...) keeps working unchanged; only the rendered text differs.
function displayCardType(type) {
  return (type || '').toLowerCase() === 'command pilot' ? 'command' : type;
}

// ════════════════════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════════════════
// CARD PREVIEW POPUP
// Shown on mouseenter, hidden on mouseleave. Singleton #card-preview div is
// repositioned each call so only one preview is ever visible at a time.
// ════════════════════════════════════════════════════════════════════════════
// gameUi=false suppresses the Paired/Linked-Unit status pill and the live
// keyword pills (Breach N, Repair N, ...) — both describe CURRENT in-match
// state (is this specific card instance paired, is a conditional bonus
// live right now), which is meaningless for a static reference card with no
// board context (Card Database, the Bot Deck Preview Modal's "View Deck"
// grid). Every other existing caller is a real in-game card (on the field,
// in hand, or a picker-modal target), so the default stays true and none of
// them need to change.
function showCardPreview(cardData, anchorEl, { gameUi = true } = {}) {
  if (state.dragCard) return; // suppress preview while dragging
  let preview = document.getElementById('card-preview');
  if (!preview) {
    preview = document.createElement('div');
    preview.id = 'card-preview';
    document.body.appendChild(preview);
  }

  // Determine card type: prefer DB categoryData, fall back to image path inference
  const imgPath = cardData.img || '';
  const type = displayCardType(cardData.type)
    || (imgPath.includes('/unit/')  ? 'Unit'
      : imgPath.includes('/pilot/') ? 'Pilot'
      : imgPath.includes('/base/')  ? 'Base'
      : '—');

  // While this card sits in hand, show the Level/Cost it would actually take
  // to play right now (see effectiveHandLevel/effectiveHandCost — e.g.
  // GD05-037 Destroy Gundam while the enemy has 7+ cards in trash), not the
  // printed value, and flag it amber so the discount is obvious.
  const _previewInHand = !!anchorEl.closest('#hand-area');
  const _previewLevel = _previewInHand ? effectiveHandLevel(cardData) : cardData.level;
  const _previewCost  = _previewInHand ? effectiveHandCost(cardData)  : cardData.cost;
  const _levelModified = _previewInHand && _previewLevel !== (parseInt(cardData.level) || 0);
  const _costModified  = _previewInHand && _previewCost  !== (parseInt(cardData.cost)  || 0);

  // Self-heal the hovered hand card's own keyboard_double_arrow_down badge
  // (see buildCardEl's _handModified) to match what's being computed here,
  // right now. The badge is normally baked into the hand row's HTML at the
  // last renderHand() call and only refreshed by the next one — this card's
  // Level/Cost reduction condition (e.g. ST08-001 Ξ Gundam: active while no
  // friendly Lv.6+ Unit is in play) can flip between renders, so relying on
  // renderHand() having already run again by the time the player hovers can
  // leave the on-card badge stale even though this preview always recomputes
  // fresh. Reconciling it here guarantees the two never visibly disagree.
  if (_previewInHand) {
    const _handModifiedNow = _levelModified || _costModified;
    let _badge = anchorEl.querySelector('.card-hand-mod-badge');
    if (_handModifiedNow && !_badge) {
      _badge = document.createElement('span');
      _badge.className = 'material-symbols-outlined card-hand-mod-badge';
      _badge.title = 'Level/Cost reduced in hand';
      _badge.textContent = 'keyboard_double_arrow_down';
      // Lives in the top-left .card-kw-badges stack (not its own absolute
      // position) so it's never clipped by the hand row's bottom edge the
      // way a bottom-left badge can be — see buildCardEl's _handModified.
      let _kwBadges = anchorEl.querySelector('.card-kw-badges');
      if (!_kwBadges) {
        _kwBadges = document.createElement('div');
        _kwBadges.className = 'card-kw-badges';
        anchorEl.insertBefore(_kwBadges, anchorEl.firstChild);
      }
      _kwBadges.insertBefore(_badge, _kwBadges.firstChild);
    } else if (!_handModifiedNow && _badge) {
      _badge.remove();
    }
  }

  const row = (label, val, cls = '') =>
    `<div class="cp-row">
       <span class="cp-label">${label}</span>
       <span class="cp-val${cls ? ' ' + cls : ''}">${val ?? '—'}</span>
     </div>`;

  // Effective stats: use effectiveBp()/effectiveHp() so pilot native boosts,
  // temp/battle AP mods, AND passive pilot AP bonuses (e.g. Kamille's white-
  // base AP+2) are all reflected — keeping the preview in sync with combat math.
  const pilot = cardData.pilot;
  const _isPilotCard = cardData.canPilot || cardData.type === 'pilot' || cardData.type === 'command pilot';
  const _isBaseCard = cardData.type === 'base'; // Base cards have no printed ATK stat.
  // Pilot/command pilot cards carry modifier values (e.g. '+2') — preserve the + prefix
  const _previewEffectiveAp = _isBaseCard ? null : _isPilotCard ? (cardData.bp ?? '—') : effectiveBp(cardData);
  const _previewEffectiveHp = _isPilotCard ? (cardData.hp ?? '—') : effectiveHp(cardData);
  const _previewApDelta = _isPilotCard ? 0 : effectiveBp(cardData) - (parseInt(cardData.bp) || 0);
  const _previewHpDelta = _isPilotCard ? 0 : effectiveHp(cardData) - (parseInt(cardData.hp) || 0);
  const _atkClass = _previewApDelta > 0 ? 'cp-atk cp-boosted' : _previewApDelta < 0 ? 'cp-atk cp-reduced' : 'cp-atk';
  const _hpClass  = _previewHpDelta > 0 ? 'cp-hp cp-boosted'  : 'cp-hp';

  // The pilot's name/trait read like the main card's own cp-name/cp-trait;
  // its effect text gets a plain left-aligned label+body instead of the
  // row() label:value layout (which right-aligns values — fine for a short
  // stat, not for a paragraph of effect text). Its card art and Link/Paired
  // status move into .cp-img-wrap below instead of living here, matching how
  // a paired pilot reads on the field (card peeking out under the unit) —
  // the status pill sits above the art rather than overlapping it, though,
  // since a badge stuck on the artwork was easy to mistake for part of the
  // card's own printed design.
  const pilotSection = pilot ? `
    <div class="cp-pilot-section">
      <div class="cp-pilot-title">${pilot.name}</div>
      ${pilot.trait ? `<div class="cp-pilot-trait">${pilot.trait}</div>` : ''}
      <div class="cp-pilot-effect-label">Pilot Card Effects</div>
      <div class="cp-pilot-effect">${pilot.effect ?? '—'}</div>
    </div>
  ` : '';

  // Active keyword pills (Blocker, Breach N, High-Maneuver, First Strike,
  // Support, Repair N, Suppression) — shown alongside the Paired/Linked Unit
  // pill so every currently-active ability reads at a glance instead of only
  // being discoverable by reading the effect text. Same hasKwIcon() presence
  // checks the on-card badge row already uses; Breach/Repair additionally
  // show their live value via getBreachValue()/getRepairValue() — the same
  // functions combat math itself reads, so the number always matches reality
  // (conditional grants, pilot-granted, tempKeywords, token auras, ...).
  const _kwPills = gameUi ? [
    hasKwIcon(cardData, 'Blocker')       && { icon: 'shield',           label: 'Blocker' },
    hasKwIcon(cardData, 'Breach')        && { icon: 'destruction',      label: `Breach ${getBreachValue(cardData)}` },
    hasKwIcon(cardData, 'High-Maneuver') && { icon: 'turn_sharp_right', label: 'High-Maneuver' },
    hasKwIcon(cardData, 'First Strike')  && { icon: 'bolt',             label: 'First Strike' },
    hasKwIcon(cardData, 'Support')       && { icon: 'fitness_center',   label: 'Support' },
    hasKwIcon(cardData, 'Repair')        && { icon: 'build',            label: `Repair ${getRepairValue(cardData)}` },
    hasKwIcon(cardData, 'Suppression')   && { icon: 'stacks',           label: 'Suppression' },
  ].filter(Boolean) : [];

  preview.innerHTML = `
    <div class="cp-img-wrap">
      ${gameUi && (pilot || _kwPills.length > 0) ? `
        <div class="cp-status-pill-row">
          ${pilot ? `
            <div class="cp-link-status-pill">
              <span class="material-symbols-outlined">${isLinkUnit(cardData) ? 'link' : 'handshake'}</span>
              <span>${isLinkUnit(cardData) ? 'Linked Unit' : 'Paired Unit'}</span>
            </div>
          ` : ''}
          ${_kwPills.map(p => `
            <div class="cp-kw-pill">
              <span class="material-symbols-outlined">${p.icon}</span>
              <span>${p.label}</span>
            </div>
          `).join('')}
        </div>
      ` : ''}
      <div class="cp-card-stack">
        ${cardData.img ? `<img class="cp-main-img" src="${cardData.img}" alt="${cardData.name}" />` : '<div class="cp-no-img">?</div>'}
        ${pilot && pilot.img ? `<img class="cp-pilot-under-img" src="${pilot.img}" alt="${pilot.name}" />` : ''}
      </div>
    </div>
    <div class="cp-stats">
      <div class="cp-name">${cardData.name}</div>
      ${cardData.trait ? `<div class="cp-trait">${cardData.trait}</div>` : ''}
      <div class="cp-divider"></div>
      <div class="cp-stat-grid">
        ${row('Level',        _previewLevel, _levelModified ? 'cp-hand-modified' : '')}
        ${row('Cost',         _previewCost,  _costModified  ? 'cp-hand-modified' : '')}
        ${row('AP',           _previewEffectiveAp, _atkClass)}
        ${row('HP',           _previewEffectiveHp, _hpClass)}
        ${row('Type',         type)}
        ${cardData.type === 'unit' ? `<div class="cp-row cp-row-full"><span class="cp-label">Link Requirement</span><span class="cp-val">${cardData.link || 'None'}</span></div>` : ''}
      </div>
      <div class="cp-effect">${cardData.effect ?? '—'}</div>
      ${pilotSection}
    </div>
  `;

  preview.style.display = 'flex';

  const rect = anchorEl.getBoundingClientRect();
  const pw   = preview.offsetWidth  || 620;
  const ph   = preview.offsetHeight || 280;
  const vw   = document.documentElement.clientWidth;
  const vh   = document.documentElement.clientHeight;
  const inHand = !!anchorEl.closest('#hand-area') || !!anchorEl.closest('#mulligan-modal');

  let left;

  if (inHand) {
    // NOTE: rect.top does NOT reliably reflect the #hand-area .card:hover
    // translateY(-20px) lift yet — this handler runs on the same 'mouseenter'
    // that triggers :hover, so getBoundingClientRect() can catch the card's
    // still-resting (pre-transition) position, before its 0.15s transform
    // transition has moved it. Sizing the gap for only the +12px static
    // clearance (assuming the lift already happened) let the preview's
    // bottom edge land as much as ~22px into the .card-publish-btn "Deploy"
    // badge (top:-14px above the card) once the card finished lifting.
    // Buffer for the worst case instead: 12 (static clearance) + 20 (full
    // hover lift, in case none of it was applied yet when measured) + 14
    // (badge's own offset above the card top) = 46px.
    const bottomGap = vh - rect.top + 46;
    left = rect.left + rect.width / 2 - pw / 2;
    if (left + pw > vw - 8) left = vw - pw - 8;
    if (left < 8) left = 8;
    preview.style.bottom    = bottomGap + 'px';
    preview.style.top       = '';
    preview.style.maxHeight = (vh - bottomGap - 8) + 'px';
    preview.style.left      = left + 'px';
  } else {
    // Field/base cards: prefer right of card, flip left if near right edge
    let top;
    left = rect.right + 12;
    if (left + pw > vw - 8) left = rect.left - pw - 12;
    top = rect.top;
    if (top + ph > vh - 8) top = vh - ph - 8;
    // Clamp all edges
    if (left + pw > vw - 8) left = vw - pw - 8;
    if (left < 8) left = 8;
    if (top + ph > vh - 8) top = vh - ph - 8;
    if (top < 8)  top  = 8;
    preview.style.bottom    = '';
    preview.style.maxHeight = '';
    preview.style.top       = top  + 'px';
    preview.style.left      = left + 'px';
  }
}

function hideCardPreview() {
  const preview = document.getElementById('card-preview');
  if (preview) preview.style.display = 'none';
}

function buildCardEl(cardData, isOpp = false, inHand = false) {
  const el = document.createElement('div');
  el.className = `card ${cardData.color || ''}`;
  el.dataset.uid    = cardData.uid;   // used by findCardSource() to locate the dragged card
  el.dataset.cardId = cardData.cardNo || cardData.id;
  // Opponent cards can't be dragged. Action-only Commands can't be dragged
  // either, UNLESS they're a Command Pilot that can still be paired with a
  // field unit during Main Phase — in that case dragging is needed for pairing.
  el.draggable      = !isOpp && (!cardData.isActionOnly || cardData.canPilot);

  // Effective stats: use effectiveBp()/effectiveHp() so pilot native boosts,
  // temp/battle AP mods, AND passive pilot/unit AP/HP bonuses (e.g. Kamille's
  // white-base AP+2, ST02-010 Heero Yuy's During-Link HP+1) are all
  // reflected — keeping the on-card display in sync with combat math.
  // Base cards have no printed AP stat, so that badge is omitted for them.
  const _isBase      = cardData.type === 'base';
  const _effectiveAp = effectiveBp(cardData);
  const _apBoost     = _effectiveAp - (parseInt(cardData.bp) || 0);
  const _effectiveHp = effectiveHp(cardData);
  const _hpBoost     = _effectiveHp - (parseInt(cardData.hp) || 0);

  // Hand-only badge: some cards' Level/Cost to play them shrinks under a
  // condition (e.g. GD05-037 Destroy Gundam while the enemy has 7+ cards in
  // trash — see handCostReduction/handLevelReduction in the effect registry).
  // Flag it right on the card face while it sits in hand so the discount is
  // visible without having to hover for the preview modal.
  const _handModified = inHand && (
    effectiveHandCost(cardData)  !== (parseInt(cardData.cost)  || 0) ||
    effectiveHandLevel(cardData) !== (parseInt(cardData.level) || 0)
  );

  el.innerHTML = `
    ${cardData.pilot
      ? `<span class="material-symbols-outlined card-pilot-badge">${isLinkUnit(cardData) ? 'link' : 'handshake'}</span>`
      : ''}
    <div class="card-kw-badges">
      ${_handModified
        ? '<span class="material-symbols-outlined card-hand-mod-badge" title="Level/Cost reduced in hand">keyboard_double_arrow_down</span>'
        : ''}
      ${hasKwIcon(cardData, 'Blocker')       ? '<span class="material-symbols-outlined card-kw-badge" title="Blocker">shield</span>'               : ''}
      ${hasKwIcon(cardData, 'Breach')        ? `<span class="card-kw-badge card-kw-badge-valued" title="Breach ${getBreachValue(cardData)}"><span class="material-symbols-outlined card-kw-badge-icon">destruction</span>${getBreachValue(cardData)}</span>` : ''}
      ${hasKwIcon(cardData, 'High-Maneuver') ? '<span class="material-symbols-outlined card-kw-badge" title="High-Maneuver">turn_sharp_right</span>' : ''}
      ${hasKwIcon(cardData, 'First Strike')  ? '<span class="material-symbols-outlined card-kw-badge" title="First Strike">bolt</span>'              : ''}
      ${hasKwIcon(cardData, 'Support')       ? '<span class="material-symbols-outlined card-kw-badge" title="Support">fitness_center</span>'         : ''}
      ${hasKwIcon(cardData, 'Repair')        ? `<span class="card-kw-badge card-kw-badge-valued" title="Repair ${getRepairValue(cardData)}"><span class="material-symbols-outlined card-kw-badge-icon">build</span>${getRepairValue(cardData)}</span>` : ''}
      ${hasKwIcon(cardData, 'Suppression')   ? '<span class="material-symbols-outlined card-kw-badge" title="Suppression">stacks</span>'             : ''}
    </div>
    ${cardData.img
      ? `<img class="card-img" src="${cardData.img}" alt="${cardData.name}" />`
      : `<div class="card-placeholder">🃏</div>`}
    <div class="card-stats">
      ${_isBase ? '' : `<div class="stat${_apBoost > 0 ? ' boosted' : _apBoost < 0 ? ' reduced' : ''}">${_apBoost > 0 ? '<span class="material-symbols-outlined stat-boost-icon" title="AP increased">keyboard_double_arrow_up</span>' : ''}${_effectiveAp}</div>`}
      <div class="stat${_hpBoost ? ' boosted' : ''}${cardData.damaged ? ' damaged' : ''}">${_hpBoost > 0 ? '<span class="material-symbols-outlined stat-boost-icon" title="HP increased">keyboard_double_arrow_up</span>' : ''}${_effectiveHp}</div>
    </div>
  `;

  // Wire drag-and-drop only for player-owned cards
  if (!isOpp) attachDragEvents(el, cardData);

  // Click to select — works for both player and opponent cards
  el.addEventListener('click', () => onCardClick(cardData));

  // Hover preview — show enlarged card detail popup
  el.addEventListener('mouseenter', () => showCardPreview(cardData, el));
  el.addEventListener('mouseleave', hideCardPreview);

  return el;
}

// Rebuilds the hand row in the bottom bar from state.hand.
// Each card in hand gets drag listeners so it can be played to the field.
// Returns the cost to play a card from hand, accounting for conditional
// reductions from the effect registry. Floored at 0.
// Hook signature: handCostReduction(card) → number. Example: GD03-082 Union
// Flag returns 1 while 2+ (Superpower Bloc)/(UN) units are on playerField.
function effectiveHandCost(card) {
  if (!card) return 0;
  const base = parseInt(card.cost) || 0;
  const reduction = window.CardEffects?.[card.cardNo]?.handCostReduction?.(card) || 0;
  return Math.max(0, base - reduction);
}

// Returns the effective level requirement to play a card from hand, accounting
// for conditional reductions from the effect registry. Floored at 1.
// Hook signature: handLevelReduction(card) → number. Example: ST08-001 Ξ Gundam
// returns (enemy unit count) while no friendly Lv.6+ unit is in play.
function effectiveHandLevel(card) {
  if (!card) return 0;
  const base = parseInt(card.level) || 0;
  const reduction = window.CardEffects?.[card.cardNo]?.handLevelReduction?.(card) || 0;
  return Math.max(1, base - reduction);
}

function canPlayCard(card) {
  if (!state.myTurn) return false;
  if (state.phase !== 'Main Phase') return false;
  let cardLevel = effectiveHandLevel(card);
  let cardCost  = effectiveHandCost(card);
  // Some cards offer an alternate "discard/destroy X for a discount" cost via
  // prePlayFromHand (e.g. ST10-014, GD01-002) — handLevelReduction/
  // handCostReduction only kick in AFTER that choice is actually made, so at
  // full price the card would look unplayable/dimmed even when the discount
  // makes it affordable. altCostPreview(card) reports the discounted level/
  // cost when a valid discard/sacrifice candidate exists, purely for this
  // playability check — the real discount is still only applied once the
  // player actually goes through prePlayFromHand in handleDrop.
  if (state.playerLevel < cardLevel || cardCost > state.res + state.exRes) {
    const altCost = window.CardEffects?.[card.cardNo]?.altCostPreview?.(card);
    if (altCost) {
      if (altCost.level != null) cardLevel = altCost.level;
      if (altCost.cost  != null) cardCost  = altCost.cost;
    }
  }
  if (state.playerLevel < cardLevel) return false;
  if (cardCost > state.res + state.exRes) return false;
  // Action-only Commands can't be played outside the Action Step.
  // Exception: a Command Pilot with canPilot can still be paired with a unit
  // during Main Phase — let it fall through to the canPilot check below.
  if (card.isActionOnly && !card.canPilot) return false;

  if (card.canPilot) {
    // Pure Pilot cards need an unpaired Unit on the field to pair onto.
    // Command Pilot cards (and plain Commands with 【Pilot】) can
    // alternatively be activated for their Command effect when dropped off
    // a Unit — see the empty-slot branch of handleDrop() — so they're
    // playable if EITHER a pairing target or a valid activation exists.
    // Action-only Command Pilots may only pair during Main Phase, never activate.
    // Excludes cantBePaired tokens (e.g. T-014 Ad Balloon) — same restriction
    // the actual candidate lists (unpairedUnits below, handleDrop) enforce,
    // so a Pilot with no OTHER unpaired Unit doesn't show as playable only
    // to have nothing legal to drop onto.
    const canPair = state.playerField.some(c => c && !c.pilot && !window.CardEffects?.[c.cardNo]?.cantBePaired?.(c));
    if (card.type === 'pilot') return canPair;
    if (card.isActionOnly) return canPair;
    return canPair || cardEffectCanActivate(card);
  }

  // A plain Command's effect must actually have something to do — see
  // cardEffectCanActivate()/canActivate in js/effects/.
  if (card.type === 'command' && !cardEffectCanActivate(card)) return false;
  return true;
}

function renderHand() {
  const area = document.getElementById('hand-area');
  area.innerHTML = '';

  const count = state.hand.length;
  const CARD_W = 100; // must match CSS #hand-area .card width
  const MIN_STEP = 24; // minimum visible strip per card before the next one overlaps

  // Compute how much negative margin-left each card needs so the full hand fits.
  // First card sits at its natural position; subsequent cards step by (CARD_W + marginLeft).
  const DEFAULT_OVERLAP = 10; // minimum px each card overlaps the previous one
  let marginLeft = -DEFAULT_OVERLAP;
  if (count > 1) {
    const availW = area.offsetWidth || 600;
    // step = visible strip per card; capped so cards always overlap by at least DEFAULT_OVERLAP
    const maxStep = CARD_W - DEFAULT_OVERLAP;
    const step = Math.min(maxStep, Math.max(MIN_STEP, (availW - CARD_W) / (count - 1)));
    marginLeft = step - CARD_W; // always <= -DEFAULT_OVERLAP
  }

  state.hand.forEach((card, i) => {
    const el = buildCardEl(card, false, true);
    el.dataset.source = 'hand';
    if (i > 0) el.style.marginLeft = marginLeft + 'px';
    const playable = canPlayCard(card);
    if (!playable) el.classList.add('hand-card-dim');

    const actionBtns = document.createElement('div');
    actionBtns.className = 'card-action-btns';

    // Publish button — plays the card without dragging it, by reusing the
    // exact same "dropped on the field, not onto a specific Unit" path a
    // drag-drop takes (handleDrop resolves Units/Bases/Commands correctly
    // from there). Excluded for cards whose ONLY valid play path is pairing
    // onto a specific Unit (pure Pilots, and action-only Command Pilots,
    // which can't activate outside the Action Step) — those get the pair
    // button below instead.
    const needsPairTarget = card.canPilot && (card.type === 'pilot' || card.isActionOnly);
    if (playable && !needsPairTarget) {
      const publishBtn = document.createElement('span');
      publishBtn.className = 'material-symbols-outlined card-publish-btn';
      publishBtn.textContent = 'publish';
      publishBtn.title = 'Play this card';
      publishBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        // handleDrop() rebuilds the hand row (removing this card's element
        // outright rather than the cursor naturally leaving it), so the
        // mouseleave that normally hides the preview never fires — left
        // unhandled, #card-preview (z-index 25000) stays stuck open and
        // blocks every modal opened afterward. Same fix attachDragEvents'
        // dragstart already applies for the identical reason.
        hideCardPreview();
        handleDrop('player-field', card, 'hand');
      });
      actionBtns.appendChild(publishBtn);
    }

    // Pair button — pairs this Pilot/Command Pilot to one of the player's
    // unpaired field Units without dragging. Shown for ANY canPilot card
    // (pure Pilots, action-only Command Pilots, and regular Command Pilots
    // alike) whenever pairing is actually possible, so a Command Pilot that
    // can both activate AND pair shows both buttons side by side (the
    // .card-action-btns wrapper centers whichever badges are present).
    // Dragging the card onto a specific Unit slot remains fully available.
    // cantBePaired: a permanent restriction printed on the TARGET Unit
    // itself (e.g. T-014 Ad Balloon, T-022 Wire-Guided Arm: "This Unit
    // can't be paired with a Pilot") — see registry.js for the hook
    // contract. The drag-and-drop path (handleDrop) already checks this
    // before ever opening showPairConfirmModal; this click-to-pair button
    // is a separate path to the same PAIR_PILOT dispatch and was missing
    // the same check, letting a Pilot be paired onto e.g. Ad Balloon simply
    // by using this button instead of dragging.
    const unpairedUnits = state.playerField
      .map((c, slot) => ({ c, slot }))
      .filter(({ c }) => c && !c.pilot && !window.CardEffects?.[c.cardNo]?.cantBePaired?.(c));
    const canPairNow = playable && card.canPilot && unpairedUnits.length > 0;
    if (canPairNow) {
      const pairBtn = document.createElement('span');
      pairBtn.className = 'material-symbols-outlined card-pair-btn';
      pairBtn.textContent = 'switch_access_shortcut_add';
      pairBtn.title = 'Pair this card to a Unit';
      pairBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        hideCardPreview(); // see the identical publishBtn comment above
        // Always the same picker modal — same shell/tile/button style as
        // every other picker (showCardPickerModal), whether there's 1
        // eligible Unit or several, rather than a bespoke single-candidate
        // "Pair this card to the unit?" preview. mandatory:false keeps the
        // auto-select shortcut from skipping straight past the modal even
        // when there's only one candidate, so the player always sees and
        // confirms the pick — consistent look, consistent interaction.
        showCardPickerModal(
          card.name,
          `Choose 1 of your Units to pair ${card.name} with.`,
          unpairedUnits.map(({ c }) => c),
          (chosenUnit) => {
            if (!chosenUnit) return;
            const slot = state.playerField.findIndex(c => c && c.uid === chosenUnit.uid);
            if (slot === -1) return; // field changed underneath the picker — bail out safely
            // Re-check cantBePaired here too, not just in the unpairedUnits
            // filter above — same "field changed underneath the picker"
            // defensive spirit as the slot check just above.
            if (window.CardEffects?.[chosenUnit.cardNo]?.cantBePaired?.(chosenUnit)) {
              const msg = `${chosenUnit.name} can't be paired with a Pilot.`;
              addLog(msg); showToast(msg, true);
              return;
            }
            // Picking a Unit here already IS the confirmation — dispatch the
            // pairing directly (mirrors dispatch('PAIR_PILOT', ...) from
            // showPairConfirmModal's own Confirm button, which is reserved
            // for the drag-a-pilot-onto-a-Unit path instead).
            dispatch('PAIR_PILOT', { pilotUid: card.uid, slot, unitCard: chosenUnit });
            render();
            setupDropZones();
            maybeAutoEndMainPhase();
          },
          card,
          { mandatory: false, skipLabel: 'Cancel' }
        );
      });
      actionBtns.appendChild(pairBtn);
    }

    if (actionBtns.children.length > 0) el.appendChild(actionBtns);
    area.appendChild(el);
  });
}

// ════════════════════════════════════════════════════════════════════════════
// GLOBAL RIGHT-CLICK SUPPRESSION
// The browser's native context menu is disabled everywhere in the game. The
// one deliberate exception — right-click to add/remove up to 4 copies at
// once in the Deck Builder's browse panel and deck grid — already calls
// e.preventDefault() itself on its own tile elements (see those two
// contextmenu handlers elsewhere in this file) before this event ever
// bubbles up to document, so this listener can't interfere with them: it
// only ever suppresses the native menu on everything else.
document.addEventListener('contextmenu', (e) => e.preventDefault());

// ════════════════════════════════════════════════════════════════════════════
// DRAG & DROP — GHOST TRACKING
// The browser's native drag ghost is suppressed (setDragImage with blank image).
// Instead, a cloned card (#drag-ghost) follows the mouse via mousemove.
// This gives a rotated, styled ghost that looks better than the browser default.
// ════════════════════════════════════════════════════════════════════════════
const ghostEl = document.getElementById('drag-ghost');

// mousemove handles non-drag hover; dragover handles position during HTML5 drag
// (Chrome/Edge report clientX=0,clientY=0 on the `drag` event so we use dragover instead)
function updateGhostPos(clientX, clientY) {
  ghostEl.style.left = (clientX - ghostEl.offsetWidth  / 2) + 'px';
  ghostEl.style.top  = (clientY - ghostEl.offsetHeight / 2) + 'px';
}
document.addEventListener('mousemove', e => {
  if (ghostEl.style.display === 'block') updateGhostPos(e.clientX, e.clientY);
});
document.addEventListener('dragover', e => {
  if (ghostEl.style.display === 'block') updateGhostPos(e.clientX, e.clientY);
});

// ════════════════════════════════════════════════════════════════════════════
// DRAG & DROP — EVENT ATTACHMENT
// Called by buildCardEl() for every draggable (player-owned) card.
// dragstart records what is being dragged and shows the ghost.
// dragend cleans up state and removes all hover highlights.
// ════════════════════════════════════════════════════════════════════════════
function attachDragEvents(el, cardData) {
  el.addEventListener('dragstart', e => {
    // Hand stays hoverable during the opponent's turn (for the preview modal),
    // but cards still can't be picked up and dragged onto the (locked) field.
    if (!state.myTurn) { e.preventDefault(); return; }

    // dragCard/dragSource are local drag-plumbing only — excluded from the
    // action dispatcher since they never represent meaningful game state.
    state.dragCard   = cardData;
    state.dragSource = findCardSource(cardData.uid); // where is this card coming from?
    el.classList.add('dragging'); // fades the original card in place
    hideCardPreview(); // dismiss any open preview immediately

    console.log(`[attachDragEvents] drag start — "${cardData.name}" (uid:${cardData.uid}) from zone: ${state.dragSource}`);

    // Clone the card into the ghost overlay so it floats with the cursor
    ghostEl.innerHTML = el.outerHTML;
    ghostEl.querySelector('.dragging')?.classList.remove('dragging');
    ghostEl.style.display = 'block';
    ghostEl.style.width  = el.offsetWidth  + 'px';
    ghostEl.style.height = el.offsetHeight + 'px';

    // Replace the browser's built-in ghost with a transparent image
    e.dataTransfer.setDragImage(new Image(), 0, 0);
    e.dataTransfer.effectAllowed = 'move';
  });


  el.addEventListener('dragend', () => {
    console.log(`[attachDragEvents] drag end — "${cardData.name}" drag finished`);
    el.classList.remove('dragging');
    ghostEl.style.display = 'none';
    state.dragCard   = null;
    state.dragSource = null;
    // Clear any lingering drag-over / pilot-target highlights
    document.querySelectorAll('.drag-over').forEach(z => z.classList.remove('drag-over'));
    document.querySelectorAll('.pilot-target').forEach(c => c.classList.remove('pilot-target'));
  });
}

// ════════════════════════════════════════════════════════════════════════════
// DRAG & DROP — DROP ZONE SETUP
// Attaches dragover / dragleave / drop listeners to every .drop-zone element.
// Must be called after render() because render() recreates DOM nodes, which
// strips previously attached event listeners.
// ════════════════════════════════════════════════════════════════════════════
function setupDropZones() {
  // All .drop-zone elements are static HTML — they persist across render() calls,
  // which only swaps their innerHTML. Without this guard, each render() + setupDropZones()
  // call stacks another set of listeners on the same elements, causing handleDrop to
  // fire multiple times per drop and duplicate cards.
  document.querySelectorAll('.drop-zone').forEach(zone => {
    if (zone.dataset.dropReady === '1') return; // listeners already attached
    zone.dataset.dropReady = '1';

    // dragover: allow the drop and highlight the target zone
    zone.addEventListener('dragover', e => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      zone.classList.add('drag-over');

      // If dragging a pilot over an occupied player slot, show red glow on the unit card
      const isPilot = state.dragCard && state.dragCard.canPilot;
      const slotMatch = (zone.dataset.zone || '').match(/player-slot-(\d+)/);
      if (isPilot && slotMatch) {
        const idx = parseInt(slotMatch[1]);
        if (state.playerField[idx]) zone.querySelector('.card')?.classList.add('pilot-target');
      }
    });

    // dragleave: remove highlight when the cursor exits the zone boundary
    zone.addEventListener('dragleave', e => {
      // relatedTarget check prevents flicker when moving over child elements
      if (!zone.contains(e.relatedTarget)) {
        zone.classList.remove('drag-over');
        zone.querySelector('.card')?.classList.remove('pilot-target');
      }
    });

    // drop: the actual card move — delegate all logic to handleDrop()
    zone.addEventListener('drop', e => {
      e.preventDefault();
      e.stopPropagation(); // prevent the parent field div from also firing drop
      zone.classList.remove('drag-over');
      zone.querySelector('.card')?.classList.remove('pilot-target');
      console.log(`[setupDropZones] drop — landed on zone: "${zone.dataset.zone}"`);
      if (state.dragCard) handleDrop(zone.dataset.zone, state.dragCard, state.dragSource);
      maybeAutoEndMainPhase();
    });
  });
}

// ════════════════════════════════════════════════════════════════════════════
// FIND CARD SOURCE
// Searches state to determine which zone a card (identified by uid) is in.
// Returns a zone string like 'player-slot-0' or 'hand', or null if not found.
// Used by dragstart to record state.dragSource before the card is moved.
// ════════════════════════════════════════════════════════════════════════════
function findCardSource(uid) {
  for (let i = 0; i < 6; i++) { // check all 6 player field slots
    if (state.playerField[i]?.uid === uid) return `player-slot-${i}`;
  }
  if (state.hand.find(c => c.uid === uid)) return 'hand';
  return null;
}

// ════════════════════════════════════════════════════════════════════════════
// FIND NEXT FIELD SLOT
// Scans player field slots 0-5 left-to-right and returns the first empty index.
// Middle-out fill: first card lands in the centre, subsequent cards spread
// left then right alternately — [2, 3, 1, 4, 0, 5].
// Returns -1 when all 6 slots are full.
// ════════════════════════════════════════════════════════════════════════════
const FIELD_SLOT_ORDER = [2, 3, 1, 4, 0, 5];
function findNextFieldSlot() {
  for (const i of FIELD_SLOT_ORDER) {
    if (state.playerField[i] === null) return i;
  }
  return -1;
}

// ════════════════════════════════════════════════════════════════════════════
// TRIGGER SNAP ANIMATION
// Adds the .snap-in CSS class to the card that was just placed on the field.
// Uses requestAnimationFrame so it fires after render() has painted the card,
// ensuring the animation starts from the card's resting position, not void.
// ════════════════════════════════════════════════════════════════════════════
function triggerSnapAnimation(slotIndex) {
  requestAnimationFrame(() => {
    const slotEl = document.getElementById(`player-slot-${slotIndex}`);
    const cardEl = slotEl?.querySelector('.card');
    if (cardEl) {
      cardEl.classList.add('snap-in');
      // Remove class after animation so hover/selected transitions work normally
      cardEl.addEventListener('animationend', () => cardEl.classList.remove('snap-in'), { once: true });
    }
  });
}

// ════════════════════════════════════════════════════════════════════════════
// HANDLE DROP
// The routing function for all drag-and-drop moves.
// Reads targetZone (where the card was dropped) and sourceZone (where it came
// from) and mutates state accordingly, then calls render() + setupDropZones().
//
// Supported moves:
//   hand → player-slot-N (occupied) : attach card as pilot under the unit in that slot
//   hand → player-field / empty slot : auto-place at next empty slot
//   player-slot-N → hand       : return a field card to hand
//   player-slot-N → player-slot-M : swap cards between two specific field slots
//   hand/field → trash         : discard a card, increment trashCount
//   hand → player-shield       : place a card face-down as a new shield
//   any → player-base          : set a card as the active base (logged only for now)
// ════════════════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════════════════
// PAIR CONFIRM MODAL
// Shown when a hand card is dropped onto an occupied field slot.
// Displays both cards with an arrow and asks the player to confirm pairing.
// ════════════════════════════════════════════════════════════════════════════
// Shared mini-card builder used by pair and attack confirm modals.
function buildMiniCard(cardData) {
  const wrap = document.createElement('div');
  wrap.className = 'pair-mini-card';
  if (cardData.isDirect) {
    wrap.classList.add('pair-mini-card--direct');
    wrap.innerHTML = '<span class="material-symbols-outlined mini-card-person">person</span>';
  } else if (cardData.img) {
    const img = document.createElement('img');
    img.src = cardData.img;
    img.alt = cardData.name;
    wrap.appendChild(img);
  }
  return wrap;
}

registerAction('PAIR_PILOT', ({ pilotUid, slot, unitCard }) => {
  const pilot = state.hand.find(c => c.uid === pilotUid);
  const _pilotH = window.CardEffects?.[pilot.cardNo];
  const _effectivePilotCost = _pilotH?.pilotCostOverride
    ? (_pilotH.pilotCostOverride(pilot, unitCard) ?? 0)
    : (parseInt(pilot.cost) || 0);
  spendResources(_effectivePilotCost);
  const pairedUnit = { ...unitCard, pilot };
  // Mark newly-linked link units so endOfTurnLinked hooks can fire at End Phase.
  // Only set when the unit had no prior pilot (pre-linked units don't qualify).
  if (!unitCard.pilot && isLinkUnit(pairedUnit)) {
    pairedUnit.linkedThisTurn = true;
    pairedUnit.tempCantAttack = false; // linking removes deploy sickness
    // Notify the player's base that a link requirement was satisfied (e.g. ST06-015 Kaneban).
    if (state.playerBase) {
      window.CardEffects?.[state.playerBase.cardNo]?.onPlayerUnitLinked?.(state.playerBase, pairedUnit, slot);
    }
  }
  // Notify the player's base of any pilot pairing (e.g. GD03-124 Ribo Colony).
  if (state.playerBase) {
    window.CardEffects?.[state.playerBase.cardNo]?.onPilotPaired?.(state.playerBase, pilot, pairedUnit, slot);
  }
  state.playerField[slot] = pairedUnit;
  state.hand = state.hand.filter(c => c.uid !== pilotUid);
  addLog(isLinkUnit(pairedUnit)
    ? `${pilot.name} <span class="material-symbols-outlined log-link-icon">link</span> as pilot to ${unitCard.name}.`
    : `${pilot.name} paired as pilot to ${unitCard.name}.`);
  triggerCardEffect(pilot, 'pair', { unit: pairedUnit, slot });
  triggerCardEffect(pairedUnit, 'pair', { unit: pairedUnit, slot });
  // Notify other field units that a white unit was just paired — allows cards
  // like GD01-065 Freedom Gundam to react to any friendly white unit pairing.
  const pairedColor = (pairedUnit.color || '').toLowerCase();
  state.playerField.forEach(fieldUnit => {
    if (!fieldUnit || fieldUnit.uid === pairedUnit.uid) return;
    const handlers = window.CardEffects?.[fieldUnit.cardNo];
    if (handlers?.onAnyUnitPair) handlers.onAnyUnitPair(fieldUnit, pairedUnit, pairedColor);
  });
});

function showPairConfirmModal(draggedCard, unitCard, slotIndex) {
  const overlay = document.createElement('div');
  overlay.id = 'pair-confirm-overlay';

  const modal = document.createElement('div');
  modal.id = 'pair-confirm-modal';

  const hideBtn = document.createElement('button');
  hideBtn.className = 'modal-hide-btn';
  hideBtn.title = 'Hide to view field';
  hideBtn.innerHTML = '<span class="modal-hide-label">View Field</span><span class="material-symbols-outlined">ad_off</span>';
  hideBtn.addEventListener('click', (e) => { e.stopPropagation(); hideModalForFieldView(overlay); });
  modal.appendChild(hideBtn);

  // Title
  const title = document.createElement('div');
  title.className = 'pair-title';
  title.textContent = 'Pair this card to the unit?';
  modal.appendChild(title);

  // Card preview row
  const row = document.createElement('div');
  row.className = 'pair-card-row';

  row.appendChild(buildMiniCard(draggedCard));

  const arrow = document.createElement('div');
  arrow.className = 'pair-arrow';
  arrow.textContent = '→';
  row.appendChild(arrow);

  row.appendChild(buildMiniCard(unitCard));
  modal.appendChild(row);

  // Buttons
  const btns = document.createElement('div');
  btns.className = 'pair-btns';

  const btnCancel = document.createElement('button');
  btnCancel.className = 'pair-btn cancel';
  btnCancel.textContent = 'Cancel';
  btnCancel.addEventListener('click', () => {
    overlay.remove();
    console.log('[showPairConfirmModal] cancelled');
  });

  const btnConfirm = document.createElement('button');
  btnConfirm.className = 'pair-btn confirm';
  btnConfirm.textContent = 'Pair';
  btnConfirm.addEventListener('click', () => {
    overlay.remove();
    console.log(`[showPairConfirmModal] confirmed — "${draggedCard.name}" → pilot of "${unitCard.name}" at slot ${slotIndex}`);
    dispatch('PAIR_PILOT', { pilotUid: draggedCard.uid, slot: slotIndex, unitCard });
    render();
    setupDropZones();
    maybeAutoEndMainPhase();
  });

  btns.appendChild(btnCancel);
  btns.appendChild(btnConfirm);
  modal.appendChild(btns);

  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  requestAnimationFrame(() => requestAnimationFrame(() => overlay.classList.add('visible')));
  startSelectionTimer(overlay, () => autoResolveCancelModal(overlay));
}

// ════════════════════════════════════════════════════════════════════════════
// ATTACK CONFIRM MODAL
// Shown when the player clicks an opponent unit or base while a player card
// is selected. Displays attacker → defender and lets the player confirm.
function showTrashModal(trashArray, isOpp) {
  const existing = document.getElementById('trash-modal-overlay');
  if (existing) { existing.remove(); return; }

  const unitCount    = trashArray.filter(c => (c.type || '').toLowerCase() === 'unit').length;
  const pilotCount   = trashArray.filter(c => (c.type || '').toLowerCase() === 'pilot').length;
  const commandCount = trashArray.filter(c => {
    const t = (c.type || '').toLowerCase();
    return t === 'command' || t === 'command pilot';
  }).length;
  const baseCount = trashArray.filter(c => {
    const t = (c.type || '').toLowerCase();
    return t === 'base' || t === 'ex base';
  }).length;

  const overlay = document.createElement('div');
  overlay.id = 'trash-modal-overlay';
  overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });

  const modal = document.createElement('div');
  modal.id = 'trash-modal';

  // Header
  const header = document.createElement('div');
  header.className = 'trash-modal-header';
  header.innerHTML = `
    <div class="trash-modal-title">${isOpp ? 'Opponent' : 'Your'} Trash</div>
    <div class="trash-modal-summary">
      <div class="trash-summary-item">
        <span class="material-symbols-outlined">smart_toy</span>
        <span>Unit</span><strong>${unitCount}</strong>
      </div>
      <div class="trash-summary-item">
        <span class="material-symbols-outlined">person</span>
        <span>Pilot</span><strong>${pilotCount}</strong>
      </div>
      <div class="trash-summary-item">
        <span class="material-symbols-outlined">sd_card_alert</span>
        <span>Command</span><strong>${commandCount}</strong>
      </div>
      <div class="trash-summary-item">
        <span class="material-symbols-outlined">castle</span>
        <span>Base</span><strong>${baseCount}</strong>
      </div>
    </div>
    <button class="trash-modal-close">✕</button>
  `;
  header.querySelector('.trash-modal-close').addEventListener('click', () => overlay.remove());
  modal.appendChild(header);

  // Card grid
  const grid = document.createElement('div');
  grid.className = 'trash-modal-grid';

  if (trashArray.length === 0) {
    grid.innerHTML = '<div class="trash-modal-empty">No cards in trash yet.</div>';
  } else {
    trashArray.forEach(card => {
      const wrap = document.createElement('div');
      wrap.className = 'trash-card-wrap';
      if (card.img) {
        const img = document.createElement('img');
        img.src = card.img;
        img.alt = card.name;
        img.className = 'trash-card-img';
        wrap.appendChild(img);
      } else {
        wrap.innerHTML = `<div class="trash-card-placeholder">${card.name}</div>`;
      }
      wrap.addEventListener('mouseenter', () => showCardPreview(card, wrap));
      wrap.addEventListener('mouseleave', hideCardPreview);
      grid.appendChild(wrap);
    });
  }
  modal.appendChild(grid);
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
}

// Same list-of-cards layout as showTrashModal (reuses its .trash-modal-*/
// .trash-card-* classes and #trash-modal's CSS via the shared #exile-modal
// selector — see style.css), just pointed at state.playerExile/oppExile
// instead. Exile is a terminal, append-only zone (nothing ever leaves it —
// see state.playerExile/oppExile in the state object), so exileArray.length
// alone is always an accurate count with no separate counter to keep in sync.
function showExileModal(exileArray, isOpp) {
  const existing = document.getElementById('exile-modal-overlay');
  if (existing) { existing.remove(); return; }

  const unitCount    = exileArray.filter(c => (c.type || '').toLowerCase() === 'unit').length;
  const pilotCount   = exileArray.filter(c => (c.type || '').toLowerCase() === 'pilot').length;
  const commandCount = exileArray.filter(c => {
    const t = (c.type || '').toLowerCase();
    return t === 'command' || t === 'command pilot';
  }).length;
  const baseCount = exileArray.filter(c => {
    const t = (c.type || '').toLowerCase();
    return t === 'base' || t === 'ex base';
  }).length;

  const overlay = document.createElement('div');
  overlay.id = 'exile-modal-overlay';
  overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });

  const modal = document.createElement('div');
  modal.id = 'exile-modal';

  const header = document.createElement('div');
  header.className = 'trash-modal-header';
  header.innerHTML = `
    <div class="trash-modal-title">${isOpp ? 'Opponent' : 'Your'} Exiled Cards</div>
    <div class="trash-modal-summary">
      <div class="trash-summary-item">
        <span class="material-symbols-outlined">smart_toy</span>
        <span>Unit</span><strong>${unitCount}</strong>
      </div>
      <div class="trash-summary-item">
        <span class="material-symbols-outlined">person</span>
        <span>Pilot</span><strong>${pilotCount}</strong>
      </div>
      <div class="trash-summary-item">
        <span class="material-symbols-outlined">sd_card_alert</span>
        <span>Command</span><strong>${commandCount}</strong>
      </div>
      <div class="trash-summary-item">
        <span class="material-symbols-outlined">castle</span>
        <span>Base</span><strong>${baseCount}</strong>
      </div>
    </div>
    <button class="trash-modal-close">✕</button>
  `;
  header.querySelector('.trash-modal-close').addEventListener('click', () => overlay.remove());
  modal.appendChild(header);

  const grid = document.createElement('div');
  grid.className = 'trash-modal-grid';

  if (exileArray.length === 0) {
    grid.innerHTML = '<div class="trash-modal-empty">No cards exiled yet.</div>';
  } else {
    exileArray.forEach(card => {
      const wrap = document.createElement('div');
      wrap.className = 'trash-card-wrap';
      if (card.img) {
        const img = document.createElement('img');
        img.src = card.img;
        img.alt = card.name;
        img.className = 'trash-card-img';
        wrap.appendChild(img);
      } else {
        wrap.innerHTML = `<div class="trash-card-placeholder">${card.name}</div>`;
      }
      wrap.addEventListener('mouseenter', () => showCardPreview(card, wrap));
      wrap.addEventListener('mouseleave', hideCardPreview);
      grid.appendChild(wrap);
    });
  }
  modal.appendChild(grid);
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
}

registerAction('ACTIVATE_BASE', ({ cost, baseName, exiledTrashUid }) => {
  if (cost.res) spendResources(cost.res);
  if (cost.restBase) {
    state.playerBase.rested = true;
    NET.send({ type: 'UNIT_REST', uid: state.playerBase.uid, rested: true });
  }
  if (cost.oncePerTurn) state.playerBase.baseAbilityUsedThisTurn = true;
  if (cost.exileCommand && exiledTrashUid) {
    const exiled = state.playerTrash.find(c => c.uid === exiledTrashUid);
    if (exiled) {
      exileFromTrash(exiled);
      addLog(`Exiled ${exiled.name} from the trash as ${baseName}'s activation cost.`);
    }
  }
  addLog(`Activated ${baseName}'s effect${cost.res ? ` (spent ${cost.res} RES)` : ''}.`);
});

// A plain Command (no 【Pilot】) dropped onto the field — it never occupies a
// slot; it's resolved on the spot and trashed.
registerAction('ACTIVATE_COMMAND', ({ uid }) => {
  const card = state.hand.find(c => c.uid === uid);
  if (!card) return;
  // _preferExPayment: opt-in per-instance flag a card's own prePlayFromHand
  // hook can set beforehand to request EX-Resource-first payment instead of
  // the normal RES-first order (e.g. GD05-118 Incendiary Spark: "If you use
  // an EX Resource to play this card..." — a player choice, not automatic).
  spendResources(effectiveHandCost(card), { preferEx: !!card._preferExPayment });
  card._preferExPayment = false;
  state.hand = state.hand.filter(c => c.uid !== uid);
  updateTempState(card.cardNo, card.copyVer, 'trash');
  state.trashCount += 1;
  state.playerTrash.push(toTrashCard(card));
  addLog(`Activated ${card.name}'s effect (sent to trash).`);
  broadcastCardToast(`${card.name} was sent to trash.`, card.img);
});

// Notifies player field units with an onCommandEffect hook that a command card's
// [Main] or [Action] effect was just activated. Reactors are called sequentially;
// the chain ends by invoking onDone. Hook sig: onCommandEffect(unit, cmdCard, next).
function fireCommandEffectPassives(commandCard, onDone = () => {}) {
  const reactors = state.playerField.filter(
    unit => unit && window.CardEffects?.[unit.cardNo]?.onCommandEffect
  );
  if (reactors.length === 0) { onDone(); return; }
  let idx = 0;
  function next() {
    if (idx >= reactors.length) { onDone(); return; }
    const unit = reactors[idx++];
    currentLogActor = cardOwnerSide(unit) || currentLogActor;
    try { window.CardEffects[unit.cardNo].onCommandEffect(unit, commandCard, next); }
    catch (err) { console.error(`[fireCommandEffectPassives] ${unit.cardNo} threw`, err); next(); }
  }
  next();
}

// Fires pilot-registered reactions to (Academy) Command cards played with EX Resources.
// Conditions: usedExRes=true, state.exRes===0 after play, command card trait includes (Academy).
// Used by Suletta Mercury (GD04-085): [During Link][Once per Turn] place 1 rested EX Resource.
function firePilotCommandPassives(commandCard, usedExRes, onDone = () => {}) {
  if (!usedExRes || state.exRes > 0 || !(commandCard.trait || '').includes('(Academy)')) {
    onDone(); return;
  }
  const reactors = state.playerField.filter(unit =>
    unit?.pilot && isLinkUnit(unit) &&
    window.CardEffects?.[unit.pilot.cardNo]?.onAcademyCommandWithExUsed
  );
  if (reactors.length === 0) { onDone(); return; }
  let idx = 0;
  function next() {
    if (idx >= reactors.length) { onDone(); return; }
    const unit = reactors[idx++];
    currentLogActor = cardOwnerSide(unit.pilot) || cardOwnerSide(unit) || currentLogActor;
    try { window.CardEffects[unit.pilot.cardNo].onAcademyCommandWithExUsed(unit.pilot, unit, commandCard, next); }
    catch (err) { console.error(`[firePilotCommandPassives] ${unit.pilot.cardNo} threw`, err); next(); }
  }
  next();
}

// Fires onDawnCommandWithExPlayed hooks on field units when a (Dawn of Fold)
// Command card is played using an EX Resource. Fires every time the condition
// is met (no "last EX" requirement). Targets unit-level handlers, not pilots.
function fireDawnCommandPassives(commandCard, usedExRes, onDone = () => {}) {
  if (!usedExRes || !state.myTurn || !(commandCard.trait || '').includes('(Dawn of Fold)')) {
    onDone(); return;
  }
  const reactors = state.playerField.filter(unit =>
    unit && window.CardEffects?.[unit.cardNo]?.onDawnCommandWithExPlayed
  );
  if (reactors.length === 0) { onDone(); return; }
  let idx = 0;
  function next() {
    if (idx >= reactors.length) { onDone(); return; }
    const unit = reactors[idx++];
    currentLogActor = cardOwnerSide(unit) || currentLogActor;
    try { window.CardEffects[unit.cardNo].onDawnCommandWithExPlayed(unit, commandCard, next); }
    catch (err) { console.error(`[fireDawnCommandPassives] ${unit.cardNo} threw`, err); next(); }
  }
  next();
}

function showCommandActivateModal(card) {
  const existing = document.getElementById('command-activate-overlay');
  if (existing) existing.remove();

  const overlay = document.createElement('div');
  overlay.id = 'command-activate-overlay';

  const modal = document.createElement('div');
  modal.id = 'command-activate-modal';

  const hideBtn = document.createElement('button');
  hideBtn.className = 'modal-hide-btn';
  hideBtn.title = 'Hide to view field';
  hideBtn.innerHTML = '<span class="modal-hide-label">View Field</span><span class="material-symbols-outlined">ad_off</span>';
  hideBtn.addEventListener('click', (e) => { e.stopPropagation(); hideModalForFieldView(overlay); });
  modal.appendChild(hideBtn);

  const question = document.createElement('div');
  question.className = 'base-activate-question';
  question.innerHTML = `Would you like to activate <span class="base-activate-name">${card.name}</span>'s effect?`;
  modal.appendChild(question);

  const row = document.createElement('div');
  row.className = 'base-activate-row';

  const imgWrap = document.createElement('div');
  imgWrap.className = 'base-activate-img-wrap';
  const img = document.createElement('img');
  img.className = 'base-activate-img';
  img.src = card.img;
  img.alt = card.name;
  imgWrap.appendChild(img);
  row.appendChild(imgWrap);

  const info = document.createElement('div');
  info.className = 'base-activate-info';
  info.innerHTML = `
    <div class="base-activate-card-name">${card.name}</div>
    <div class="base-activate-stats">
      <div class="cp-row"><span class="cp-label">Level</span><span class="cp-val">${card.level ?? '—'}</span></div>
      <div class="cp-row"><span class="cp-label">Cost</span><span class="cp-val">${card.cost ?? '—'}</span></div>
      ${card.hp != null ? `<div class="cp-row"><span class="cp-label">HP</span><span class="cp-val cp-hp">${card.hp}</span></div>` : ''}
    </div>
    <div class="base-activate-effect-label">Effect</div>
    <div class="base-activate-effect">${card.effect ?? '—'}</div>
  `;
  row.appendChild(info);
  modal.appendChild(row);

  const btns = document.createElement('div');
  btns.className = 'base-activate-btns';

  const btnCancel = document.createElement('button');
  btnCancel.className = 'pair-btn cancel';
  btnCancel.textContent = 'Cancel';
  btnCancel.addEventListener('click', () => overlay.remove());

  const btnActivate = document.createElement('button');
  btnActivate.className = 'pair-btn confirm';
  btnActivate.textContent = 'Activate';
  btnActivate.addEventListener('click', () => {
    overlay.remove();
    const proceedActivate = () => {
      const exResBefore = state.exRes;
      dispatch('ACTIVATE_COMMAND', { uid: card.uid, card });
      const usedExRes = exResBefore > state.exRes;
      render();
      setupDropZones();
      // [Main] card effects (see js/effects/) run after the trash dispatch
      // above so they operate on the post-activation state. A registered
      // handler is responsible for calling maybeAutoEndMainPhase() itself
      // once its own (possibly async) flow finishes, since it may still need
      // the Main Phase to stay open for a follow-up prompt (e.g. a discard).
      const handled = triggerCardEffect(card, 'main', {
        usedExRes,
        onDone: () => fireCommandEffectPassives(card, () => firePilotCommandPassives(card, usedExRes, () => fireDawnCommandPassives(card, usedExRes, maybeAutoEndMainPhase)))
      });
      if (!handled) fireCommandEffectPassives(card, () => firePilotCommandPassives(card, usedExRes, () => fireDawnCommandPassives(card, usedExRes, maybeAutoEndMainPhase)));
    };
    // Same prePlayFromHand hook used by Unit cards (e.g. GD01-002 Unicorn
    // Gundam) — lets a Command offer an alternate-cost prompt (e.g. ST10-014
    // "discard 1 (G Generation) Unit card, play as if Lv.2/cost 2") before
    // ACTIVATE_COMMAND actually spends resources. slot is null (Commands
    // don't occupy a field slot).
    const prePlay = window.CardEffects?.[card.cardNo]?.prePlayFromHand;
    if (prePlay) { prePlay(card, null, proceedActivate); } else { proceedActivate(); }
  });

  btns.appendChild(btnCancel);
  btns.appendChild(btnActivate);
  modal.appendChild(btns);

  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  startSelectionTimer(overlay, () => autoResolveCancelModal(overlay));

  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
}

// Informational pop-up shown whenever the opponent activates a base/command
// effect or plays an Action Step card — no choice to make, just notifies the
// player what happened. Closes on any click, or automatically after 5s.
function showOpponentPlayModal(card, message) {
  const existing = document.getElementById('opponent-play-overlay');
  if (existing) existing.remove();

  const overlay = document.createElement('div');
  overlay.id = 'opponent-play-overlay';

  const modal = document.createElement('div');
  modal.id = 'opponent-play-modal';

  const question = document.createElement('div');
  question.className = 'base-activate-question';
  question.textContent = message;
  modal.appendChild(question);

  const row = document.createElement('div');
  row.className = 'base-activate-row';

  const imgWrap = document.createElement('div');
  imgWrap.className = 'base-activate-img-wrap';
  const img = document.createElement('img');
  img.className = 'base-activate-img';
  img.src = card.img;
  img.alt = card.name;
  imgWrap.appendChild(img);
  row.appendChild(imgWrap);

  const info = document.createElement('div');
  info.className = 'base-activate-info';
  info.innerHTML = `
    <div class="base-activate-card-name">${card.name}</div>
    <div class="base-activate-stats">
      <div class="cp-row"><span class="cp-label">Level</span><span class="cp-val">${card.level ?? '—'}</span></div>
      <div class="cp-row"><span class="cp-label">Cost</span><span class="cp-val">${card.cost ?? '—'}</span></div>
      ${card.hp != null ? `<div class="cp-row"><span class="cp-label">HP</span><span class="cp-val cp-hp">${card.hp}</span></div>` : ''}
    </div>
    <div class="base-activate-effect-label">Effect</div>
    <div class="base-activate-effect">${card.effect ?? '—'}</div>
  `;
  row.appendChild(info);
  modal.appendChild(row);

  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  let resolved = false;
  const finish = () => {
    if (resolved) return;
    resolved = true;
    clearTimeout(timer);
    overlay.remove();
  };
  const timer = setTimeout(finish, 5000);
  // Deferred by a tick: this overlay pops up unannounced (triggered by a
  // remote action, not a local click), so if the player already had a
  // mousedown in flight elsewhere on the page right when it appeared, the
  // resulting mouseup/click would otherwise land on the freshly-inserted
  // overlay and dismiss it within the same event — the modal would flash and
  // vanish before it was ever visible. Attaching the listener on the next
  // tick means that in-flight click can't be the one that closes it.
  setTimeout(() => overlay.addEventListener('click', finish), 0);
}

// When both a unit's own [Activate･Main] and its pilot's [Activate･Main] are
// simultaneously eligible, this modal lets the player pick which to activate.
function showActivationChoiceModal(unit) {
  const existing = document.getElementById('activation-choice-overlay');
  if (existing) existing.remove();

  const overlay = document.createElement('div');
  overlay.id = 'activation-choice-overlay';
  overlay.className = 'mk-overlay';

  const modal = document.createElement('div');
  modal.className = 'mk-modal';

  const hideBtn = document.createElement('button');
  hideBtn.className = 'modal-hide-btn';
  hideBtn.title = 'Hide to view field';
  hideBtn.innerHTML = '<span class="modal-hide-label">View Field</span><span class="material-symbols-outlined">ad_off</span>';
  hideBtn.addEventListener('click', (e) => { e.stopPropagation(); hideModalForFieldView(overlay); });
  modal.appendChild(hideBtn);

  const title = document.createElement('div');
  title.className = 'mk-title';
  title.textContent = `${unit.name} — Activate Which Effect?`;
  modal.appendChild(title);

  const btnRow = document.createElement('div');
  btnRow.className = 'base-activate-btns';

  const btnUnit = document.createElement('button');
  btnUnit.textContent = 'Unit Effect';
  btnUnit.className = 'btn-primary';
  btnUnit.addEventListener('click', () => { overlay.remove(); showUnitActivateModal(unit); });

  const btnPilot = document.createElement('button');
  btnPilot.textContent = `Pilot — ${unit.pilot?.name || 'Pilot'}`;
  btnPilot.className = 'btn-primary';
  btnPilot.addEventListener('click', () => { overlay.remove(); showPilotActivateModal(unit); });

  const btnCancel = document.createElement('button');
  btnCancel.textContent = 'Cancel';
  btnCancel.className = 'btn-secondary';
  btnCancel.addEventListener('click', () => overlay.remove());

  btnRow.appendChild(btnUnit);
  btnRow.appendChild(btnPilot);
  btnRow.appendChild(btnCancel);
  modal.appendChild(btnRow);
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  startSelectionTimer(overlay, () => autoResolveCancelModal(overlay));
}

// Shows a confirmation modal for a unit's own [Activate･Main] ability.
// Spends the cost, fires the unit's 'activate' trigger, then calls
// notifyUnitEffectResSpent so [Once per Turn] watchers like Sochie Heim fire
// AFTER the effect resolves.
function showUnitActivateModal(unit) {
  const existing = document.getElementById('unit-activate-overlay');
  if (existing) existing.remove();

  const cost = getUnitActivationCost(unit);

  const overlay = document.createElement('div');
  overlay.id = 'unit-activate-overlay';
  overlay.className = 'mk-overlay';

  const modal = document.createElement('div');
  modal.className = 'mk-modal act-modal';

  const hideBtn = document.createElement('button');
  hideBtn.className = 'modal-hide-btn';
  hideBtn.title = 'Hide to view field';
  hideBtn.innerHTML = '<span class="modal-hide-label">View Field</span><span class="material-symbols-outlined">ad_off</span>';
  hideBtn.addEventListener('click', (e) => { e.stopPropagation(); hideModalForFieldView(overlay); });
  modal.appendChild(hideBtn);

  const title = document.createElement('div');
  title.className = 'mk-title';
  title.textContent = `${unit.name} — Activate Effect?`;
  modal.appendChild(title);

  if (unit.img) {
    const img = document.createElement('img');
    img.src = unit.img; img.alt = unit.name; img.className = 'base-activate-img';
    modal.appendChild(img);
  }

  const effectDiv = document.createElement('div');
  effectDiv.className = 'base-activate-effect';
  effectDiv.innerHTML = unit.effect || '';
  modal.appendChild(effectDiv);

  const btnRow = document.createElement('div');
  btnRow.className = 'base-activate-btns';

  const btnCancel = document.createElement('button');
  btnCancel.textContent = 'Cancel';
  btnCancel.className = 'btn-secondary';
  btnCancel.addEventListener('click', () => overlay.remove());

  const btnActivate = document.createElement('button');
  btnActivate.textContent = cost.res > 0 ? `Activate (${cost.res} RES)` : 'Activate';
  btnActivate.className = 'btn-primary';
  btnActivate.addEventListener('click', () => {
    overlay.remove();
    const proceedActivate = () => {
      // Snapshot resources BEFORE spending so handlers can restore on cancel.
      const resSnapshot = { res: state.res, exRes: state.exRes };
      if (cost.res > 0) spendResources(cost.res);
      if (cost.oncePerTurn) unit.activateMainUsedThisTurn = true;
      // Track for GD04-069: did the player pay ① or more for a (Militia)/(Dianna Counter) unit's effect?
      if (cost.res >= 1 && ((unit.trait || '').includes('(Militia)') || (unit.trait || '').includes('(Dianna Counter)'))) {
        state.militiaOrDiannaEffectPaidThisTurn = true;
      }
      // Track for GD06-070 Eagail: running total of RES paid for Units'
      // (Activate-Main/Action) effects this turn — reset in
      // registerAction('CLEAR_TURN_EFFECTS', ...) alongside the flag above.
      if (cost.res >= 1) {
        state.playerUnitEffectResSpentThisTurn = (state.playerUnitEffectResSpentThisTurn || 0) + cost.res;
      }
      addLog(`${unit.name} — [Activate･Main] activated${cost.res > 0 ? ` (paid ${cost.res} RES)` : ''}.`);
      broadcastCounters();
      const notify = () => {
        notifyUnitEffectResSpent('player', cost.res, unit);
        render();
        setupDropZones();
        maybeAutoEndMainPhase();
      };
      const handled = triggerCardEffect(unit, 'activate', { unit, onDone: notify, resSnapshot, cost });
      if (!handled) {
        addLog(`${unit.name} — no programmatic effect registered; resolve manually.`);
        notify();
      }
    };
    // "Rest N of your Units" cost (e.g. GD05-001 V2 Gundam) — choose which
    // OTHER active units pay it before the effect resolves.
    if (cost.restUnits > 0) {
      const restCandidates = state.playerField.filter(c => c && c.uid !== unit.uid && !c.rested);
      showCardPickerModalMulti(
        unit.name,
        `Rest ${cost.restUnits} of your Units to pay this ability's cost.`,
        restCandidates,
        { min: cost.restUnits, max: cost.restUnits },
        (chosen) => {
          chosen.forEach(c => {
            c.rested = true;
            NET.send({ type: 'UNIT_REST', uid: c.uid, rested: true });
          });
          addLog(`${unit.name} — rested ${chosen.map(c => c.name).join(', ')} to pay the cost.`);
          render();
          proceedActivate();
        },
        unit
      );
    } else {
      proceedActivate();
    }
  });

  btnRow.appendChild(btnCancel);
  btnRow.appendChild(btnActivate);
  modal.appendChild(btnRow);
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  startSelectionTimer(overlay, () => autoResolveCancelModal(overlay));
}

// Shows a confirmation modal for a pilot card's [Activate･Main] ability.
// Fires the pilot's 'activate' trigger with { unit, onDone } context.
function showPilotActivateModal(unit) {
  const existing = document.getElementById('pilot-activate-overlay');
  if (existing) existing.remove();

  const pilot = unit.pilot;
  const overlay = document.createElement('div');
  overlay.id = 'pilot-activate-overlay';
  overlay.className = 'mk-overlay';

  const modal = document.createElement('div');
  modal.className = 'mk-modal act-modal';

  const hideBtn = document.createElement('button');
  hideBtn.className = 'modal-hide-btn';
  hideBtn.title = 'Hide to view field';
  hideBtn.innerHTML = '<span class="modal-hide-label">View Field</span><span class="material-symbols-outlined">ad_off</span>';
  hideBtn.addEventListener('click', (e) => { e.stopPropagation(); hideModalForFieldView(overlay); });
  modal.appendChild(hideBtn);

  const title = document.createElement('div');
  title.className = 'mk-title';
  title.textContent = `${pilot.name} — Activate Effect?`;
  modal.appendChild(title);

  if (pilot.img) {
    const img = document.createElement('img');
    img.src = pilot.img;
    img.alt = pilot.name;
    img.className = 'base-activate-img';
    modal.appendChild(img);
  }

  const effectDiv = document.createElement('div');
  effectDiv.className = 'base-activate-effect';
  effectDiv.innerHTML = pilot.effect || '';
  modal.appendChild(effectDiv);

  const btnRow = document.createElement('div');
  btnRow.className = 'base-activate-btns';

  const btnCancel = document.createElement('button');
  btnCancel.textContent = 'Cancel';
  btnCancel.className = 'btn-secondary';
  btnCancel.addEventListener('click', () => overlay.remove());

  const btnActivate = document.createElement('button');
  btnActivate.textContent = 'Activate';
  btnActivate.className = 'btn-primary';
  btnActivate.addEventListener('click', () => {
    overlay.remove();
    pilot.passiveUsedThisTurn = true;
    triggerCardEffect(pilot, 'activate', {
      unit,
      onDone: () => { render(); setupDropZones(); maybeAutoEndMainPhase(); },
    });
    render();
    setupDropZones();
  });

  btnRow.appendChild(btnCancel);
  btnRow.appendChild(btnActivate);
  modal.appendChild(btnRow);
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  startSelectionTimer(overlay, () => autoResolveCancelModal(overlay));
}

// Prompt shown when a unit with baseRestSubstitute can absorb the "Rest this
// Base" activation cost. onChoice(true) = rest the unit; onChoice(false) =
// rest the base as normal.
function showBaseRestSubstituteModal(substituteUnit, baseCard, onChoice) {
  const existing = document.getElementById('base-rest-sub-overlay');
  if (existing) existing.remove();

  const overlay = document.createElement('div');
  overlay.id = 'base-rest-sub-overlay';
  overlay.className = 'mk-overlay';

  const modal = document.createElement('div');
  modal.className = 'mk-modal';

  const hideBtn = document.createElement('button');
  hideBtn.className = 'modal-hide-btn';
  hideBtn.title = 'Hide to view field';
  hideBtn.innerHTML = '<span class="modal-hide-label">View Field</span><span class="material-symbols-outlined">ad_off</span>';
  hideBtn.addEventListener('click', (e) => { e.stopPropagation(); hideModalForFieldView(overlay); });
  modal.appendChild(hideBtn);

  const title = document.createElement('div');
  title.className = 'mk-title';
  title.textContent = `${substituteUnit.name}`;
  modal.appendChild(title);

  const body = document.createElement('div');
  body.className = 'mk-body';
  body.style.marginBottom = '14px';
  body.textContent = `Rest ${substituteUnit.name} instead of ${baseCard.name} to pay the activation cost?`;
  modal.appendChild(body);

  const btnRow = document.createElement('div');
  btnRow.className = 'base-activate-btns';

  const btnYes = document.createElement('button');
  btnYes.className = 'pair-btn confirm';
  btnYes.textContent = `Yes — Rest ${substituteUnit.name}`;
  btnYes.addEventListener('click', () => { overlay.remove(); onChoice(true); });

  const btnNo = document.createElement('button');
  btnNo.className = 'pair-btn cancel';
  btnNo.textContent = `No — Rest the Base`;
  btnNo.addEventListener('click', () => { overlay.remove(); onChoice(false); });

  btnRow.appendChild(btnYes);
  btnRow.appendChild(btnNo);
  modal.appendChild(btnRow);
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  startSelectionTimer(overlay, () => autoResolveCancelModal(overlay));
}

function showBaseActivateModal(baseCard) {
  const existing = document.getElementById('base-activate-overlay');
  if (existing) existing.remove();

  const cost = getBaseActivationCost(baseCard);

  const overlay = document.createElement('div');
  overlay.id = 'base-activate-overlay';

  const modal = document.createElement('div');
  modal.id = 'base-activate-modal';

  const hideBtn = document.createElement('button');
  hideBtn.className = 'modal-hide-btn';
  hideBtn.title = 'Hide to view field';
  hideBtn.innerHTML = '<span class="modal-hide-label">View Field</span><span class="material-symbols-outlined">ad_off</span>';
  hideBtn.addEventListener('click', (e) => { e.stopPropagation(); hideModalForFieldView(overlay); });
  modal.appendChild(hideBtn);

  const question = document.createElement('div');
  question.className = 'base-activate-question';
  question.innerHTML = `Would you like to activate <span class="base-activate-name">${baseCard.name}</span>'s effect?`;
  modal.appendChild(question);

  const costLine = document.createElement('div');
  costLine.className = 'base-activate-cost';
  let costText;
  if (cost.exileCommand) costText = 'Exile 1 Command card from your trash';
  else if (cost.restBase && cost.res) costText = `Rest this Base + ${cost.res} RES`;
  else if (cost.restBase) costText = 'Rest this Base';
  else costText = `${cost.res} RES`;
  costLine.textContent = `Activation cost: ${costText}`;
  modal.appendChild(costLine);

  const row = document.createElement('div');
  row.className = 'base-activate-row';

  const imgWrap = document.createElement('div');
  imgWrap.className = 'base-activate-img-wrap';
  const img = document.createElement('img');
  img.className = 'base-activate-img';
  img.src = baseCard.img;
  img.alt = baseCard.name;
  imgWrap.appendChild(img);
  row.appendChild(imgWrap);

  const info = document.createElement('div');
  info.className = 'base-activate-info';
  info.innerHTML = `
    <div class="base-activate-card-name">${baseCard.name}</div>
    <div class="base-activate-stats">
      <div class="cp-row"><span class="cp-label">Level</span><span class="cp-val">${baseCard.level ?? '—'}</span></div>
      <div class="cp-row"><span class="cp-label">Cost</span><span class="cp-val">${baseCard.cost ?? '—'}</span></div>
      ${baseCard.hp != null ? `<div class="cp-row"><span class="cp-label">HP</span><span class="cp-val cp-hp">${baseCard.hp}</span></div>` : ''}
    </div>
    <div class="base-activate-effect-label">Effect</div>
    <div class="base-activate-effect">${baseCard.effect ?? '—'}</div>
  `;
  row.appendChild(info);
  modal.appendChild(row);

  const btns = document.createElement('div');
  btns.className = 'base-activate-btns';

  const btnCancel = document.createElement('button');
  btnCancel.className = 'pair-btn cancel';
  btnCancel.textContent = 'Cancel';
  btnCancel.addEventListener('click', () => overlay.remove());

  const btnActivate = document.createElement('button');
  btnActivate.className = 'pair-btn confirm';
  btnActivate.textContent = 'Activate';
  btnActivate.addEventListener('click', () => {
    overlay.remove();
    const proceed = (exiledTrashUid, effectiveCost) => {
      dispatch('ACTIVATE_BASE', { cost: effectiveCost, baseName: baseCard.name, exiledTrashUid });
      render();
      setupDropZones();
      // [Activate･Main] base effects (see js/effects/) run after the cost is
      // paid above, mirroring the 'main' Command convention — a registered
      // handler is responsible for calling maybeAutoEndMainPhase() itself.
      const handled = triggerCardEffect(baseCard, 'activate', { onDone: maybeAutoEndMainPhase });
      if (!handled) maybeAutoEndMainPhase();
    };
    const afterSubCheck = (effectiveCost) => {
      if (cost.exileCommand) {
        const commandsInTrash = state.playerTrash.filter(c => c.type === 'command' || c.type === 'command pilot');
        showCardPickerModal(
          baseCard.name,
          `Choose 1 Command card from your trash to exile as ${baseCard.name}'s activation cost.`,
          commandsInTrash,
          (picked) => { if (picked) proceed(picked.uid, effectiveCost); }
        );
      } else {
        proceed(null, effectiveCost);
      }
    };
    // If the activation cost includes "Rest this Base", check whether an active
    // unit with baseRestSubstitute (e.g. GD03-079 G-Defenser) is in play —
    // if so, offer the player the choice to rest that unit instead.
    if (cost.restBase) {
      const sub = state.playerField.find(c => c && !c.rested && window.CardEffects?.[c.cardNo]?.baseRestSubstitute);
      if (sub) {
        showBaseRestSubstituteModal(sub, baseCard, (useSubstitute) => {
          if (useSubstitute) {
            sub.rested = true;
            NET.send({ type: 'UNIT_REST', uid: sub.uid, rested: true });
            addLog(`${sub.name} — rested instead of ${baseCard.name}.`);
            render();
            afterSubCheck({ ...cost, restBase: false });
          } else {
            afterSubCheck(cost);
          }
        });
        return;
      }
    }
    afterSubCheck(cost);
  });

  btns.appendChild(btnCancel);
  btns.appendChild(btnActivate);
  modal.appendChild(btns);

  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  startSelectionTimer(overlay, () => autoResolveCancelModal(overlay));

  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
}

// A bot match has no Bo3/lobby concept — "Return to Menu" alone wasn't a
// very useful dead end there, so the win/loss screen instead offers two real
// next steps. Both reload the page rather than calling
// showBotDeckSelectScreen()/showDeckBuilder() directly: BotEngine's own
// pending setTimeout chains (phase timers, botDelay's PER_ACTION_DELAY_MS
// gaps, ...) check isMatchOver() — which just tests whether #victory-overlay
// is still in the DOM — before each step, and that's the ONLY thing that
// stops a still-in-flight callback from the just-finished match acting on
// the new one once this overlay is removed. A full reload is the one way to
// guarantee that can never happen, same as the existing default "Return to
// Menu" path already relies on. POST_MATCH_ACTION_SS_KEY survives the
// reload (consumed once at STARTUP below) so the fresh load lands directly
// on the requested screen instead of the bare main menu.
const POST_MATCH_ACTION_SS_KEY = 'postMatchAction';
function reloadToPostMatchAction(action) {
  sessionStorage.setItem(POST_MATCH_ACTION_SS_KEY, action);
  location.reload();
}

function showVictoryModal(won = true) {
  const overlay = document.createElement('div');
  overlay.id = 'victory-overlay';

  // Set (by showPrivateGamesLobby's NET.onMatchReady) whenever this match
  // came from a private lobby — leaveLobbyMatch() routes the click back to
  // that lobby instead of the main menu, so the button should say so. A Bo3
  // series always says "Continue" here instead — the click handler below
  // decides whether that means sideboarding for the next game or a second,
  // final series-result screen, so the label can't promise either outcome
  // up front.
  const isBotMatchEnd = state.isBotMatch && !_bo3MatchState && !_lobbyResumeState;
  const btnLabel = _bo3MatchState ? 'Continue' : (_lobbyResumeState ? 'Go Back to Lobby' : 'Return to Menu');
  const bo3ScoreLine = _bo3MatchState
    ? `<div class="victory-sub victory-bo3-score">Game ${_bo3MatchState.gameNumber} — Series: You ${_bo3MatchState.gamesWonByMe} - ${_bo3MatchState.gamesWonByOpp} Opponent</div>`
    : '';
  const buttonsHtml = isBotMatchEnd ? `
    <div class="victory-btn-row">
      <button class="victory-btn" id="btn-victory-rebot">Challenge Another Bot</button>
      <button class="victory-btn victory-btn-secondary" id="btn-victory-deckbuilder">Deck Builder</button>
    </div>
    <button class="victory-link-btn" id="btn-victory-menu">Return to Menu</button>
  ` : `<button class="victory-btn" id="btn-victory-menu">${btnLabel}</button>`;

  const modal = document.createElement('div');
  modal.id = 'victory-modal';
  modal.innerHTML = won ? `
    <div class="victory-icon"><span class="material-symbols-outlined">emoji_events</span></div>
    <div class="victory-title">Victory!</div>
    <div class="victory-sub">You defeated the opponent with a direct attack.</div>
    ${bo3ScoreLine}
    ${buttonsHtml}
  ` : `
    <div class="victory-icon"><span class="material-symbols-outlined">heart_broken</span></div>
    <div class="victory-title">Defeat</div>
    <div class="victory-sub">The opponent attacked you directly.</div>
    ${bo3ScoreLine}
    ${buttonsHtml}
  `;

  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  if (isBotMatchEnd) {
    document.getElementById('btn-victory-rebot').addEventListener('click', () => reloadToPostMatchAction('bot-select'));
    document.getElementById('btn-victory-deckbuilder').addEventListener('click', () => reloadToPostMatchAction('deck-builder'));
    document.getElementById('btn-victory-menu').addEventListener('click', () => location.reload());
    return;
  }

  document.getElementById('btn-victory-menu').addEventListener('click', () => {
    if (_bo3MatchState) {
      if (won) _bo3MatchState.gamesWonByMe++; else _bo3MatchState.gamesWonByOpp++;
      const seriesOver = _bo3MatchState.gamesWonByMe === 2 || _bo3MatchState.gamesWonByOpp === 2;
      if (seriesOver) {
        // Swap this same modal's content in place for the final series
        // result rather than removing #victory-overlay outright — matches
        // the non-Bo3 path's "remove only once the player is actually done
        // looking at a result" shape, just with one extra step in between.
        const seriesWon = _bo3MatchState.gamesWonByMe > _bo3MatchState.gamesWonByOpp;
        const finalScore = `${_bo3MatchState.gamesWonByMe} - ${_bo3MatchState.gamesWonByOpp}`;
        _bo3MatchState = null;
        const finalBtnLabel = _lobbyResumeState ? 'Go Back to Lobby' : 'Return to Menu';
        modal.innerHTML = `
          <div class="victory-icon"><span class="material-symbols-outlined">${seriesWon ? 'emoji_events' : 'heart_broken'}</span></div>
          <div class="victory-title">${seriesWon ? 'You Won the Series!' : 'You Lost the Series'}</div>
          <div class="victory-sub">Final score: ${finalScore}</div>
          <button class="victory-btn" id="btn-victory-menu-final">${finalBtnLabel}</button>
        `;
        document.getElementById('btn-victory-menu-final').addEventListener('click', () => {
          overlay.remove();
          if (!leaveLobbyMatch(seriesWon ? 'win' : 'loss')) location.reload();
        });
        return;
      }
      overlay.remove();
      handleBo3GameEnd(won);
      return;
    }
    // Must remove this overlay BEFORE handing off to leaveLobbyMatch()/
    // reload — #victory-overlay sits at z-index 20000, above
    // #private-games-overlay's 11000, so leaving it in the DOM after a
    // lobby-originated leaveLobbyMatch() call still visually blocks the
    // lobby view its resume() just restored underneath. The player, seeing
    // what looks like an unresponsive button, would click it again — by
    // then _lobbyResumeState is already null (cleared on the first call),
    // so leaveLobbyMatch() returns false and the fallback location.reload()
    // fires, dumping them at the main menu instead of back in the lobby.
    overlay.remove();
    if (!leaveLobbyMatch(won ? 'win' : 'loss')) location.reload();
  });
}

// ════════════════════════════════════════════════════════════════════════════
// Trash bookkeeping isn't perfectly symmetric in property naming
// (`trashCount`/`playerTrash` vs `oppTrashCount`/`oppTrash`), so these three
// small lookups translate a logical side ('player'|'opp') into the right keys.
function trashCountKey(side) { return side === 'player' ? 'trashCount' : 'oppTrashCount'; }
function trashArrayKey(side) { return side === 'player' ? 'playerTrash' : 'oppTrash'; }
function trashDomId(side)    { return side === 'player' ? 'trash-count-2' : 'opp-trash-count'; }

// Returns true if a base card is an EX Base. When an EX Base is destroyed it
// triggers its destroyed effect like any base, but is removed from the game
// (exiled) rather than sent to the trash. Consolidated here so every
// destruction path uses one consistent check instead of duplicating fragile
// string logic.
function isExBaseCard(card) {
  if (!card) return false;
  if ((card.type   || '').toUpperCase() === 'EX BASE')  return true;
  if ((card.cardNo || '').toUpperCase().startsWith('EXB')) return true;
  if ((card.name   || '').toLowerCase().includes('ex base')) return true;
  return false;
}

// Returns true for any Base that should never enter the trash — EX Bases
// (isExBaseCard above) plus Base Tokens (categoryData 'unit-base-token',
// e.g. T-037 Axis, deployed by GD06-129 5th Luna's [Destroyed] effect).
// A Base Token isn't part of any deck, same as a Unit token (isUnitToken) —
// it's removed from the game outright whenever it leaves the Base zone.
// card.type is set straight from the DB's categoryData by
// buildBaseCardFromEntry(). Use this (not isExBaseCard alone) at every
// "trash the old/destroyed base, unless..." site.
function isRemovedFromGameBase(card) {
  return isExBaseCard(card) || (!!card && card.type === 'unit-base-token');
}

// `mine: true` (default) is a local attack — attacker on my player side,
// defender on my opp side. `mine: false` is a mirrored remote attack arriving
// over the network — attacker on my opp side, defender on my own player side.
// This parameterization lets one resolver serve both directions without
// duplicating the combat math (see js/network.js for the remote call site).
registerAction('ATTACK', ({ attackerUid, defenderUid, isShield, isDirect, mine = true, skipToDamage = false }) => {
  // GD06-102 "A Miracle" reads this from the non-attacking side's client —
  // set unconditionally regardless of which side attacked or how (including
  // skipToDamage mutual battles), see its reset in CLEAR_TURN_EFFECTS/UNREST_ALL.
  state.attackOccurredThisTurn = true;
  const attackerSide = mine ? 'player' : 'opp';
  const defenderSide  = mine ? 'opp'    : 'player';

  const attacker = state[`${attackerSide}Field`].find(c => c && c.uid === attackerUid);
  let defender;
  if (isDirect) {
    defender = { name: 'Opponent', uid: '__opp-direct__', isDirect: true, bp: 0, hp: 1, img: null };
  } else if (isShield) {
    defender = { name: 'Shield', uid: '__opp-shield__', isShield: true, bp: 0, hp: 1, img: 'assets/images/back.webp' };
  } else {
    defender = state[`${defenderSide}Field`].find(c => c && c.uid === defenderUid) ||
               (state[`${defenderSide}Base`] && state[`${defenderSide}Base`].uid === defenderUid ? state[`${defenderSide}Base`] : null);
  }

  // A stale attackerUid/defenderUid (e.g. the target was returned to hand or
  // otherwise removed by an effect, and this client's mirrored view hadn't
  // caught up yet) must never be allowed to reach the Block/Action Step
  // pipeline as a null ctx.attacker/ctx.defender — every downstream step
  // (runBlockStep's eligible-blocker filter, runDamageStep's stat math)
  // dereferences it unconditionally, and a thrown exception there would
  // abort before ever sending the network response the OTHER client may
  // already be waiting on, hanging their "Waiting on opponent's ___
  // decision…" overlay forever. Fail loudly but cleanly instead, and still
  // unblock a peer that might already be stuck at the Block Step.
  if (!attacker || !defender) {
    console.error('[ATTACK] missing attacker or defender — target was likely already removed', { attackerUid, defenderUid, attacker, defender });
    addLog('Attack could not resolve — the target was already removed.');
    state.attackInProgress = false;
    // This attack never reaches runBlockStep, so bufferedAttackTriggersDone
    // wouldn't otherwise get drained — clear it here so a stray early-arrived
    // flag from this abandoned attempt can't be misread as "already done" by
    // the next real attack (see bufferedAttackTriggersDone's declaration).
    bufferedAttackTriggersDone = false;
    NET.send({ type: 'ATTACK_BLOCK', blockerUid: null });
    render();
    setupDropZones();
    return;
  }

  const ctx = { attacker, defender, attackerSide, defenderSide, isShield, isDirect, mine, skipToDamage };

  // 'defended' trigger (see registry.js) — "this Unit was just attacked",
  // the mirror image of the attacker-side 'attack' trigger. Only a genuine
  // field Unit qualifies (never the synthetic Base/Shield/Direct defender
  // objects built above), and only fires on the defending side's own client.
  if (!isShield && !isDirect && (defender.type === 'unit' || defender.type === 'unit-token') && defenderSide === 'player') {
    triggerCardEffect(defender, 'defended', ctx);
  }

  // Opponent's unit (mine=false): rest it immediately so the defender can see
  // the attack declare. Own unit (mine=true): rest is deferred until after all
  // player-facing modals close, then animated over 2 seconds.
  // skipToDamage attacks (every printed "Begin a battle ... and only perform
  // the damage step" effect — GD05-017 Nu Gundam, ST13-001 Qubeley, ST12-013
  // The Final Victor, GD06-022 Merrybell's Bandit) never rest the attacker:
  // none of those cards' text says to rest anything, and skipping straight to
  // the Damage Step means the normal attack-declare step (the thing that
  // actually rests a unit) never runs at all. This has to hold regardless of
  // which unit ends up attacking — ST12-013/Qubeley hand that choice to a
  // player, so a per-card canStayActiveAfterAttack hook on the attacker's own
  // cardNo (see below) can't cover them the way it covers GD05-017.
  // canStayActiveAfterAttack: optional per-card hook for the OTHER case — a
  // normal (non-skipToDamage) attack that still shouldn't rest the attacker,
  // e.g. EB01-002 Hi-Nu Gundam's "3+ other rested Units in play" condition.
  // Checked here (not just in restAttackerThenDamage) since the mine=false
  // path rests the attacker immediately, before any trigger runs.
  const _staysActive = !!ctx.skipToDamage || !!window.CardEffects?.[attacker.cardNo]?.canStayActiveAfterAttack?.(attacker, ctx);
  if (!mine && !_staysActive) attacker.rested = true;
  state.attackInProgress = true;
  currentAttackCtx = ctx;
  bufferedActionPayload = null; // clear any stale buffer from a previous combat
  bufferedBlockPayload   = null;
  bufferedDestroyedEffectDone = false;
  // bufferedAttackTriggersDone is deliberately NOT reset here (unlike the
  // other buffered-message flags above). ATTACK_TRIGGERS_DONE is sent
  // synchronously from deep inside THIS SAME dispatch('ATTACK', ...) call
  // (see fireAttackerPilotAttackTrigger's continuation below) — for any
  // attacker with no [Attack] trigger of its own (the common case), that
  // send happens before dispatch()'s own onAction() mirror even fires the
  // ATTACK message to the peer, so on the peer's client ATTACK_TRIGGERS_DONE
  // routinely ARRIVES AND GETS BUFFERED before the mirrored ATTACK message
  // that triggers this very handler. Resetting the buffer here unconditionally
  // would wipe out that already-legitimate flag for the attack currently
  // being dispatched, permanently stranding both clients (attacker waiting on
  // its own Block Step, defender waiting on an Attack Step signal that
  // already came and went) on every single attack. It's still safe against
  // staleness from a genuinely abandoned attack — see the "missing attacker
  // or defender" branch above, the only path that can skip past runBlockStep
  // (which always drains this flag itself) without resolving it.

  // skipToDamage: e.g. GD05-017 Nu Gundam's "[When Paired] ... Begin a battle
  // between this Unit and it and only perform the damage step" — the Attack
  // Step's own triggers, the Block Step, and both Action Steps never run at
  // all (so cards gated on those steps can't activate), going straight to
  // restAttackerThenDamage → runDamageStep. Both clients agree to take this
  // shortcut via the mirrored skipToDamage flag (see network.js).
  if (skipToDamage) {
    addLog(`${attacker.name} begins a battle with ${defender.name} — Attack, Action, and Block Steps are skipped.`);
    render();
    setupDropZones();
    restAttackerThenDamage(ctx);
    return;
  }

  addLog(`${attacker.name} attacked ${defender.name}.`);
  addLog('Attack Step.');
  render();
  setupDropZones();

  fireAttackerUnitAttackTrigger(ctx, () =>
    fireAttackerPilotAttackTrigger(ctx, () => {
      // Tell the defender it's now safe to open the Block prompt — only the
      // real attacker's own client ever runs the two trigger functions above
      // to completion (the mirrored dispatch on the defender's client skips
      // them immediately, see their attackerSide-gate), so without this the
      // defender has no way to know an [Attack] trigger like GD05-049
      // Sazabi's is still resolving on the attacker's side and would pop the
      // Block modal immediately upon receiving ATTACK instead of waiting.
      if (ctx.attackerSide === 'player' && state.networked) {
        NET.send({ type: 'ATTACK_TRIGGERS_DONE' });
      }
      runBlockStep(ctx, () =>
        runActionStep(ctx, 'defender', () =>
          runActionStep(ctx, 'attacker', () =>
            restAttackerThenDamage(ctx))));
    }));
});

// After all player-facing modals have closed, animate the attacking unit
// rotating from active to rested over 2 seconds, then proceed to damage.
// For opponent attacks (mine=false) the unit was already rested at the start
// of the handler, so this just calls runDamageStep directly.
function restAttackerThenDamage(ctx) {
  const { attacker, attackerSide, mine } = ctx;

  if (!mine) { runDamageStep(ctx); return; }

  // skipToDamage never rests the attacker (see the matching check in
  // registerAction('ATTACK', ...) above for why) regardless of which unit it
  // turned out to be; canStayActiveAfterAttack is the per-card hook for a
  // normal attack that still shouldn't rest the attacker (e.g. EB01-002
  // Hi-Nu Gundam).
  const staysActive = !!ctx.skipToDamage || !!window.CardEffects?.[attacker.cardNo]?.canStayActiveAfterAttack?.(attacker, ctx);

  const idx    = state[`${attackerSide}Field`].indexOf(attacker);
  const slotEl = idx !== -1 ? document.getElementById(`${attackerSide}-slot-${idx}`) : null;

  if (staysActive) { runDamageStep(ctx); return; }

  if (!slotEl) {
    attacker.rested = true;
    render();
    runDamageStep(ctx);
    return;
  }

  slotEl.classList.add('resting-anim');

  setTimeout(() => {
    attacker.rested = true;
    render(); // rebuilds slot with .rested class, replaces resting-anim
    runDamageStep(ctx);
  }, 500);
}

// ── Attack Trigger (unit [Attack]) ──
// Fires FIRST in the attack step — before the pilot trigger and before the
// Block Step. Same bufferedBlockPayload race protection applies here as for
// the pilot trigger. Only fires on the local attacker's client; the opponent's
// mirrored ATTACK dispatch calls next() immediately.
function fireAttackerUnitAttackTrigger(ctx, next) {
  const { attacker, attackerSide } = ctx;
  if (attackerSide !== 'player') { next(); return; }
  // Notify other field units that a friendly unit is attacking (e.g. GD03-002 The-O)
  state.playerField.forEach(watcher => {
    if (!watcher || watcher.uid === attacker.uid) return;
    window.CardEffects?.[watcher.cardNo]?.onFriendlyUnitAttacks?.(watcher, attacker, ctx);
  });
  const handlers = window.CardEffects?.[attacker.cardNo];
  if (!handlers?.attack) { next(); return; }
  triggerCardEffect(attacker, 'attack', { ...ctx, onDone: next });
}

// ── Attack Trigger (pilot [During Link][Attack]) ──
// Fires during the Attack Step — BEFORE the Block Step — so the correct game
// sequence is maintained: Attack → Block → Action → Damage.
// The race that previously forced it after the Block Step is solved by
// bufferedBlockPayload: if the defender sends ATTACK_BLOCK while this picker
// is still open (before runBlockStep registers pendingBlockResolve), the uid
// is stored in bufferedBlockPayload and drained immediately when runBlockStep
// runs on the attacker's client.
// Only fires on the local player's attacker (attackerSide === 'player') — the
// opponent's mirrored flow calls next() immediately and proceeds to runBlockStep.
// If the trigger effect removes ctx.defender from the field, runDamageStep
// will detect this and exit cleanly — no explicit battle-cancel signal needed.
function fireAttackerPilotAttackTrigger(ctx, next) {
  const { attacker, attackerSide } = ctx;
  if (attackerSide !== 'player') { next(); return; }
  if (!attacker.pilot) { next(); return; }
  const handlers = window.CardEffects?.[attacker.pilot.cardNo];
  if (!handlers?.attack) { next(); return; }
  triggerCardEffect(attacker.pilot, 'attack', { ...ctx, onDone: next });
}

// ── Block Step ──
// Only the client that owns the defending side's real data (defenderSide
// === 'player') decides whether to block — the same rule resolveMyShieldHit
// uses for shield privacy. The dummy solo-practice opponent never blocks.
function runBlockStep(ctx, next) {
  if (ctx.defenderSide === 'player') {
    // Wait for ATTACK_TRIGGERS_DONE before ever showing the Block prompt —
    // see bufferedAttackTriggersDone's declaration for why: the attacker's
    // own unit/pilot [Attack] triggers (e.g. GD05-049 Sazabi) resolve only on
    // their client, and without this gate this client would open the Block
    // modal immediately upon receiving ATTACK, racing that still-open picker.
    const proceedToBlockDecision = () => {
      const original = ctx.defender;
      // cantBlockLevelCeiling: optional per-attacker hook (e.g. EB01-042 Psycho
      // Haro's "[Attack] Units that are Lv.7 or lower can't activate <Blocker>
      // during this battle") — units at or below the returned level are
      // excluded from the eligible-blocker pool for this attack. Default is -1
      // (no restriction) rather than 0 — Unit tokens carry no level (parses to
      // 0), so a 0 default would wrongly exclude every token Blocker from ever
      // blocking even with no such ability in play.
      const cantBlockCeiling = window.CardEffects?.[ctx.attacker.cardNo]?.cantBlockLevelCeiling?.(ctx.attacker) ?? -1;
      // [High-Maneuver] on the attacker means no Blocker can redirect the attack.
      // tempCantBlock: "can't activate <Blocker> during this turn" (e.g.
      // GD05-127 Girty Lue), independent of cantBlockLevelCeiling.
      // effectiveHp(c) > 0: a Unit killed by effect damage during THIS SAME
      // Attack Step (e.g. ST04-006 Aegis Gundam's own [Attack] trigger) isn't
      // actually removed from its field slot until a ~500ms vibrate/destroy
      // animation finishes (see dealDamageToUnit and FIELD_HP_MOD's handler
      // in network.js) — but ATTACK_TRIGGERS_DONE fires as soon as the
      // trigger's JS callback completes, well before that animation (local
      // OR the peer's own mirrored one via FIELD_HP_MOD) has actually
      // cleared the slot. Without this check, a Blocker unit the attacker
      // just destroyed could still be offered as a block candidate.
      const eligible = hasNativeKeyword(ctx.attacker, 'High-Maneuver') ? [] : state.playerField.filter(c =>
        c && !c.rested && c.uid !== original.uid && hasNativeKeyword(c, 'Blocker') && !c.tempCantBlock &&
        effectiveHp(c) > 0 && (parseInt(c.level) || 0) > cantBlockCeiling);
      if (eligible.length === 0) {
        NET.send({ type: 'ATTACK_BLOCK', blockerUid: null });
        next();
        return;
      }
      showBlockPromptModal(eligible, ctx.attacker, (blockerUid) => {
        applyBlockDecision(ctx, blockerUid);
        NET.send({ type: 'ATTACK_BLOCK', blockerUid: blockerUid || null });
        next();
      });
    };
    // state.isBotMatch: there's no second client to ever send
    // ATTACK_TRIGGERS_DONE when the bot is the attacker — the wait below
    // exists purely to let a REAL peer's own attack-trigger modal (e.g.
    // Sazabi) finish resolving on their client before this one opens the
    // Block prompt. A bot has no such cross-client race (its own attacker
    // trigger, if any, already ran synchronously in this same call chain,
    // immediately above), so proceed straight through instead of waiting
    // forever on a message that will never arrive.
    if (bufferedAttackTriggersDone || state.isBotMatch) {
      bufferedAttackTriggersDone = false;
      proceedToBlockDecision();
    } else {
      showDecisionWaitingOverlay("Waiting on opponent's Attack Step…");
      pendingAttackTriggersResolve = () => {
        hideDecisionWaitingOverlay();
        proceedToBlockDecision();
      };
      armStuckWaitWatchdog(() => pendingAttackTriggersResolve, (fn) => { pendingAttackTriggersResolve = null; fn(); });
    }
  } else if (state.isBotMatch) {
    BotEngine.decideBlock(ctx, next);
  } else if (!state.networked) {
    next(); // dummy opponent never blocks
  } else {
    // If ATTACK_BLOCK already arrived while the [Attack] trigger picker was open
    // (bufferedBlockPayload set in onAttackBlock), drain it immediately instead
    // of registering a new wait — same pattern as bufferedActionPayload.
    if (bufferedBlockPayload !== null) {
      const { blockerUid } = bufferedBlockPayload;
      bufferedBlockPayload = null;
      applyBlockDecision(ctx, blockerUid);
      next();
      return;
    }
    showDecisionWaitingOverlay("Waiting on opponent's block decision…");
    pendingBlockResolve = (blockerUid) => {
      hideDecisionWaitingOverlay();
      applyBlockDecision(ctx, blockerUid);
      next();
    };
    armStuckWaitWatchdog(() => pendingBlockResolve, (fn) => { pendingBlockResolve = null; fn(null); });
  }
}

// Generic "the peer is mid-decision in a modal I can't see" overlay — shown
// any time this client is blocked on a pendingXResolve waiting for the
// opponent to act (Block Step, Action Step, ...), so the local player always
// has feedback instead of a frozen-looking board.
function showDecisionWaitingOverlay(message, activatedCard = null) {
  let overlay = document.getElementById('decision-waiting-overlay');
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.id = 'decision-waiting-overlay';
    document.body.appendChild(overlay);
  }
  overlay.innerHTML = `
    <div class="mk-modal">
      <button class="modal-hide-btn" title="Hide to view field"><span class="modal-hide-label">View Field</span><span class="material-symbols-outlined">ad_off</span></button>
      ${activatedCard ? `
      <div class="dwo-activated-card">
        <div class="dwo-card-img-wrap">
          ${activatedCard.img ? `<img src="${activatedCard.img}" alt="${activatedCard.name}" />` : '<div class="dwo-no-img">?</div>'}
        </div>
        <div class="dwo-card-info">
          <div class="dwo-card-name">${activatedCard.name}</div>
          <div class="dwo-card-meta">${[activatedCard.type, activatedCard.color].filter(Boolean).map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' · ')}</div>
          ${activatedCard.effect ? `<div class="dwo-card-effect">${activatedCard.effect}</div>` : ''}
        </div>
      </div>` : ''}
      <div class="mk-spinner"></div>
      <div class="mk-title">${message}</div>
    </div>`;
  overlay.querySelector('.modal-hide-btn').addEventListener('click', (e) => {
    e.stopPropagation();
    hideModalForFieldView(overlay);
  });
}

function hideDecisionWaitingOverlay() {
  const overlay = document.getElementById('decision-waiting-overlay');
  if (overlay) overlay.remove();
  restoreHiddenModal();
}

// Redirects the attack to the chosen blocker — fully replaces the original
// target (shield/base/direct attacks become a plain unit-vs-unit fight).
function applyBlockDecision(ctx, blockerUid) {
  if (!blockerUid) { addLog('No block declared.'); return; }
  const blocker = state[`${ctx.defenderSide}Field`].find(c => c && c.uid === blockerUid);
  if (!blocker) return;
  // The attacker's own client is authoritative for combat outcomes (see
  // HP_RECONCILE's comment — "the attacker is authoritative for HP and unit
  // survival"). The defender's ATTACK_BLOCK naming a unit that's already
  // dead by the ATTACKER'S OWN reckoning means the defender's local view of
  // an earlier battle hasn't caught up yet (the two clients' independent
  // combat replays briefly disagreed — the exact scenario HP_RECONCILE
  // exists to correct). Reject it here as a defensive backstop instead of
  // letting an already-dead Unit "block" (and get destroyed) a second time
  // before that correction lands.
  if (effectiveHp(blocker) <= 0) {
    addLog(`${blocker.name} is already destroyed — treating as no block declared.`);
    return;
  }
  blocker.rested = true;
  addLog(`${blocker.name} blocks the attack!`);
  // onLinkedUnitBlockerActivated: standing pilot passive for "when this Unit
  // activates <Blocker>, ..." (e.g. GD06-097 Harry Ord — "this Unit" is the
  // Unit the pilot is paired with, not the pilot itself). Only runs on the
  // blocking player's own client — applyBlockDecision also runs as a mirror
  // on the attacker's client (ctx.defenderSide === 'opp' there), which has no
  // authority over the blocker's own field.
  if (ctx.defenderSide === 'player' && blocker.pilot) {
    window.CardEffects?.[blocker.pilot.cardNo]?.onLinkedUnitBlockerActivated?.(blocker.pilot, blocker);
  }
  ctx.defender = blocker;
  ctx.isShield = false;
  ctx.isDirect = false;
  render();
  setupDropZones();
}

// Renders an image + live ATK/HP stat overlay for a unit or base card tile.
// Used by showBlockPromptModal, showCardPickerModal, showCardPickerModalMulti,
// and showBaseOrderPrompt for any card whose type is 'unit', 'unit-token', or
// 'base' — effective stats (pilot bonus + temp modifiers) are shown so the
// player sees the real field values. Base cards have no printed ATK stat, so
// that badge is omitted for them — same convention as buildCardEl's on-field
// card rendering (see its _isBase comment).
function statTileHtml(card) {
  const isBase      = card.type === 'base';
  const effectiveAp = effectiveBp(card);
  const apBoost     = effectiveAp - (parseInt(card.bp) || 0);
  const effectiveHpVal = effectiveHp(card);
  const hpBoost     = effectiveHpVal - (parseInt(card.hp) || 0);
  return `
    ${card.img ? `<img src="${card.img}" alt="${card.name}" />` : '<div class="cp-no-img">?</div>'}
    ${card.pilot ? `<span class="material-symbols-outlined card-pilot-badge">${isLinkUnit(card) ? 'link' : 'handshake'}</span>` : ''}
    <div class="card-kw-badges">
      ${hasKwIcon(card, 'Blocker')       ? '<span class="material-symbols-outlined card-kw-badge" title="Blocker">shield</span>'               : ''}
      ${hasKwIcon(card, 'Breach')        ? `<span class="card-kw-badge card-kw-badge-valued" title="Breach ${getBreachValue(card)}"><span class="material-symbols-outlined card-kw-badge-icon">destruction</span>${getBreachValue(card)}</span>` : ''}
      ${hasKwIcon(card, 'High-Maneuver') ? '<span class="material-symbols-outlined card-kw-badge" title="High-Maneuver">turn_sharp_right</span>' : ''}
      ${hasKwIcon(card, 'First Strike')  ? '<span class="material-symbols-outlined card-kw-badge" title="First Strike">bolt</span>'              : ''}
      ${hasKwIcon(card, 'Support')       ? '<span class="material-symbols-outlined card-kw-badge" title="Support">fitness_center</span>'         : ''}
      ${hasKwIcon(card, 'Repair')        ? `<span class="card-kw-badge card-kw-badge-valued" title="Repair ${getRepairValue(card)}"><span class="material-symbols-outlined card-kw-badge-icon">build</span>${getRepairValue(card)}</span>` : ''}
      ${hasKwIcon(card, 'Suppression')   ? '<span class="material-symbols-outlined card-kw-badge" title="Suppression">stacks</span>'             : ''}
    </div>
    <div class="card-stats">
      ${isBase ? '' : `<div class="stat${apBoost > 0 ? ' boosted' : apBoost < 0 ? ' reduced' : ''}">${apBoost > 0 ? '<span class="material-symbols-outlined stat-boost-icon" title="AP increased">keyboard_double_arrow_up</span>' : ''}${effectiveAp}</div>`}
      <div class="stat${hpBoost ? ' boosted' : ''}${card.damaged ? ' damaged' : ''}">${hpBoost > 0 ? '<span class="material-symbols-outlined stat-boost-icon" title="HP increased">keyboard_double_arrow_up</span>' : ''}${effectiveHpVal}</div>
    </div>`;
}

// The .bop-tile modifier class for a field card's current rested/sickness
// state — rested takes priority (a rested card is never usefully "also
// sick", and its rotated+greyscale treatment already reads as unavailable)
// over tempCantAttack (active but barred from attacking this turn, e.g.
// summoning sickness), which gets its own greyscale-only treatment. Used by
// every picker/prompt that lists field units/bases as statTileHtml tiles.
function tileStateClass(card) {
  if (card.rested) return ' bop-rested';
  if (card.tempCantAttack) return ' bop-cant-attack';
  return '';
}

function showBlockPromptModal(eligibleBlockers, attacker, onChoice) {
  const overlay = document.createElement('div');
  overlay.id = 'ingame-menu-overlay';

  const modal = document.createElement('div');
  modal.id = 'ingame-menu-modal';
  modal.classList.add('bop-modal');

  let pickedIdx = -1;

  function renderTiles() {
    modal.innerHTML = `
      <button class="modal-hide-btn" title="Hide to view field"><span class="modal-hide-label">View Field</span><span class="material-symbols-outlined">ad_off</span></button>
      <div class="igm-title">Block the attack?</div>
      <div class="igm-body">${attacker.name} is attacking. Choose an active Blocker unit to redirect the attack onto it, or skip.</div>
      <div class="bop-block-row">
        <div class="bop-tile bop-attacker-tile">
          <div class="bop-order-label">Attacking</div>
          <div class="cp-img-wrap">${statTileHtml(attacker)}</div>
          <div class="bop-name">${attacker.name}</div>
        </div>
        <div class="pair-arrow"><span class="material-symbols-outlined">double_arrow</span></div>
        <div class="bop-shield-icon"><span class="material-symbols-outlined">shield</span></div>
        <div class="bop-row">
          ${eligibleBlockers.map((card, i) => `
            <div class="bop-tile${i === pickedIdx ? ' bop-selected' : ''}${tileStateClass(card)}" data-idx="${i}">
              <div class="bop-order-label">${i === pickedIdx ? 'Block Attack' : ''}</div>
              <div class="cp-img-wrap">${statTileHtml(card)}</div>
              <div class="bop-name">${card.name}</div>
            </div>`).join('')}
        </div>
      </div>
      <div class="igm-btn-row">
        <button class="igm-btn igm-skip">Skip</button>
        <button class="igm-btn igm-confirm-base"${pickedIdx === -1 ? ' disabled' : ''}>Confirm Block</button>
      </div>
    `;
    modal.querySelector('.modal-hide-btn').addEventListener('click', (e) => {
      e.stopPropagation();
      hideModalForFieldView(overlay);
    });
    // Hover preview on every tile (attacker included) so the picker can
    // inspect full card text/traits before committing to a block.
    modal.querySelector('.bop-attacker-tile').addEventListener('mouseenter', e => showCardPreview(attacker, e.currentTarget));
    modal.querySelector('.bop-attacker-tile').addEventListener('mouseleave', hideCardPreview);
    modal.querySelectorAll('.bop-tile[data-idx]').forEach(tile => {
      const card = eligibleBlockers[parseInt(tile.dataset.idx, 10)];
      tile.addEventListener('mouseenter', e => showCardPreview(card, e.currentTarget));
      tile.addEventListener('mouseleave', hideCardPreview);
      tile.addEventListener('click', () => {
        const idx = parseInt(tile.dataset.idx, 10);
        pickedIdx = pickedIdx === idx ? -1 : idx;
        renderTiles();
      });
    });
    modal.querySelector('.igm-skip').addEventListener('click', () => {
      overlay.remove();
      restoreHiddenModal();
      hideCardPreview();
      onChoice(null);
    });
    modal.querySelector('.igm-confirm-base').addEventListener('click', () => {
      if (pickedIdx === -1) return;
      overlay.remove();
      restoreHiddenModal();
      hideCardPreview();
      onChoice(eligibleBlockers[pickedIdx].uid);
    });
  }

  renderTiles();
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  startSelectionTimer(overlay, () => autoResolveIgmModal(overlay));
}

// Generic single-select picker over an already-filtered list of cards —
// used by card effects that search a zone (e.g. trash) for a card matching
// some condition and offer it up. Caller is responsible for filtering
// `cards` first and skipping the call entirely if the list is empty.
// onChoice(card|null) — null means the player hit Skip.
// opts.mandatory = true  — hides the Skip button; the player MUST pick a card.
//   Use for effects whose text has no "you may" qualifier.
// ── PICKER MODAL QUEUE ───────────────────────────────────────────────────────
// Multiple card effects can fire "simultaneously" — synchronously, back to
// back in the same tick — each wanting to show its own picker (e.g. GD04-033
// Neo Zeong: deploying a SECOND copy fires both its own [Deploy] pick AND
// the first copy's onAnyPlayerUnitDeployed pick within the same PLAY_CARD
// call, before either has a chance to resolve — same thing happens when one
// picker's own onChoice callback opens a follow-up picker, e.g. GD02-058
// Ryusei-Go's "pick a Unit to damage, THEN pick a hand card to discard").
// Showing them both at once would stack two #ingame-menu-overlay/
// #ingame-menu-modal elements (duplicate ids) and silently break one of
// them. Instead, any showCardPickerModal(Multi) call that arrives while a
// picker is already open gets queued; once the active one resolves, the
// next queued request's candidates are re-validated against CURRENT
// field/base state before it's shown — otherwise a unit the first picker
// just destroyed could still appear (and be selectable) in the second,
// already-stale, candidate list built before that destruction.
//
// That re-validation must only apply to candidates that actually WERE field/
// base occupants when the request was queued — snapshotted then, by uid, not
// guessed from `.type` at dequeue time. An earlier version filtered any
// `type === 'unit'` candidate against playerField/oppField presence
// unconditionally, which wrongly nuked Ryusei-Go's *hand* discard picker:
// every Unit-type card sitting in hand (never on a field to begin with) got
// treated as an invalidated field target and silently dropped, leaving only
// pilot/command cards selectable. A candidate never on a field/base at
// queue-time is never subject to this check, regardless of its `.type`.
let _pickerModalBusy = false;
const _pickerModalQueue = [];

function _isOnFieldOrBase(c) {
  if (!c) return false;
  return state.playerField.some(f => f && f.uid === c.uid) ||
         state.oppField.some(f => f && f.uid === c.uid) ||
         (state.playerBase && state.playerBase.uid === c.uid) ||
         (state.oppBase && state.oppBase.uid === c.uid);
}

function _runNextQueuedPickerModal() {
  if (_pickerModalBusy || _pickerModalQueue.length === 0) return;
  const next = _pickerModalQueue.shift();
  const freshCards = next.cards.filter(c => !next.wasOnFieldUids.has(c.uid) || _isOnFieldOrBase(c));
  // Every field/base candidate got invalidated while this request was
  // queued (e.g. the picker just ahead of it in line destroyed the only
  // eligible target) — showing an empty picker would softlock a mandatory
  // one (no Skip button, Confirm permanently disabled with nothing to
  // select). Resolve as "no choice" instead, same as any other effect
  // finding zero valid targets.
  if (freshCards.length === 0) {
    next.onChoice(null);
    return;
  }
  _pickerModalBusy = true;
  next.impl(next.title, next.body, freshCards, next.onChoice, next.activatorCard, next.opts);
}

function showCardPickerModal(title, body, cards, onChoice, activatorCard = null, opts = {}) {
  // Bot-owned effect choice — resolved programmatically by bot-engine.js,
  // never shown to the human. Bypasses the busy/queue machinery entirely
  // since it resolves synchronously with no modal ever opening.
  if (isBotControlledCard(activatorCard)) {
    onChoice(BotEngine.resolvePick(title, cards, opts, activatorCard));
    return;
  }
  const queuedOnChoice = (picked) => {
    onChoice(picked);
    _pickerModalBusy = false;
    _runNextQueuedPickerModal();
  };
  if (_pickerModalBusy) {
    const wasOnFieldUids = new Set(cards.filter(_isOnFieldOrBase).map(c => c.uid));
    _pickerModalQueue.push({ impl: _showCardPickerModalImpl, title, body, cards, wasOnFieldUids, onChoice: queuedOnChoice, activatorCard, opts });
    return;
  }
  _pickerModalBusy = true;
  _showCardPickerModalImpl(title, body, cards, queuedOnChoice, activatorCard, opts);
}

function _showCardPickerModalImpl(title, body, cards, onChoice, activatorCard = null, opts = {}) {
  // Wrap onChoice so the ambient "which card's effect is resolving" context
  // (see currentEffectSourceCard) is set correctly for shared low-level
  // mutators (dealDamageToUnit, setRested, bounceEnemyUnit, ...) called from
  // inside onChoice — that callback normally fires asynchronously (a later
  // user click), well after triggerCardEffect's own synchronous context has
  // already unwound, so it needs to be re-established here from the
  // (reliably passed) activatorCard.
  const wrappedOnChoice = (picked) => {
    const prevSource = currentEffectSourceCard;
    if (activatorCard) currentEffectSourceCard = activatorCard;
    try {
      onChoice(picked);
    } finally {
      currentEffectSourceCard = prevSource;
    }
  };

  // Single mandatory choice — no need to show a picker. This is the "skips
  // selection because there's only 1 target" case — tell the player what
  // got auto-targeted instead of silently resolving.
  if (cards.length === 1 && !!opts.mandatory) {
    if (activatorCard) broadcastCardToast(`${activatorCard.name} effect targeted ${cards[0].name}.`);
    wrappedOnChoice(cards[0]);
    return;
  }

  if (state.networked) NET.send({ type: 'PICKER_OPEN', card: activatorCard });
  const overlay = document.createElement('div');
  overlay.id = 'ingame-menu-overlay';

  const modal = document.createElement('div');
  modal.id = 'ingame-menu-modal';
  modal.classList.add('bop-modal');

  const mandatory = !!opts.mandatory;
  const skipLabel = opts.skipLabel || 'Skip';
  const rowClass = cards.length > 5 ? 'bop-grid' : 'bop-row';
  // Cards shown for information only (e.g. the non-qualifying members of a
  // "look at top 3, may reveal 1" peek) — visible but not selectable.
  const disabledIdx = new Set(opts.disabledIndices || []);
  let pickedIdx = -1;

  function renderTiles() {
    modal.innerHTML = `
      <button class="modal-hide-btn" title="Hide to view field"><span class="modal-hide-label">View Field</span><span class="material-symbols-outlined">ad_off</span></button>
      <div class="igm-title">${title}</div>
      <div class="igm-body">${body}</div>
      <div class="${rowClass}">
        ${cards.map((card, i) => `
          <div class="bop-tile${i === pickedIdx ? ' bop-selected' : ''}${tileStateClass(card)}${disabledIdx.has(i) ? ' bop-unselectable' : ''}" data-idx="${i}">
            <div class="bop-order-label">${i === pickedIdx ? 'Selected' : (disabledIdx.has(i) ? 'Not eligible' : '')}</div>
            <div class="cp-img-wrap">
              ${(card.type === 'unit' || card.type === 'unit-token' || card.type === 'base') ? statTileHtml(card) : (card.img ? `<img src="${card.img}" alt="${card.name}" />` : '<div class="cp-no-img">?</div>')}
            </div>
            <div class="bop-name">${card.name}</div>
          </div>`).join('')}
      </div>
      <div class="igm-btn-row">
        ${mandatory ? '' : `<button class="igm-btn igm-skip">${skipLabel}</button>`}
        <button class="igm-btn igm-confirm-base"${pickedIdx === -1 ? ' disabled' : ''}>Confirm</button>
      </div>
    `;
    modal.querySelector('.modal-hide-btn').addEventListener('click', (e) => {
      e.stopPropagation();
      hideModalForFieldView(overlay);
    });
    modal.querySelectorAll('.bop-tile').forEach(tile => {
      const card = cards[parseInt(tile.dataset.idx, 10)];
      tile.addEventListener('mouseenter', e => showCardPreview(card, e.currentTarget));
      tile.addEventListener('mouseleave', hideCardPreview);
      tile.addEventListener('click', () => {
        const idx = parseInt(tile.dataset.idx, 10);
        if (disabledIdx.has(idx)) return;
        pickedIdx = pickedIdx === idx ? -1 : idx;
        // Update selection state in-place to preserve scroll position.
        modal.querySelectorAll('.bop-tile[data-idx]').forEach(t => {
          const i = parseInt(t.dataset.idx, 10);
          const isSel = i === pickedIdx;
          t.classList.toggle('bop-selected', isSel);
          t.querySelector('.bop-order-label').textContent = isSel ? 'Selected' : (disabledIdx.has(i) ? 'Not eligible' : '');
        });
        const confirmBtn = modal.querySelector('.igm-confirm-base');
        if (confirmBtn) confirmBtn.disabled = pickedIdx === -1;
      });
    });
    modal.querySelector('.igm-skip')?.addEventListener('click', () => {
      overlay.remove();
      restoreHiddenModal();
      hideCardPreview();
      if (state.networked) NET.send({ type: 'PICKER_DONE' });
      wrappedOnChoice(null);
    });
    modal.querySelector('.igm-confirm-base').addEventListener('click', () => {
      if (pickedIdx === -1) return;
      overlay.remove();
      restoreHiddenModal();
      hideCardPreview();
      if (state.networked) NET.send({ type: 'PICKER_DONE' });
      wrappedOnChoice(cards[pickedIdx]);
    });
  }

  renderTiles();
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  startSelectionTimer(overlay, () => autoResolveIgmModal(overlay));
}

// Multi-select variant of showCardPickerModal — toggles selection up to
// `max`, Confirm enables once `selected.size >= min`, Skip always returns [].
function showCardPickerModalMulti(title, body, cards, opts, onChoice, activatorCard = null) {
  // See showCardPickerModal's identical bot-interception guard above.
  if (isBotControlledCard(activatorCard)) {
    onChoice(BotEngine.resolvePickMulti(title, cards, opts, activatorCard));
    return;
  }
  const queuedOnChoice = (picked) => {
    onChoice(picked);
    _pickerModalBusy = false;
    _runNextQueuedPickerModal();
  };
  if (_pickerModalBusy) {
    const wasOnFieldUids = new Set(cards.filter(_isOnFieldOrBase).map(c => c.uid));
    _pickerModalQueue.push({ impl: _showCardPickerModalMultiImpl, title, body, cards, wasOnFieldUids, onChoice: queuedOnChoice, activatorCard, opts });
    return;
  }
  _pickerModalBusy = true;
  _showCardPickerModalMultiImpl(title, body, cards, queuedOnChoice, activatorCard, opts);
}

function _showCardPickerModalMultiImpl(title, body, cards, onChoice, activatorCard, { min, max, layout = 'row', mandatory = false }) {
  // See showCardPickerModal's identical wrapper for why this is needed.
  const wrappedOnChoice = (picked) => {
    const prevSource = currentEffectSourceCard;
    if (activatorCard) currentEffectSourceCard = activatorCard;
    try {
      onChoice(picked);
    } finally {
      currentEffectSourceCard = prevSource;
    }
  };

  if (state.networked) NET.send({ type: 'PICKER_OPEN', card: activatorCard });
  const overlay = document.createElement('div');
  overlay.id = 'ingame-menu-overlay';

  const modal = document.createElement('div');
  modal.id = 'ingame-menu-modal';
  modal.classList.add('bop-modal');

  const selected = new Set();
  const rowClass = cards.length > 5 ? 'bop-grid' : 'bop-row';

  function renderTiles() {
    modal.innerHTML = `
      <button class="modal-hide-btn" title="Hide to view field"><span class="modal-hide-label">View Field</span><span class="material-symbols-outlined">ad_off</span></button>
      <div class="igm-title">${title}</div>
      <div class="igm-body">${body}</div>
      <div class="${rowClass}">
        ${cards.map((card, i) => `
          <div class="bop-tile${selected.has(i) ? ' bop-selected' : ''}${tileStateClass(card)}" data-idx="${i}">
            <div class="bop-order-label">${selected.has(i) ? 'Selected' : ''}</div>
            <div class="cp-img-wrap">
              ${(card.type === 'unit' || card.type === 'unit-token' || card.type === 'base') ? statTileHtml(card) : (card.img ? `<img src="${card.img}" alt="${card.name}" />` : '<div class="cp-no-img">?</div>')}
            </div>
            <div class="bop-name">${card.name}</div>
          </div>`).join('')}
      </div>
      <div class="igm-btn-row">
        ${!mandatory ? '<button class="igm-btn igm-skip">Skip</button>' : ''}
        <button class="igm-btn igm-confirm-base"${selected.size < min ? ' disabled' : ''}>Confirm</button>
      </div>
    `;
    modal.querySelector('.modal-hide-btn').addEventListener('click', (e) => {
      e.stopPropagation();
      hideModalForFieldView(overlay);
    });
    modal.querySelectorAll('.bop-tile').forEach(tile => {
      const idx = parseInt(tile.dataset.idx, 10);
      const card = cards[idx];
      tile.addEventListener('mouseenter', e => showCardPreview(card, e.currentTarget));
      tile.addEventListener('mouseleave', hideCardPreview);
      tile.addEventListener('click', () => {
        if (selected.has(idx)) {
          selected.delete(idx);
        } else if (selected.size < max) {
          selected.add(idx);
        }
        // Update selection state in-place to preserve the container's scroll position.
        modal.querySelectorAll('.bop-tile[data-idx]').forEach(t => {
          const i = parseInt(t.dataset.idx, 10);
          const isSel = selected.has(i);
          t.classList.toggle('bop-selected', isSel);
          t.querySelector('.bop-order-label').textContent = isSel ? 'Selected' : '';
        });
        const confirmBtn = modal.querySelector('.igm-confirm-base');
        if (confirmBtn) confirmBtn.disabled = selected.size < min;
      });
    });
    if (!mandatory) {
      modal.querySelector('.igm-skip').addEventListener('click', () => {
        overlay.remove();
        restoreHiddenModal();
        hideCardPreview();
        if (state.networked) NET.send({ type: 'PICKER_DONE' });
        wrappedOnChoice([]);
      });
    }
    modal.querySelector('.igm-confirm-base').addEventListener('click', () => {
      if (selected.size < min) return;
      overlay.remove();
      restoreHiddenModal();
      hideCardPreview();
      if (state.networked) NET.send({ type: 'PICKER_DONE' });
      wrappedOnChoice([...selected].map(i => cards[i]));
    });
  }

  renderTiles();
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  startSelectionTimer(overlay, () => autoResolveIgmModal(overlay));
}

// ── Action Step ──
// Runs once for 'defender' then once for 'attacker'. Only Command / Command
// Pilot cards from hand are eligible (no field unit in the database has a
// self-activated [Action] ability with a cost — see plan notes).
function getActionEligibleHandCards(ctx) {
  const totalRes = state.res + state.exRes;
  const playerLevel = state.resBase + state.exRes + state.exResRested;
  return state.hand.filter(c =>
    (c.type === 'command' || c.type === 'command pilot') &&
    (c.effectRaw || '').includes('[Action]') &&
    (parseInt(c.cost) || 0) <= totalRes &&
    (parseInt(c.level) || 0) <= playerLevel &&
    cardEffectCanActivate(c, ctx));
}

// Field units whose paired pilot has a usable [Activate･Action] effect.
// Returns the unit objects (not the pilots) — pilot is accessible via unit.pilot.
function getActionEligiblePilots(ctx) {
  return state.playerField.filter(unit => {
    if (!unit || !unit.pilot) return false;
    const pilot = unit.pilot;
    if (!(pilot.effectRaw || '').includes('[Activate･Action]')) return false;
    if (pilot.passiveUsedThisTurn) return false;
    return cardEffectCanActivate(pilot, ctx);
  });
}

// Field units that have their own [Activate･Action] handler in the effects
// registry (e.g. GD01-082 Gundam Aerial). canAction hook enforces per-card
// preconditions (pilot present, RES, etc.). activateActionUsedThisTurn
// enforces the once-per-turn gate; cleared by UNREST_ALL each new turn.
function getActionEligibleUnits(ctx) {
  return state.playerField.filter(unit => {
    if (!unit) return false;
    if (unit.activateActionUsedThisTurn) return false;
    const handlers = window.CardEffects?.[unit.cardNo];
    if (!handlers?.action) return false;
    if (!cardEffectCanAction(handlers, unit, ctx)) return false;
    return true;
  });
}

// Player's own base, if it has a registered [Activate･Action] handler and all
// eligibility conditions pass (not rested, not used this turn, canAction hook).
function getActionEligibleBase(ctx) {
  const base = state.playerBase;
  if (!base) return null;
  if (base.rested) return null;
  if (base.baseActionUsedThisTurn) return null;
  const handlers = window.CardEffects?.[base.cardNo];
  if (!handlers?.action) return null;
  if (!cardEffectCanAction(handlers, base, ctx)) return null;
  return base;
}

// Guards the actual step body below behind pendingEffectDestroys draining to
// 0 first — a Unit destroyed by effect damage during THIS step (e.g. a
// Command card's [Action] dealing lethal damage) schedules its [Destroyed]
// resolution (dealDamageToUnit's doDestroy, game.js, or its network.js
// FIELD_HP_MOD mirror) on a 500ms delay that outlives the action handler's
// own onDone() call. Without waiting here, the next actor's own Action Step
// modal — showActionStepModal, which reuses the same #ingame-menu-overlay
// id as the picker modal system — could open (or the step could advance
// straight to ending the turn) before that destroy, and any modal its
// [Destroyed] handler opens (e.g. GD01-005 Unicorn Gundam (Unicorn Mode)'s
// "return paired Pilot to hand, then discard 1"), ever runs.
function runActionStep(ctx, side, next) {
  if (pendingEffectDestroys > 0) {
    const poll = setInterval(() => {
      if (pendingEffectDestroys === 0) {
        clearInterval(poll);
        runActionStep(ctx, side, next);
      }
    }, 100);
    return;
  }
  // During the end phase action step, use END_PHASE_ACTION messages instead of
  // ATTACK_ACTION so they don't collide with mid-battle action step messages.
  const actionMsgType = state.endPhaseStep ? 'END_PHASE_ACTION' : 'ATTACK_ACTION';
  const actingSide = side === 'defender' ? ctx.defenderSide : ctx.attackerSide;
  if (actingSide === 'player') {
    const handSources = getActionEligibleHandCards(ctx);
    // Pilot [Activate･Action] entries — wrapped so showActionStepModal can tell them apart
    const pilotEntries = getActionEligiblePilots(ctx)
      .map(unit => ({ isPilotAction: true, pilot: unit.pilot, unit }));
    const unitEntries = getActionEligibleUnits(ctx)
      .map(unit => ({ isUnitAction: true, unit }));
    const eligibleBase = getActionEligibleBase(ctx);
    const baseEntries = eligibleBase ? [{ isBaseAction: true, base: eligibleBase }] : [];
    const sources = [...handSources, ...pilotEntries, ...unitEntries, ...baseEntries];
    if (sources.length === 0) {
      NET.send({ type: actionMsgType, played: null });
      next();
      return;
    }
    showActionStepModal(sources, side, (entry) => {
      if (!entry) {
        NET.send({ type: actionMsgType, played: null });
        next();
        return;
      }
      if (entry.isUnitAction) {
        resolveUnitActionActivate(entry.unit, ctx, next);
      } else if (entry.isPilotAction) {
        resolvePilotActionActivate(entry.unit, entry.pilot, ctx, next);
      } else if (entry.isBaseAction) {
        resolveBaseActionActivate(entry.base, ctx, next);
      } else {
        resolveActionPlay(entry, ctx, next);
      }
    });
  } else if (!state.networked) {
    // Also covers bot matches (state.isBotMatch): Deck 01 has no [Action]-
    // triggered cards, so "never plays Action cards" is already correct
    // behavior for it. See BotEngine.decideActionStep's own comment
    // (js/bot_logic/bot-engine.js) for the seam a future deck would need.
    next();
  } else if (state.endPhaseStep) {
    // Waiting for peer's END_PHASE_ACTION (their end phase action step).
    // Almost every End Phase, on almost every turn, neither side has an
    // eligible [Action] source at all — the peer's own runActionStep()
    // branch above already auto-replies with played:null the instant it
    // determines that, with no modal on their end. The only real question
    // is whether OUR reply arrives before we'd otherwise paint the overlay.
    // A single requestAnimationFrame (~16ms) only ever masks that for a
    // same-tick/solo-style resolution — any genuine network round trip
    // (even a fast one) routinely exceeds a frame, so the overlay flashed
    // on screen for the empty-hand case almost every time. Give the reply
    // a real grace window instead; a peer who DOES have something to
    // decide will almost always exceed it anyway, so the overlay still
    // shows exactly when it's actually needed.
    pendingEndPhaseActionResolve = (playedSummary) => {
      hideDecisionWaitingOverlay();
      applyPeerActionPlay(playedSummary);
      next();
    };
    armStuckWaitWatchdog(() => pendingEndPhaseActionResolve, (fn) => { pendingEndPhaseActionResolve = null; fn(null); });
    setTimeout(() => {
      if (pendingEndPhaseActionResolve !== null) {
        showDecisionWaitingOverlay("Waiting on opponent's End Phase Action Step…");
      }
    }, 400);
  } else {
    // If the peer's ATTACK_ACTION arrived early (while this side was still
    // inside an async action handler), drain the buffer immediately instead
    // of waiting forever with a hung overlay.
    if (bufferedActionPayload !== null) {
      const payload = bufferedActionPayload;
      bufferedActionPayload = null;
      applyPeerActionPlay(payload.played);
      next();
      return;
    }
    // Set the resolve callback immediately so it's ready if ATTACK_ACTION
    // arrives before we show the overlay. Defer the overlay itself by a
    // short grace window rather than a single animation frame (~16ms) —
    // when the peer has no eligible [Action] source (the common case) they
    // reply immediately, but a real network round trip routinely takes
    // longer than one frame, so a single-frame defer still flashed the
    // overlay on screen for that case almost every time.
    pendingActionResolve = (playedSummary) => {
      hideDecisionWaitingOverlay();
      applyPeerActionPlay(playedSummary);
      next();
    };
    armStuckWaitWatchdog(() => pendingActionResolve, (fn) => { pendingActionResolve = null; fn(null); });
    setTimeout(() => {
      if (pendingActionResolve !== null) {
        showDecisionWaitingOverlay("Waiting on opponent's Action Step…");
      }
    }, 400);
  }
}

// Pays the card's cost, trashes it, and logs its effect text for the players
// to read and apply manually — this codebase has no generic effect-execution
// engine; Repair/Breach/Suppression are the only programmatically-applied
// effects not opted into the per-card registry (see js/effects/). Cards with
// a registered 'action' handler are responsible for calling `onDone` once
// their own (possibly async) flow finishes, same convention as 'main'.
function resolveActionPlay(card, battleCtx, onDone) {
  const exResBefore = state.exRes;
  spendResources(parseInt(card.cost) || 0);
  const usedExRes = exResBefore > state.exRes;
  state.hand = state.hand.filter(c => c.uid !== card.uid);
  updateTempState(card.cardNo, card.copyVer, 'trash');
  state.trashCount++;
  state.playerTrash.push(toTrashCard(card));
  addLog(`(Action Step) Played ${card.name}.`);
  broadcastCounters();
  render();
  setupDropZones();
  // Delay ATTACK_ACTION until after the effect's full async flow finishes
  // (e.g. a card-picker modal closes). Sending early let the peer's
  // pendingActionResolve fire and advance their step while this client was
  // still inside the modal — the peer now waits until onDone() is reached.
  const finalOnDone = () => {
    NET.send({ type: state.endPhaseStep ? 'END_PHASE_ACTION' : 'ATTACK_ACTION', played: { card } });
    onDone();
  };
  // After the card's own effect resolves, fire any onCommandEffect passives
  // (e.g. GD04-066 Unicorn Gundam Awakened) before advancing the step.
  const wrappedOnDone = () => fireCommandEffectPassives(card, () => firePilotCommandPassives(card, usedExRes, () => fireDawnCommandPassives(card, usedExRes, finalOnDone)));
  let handled = false;
  try {
    handled = triggerCardEffect(card, 'action', { onDone: wrappedOnDone, battle: battleCtx, usedExRes });
  } catch (err) {
    console.error(`[resolveActionPlay] ${card.cardNo} action handler threw`, err);
  }
  if (!handled) wrappedOnDone();
}

// Activates a paired pilot's [Activate･Action] effect during the Action Step.
// Unlike resolveActionPlay, the pilot stays on the unit — nothing moves to
// trash. Sets the once-per-turn gate, fires the 'activate' trigger, then
// sends ATTACK_ACTION with a pilot-flavoured payload so the peer can log it.
function resolvePilotActionActivate(unit, pilot, battleCtx, onDone) {
  pilot.passiveUsedThisTurn = true;
  addLog(`(Action Step) ${pilot.name} — [Activate･Action] on ${unit.name}.`);
  broadcastCounters();
  render();
  setupDropZones();
  const wrappedOnDone = () => {
    NET.send({ type: state.endPhaseStep ? 'END_PHASE_ACTION' : 'ATTACK_ACTION', played: { pilotActivation: true, pilotName: pilot.name, unitName: unit.name } });
    onDone();
  };
  let handled = false;
  try {
    handled = triggerCardEffect(pilot, 'activate', { unit, battle: battleCtx, onDone: wrappedOnDone });
  } catch (err) {
    console.error(`[resolvePilotActionActivate] ${pilot.cardNo} activate handler threw`, err);
  }
  if (!handled) wrappedOnDone();
}

// Activates a field unit's own [Activate･Action] effect during the Action Step.
// Sets activateActionUsedThisTurn, fires the 'action' trigger, then sends
// ATTACK_ACTION so the peer can log it. The unit itself does NOT move to trash.
function resolveUnitActionActivate(unit, battleCtx, onDone) {
  unit.activateActionUsedThisTurn = true;
  addLog(`(Action Step) ${unit.name} — [Activate･Action].`);
  broadcastCounters();
  render();
  setupDropZones();
  const wrappedOnDone = () => {
    NET.send({ type: state.endPhaseStep ? 'END_PHASE_ACTION' : 'ATTACK_ACTION', played: { unitActivation: true, unitName: unit.name } });
    onDone();
  };
  let handled = false;
  try {
    handled = triggerCardEffect(unit, 'action', { unit, battle: battleCtx, onDone: wrappedOnDone });
  } catch (err) {
    console.error(`[resolveUnitActionActivate] ${unit.cardNo} action handler threw`, err);
  }
  if (!handled) wrappedOnDone();
}

// Activates a base's own [Activate･Action] effect during the Action Step.
// Rests the base, sets the once-per-turn gate, fires the 'action' trigger,
// then sends ATTACK_ACTION so the peer can mirror the rest state and log it.
function resolveBaseActionActivate(base, battleCtx, onDone) {
  base.baseActionUsedThisTurn = true;
  base.rested = true;
  addLog(`(Action Step) ${base.name} — [Activate･Action].`);
  broadcastCounters();
  render();
  setupDropZones();
  const wrappedOnDone = () => {
    NET.send({ type: state.endPhaseStep ? 'END_PHASE_ACTION' : 'ATTACK_ACTION',
               played: { baseActivation: true, baseName: base.name, baseRested: true } });
    onDone();
  };
  let handled = false;
  try {
    handled = triggerCardEffect(base, 'action', { base, battle: battleCtx, onDone: wrappedOnDone });
  } catch (err) {
    console.error(`[resolveBaseActionActivate] ${base.cardNo} action handler threw`, err);
  }
  if (!handled) wrappedOnDone();
}

function applyPeerActionPlay(playedSummary) {
  if (!playedSummary) { addLog('Opponent skipped their Action Step.'); return; }
  if (playedSummary.pilotActivation) {
    addLog(`Opponent activated ${playedSummary.pilotName} (Pilot) on ${playedSummary.unitName} during the Action Step.`);
    return;
  }
  if (playedSummary.unitActivation) {
    addLog(`Opponent activated ${playedSummary.unitName} (Unit) during the Action Step.`);
    return;
  }
  if (playedSummary.baseActivation) {
    if (state.oppBase && playedSummary.baseRested) state.oppBase.rested = true;
    addLog(`Opponent activated ${playedSummary.baseName} (Base) during the Action Step.`);
    render();
    return;
  }
  const card = playedSummary.card;
  state.oppTrash.push(toTrashCard(card));
  addLog(`Opponent played ${card.name} (Action Step).`);
  showOpponentPlayModal(card, `Opponent played ${card.name} during the Action Step.`);
  render();
}

function showActionStepModal(sources, side, onChoice) {
  const overlay = document.createElement('div');
  overlay.id = 'ingame-menu-overlay';

  const modal = document.createElement('div');
  modal.id = 'ingame-menu-modal';
  modal.classList.add('bop-modal');

  let pickedIdx = -1;
  const stepLabel = state.endPhaseStep ? 'End Phase' : (side === 'defender' ? 'Defender' : 'Attacker');
  const rowClass = sources.length > 5 ? 'bop-grid' : 'bop-row';

  function renderTiles() {
    modal.innerHTML = `
      <button class="modal-hide-btn" title="Hide to view field"><span class="modal-hide-label">View Field</span><span class="material-symbols-outlined">ad_off</span></button>
      <div class="igm-title">Action Step — ${stepLabel}</div>
      <div class="igm-body">Play a Command card with [Action], activate a Pilot's or Base's [Activate･Action], or skip.</div>
      <div class="${rowClass}">
        ${sources.map((entry, i) => {
          const isPilot = entry.isPilotAction;
          const isUnit  = entry.isUnitAction;
          const isBase  = entry.isBaseAction;
          // displayCard names/identifies the entry (the pilot itself for a
          // pilot activation) — fieldCard is whichever actual on-field card
          // its current state (AP/HP, rested, keywords, pairing) should be
          // read from. For a pilot activation that's the unit it's paired
          // to, since a bare pilot card has no field state of its own.
          const displayCard = isPilot ? entry.pilot : isUnit ? entry.unit : isBase ? entry.base : entry;
          const fieldCard = isPilot ? entry.unit : isUnit ? entry.unit : isBase ? entry.base : null;
          const label = isPilot ? `${displayCard.name} — Pilot`
                      : isUnit  ? `${displayCard.name} — Unit`
                      : isBase  ? `${displayCard.name} — Base`
                      : displayCard.name;
          const selLabel = isPilot ? 'Activate Pilot' : isUnit ? 'Activate Unit' : isBase ? 'Activate Base' : 'Play Action Card';
          return `<div class="bop-tile${i === pickedIdx ? ' bop-selected' : ''}${fieldCard ? tileStateClass(fieldCard) : ''}" data-idx="${i}" data-sel-label="${selLabel}">
            <div class="bop-order-label">${i === pickedIdx ? selLabel : ''}</div>
            <div class="cp-img-wrap">
              ${fieldCard ? statTileHtml(fieldCard) : (displayCard.img ? `<img src="${displayCard.img}" alt="${displayCard.name}" />` : '<div class="cp-no-img">?</div>')}
            </div>
            <div class="bop-name">${label}</div>
          </div>`;
        }).join('')}
      </div>
      <div class="igm-btn-row">
        <button class="igm-btn igm-skip">Skip</button>
        <button class="igm-btn igm-confirm-base"${pickedIdx === -1 ? ' disabled' : ''}>Confirm</button>
      </div>
    `;
    modal.querySelector('.modal-hide-btn').addEventListener('click', (e) => {
      e.stopPropagation();
      hideModalForFieldView(overlay);
    });
    modal.querySelectorAll('.bop-tile').forEach(tile => {
      const entry = sources[parseInt(tile.dataset.idx, 10)];
      const previewCard = entry.isPilotAction ? entry.pilot : entry.isUnitAction ? entry.unit : entry.isBaseAction ? entry.base : entry;
      tile.addEventListener('mouseenter', e => showCardPreview(previewCard, e.currentTarget));
      tile.addEventListener('mouseleave', hideCardPreview);
      tile.addEventListener('click', () => {
        const idx = parseInt(tile.dataset.idx, 10);
        pickedIdx = pickedIdx === idx ? -1 : idx;
        // Update selection state in-place to preserve scroll position.
        modal.querySelectorAll('.bop-tile[data-idx]').forEach(t => {
          const i = parseInt(t.dataset.idx, 10);
          const isSel = i === pickedIdx;
          t.classList.toggle('bop-selected', isSel);
          t.querySelector('.bop-order-label').textContent = isSel ? (t.dataset.selLabel || '') : '';
        });
        const confirmBtn = modal.querySelector('.igm-confirm-base');
        if (confirmBtn) confirmBtn.disabled = pickedIdx === -1;
      });
    });
    modal.querySelector('.igm-skip').addEventListener('click', () => {
      overlay.remove();
      restoreHiddenModal();
      hideCardPreview();
      onChoice(null);
    });
    modal.querySelector('.igm-confirm-base').addEventListener('click', () => {
      if (pickedIdx === -1) return;
      overlay.remove();
      restoreHiddenModal();
      hideCardPreview();
      onChoice(sources[pickedIdx]);
    });
  }

  renderTiles();
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  startSelectionTimer(overlay, () => autoResolveIgmModal(overlay));
}

// ── Damage Step ──
// Combat math identical to the original single-shot resolveAttack(), just
// reading the (possibly block-redirected) attacker/defender off ctx instead
// of closed-over variables, and recomputing stats fresh since ctx.defender
// may now be a blocker rather than the originally-declared target.
function runDamageStep(ctx) {
  const { attacker, attackerSide, defenderSide, mine } = ctx;
  // Apply any pending defender override (e.g. ST07-013 Armed Intervention changes attack target)
  if (state.pendingDefenderOverride) {
    const newDef = state.pendingDefenderOverride;
    state.pendingDefenderOverride = null;
    ctx.defender = newDef;
    ctx.isShield = false;
    ctx.isDirect = false;
    addLog(`Attack redirected to ${newDef.name}.`);
  }
  const defender = ctx.defender;

  const attackerIdx = state[`${attackerSide}Field`].findIndex(c => c && c.uid === attacker.uid);
  if (attackerIdx === -1) {
    // Attacker was removed from the field mid-combat (e.g. bounced by a
    // defending Command Pilot's [Action] effect during the Action Step) —
    // the whole attack fizzles before any damage math runs.
    addLog(`${attacker.name} is no longer on the field — the attack fizzles.`);
    addLog('End of Battle Step.');
    state.attackInProgress = false;
    render();
    setupDropZones();
    maybeAutoEndMainPhase();
    return;
  }

  // Defender might have been removed by an attack trigger (e.g. Ali al-Saachez
  // pilot-unpair destroying the target). Skip damage if so.
  if (!ctx.isDirect && !ctx.isShield) {
    const defInField = state[`${defenderSide}Field`].some(c => c && c.uid === defender.uid);
    const defIsBase  = state[`${defenderSide}Base`]?.uid === defender.uid;
    if (!defInField && !defIsBase) {
      addLog(`${defender.name} is no longer on the field — the attack fizzles.`);
      addLog('End of Battle Step.');
      state.attackInProgress = false;
      render();
      setupDropZones();
      maybeAutoEndMainPhase();
      return;
    }
  }

  const atkBp = effectiveBp(attacker);
  const atkHp = effectiveHp(attacker);
  const defBp = effectiveBp(defender);
  const defHp = effectiveHp(defender);
  // Capture "during this battle" defense/immunity values BEFORE clearing all battle-scoped mods
  const defBattleDefMod    = defender.battleDefMod    || 0;
  const defBattleImmuneAp  = defender.battleImmuneAp  || 0;
  const defBattleImmuneAll = defender.battleImmuneAll  || false;
  // Capture per-battle shield immunity level (only non-zero on the defending client)
  const shieldImmuneLv = defenderSide === 'player' ? (state.battleShieldImmuneLv || -1) : -1;
  state.battleShieldImmuneLv = 0;
  // Capture per-battle shield immunity AP threshold (e.g. GD06-106 Presence
  // Detected: "shield area cards can't receive damage from enemy Units with
  // 6 or less AP") — same capture/consume shape as shieldImmuneLv above, just
  // keyed on the attacker's effective AP instead of its Lv.
  const shieldImmuneAp = defenderSide === 'player' ? (state.battleShieldImmuneAp || -1) : -1;
  state.battleShieldImmuneAp = 0;
  // battleOnDestroyEnemyDestroyLowAp (e.g. ST12-014 Wise Leader's Pride:
  // "gains ... during this battle: when it destroys an enemy Unit with
  // battle damage, choose 1 enemy Unit with 2 or less AP, destroy it")
  // is captured here — BEFORE the blanket clear below wipes it from every
  // unit on both fields (correctly expiring it on any OTHER unit whose
  // battle already ended without a kill) — then restored just for THIS
  // battle's attacker/defender so notifyUnitDestroyedByBattle can still
  // read it further down in this same resolveAttack() run.
  const attackerLowApDestroyGrant = attacker.battleOnDestroyEnemyDestroyLowAp || false;
  const defenderLowApDestroyGrant = defender.battleOnDestroyEnemyDestroyLowAp || false;
  // Clear all battle-scoped mods on ALL field units after values are captured
  state.playerField.forEach(c => { if (c) { c.battleApMod = 0; c.battleDefMod = 0; c.battleImmuneAp = 0; c.battleImmuneAll = false; c.battleOnDestroyEnemyDestroyLowAp = false; } });
  state.oppField.forEach(c => { if (c) { c.battleApMod = 0; c.battleDefMod = 0; c.battleImmuneAp = 0; c.battleImmuneAll = false; c.battleOnDestroyEnemyDestroyLowAp = false; } });
  attacker.battleOnDestroyEnemyDestroyLowAp = attackerLowApDestroyGrant;
  defender.battleOnDestroyEnemyDestroyLowAp = defenderLowApDestroyGrant;

  const defenderIdx = state[`${defenderSide}Field`].findIndex(c => c && c.uid === defender.uid);
  const isUnitAttack = defenderIdx !== -1; // false when target is a base or shield
  // Capture and clear attacker counter-damage proxy (e.g. GD04-087 Elan Ceres)
  const capturedAttackerBdProxyUid = isUnitAttack ? state.attackerBdProxyUid : null;
  state.attackerBdProxyUid = null;
  const attackerProxy = capturedAttackerBdProxyUid
    ? (state[`${attackerSide}Field`].find(c => c && c.uid === capturedAttackerBdProxyUid) || null)
    : null;
  // nextDamageReduction: one-time incoming battle damage reduction (e.g. GD04-093 Rey Za Burrel).
  // Both clients independently read+clear this field; they agree because the pair handler
  // syncs nextDamageReduction to the peer via FIELD_AP_MOD (delta:+2) on the opp's oppField copy.
  const ndrVal = isUnitAttack ? (defender.nextDamageReduction || 0) : 0;
  if (ndrVal > 0) {
    defender.nextDamageReduction = 0;
    addLog(`${defender.name} — next damage reduced by ${ndrVal}.`);
  }
  // Passive per-unit battle damage reduction hook (e.g. GD04-053 Rey's Blaze Zaku Phantom)
  const passiveBattleReduce = isUnitAttack
    ? (window.CardEffects?.[defender.cardNo]?.passiveUnitBattleDamageReduction?.(defender) ?? 0)
    : 0;
  // tempIncomingDamageReduction: a per-instance "reduce all damage received
  // by N until end of turn" grant (e.g. GD05-018 Gundam Calibarn, GD05-125 Ra
  // Cailum) — same field consulted for effect damage in dealDamageToUnit().
  const defTempReduce = isUnitAttack ? (defender.tempIncomingDamageReduction || 0) : 0;
  // tempBattleDamageReduction: same shape, but BATTLE damage only — not
  // consulted by dealDamageToUnit (e.g. GD05-094 Quess Paraya: "[Destroyed]
  // Choose 1 of your (Neo Zeon) Units. During this turn, when it receives
  // enemy BATTLE damage, reduce it by 2").
  const defTempBattleReduce = isUnitAttack ? (defender.tempBattleDamageReduction || 0) : 0;
  // Outgoing battle-damage bonus from the ATTACKER's own side (e.g. GD06-033
  // ZZ Gundam: "your (AEUG) cards deal +1") — folded into atkBp BEFORE the
  // defender's reductions below, same "before, not an unreducible top-up"
  // ordering dealDamageToUnit uses for effect damage. Scoped to isUnitAttack
  // only — the card text says "enemy Units", so a base/shield/direct attack
  // never qualifies.
  const atkOutgoingBonus = isUnitAttack ? getGlobalOutgoingDamageBonus(attacker, attackerSide) : 0;
  const atkBpNet = isUnitAttack ? Math.max(0, (atkBp + atkOutgoingBonus) - defBattleDefMod - ndrVal - passiveBattleReduce - defTempReduce - defTempBattleReduce) : atkBp;
  // Same hook, but for the ATTACKER's own counter-damage taken from the
  // defender's AP (e.g. GD04-029 Gundam Dynames GN Full Shield: "when this
  // Unit receives damage from an enemy, reduce it by 1" — applies whether
  // this unit is attacking or defending). If counter-damage is redirected to
  // a proxy (GD04-087 Elan Ceres), the proxy is the one actually taking the
  // damage, so its own reduction hook applies instead of the attacker's.
  const attackerPassiveBattleReduce = isUnitAttack
    ? (window.CardEffects?.[(attackerProxy || attacker).cardNo]?.passiveUnitBattleDamageReduction?.(attackerProxy || attacker) ?? 0)
    : 0;
  const attackerTempReduce = (attackerProxy || attacker).tempIncomingDamageReduction || 0;
  const attackerTempBattleReduce = (attackerProxy || attacker).tempBattleDamageReduction || 0;
  // Mirror of atkOutgoingBonus above, but for the DEFENDER's own counter-
  // damage dealt back to the attacker (e.g. a defending ZZ Gundam boosting
  // its own counter-hit) — same isUnitAttack scoping.
  const defOutgoingBonus = isUnitAttack ? getGlobalOutgoingDamageBonus(defender, defenderSide) : 0;
  const defBpNet = Math.max(0, (defBp + defOutgoingBonus) - attackerPassiveBattleReduce - attackerTempReduce - attackerTempBattleReduce);
  // tempDestroyOnHit: attacker's flag to force-destroy enemy Lv.N or lower on any damage hit (e.g. GD04-115 Backup)
  // passiveDestroyOnHitLevel: permanent hook version (e.g. GD04-054 Virtue Trans-Am, GD03-052 Gundam Virtue Lv5,
  // GD05-050 Gundam Exia Repair). Gated on !defender.tempImmuneToEffectDestruction — this
  // family of "destroy regardless of remaining HP" abilities is conceptually an
  // effect-destroy riding along with genuine battle damage (still fully resolves
  // through this same branch — breach, onUnitDestroysEnemy, etc. all fire normally,
  // satisfying "still counts as dealing battle damage"), so it respects the same
  // effect-destruction immunity flag effectDestroyEnemyUnit/effectDestroyOwnUnit do.
  const passiveDestroyLv = isUnitAttack && atkBp > 0 && !defender.tempImmuneToEffectDestruction
    ? (window.CardEffects?.[attacker.cardNo]?.passiveDestroyOnHitLevel?.(attacker, defender) ?? -1)
    : -1;
  const destroyOnHit = isUnitAttack && atkBp > 0 && (
    (attacker.tempDestroyOnHit && (parseInt(defender.level) || 0) <= attacker.tempDestroyOnHit) ||
    (passiveDestroyLv >= 0 && (parseInt(defender.level) || 0) <= passiveDestroyLv)
  );
  const atkBpForDestCheck = destroyOnHit ? Math.max(atkBpNet, defHp) : atkBpNet;
  // Mirror of passiveDestroyLv/destroyOnHit above, but for the DEFENDER's own
  // "destroy on hit" passive applying to the ATTACKER — e.g. GD05-050 Gundam
  // Exia Repair's "When this Unit deals battle damage to an enemy Unit that
  // is Lv.4 or lower with no paired Pilot, destroy that enemy Unit" has no
  // "while attacking" qualifier, so it must also fire when Exia Repair is the
  // one being attacked and deals its own counter-damage back (this engine's
  // combat is a mutual exchange — see defBpNet above). Checked against
  // (attackerProxy || attacker) — whichever unit actually receives the
  // defender's damage (Elan Ceres redirect included) — so the same boolean
  // covers both the proxy and non-proxy "Attacker destroyed?" branches below.
  const passiveDestroyLvForAttacker = isUnitAttack && defBpNet > 0 && !(attackerProxy || attacker).tempImmuneToEffectDestruction
    ? (window.CardEffects?.[defender.cardNo]?.passiveDestroyOnHitLevel?.(defender, attackerProxy || attacker) ?? -1)
    : -1;
  const destroyOnHitForAttacker = passiveDestroyLvForAttacker >= 0 &&
    (parseInt((attackerProxy || attacker).level) || 0) <= passiveDestroyLvForAttacker;

  // If the original target was a unit (not a base/shield/direct) but is no
  // longer on the field, the attack fizzles — the attacker is already rested
  // (rested when the attack was declared) but takes no damage.
  if (!isUnitAttack && !defender.isShield && !defender.isDirect) {
    const isDefBase = state[`${defenderSide}Base`]?.uid === defender.uid;
    if (!isDefBase) {
      addLog(`${defender.name} is no longer on the field — the attack fizzles.`);
      addLog('End of Battle Step.');
      state.attackInProgress = false;
      render();
      setupDropZones();
      maybeAutoEndMainPhase();
      return;
    }
  }

  // Pre-compute for the animation setTimeout below: when the attacker has First
  // Strike and its AP is enough to destroy the defender, the attacker takes no
  // counter-damage, so we skip vibrating the attacker card.
  // (The full defenderDestroyedByFirstStrike check inside resolveAttack also
  // accounts for immunity and proxy units — this simplified version is
  // only used to decide whether to play the attacker shake animation.)
  const skipAttackerVibrate = isUnitAttack && atkBpForDestCheck >= defHp && (
    hasNativeKeyword(attacker, 'First Strike') ||
    !!(window.CardEffects?.[defender.cardNo]?.grantsFirstStrikeToAttacker?.(defender, ctx))
  );

  function resolveAttack() {
    // Direct attack — defending side has no shields left. Same "0 AP deals
    // no damage" rule as the shield-attack branch below (atkBp <= 0) — a
    // Unit with 0 AP attacking the enemy player doesn't count as dealing
    // damage, so it must not end the game either.
    if (defender.isDirect) {
      if (atkBp <= 0) {
        addLog(`${attacker.name} attacks ${mine ? 'the opponent' : 'you'} directly — 0 AP, dealing no damage.`);
        addLog('End of Battle Step.');
        state.attackInProgress = false;
        render();
        setupDropZones();
        maybeAutoEndMainPhase();
        return;
      }
      addLog(mine
        ? `${attacker.name} attacks the opponent directly — Victory!`
        : `${attacker.name} attacks you directly — Defeat.`);
      addLog('End of Battle Step.');
      state.attackInProgress = false;
      render();
      setupDropZones();
      showVictoryModal(mine);
      return;
    }

    // Shield attack — remove one shield card (two if the attacker has the
    // native [Suppression] keyword), then reveal/resolve it.
    if (defender.isShield) {
      // Check if any rested defender unit grants shield immunity (e.g. GD03-070
      // Freedom Gundam). Shields have 1 HP by default; if the passive effectively
      // reduces attacker AP to 0 the attack fails to destroy the shield.
      const defenderFieldKey = defenderSide === 'player' ? 'playerField' : 'oppField';
      const shieldImmune = state[defenderFieldKey].some(c =>
        c && window.CardEffects?.[c.cardNo]?.grantsShieldImmunity?.(c)
      );
      if (shieldImmune) {
        addLog(`${attacker.name} attacks the shield — blocked by a defensive effect (attacker AP reduced to 0).`);
        addLog('End of Battle Step.');
        state.attackInProgress = false;
        render();
        setupDropZones();
        maybeAutoEndMainPhase();
        return;
      }
      if (atkBp <= 0) {
        addLog(`${attacker.name} attacks the shield — 0 AP, cannot destroy it.`);
        addLog('End of Battle Step.');
        state.attackInProgress = false;
        render();
        setupDropZones();
        maybeAutoEndMainPhase();
        return;
      }
      // Per-battle shield immunity from Action card (e.g. ST02-013 Peaceful Timbre, GD02-106 White Wolf)
      // Only non-zero on the defending client; attacker client has shieldImmuneLv = -1.
      if (shieldImmuneLv >= 0) {
        const attackerLv = parseInt(attacker.level) || 0;
        if (attackerLv <= shieldImmuneLv) {
          addLog(`${attacker.name} attacks the shield — blocked by shield immunity (Lv.${attackerLv} ≤ Lv.${shieldImmuneLv}).`);
          addLog('End of Battle Step.');
          state.attackInProgress = false;
          render();
          setupDropZones();
          maybeAutoEndMainPhase();
          if (state.networked) broadcastCounters();
          return;
        }
      }
      // Per-battle shield immunity from an AP threshold (e.g. GD06-106 Presence
      // Detected). Same shape as shieldImmuneLv above, just keyed on AP.
      if (shieldImmuneAp >= 0 && atkBp <= shieldImmuneAp) {
        addLog(`${attacker.name} attacks the shield — blocked by shield immunity (AP.${atkBp} ≤ AP.${shieldImmuneAp}).`);
        addLog('End of Battle Step.');
        state.attackInProgress = false;
        render();
        setupDropZones();
        maybeAutoEndMainPhase();
        if (state.networked) broadcastCounters();
        return;
      }
      const hasSuppression = hasNativeKeyword(attacker, 'Suppression');
      const shieldsToRemove = Math.min(hasSuppression ? 2 : 1, state[`${defenderSide}Shield`]);
      state[`${defenderSide}Shield`] = Math.max(0, state[`${defenderSide}Shield`] - shieldsToRemove);
      addLog(`${attacker.name} attacks the opponent's shield${hasSuppression ? 's (Suppression)' : ''} — ${shieldsToRemove} shield${shieldsToRemove !== 1 ? 's' : ''} removed (${state[`${defenderSide}Shield`]} remaining).`);
      addLog('End of Battle Step.');
      state.attackInProgress = false;
      render();
      setupDropZones();
      maybeAutoEndMainPhase();
      // Hook: fire onAttackDestroysShield on the attacker (e.g. ST03-001 Sinanju),
      // onLinkedUnitAttackDestroysShield on the attacker's paired PILOT (e.g.
      // GD05-098 Heero Yuy), and onAnyPlayerAttackDestroysShield on any field
      // watcher (e.g. GD04-042). Only fires when the attacker is ours and at
      // least 1 shield was removed. turnDestroyWatchers (targetType 'shield')
      // alongside them — e.g. GD05-002 Strike Freedom Gundam's "destroys an
      // enemy card" grant, which (unlike most turnDestroyWatchers users) must
      // also fire for a shield, not just a Unit.
      if (shieldsToRemove > 0 && attackerSide === 'player') {
        window.CardEffects?.[attacker.cardNo]?.onAttackDestroysShield?.(attacker, shieldsToRemove);
        if (attacker.pilot) {
          window.CardEffects?.[attacker.pilot.cardNo]?.onLinkedUnitAttackDestroysShield?.(attacker.pilot, attacker, shieldsToRemove);
        }
        state.playerField.forEach(c => {
          if (c) window.CardEffects?.[c.cardNo]?.onAnyPlayerAttackDestroysShield?.(c, attacker, shieldsToRemove);
        });
        turnDestroyWatchers.forEach(w => w(attacker, null, attackerSide, 'shield'));
      }
      if (defenderSide === 'player') {
        // My own real shield — only I have the real card data (shield
        // identities are private per-client), so I pick/reveal/resolve it.
        resolveShieldHit('player', shieldsToRemove);
      } else if (state.isBotMatch) {
        // Bot match — the bot's shields are real cards too (see
        // state.oppTempDeck), unlike the dead solo-practice dummy below.
        resolveShieldHit('opp', shieldsToRemove);
      } else if (!state.networked) {
        // Solo practice — the dummy opponent has no real shield card data
        // to reveal, so fall back to the old generic trash-increment.
        animateTrashDestroy(trashDomId(defenderSide), shieldsToRemove, () => { state[trashCountKey(defenderSide)] += shieldsToRemove; render(); });
      }
      // Networked + defenderSide === 'opp': the real opponent owns that
      // shield and reveals/resolves it themselves, then broadcastCounters()
      // to correct my oppTrashCount/oppShield mirror — nothing to do here.
      return;
    }

    let attackerTrashGain  = 0;
    let defenderTrashGain  = 0;
    let attackerTrashCards = [];
    let defenderTrashCards = [];
    let epilogueDeferred   = false;

    function runEpilogue() {
      addLog('End of Battle Step.');
      state.attackInProgress = false;
      currentAttackCtx = null;
      render();
      setupDropZones();
      maybeAutoEndMainPhase();
      if (attackerTrashGain > 0) animateTrashDestroy(trashDomId(attackerSide), attackerTrashGain, () => { state[trashCountKey(attackerSide)] += attackerTrashGain; state[trashArrayKey(attackerSide)].push(...attackerTrashCards); render(); });
      if (defenderTrashGain > 0) animateTrashDestroy(trashDomId(defenderSide), defenderTrashGain, () => { state[trashCountKey(defenderSide)] += defenderTrashGain; state[trashArrayKey(defenderSide)].push(...defenderTrashCards); render(); });
      // Post-combat sync: the attacker is authoritative for HP and unit survival.
      // animateTrashDestroy calls onComplete() synchronously, so trashCount is
      // already updated here. Send HP_RECONCILE so the peer can correct any HP
      // or destruction desync caused by independent runDamageStep computation.
      if (state.networked && attackerSide === 'player') {
        NET.send({
          type: 'HP_RECONCILE',
          // Full card snapshots (not just uid/hp/damaged) + original slot +
          // which side owned them (from MY perspective) — enough for the
          // peer to fully RESTORE a unit (pilot pairing included) if their
          // own independent runDamageStep run already destroyed it locally
          // before this message arrives (see HP_RECONCILE's handler in
          // network.js for why that race is possible, and why a bare
          // uid/hp/damaged patch can't fix an already-emptied field slot).
          units: [
            ...state.playerField.map((c, slot) => c && { card: c, slot, mine: true }).filter(Boolean),
            ...state.oppField.map((c, slot) => c && { card: c, slot, mine: false }).filter(Boolean),
          ],
          // Explicit list of what THIS combat actually destroyed (units and
          // their paired pilots) — the peer must only ever destroy uids named
          // here, never infer "missing from the field snapshot above means
          // destroyed" (see HP_RECONCILE handler in network.js for why).
          destroyedUids: [...attackerTrashCards, ...defenderTrashCards]
            .filter(c => c.type !== 'base')
            .map(c => c.uid),
        });
        broadcastCounters();
      }
      // Post-combat pilot re-sync: each client is the sole authority on its
      // OWN units' pilot pairing (unlike HP, which both clients compute
      // independently from the same inputs), so — regardless of
      // attacker/defender role — each side re-sends its own playerField's
      // pilot data after every battle. This heals any stale/missing .pilot
      // on the peer's oppField mirror (e.g. a paired pilot's HP/AP bonus
      // silently dropping out of a card's effective-stat display on the
      // other client) without needing a fresh PAIR_PILOT to fix it.
      if (state.networked) {
        NET.send({
          type: 'FIELD_PILOT_RESYNC',
          units: state.playerField.filter(Boolean).map(c => ({ uid: c.uid, pilot: c.pilot || null })),
        });
      }
      // Post-battle field consistency check — regardless of attacker/
      // defender role, each side sends its own real playerField's uid list
      // after every battle so the peer can catch a mirror that has drifted
      // to hold MORE cards than actually exist (a "lingering" ghost entry —
      // e.g. a destroy/removal message that got lost, or a duplicate PLAY_CARD
      // mirror). The peer's handler (FIELD_SYNC_CHECK in network.js) only
      // ever trims its own oppField mirror down to match this list — it
      // never adds anything back and never touches its OWN authoritative
      // playerField, so it can't be the mechanism that deletes a real,
      // still-alive card; that direction (a mirror MISSING a card the real
      // owner still has) stays covered by HP_RECONCILE's resurrect logic
      // above, which is why both checks run here together.
      if (state.networked) {
        NET.send({
          type: 'FIELD_SYNC_CHECK',
          uids: state.playerField.filter(Boolean).map(c => c.uid),
        });
      }
    }

    // Fires the destroyed trigger for a base, with async coordination:
    // if the base has a registered destroyed handler and we're in a networked
    // game, the owner's client passes onDone (which calls runEpilogue + sends
    // DESTROYED_EFFECT_DONE to the peer) while the peer's client shows a
    // waiting overlay and defers its epilogue until DESTROYED_EFFECT_DONE fires.
    function fireBaseDestroyedEffect(base) {
      const hasHandler = !!(window.CardEffects?.[base.cardNo]?.destroyed);
      if (hasHandler && state.networked) {
        epilogueDeferred = true;
        if (defenderSide === 'player') {
          triggerCardEffect(base, 'destroyed', { ...ctx, onDone: () => {
            NET.send({ type: 'DESTROYED_EFFECT_DONE' });
            runEpilogue();
          }});
        } else if (bufferedDestroyedEffectDone) {
          // The owner's DESTROYED_EFFECT_DONE already arrived (e.g. their
          // picker had zero candidates and resolved instantly) before we got
          // through our own Block Step/Action Step round trips to reach this
          // point — resolve immediately instead of registering a wait for a
          // reply that's already here (see bufferedDestroyedEffectDone).
          bufferedDestroyedEffectDone = false;
          triggerCardEffect(base, 'destroyed', ctx);
          runEpilogue();
        } else {
          showDecisionWaitingOverlay(`Waiting on ${base.name}’s destroyed effect…`);
          pendingDestroyedEffectResolve = () => {
            hideDecisionWaitingOverlay();
            runEpilogue();
          };
          armStuckWaitWatchdog(() => pendingDestroyedEffectResolve, (fn) => { pendingDestroyedEffectResolve = null; fn(); });
          triggerCardEffect(base, 'destroyed', ctx);
        }
      } else {
        triggerCardEffect(base, 'destroyed', ctx);
      }
    }

    try {

    // Base battle damage immunity hook (e.g. ST07-015 Ptolemaios), plus any
    // rested Linked pilot's own grant (e.g. GD06-098 Duo Maxwell — see
    // hasPilotBaseBattleImmunity). Fires when a unit attacks a base directly
    // — if either source says immune, the entire attack fizzles.
    if (!isUnitAttack) {
      const baseHandlers = window.CardEffects?.[defender.cardNo];
      if (baseHandlers?.isImmuneToBattleDamageFrom?.(defender, attacker, defenderSide) ||
          hasPilotBaseBattleImmunity(defenderSide, attacker)) {
        addLog(`${defender.name} — immune to damage from this attacker. Attack fizzles.`);
        runEpilogue();
        return;
      }
    }

    // Battle damage proxy: if the defender has a BDProxy_<uid> tempKeyword, redirect
    // incoming atkBp to the proxy unit instead. Defender deals counter-damage normally
    // but takes 0 incoming damage. (e.g. GD04-095 Lunamaria Hawke)
    let proxyUnit = null;
    let proxyHp = 0;
    let proxyIdx = -1;
    if (isUnitAttack) {
      const proxyKw = (defender.tempKeywords || []).find(kw => kw.startsWith('BDProxy_'));
      if (proxyKw) {
        const proxyUid = proxyKw.replace('BDProxy_', '');
        const fieldKey = `${defenderSide}Field`;
        proxyUnit = state[fieldKey].find(c => c && c.uid === proxyUid) || null;
        if (proxyUnit) {
          proxyHp = effectiveHp(proxyUnit);
          proxyIdx = state[fieldKey].findIndex(c => c && c.uid === proxyUnit.uid);
          addLog(`${proxyUnit.name} intercepts battle damage in place of ${defender.name}!`);
        }
      }
    }

    // Distant Reunion: unit immune to battle damage from attackers with AP ≤ threshold.
    // Counter-damage from the protected unit still fires normally.
    // (e.g. GD03-115 Distant Reunion — adds BattleImmunityFromAp2 or BattleImmunityFromAp5)
    let defenderImmune = false;
    if (isUnitAttack) {
      const immApKw = (defender.tempKeywords || []).find(kw => /^BattleImmunityFromAp\d+$/.test(kw));
      if (immApKw) {
        const threshold = parseInt(immApKw.replace('BattleImmunityFromAp', ''));
        if (effectiveBp(attacker) <= threshold) {
          defenderImmune = true;
          addLog(`${defender.name} — immune to battle damage from ${attacker.name} (AP ${effectiveBp(attacker)} ≤ ${threshold}).`);
        }
      }
      // BattleImmunityFromLv<N>: turn-long level-based immunity (e.g. ST06-013 Fierce Unity)
      if (!defenderImmune) {
        const immLvKw = (defender.tempKeywords || []).find(kw => /^BattleImmunityFromLv\d+$/.test(kw));
        if (immLvKw) {
          const threshold = parseInt(immLvKw.replace('BattleImmunityFromLv', ''));
          if ((parseInt(attacker.level) || 0) <= threshold) {
            defenderImmune = true;
            addLog(`${defender.name} — immune to battle damage from ${attacker.name} (Lv.${parseInt(attacker.level) || 0} ≤ Lv.${threshold}).`);
          }
        }
      }
      // battleImmuneAp: per-battle AP-based immunity (e.g. ST03-014 The Blue Giant)
      if (!defenderImmune && defBattleImmuneAp > 0 && effectiveBp(attacker) <= defBattleImmuneAp) {
        defenderImmune = true;
        addLog(`${defender.name} — immune to battle damage from ${attacker.name} (AP ${effectiveBp(attacker)} ≤ ${defBattleImmuneAp}).`);
      }
      // battleImmuneAll: per-battle total immunity (e.g. GD02-105 Valedictorian)
      if (!defenderImmune && defBattleImmuneAll) {
        defenderImmune = true;
        addLog(`${defender.name} — immune to all battle damage this battle.`);
      }
      // passiveUnitBattleDamageImmune: standing unit-level passive (e.g. GD03-020 Zaku II FZ)
      if (!defenderImmune) {
        const unitH = window.CardEffects?.[defender.cardNo];
        if (unitH?.passiveUnitBattleDamageImmune?.(defender, attacker)) {
          defenderImmune = true;
          addLog(`${defender.name} — immune to enemy battle damage.`);
        }
      }
    }
    // Base-specific battle damage immunity (e.g. GD04-123 A Baoa Qu), plus any
    // rested Linked pilot's own grant (e.g. GD06-098 Duo Maxwell). Fires for
    // direct-base attacks only; Breach damage and effect damage still apply.
    // Counter-damage from the base to the attacker resolves normally.
    if (!isUnitAttack && !defender.isShield && !defenderImmune) {
      const baseHandlers = window.CardEffects?.[defender.cardNo];
      if (baseHandlers?.isImmuneToBattleDamageFrom?.(defender, attacker, defenderSide) ||
          hasPilotBaseBattleImmunity(defenderSide, attacker)) {
        defenderImmune = true;
        addLog(`${defender.name} — immune to battle damage from ${attacker.name}.`);
      }
    }

    // First Strike: if the attacker has this keyword (natively, via tempKeywords,
    // or granted by the defender's grantsFirstStrikeToAttacker hook, e.g.
    // GD02-073 Carta's Graze Ritter) it resolves its damage before the defender
    // can retaliate. If the defender is destroyed the attacker takes no damage.
    // If the defender survives, damage flows back normally.
    const attackerHasFirstStrike =
      hasNativeKeyword(attacker, 'First Strike') ||
      !!(window.CardEffects?.[defender.cardNo]?.grantsFirstStrikeToAttacker?.(defender, ctx));
    const effectiveTargetHp = proxyUnit ? proxyHp : defHp;
    const defenderDestroyedByFirstStrike = attackerHasFirstStrike && !defenderImmune && atkBpForDestCheck >= effectiveTargetHp;
    if (defenderDestroyedByFirstStrike)
      addLog(`First Strike — ${attacker.name} resolves damage before ${defender.name} can retaliate.`);

    // Defender destroyed? (or proxy unit if Lunamaria redirect is active)
    if (!defenderImmune && atkBpForDestCheck >= effectiveTargetHp) {
      // isDefBase only when there's no proxy — proxies are always field units
      const isDefBase = !proxyUnit && state[`${defenderSide}Base`] && defender.uid === state[`${defenderSide}Base`].uid;
      if (isDefBase) {
        const removedFromGame = isRemovedFromGameBase(defender);
        state[`${defenderSide}Base`] = null;
        if (!removedFromGame) { defenderTrashGain += 1; defenderTrashCards.push(toTrashCard(defender)); }
        addLog(`${defender.name} is destroyed!${removedFromGame ? ' (removed from game)' : ''}`, { destroyedCard: defender });
        showCardToast(`${defender.name} was destroyed!`, defender.img);
        fireBaseDestroyedEffect(defender);
        // "Shield area card" includes the base — fire the same hooks that fire
        // on a shield destruction so effects like Exia Trans-Am and Sinanju
        // trigger correctly when they destroy the enemy base with battle damage.
        // turnDestroyWatchers (targetType 'base') alongside them — e.g.
        // GD05-002 Strike Freedom Gundam's "destroys an enemy card" grant.
        if (attackerSide === 'player') {
          window.CardEffects?.[attacker.cardNo]?.onAttackDestroysShield?.(attacker, 1);
          if (attacker.pilot) {
            window.CardEffects?.[attacker.pilot.cardNo]?.onLinkedUnitAttackDestroysShield?.(attacker.pilot, attacker, 1);
          }
          state.playerField.forEach(c => {
            if (c) window.CardEffects?.[c.cardNo]?.onAnyPlayerAttackDestroysShield?.(c, attacker, 1);
          });
          turnDestroyWatchers.forEach(w => w(attacker, defender, attackerSide, 'base'));
        }
      } else {
        // Unit destroyed — either the proxy (Lunamaria intercept) or the actual defender
        const destroyedUnit = proxyUnit || defender;
        const destroyedIsToken = isUnitToken(destroyedUnit);
        if (!destroyedIsToken) { defenderTrashCards.push(toTrashCard(destroyedUnit)); defenderTrashGain++; }
        if (destroyedUnit.pilot) { defenderTrashCards.push(toTrashCard(destroyedUnit.pilot)); defenderTrashGain++; }
        const destroyedFieldIdx = proxyUnit ? proxyIdx : state[`${defenderSide}Field`].findIndex(c => c && c.uid === defender.uid);
        if (destroyedFieldIdx !== -1) state[`${defenderSide}Field`][destroyedFieldIdx] = null;
        if (proxyUnit) {
          addLog(`${destroyedUnit.name} is destroyed (intercepted for ${defender.name})!${destroyedIsToken ? ' (token — removed from the game)' : ''}`, { destroyedCard: destroyedUnit });
        } else {
          addLog(`${destroyedUnit.name} is destroyed!${destroyedIsToken ? ' (token — removed from the game)' : ''}`, { destroyedCard: destroyedUnit });
        }
        showCardToast(`${destroyedUnit.name} was destroyed!`, destroyedUnit.img);
        // Friendly-unit battle damage watchers (e.g. GD04-018 Gundam Pharact)
        // — the card text ("when one of your other Units receives damage from
        // an enemy") has no exception for lethal damage, but this watcher was
        // previously only fired from the non-lethal branch below, so a
        // friendly Unit destroyed outright by battle damage never notified
        // (see the "player" gating rationale on the non-lethal branch below).
        if (defenderSide === 'player') {
          state.playerField.forEach(w => {
            if (w && w.uid !== destroyedUnit.uid)
              window.CardEffects?.[w.cardNo]?.onAnyFriendlyUnitTakesBattleDamage?.(w, destroyedUnit, attackerSide);
          });
        }
        // Allows a unit's destroyed handler to pull its pilot out of the
        // trash queue before runEpilogue commits it (e.g. GD03-078).
        const rescueDestroyedPilot = () => {
          const idx = defenderTrashCards.findIndex(c => c.uid === destroyedUnit.pilot?.uid);
          if (idx !== -1) { defenderTrashCards.splice(idx, 1); defenderTrashGain--; }
        };
        // ownerSide: defenderSide — this unit belongs to the defending side.
        // Previously omitted here (only ever set on the pilot's own trigger
        // call below), so every Unit-level "[Destroyed] ... " effect that
        // gates on ctx.ownerSide (e.g. GD05-024 Gundam AGE 2 Normal (SP
        // Ver.), GD04-019 GN Armor Type-D, GD04-025 Gundvölva) silently
        // never fired when destroyed as the defender.
        triggerCardEffect(destroyedUnit, 'destroyed', { ...ctx, ownerSide: defenderSide, rescuePilot: rescueDestroyedPilot });
        if (destroyedUnit.pilot) {
          triggerCardEffect(destroyedUnit.pilot, 'destroyed', { ...ctx, ownerSide: defenderSide, unit: destroyedUnit, rescuePilot: rescueDestroyedPilot });
        }
        notifyUnitDestroyedByBattle(attacker, destroyedUnit, attackerSide, true);

        // Breach: deal bonus damage to the defending side's base or first shield
        const breachVal = getBreachValue(attacker);
        if (breachVal > 0) {
          if (state[`${defenderSide}Base`] && state[`${defenderSide}Base`].cantReceiveEffectDamage) {
            // Breach's bonus damage to a Base is "effect damage", not normal
            // combat damage — a permanently-immune Base (e.g. GD02-129
            // Argama) takes none of it (see dealDamageToBase()).
            addLog(`Breach ${breachVal} — ${state[`${defenderSide}Base`].name} is immune to effect damage.`);
          } else if (state[`${defenderSide}Base`]) {
            const base = state[`${defenderSide}Base`];
            base.hp = Math.max(0, (parseInt(base.hp) || 0) - breachVal);
            base.damaged = true;
            addLog(`Breach ${breachVal} — ${base.name} takes ${breachVal} damage (HP: ${base.hp}).`);
            if (base.hp <= 0) {
              const removedFromGame = isRemovedFromGameBase(base);
              if (!removedFromGame) { defenderTrashGain++; defenderTrashCards.push(toTrashCard(base)); }
              addLog(`Breach — ${base.name} is destroyed!${removedFromGame ? ' (removed from game)' : ''}`, { destroyedCard: base });
              showCardToast(`${base.name} was destroyed!`, base.img);
              state[`${defenderSide}Base`] = null;
              fireBaseDestroyedEffect(base);
              // "Shield area card" includes the base — mirrors the direct
              // base-attack branch above (previously missing here entirely,
              // so Breach's own bonus damage destroying a base never fired
              // these), so effects like Exia Trans-Am, Sinanju, and GD05-002
              // Strike Freedom Gundam's "destroys an enemy card" grant
              // (turnDestroyWatchers, targetType 'base') trigger correctly
              // when Breach's bonus damage destroys the base too, not just a
              // direct base attack.
              if (attackerSide === 'player') {
                window.CardEffects?.[attacker.cardNo]?.onAttackDestroysShield?.(attacker, 1);
                if (attacker.pilot) {
                  window.CardEffects?.[attacker.pilot.cardNo]?.onLinkedUnitAttackDestroysShield?.(attacker.pilot, attacker, 1);
                }
                state.playerField.forEach(c => {
                  if (c) window.CardEffects?.[c.cardNo]?.onAnyPlayerAttackDestroysShield?.(c, attacker, 1);
                });
                turnDestroyWatchers.forEach(w => w(attacker, base, attackerSide, 'base'));
              }
            } else {
              requestAnimationFrame(() => vibrateCard(defenderSide, base));
            }
          } else if (state[`${defenderSide}Shield`] > 0) {
            const breachAtkLv = parseInt(attacker.level) || 0;
            if (shieldImmuneLv >= 0 && breachAtkLv <= shieldImmuneLv) {
              addLog(`Breach ${breachVal} — shield damage blocked by shield immunity (Lv.${breachAtkLv} ≤ Lv.${shieldImmuneLv}).`);
              if (state.networked && defenderSide === 'player') broadcastCounters();
            } else if (shieldImmuneAp >= 0 && atkBp <= shieldImmuneAp) {
              addLog(`Breach ${breachVal} — shield damage blocked by shield immunity (AP.${atkBp} ≤ AP.${shieldImmuneAp}).`);
              if (state.networked && defenderSide === 'player') broadcastCounters();
            } else {
              state[`${defenderSide}Shield`] = Math.max(0, state[`${defenderSide}Shield`] - 1);
              addLog(`Breach ${breachVal} — opponent shield destroyed!`);
              // onAnyPlayerAttackDestroysShield: also fires for Breach-driven
              // shield destruction (e.g. GD02-001 Psycho Gundam). turnDestroyWatchers
              // (targetType 'shield') alongside it — e.g. GD05-002 Strike
              // Freedom Gundam's "destroys an enemy card" grant. onAttackDestroysShield/
              // onLinkedUnitAttackDestroysShield also fire here (e.g. ST03-001 Sinanju,
              // GD06-089 McGillis Fareed) — previously missing from this one branch even
              // though the Breach-driven BASE-destroy branch just above already fires
              // them, so a Breach shield kill silently didn't count for these while a
              // Breach base kill did.
              if (attackerSide === 'player') {
                window.CardEffects?.[attacker.cardNo]?.onAttackDestroysShield?.(attacker, 1);
                if (attacker.pilot) {
                  window.CardEffects?.[attacker.pilot.cardNo]?.onLinkedUnitAttackDestroysShield?.(attacker.pilot, attacker, 1);
                }
                state.playerField.forEach(c => {
                  if (c) window.CardEffects?.[c.cardNo]?.onAnyPlayerAttackDestroysShield?.(c, attacker, 1);
                });
                turnDestroyWatchers.forEach(w => w(attacker, null, attackerSide, 'shield'));
              }
              // Mirrors the direct shield-attack path above: only the shield's
              // owner has the real card data (shield identities are private
              // per-client) to pick/reveal/resolve, and it self-syncs to the
              // peer via SHIELD_REVEAL. Previously this just bumped a generic
              // trash counter without ever revealing which card was hit.
              if (defenderSide === 'player') {
                resolveShieldHit('player', 1);
              } else if (state.isBotMatch) {
                resolveShieldHit('opp', 1);
              } else if (!state.networked) {
                // Solo practice — dummy opponent has no real shield card data.
                defenderTrashGain++;
              }
            }
          }
        }
      }
    } else if (!defenderImmune && atkBpNet > 0) {
      if (proxyUnit) {
        // Non-lethal damage to proxy unit (Lunamaria Hawke intercept).
        // atkBpNet (not raw atkBp) — see the non-proxy branch below for why:
        // this must match the reduced value the lethality check above used.
        proxyUnit.hp = (parseInt(proxyUnit.hp) || 0) - atkBpNet;
        proxyUnit.damaged = true;
        addLog(`${proxyUnit.name} takes ${atkBpNet} damage in place of ${defender.name} (HP now: ${effectiveHp(proxyUnit)}).`);
        // Friendly-unit battle damage watchers (e.g. GD04-018 Gundam Pharact)
        // — same notification the non-proxy branch below fires. Gated to
        // defenderSide === 'player' — same "fires on the defending client
        // only" idiom used by onBaseTakesBattleDamage right below — because
        // both clients independently replay this whole ATTACK action (see
        // network.js's mirrored dispatch('ATTACK', {mine:false})), so the
        // OTHER side's own watchers already fire on THEIR client when
        // defenderSide is 'player' from their perspective. Looping the
        // opponent's field here as well used state.myTurn (which only
        // reflects this client's own turn) to gate an opponent-owned card's
        // "during your turn" text, and credited any EX Resource gained to
        // this client's own counters — both wrong for a card that isn't ours.
        if (defenderSide === 'player') {
          state.playerField.forEach(w => {
            if (w && w.uid !== proxyUnit.uid)
              window.CardEffects?.[w.cardNo]?.onAnyFriendlyUnitTakesBattleDamage?.(w, proxyUnit, attackerSide);
          });
        }
      } else {
        // 'NoBattleDamageFromHP<N>' tempKeyword: unit is immune to battle damage
        // from attackers whose current HP ≤ N (e.g. GD02-040 Gundam Ashtaron deploy).
        const immKw = (defender.tempKeywords || []).find(kw => /^NoBattleDamageFromHP\d+$/.test(kw));
        const immuneThreshold = immKw ? parseInt(immKw.replace('NoBattleDamageFromHP', '')) : -1;
        if (immuneThreshold >= 0 && atkHp <= immuneThreshold) {
          addLog(`${defender.name} — immune to battle damage from ${attacker.name} (attacker HP: ${atkHp} ≤ ${immuneThreshold}).`);
        } else {
          // atkBpNet (not raw atkBp): defBattleDefMod/nextDamageReduction/
          // passiveUnitBattleDamageReduction/tempIncomingDamageReduction/
          // tempBattleDamageReduction are all folded into atkBpNet and were
          // already respected by the lethality check above (atkBpForDestCheck,
          // derived from atkBpNet) — applying the unreduced atkBp here instead
          // let a defender with any active reduction take MORE damage than the
          // "non-lethal" classification assumed, silently reaching 0 HP (or
          // below) without ever being destroyed (reported: a 0-HP enemy unit
          // stuck on the field after taking exactly-lethal-looking damage).
          defender.hp      = (parseInt(defender.hp) || 0) - atkBpNet;
          defender.damaged = true;
          addLog(`${defender.name} takes ${atkBpNet} damage (HP now: ${effectiveHp(defender)}).`);
          // Friendly-unit battle damage watchers (e.g. GD04-018 Gundam Pharact).
          // Gated to defenderSide === 'player' (see the proxy branch above for
          // why) AND isUnitAttack, since `defender` here can be a Base (a
          // direct non-lethal attack on a Base falls into this same branch) —
          // the hook's contract is "a friendly UNIT took damage," and a Base
          // isn't a Unit even when its own trait happens to match (e.g.
          // ST01-016 Asticassia is itself "(Academy)", which previously made
          // attacking it falsely trigger Gundam Pharact's Unit-only watcher).
          if (defenderSide === 'player' && isUnitAttack) {
            state.playerField.forEach(w => {
              if (w && w.uid !== defender.uid)
                window.CardEffects?.[w.cardNo]?.onAnyFriendlyUnitTakesBattleDamage?.(w, defender, attackerSide);
            });
          }
        }
      }
    }

    // Passive hook: base takes battle damage (e.g. GD04-126 Izuma Colony).
    // Fires on the defending client only (defenderSide === 'player') so NET.send
    // inside the handler syncs the result once without doubling in networked games.
    // Skipped when defenderImmune — the base took no damage (e.g. A Baoa Qu).
    if (!isUnitAttack && !defender.isShield && !defenderImmune && atkBp > 0 && defenderSide === 'player') {
      const baseHandlers = window.CardEffects?.[defender.cardNo];
      if (baseHandlers?.onBaseTakesBattleDamage) {
        baseHandlers.onBaseTakesBattleDamage(defender, attacker, atkBp);
      }
    }

    // Attacker destroyed? (skipped entirely when First Strike already destroyed
    // the defender — the attacker takes no damage in that case)
    // passiveAttackerImmuneFromDefender: pilot grants immunity to counter-damage (Chang Wufei, Allelujah, Tokwan)
    let attackerImmuneFromDef = false;
    if (!defenderDestroyedByFirstStrike && isUnitAttack && attacker.pilot) {
      const h = window.CardEffects?.[attacker.pilot.cardNo];
      if (h?.passiveAttackerImmuneFromDefender?.(attacker.pilot, attacker, defender, attackerSide)) {
        attackerImmuneFromDef = true;
        addLog(`${attacker.name} — immune to counter-damage from ${defender.name} this battle.`);
      }
    }
    // Unit-level counter-damage immunity (e.g. GD02-006 Forbidden Gundam: immune to Lv.2 or lower)
    if (!attackerImmuneFromDef && !defenderDestroyedByFirstStrike && isUnitAttack) {
      const uh = window.CardEffects?.[attacker.cardNo];
      if (uh?.passiveUnitAttackerImmuneFromDefender?.(attacker, defender, attackerSide)) {
        attackerImmuneFromDef = true;
        addLog(`${attacker.name} — immune to counter-damage from ${defender.name} (unit passive).`);
      }
    }
    if (!defenderDestroyedByFirstStrike && !attackerImmuneFromDef) {
      if (attackerProxy) {
        // Elan Ceres (GD04-087): counter-damage redirected to chosen (Academy) unit
        const proxyHpNow = effectiveHp(attackerProxy);
        if (defBpNet >= proxyHpNow || destroyOnHitForAttacker) {
          const proxyIsToken = isUnitToken(attackerProxy);
          if (!proxyIsToken) { attackerTrashCards.push(toTrashCard(attackerProxy)); attackerTrashGain++; }
          if (attackerProxy.pilot) { attackerTrashCards.push(toTrashCard(attackerProxy.pilot)); attackerTrashGain++; }
          const proxyFieldIdx = state[`${attackerSide}Field`].findIndex(c => c && c.uid === attackerProxy.uid);
          if (proxyFieldIdx !== -1) state[`${attackerSide}Field`][proxyFieldIdx] = null;
          addLog(`${attackerProxy.name} is destroyed (intercepted counter-damage for ${attacker.name})!${proxyIsToken ? ' (token)' : ''}`, { destroyedCard: attackerProxy });
          showCardToast(`${attackerProxy.name} was destroyed!`, attackerProxy.img);
          // Friendly-unit battle damage watchers (e.g. GD04-018 Gundam Pharact)
          // — fires regardless of whether the counter-damage was lethal (see
          // the non-lethal branch below for the full rationale).
          if (attackerSide === 'player') {
            state.playerField.forEach(w => {
              if (w && w.uid !== attackerProxy.uid)
                window.CardEffects?.[w.cardNo]?.onAnyFriendlyUnitTakesBattleDamage?.(w, attackerProxy, defenderSide);
            });
          }
          // ownerSide: attackerSide — see the defender branch above for why
          // this was previously missing on the unit's own trigger call.
          triggerCardEffect(attackerProxy, 'destroyed', { ...ctx, ownerSide: attackerSide });
          if (attackerProxy.pilot) triggerCardEffect(attackerProxy.pilot, 'destroyed', { ...ctx, ownerSide: attackerSide, unit: attackerProxy });
          notifyUnitDestroyedByBattle(defender, attackerProxy, defenderSide, false);
        } else if (defBpNet > 0) {
          attackerProxy.hp = (parseInt(attackerProxy.hp) || 0) - defBpNet;
          attackerProxy.damaged = true;
          addLog(`${attackerProxy.name} takes ${defBpNet} counter-damage in place of ${attacker.name} (HP now: ${effectiveHp(attackerProxy)}).`);
          // Friendly-unit battle damage watchers (e.g. GD04-018 Gundam Pharact:
          // "when one of your other (Academy) Units receives damage from an
          // enemy") — same notification the non-proxy branch below fires.
          // Gated to attackerSide === 'player' (see the defender branch above
          // for why — the opposing client independently fires its own
          // watchers when it's their turn from their perspective).
          if (attackerSide === 'player') {
            state.playerField.forEach(w => {
              if (w && w.uid !== attackerProxy.uid)
                window.CardEffects?.[w.cardNo]?.onAnyFriendlyUnitTakesBattleDamage?.(w, attackerProxy, defenderSide);
            });
          }
        }
      } else {
        if (defBpNet >= atkHp || destroyOnHitForAttacker) {
          const attackerIsToken = isUnitToken(attacker);
          if (!attackerIsToken) { attackerTrashCards.push(toTrashCard(attacker)); attackerTrashGain++; }
          if (attacker.pilot) { attackerTrashCards.push(toTrashCard(attacker.pilot)); attackerTrashGain++; }
          const idx = state[`${attackerSide}Field`].findIndex(c => c && c.uid === attacker.uid);
          if (idx !== -1) state[`${attackerSide}Field`][idx] = null;
          addLog(`${attacker.name} is destroyed!${attackerIsToken ? ' (token — removed from the game)' : ''}`, { destroyedCard: attacker });
          showCardToast(`${attacker.name} was destroyed!`, attacker.img);
          // Friendly-unit battle damage watchers (e.g. GD04-018 Gundam Pharact)
          // — fires regardless of whether the counter-damage was lethal (see
          // the non-lethal branch below for the full rationale). `attacker`
          // is always a genuine field Unit here (only Units can attack).
          if (attackerSide === 'player') {
            state.playerField.forEach(w => {
              if (w && w.uid !== attacker.uid)
                window.CardEffects?.[w.cardNo]?.onAnyFriendlyUnitTakesBattleDamage?.(w, attacker, defenderSide);
            });
          }
          const rescueAttackerPilot = () => {
            const idx = attackerTrashCards.findIndex(c => c.uid === attacker.pilot?.uid);
            if (idx !== -1) { attackerTrashCards.splice(idx, 1); attackerTrashGain--; }
          };
          // ownerSide: attackerSide — see the defender branch above for why
          // this was previously missing on the unit's own trigger call (e.g.
          // GD05-024 Gundam AGE 2 Normal (SP Ver.) never fired its
          // [Destroyed] effect when destroyed by counter-damage as attacker).
          triggerCardEffect(attacker, 'destroyed', { ...ctx, ownerSide: attackerSide, rescuePilot: rescueAttackerPilot });
          if (attacker.pilot) {
            triggerCardEffect(attacker.pilot, 'destroyed', { ...ctx, ownerSide: attackerSide, unit: attacker, rescuePilot: rescueAttackerPilot });
          }
          notifyUnitDestroyedByBattle(defender, attacker, defenderSide, false);
        } else if (defBpNet > 0) {
          attacker.hp      = (parseInt(attacker.hp) || 0) - defBpNet;
          attacker.damaged = true;
          addLog(`${attacker.name} takes ${defBpNet} damage (HP now: ${effectiveHp(attacker)}).`);
          // Friendly-unit battle damage watchers (e.g. GD04-018 Gundam Pharact).
          // Gated to attackerSide === 'player' (see above); `attacker` is
          // always a genuine field Unit here (only Units can attack).
          if (attackerSide === 'player') {
            state.playerField.forEach(w => {
              if (w && w.uid !== attacker.uid)
                window.CardEffects?.[w.cardNo]?.onAnyFriendlyUnitTakesBattleDamage?.(w, attacker, defenderSide);
            });
          }
        }
      }
    } // end !defenderDestroyedByFirstStrike && !attackerImmuneFromDef

    // [Once per Turn] passives that react when a player (TSA) unit deals
    // non-lethal battle damage — e.g. GD03-076 Freedom Gundam (METEOR) bounce.
    // Only fires when: player is attacker, target is a unit (not shield/base),
    // attacker has (Triple Ship Alliance) trait, defender survived
    // (atkBpNet < defHp — must match the actual damage applied above, not
    // the unreduced atkBp, or this could disagree with whether the defender
    // is really still alive when a damage reduction was active).
    if (!epilogueDeferred && isUnitAttack &&
        attackerSide === 'player' &&
        atkBpNet > 0 && atkBpNet < defHp &&
        (attacker.trait || '').includes('(Triple Ship Alliance)')) {
      const reactor = state.playerField.find(unit =>
        unit && !unit.passiveUsedThisTurn &&
        window.CardEffects?.[unit.cardNo]?.onBattleDamageSurvived
      );
      if (reactor) {
        epilogueDeferred = true;
        reactor.passiveUsedThisTurn = true;
        currentLogActor = cardOwnerSide(reactor) || currentLogActor;
        window.CardEffects[reactor.cardNo].onBattleDamageSurvived(reactor, attacker, defender, runEpilogue);
      }
    }

    if (!epilogueDeferred) runEpilogue();

    } catch (err) {
      console.error('[resolveAttack] unhandled exception during combat resolution — forcing epilogue:', err);
      if (!epilogueDeferred) runEpilogue();
    }
  }

  if (isUnitAttack) {
    // 0.5s delay then vibrate both units for 0.5s, then resolve.
    // Skip the attacker vibrate when First Strike destroyed the defender —
    // the attacker took no damage and should not shake.
    setTimeout(() => {
      if (!skipAttackerVibrate) vibrateCard(attackerSide, attacker);
      vibrateCard(defenderSide, defender);
      setTimeout(resolveAttack, 1000);
    }, 500);
  } else if (defender.isDirect) {
    resolveAttack();
  } else {
    // Base or shield attack — only the target card vibrates; attacker takes no damage
    setTimeout(() => {
      const targetEl = vibrateCard(defenderSide, defender);
      if (targetEl) {
        setTimeout(resolveAttack, 1000);
      } else {
        resolveAttack();
      }
    }, 500);
  }
}

// Collects all valid attack targets for `attacker` and shows a picker so the
// player can choose one without any intermediate "select unit then click
// target" step. Respects forced targets, active-unit grants, cantAttackNonUnit
// restrictions, and the base→shield→direct priority order. If only one valid
// target exists the picker is skipped and the confirm modal opens directly.
// Builds the full list of valid attack targets for `attacker` right now —
// enemy Units (rested, or active if some exception grants it), then the
// enemy Base/Shield/Direct trio unless cardCantAttackNonUnit blocks all
// three (e.g. GD04-065 Banshee Norn (Destroy Mode) after its own [Activate･
// Main]: "can't choose the enemy player as its attack target" = can't
// target Base, Shield, OR Direct — see isAttackingEnemyPlayer's identical
// convention in white.js). Shared by showAttackTargetPicker (to actually
// list them) and the attack-icon visibility check in renderSlot (so the
// icon is never shown when this would come back empty) — keeping this in
// one place means the icon can never promise an attack the picker can't
// actually offer.
function getValidAttackTargets(attacker, attackerSide = 'player') {
  // defenderSide: the side being attacked. Used by the bot engine, which
  // computes its own attacks from the 'opp' side's perspective (attackerSide
  // === 'opp' → defenderSide === 'player') — both existing call sites omit
  // this param and keep the original player-attacks-opp behavior unchanged.
  const defenderSide = attackerSide === 'player' ? 'opp' : 'player';
  const targets = [];
  const _ftPool = getForcedAttackTargetPool(defenderSide);
  const _ftUid  = _ftPool ? null : getForcedAttackTargetUid(defenderSide, attacker);

  if (_ftPool && _ftPool.length > 0) {
    _ftPool.forEach(c => { if (c) targets.push(c); });
  } else if (_ftUid) {
    const ft = state[`${defenderSide}Field`].find(c => c && c.uid === _ftUid);
    if (ft) targets.push(ft);
  } else {
    for (const c of state[`${defenderSide}Field`]) {
      if (!c) continue;
      // passiveCantBeAttackTarget: absolute "enemy Units can't choose this
      // Unit as their attack target" restriction (e.g. ST11-001 Char's
      // Z'Gok) — excluded from the normal target list entirely, active or
      // rested, unlike the CanAttackActive* exceptions below which only ever
      // ADD extra targetable active units.
      if (window.CardEffects?.[c.cardNo]?.passiveCantBeAttackTarget?.(c)) continue;
      // CantBeAttackTarget tempKeyword: turn-scoped counterpart to the
      // permanent hook above (e.g. ST11-014 The Orca of Red Sea's [Action]
      // "Enemy Units can't choose it as their attack target this turn").
      // Synced via TEMP_KEYWORD_ADD so it reads correctly on the attacker's
      // client too (checking their own oppField mirror of this card).
      if ((c.tempKeywords || []).includes('CantBeAttackTarget')) continue;
      if (!c.rested) {
        const canAnyActive = canAttackActiveUnits(attacker);
        let maxActiveLevel = -1;
        [2, 3, 4, 5].forEach(n => {
          if (hasNativeKeyword(attacker, `CanAttackActiveLv${n}`)) maxActiveLevel = Math.max(maxActiveLevel, n);
        });
        let maxActiveAp = window.CardEffects?.[attacker.cardNo]?.canAttackActiveMaxAp?.(attacker) ?? -1;
        // CanAttackActiveAP<N> tempKeyword: attacker can target active units whose
        // current AP ≤ N (e.g. token buffed by GD02-123 Sodon's deploy effect).
        (attacker.tempKeywords || []).forEach(kw => {
          const m = kw.match(/^CanAttackActiveAP(\d+)$/);
          if (m) maxActiveAp = Math.max(maxActiveAp, parseInt(m[1]));
        });
        const canAttackActiveDamaged  = hasNativeKeyword(attacker, 'CanAttackActiveDamaged') && c.damaged;
        const canTargetByLevel        = maxActiveLevel !== -1 && (parseInt(c.level) || 0) <= maxActiveLevel;
        const canTargetByAp           = maxActiveAp   !== -1 && effectiveBp(c) <= maxActiveAp;
        const canAttackThisActive     = !!(window.CardEffects?.[attacker.cardNo]?.canAttackThisActiveUnit?.(attacker, c));
        const canAttackActiveNoPilot  = (attacker.tempKeywords || []).includes('CanAttackActiveNoPilot') && !c.pilot;
        // CanAttackActiveBlocker tempKeyword: attacker can target active enemy
        // Units with <Blocker> specifically (e.g. EB01-066 Reiji).
        const canAttackActiveBlocker  = (attacker.tempKeywords || []).includes('CanAttackActiveBlocker') && hasNativeKeyword(c, 'Blocker');
        if (!canAnyActive && !canTargetByLevel && !canTargetByAp && !canAttackActiveDamaged && !canAttackThisActive && !canAttackActiveNoPilot && !canAttackActiveBlocker) continue;
      }
      targets.push(c);
    }
    if (!cardCantAttackNonUnit(attacker)) {
      if (state[`${defenderSide}Base`]) {
        targets.push(state[`${defenderSide}Base`]);
      } else if (state[`${defenderSide}Shield`] > 0) {
        targets.push({ name: 'Shield', uid: `__${defenderSide}-shield__`, isShield: true,
                       type: 'shield', bp: 0, hp: 1, img: 'assets/images/back.webp' });
      } else if (!attacker.tempCantDirectAttack && !window.CardEffects?.[attacker.cardNo]?.cantDirectAttack?.(attacker)) {
        // tempCantDirectAttack: "can't choose the enemy player as its attack
        // target during this turn" (e.g. GD05-057 Gyunei's Jagd Doga) — a
        // narrower restriction than tempCantAttackNonUnit: base and shield
        // targeting are still allowed, only the final direct-attack-the-
        // player target (available once base/shields are both gone) is
        // blocked. Self-only read of the controller's own field, so no NET
        // mirroring is needed (same convention as tempCantAttackNonUnit).
        // cantDirectAttack(card) → bool is the PERMANENT (not turn-scoped)
        // hook counterpart — e.g. GD05-075 Royal Gundam: "This Unit can't
        // choose the enemy player as its attack target" (always true, base/
        // shield still allowed).
        targets.push({ name: 'Direct Attack', uid: `__${defenderSide}-direct__`, isDirect: true,
                       type: 'direct', bp: 0, hp: 0, img: null });
      }
    }
  }
  return targets;
}

function showAttackTargetPicker(attacker) {
  if (!state.myTurn || state.attackInProgress || state.modalHidden) return;
  if (cardCantAttackNow(attacker)) return;

  const targets = getValidAttackTargets(attacker);
  if (targets.length === 0) {
    addLog(`${attacker.name} has no valid attack targets.`);
    return;
  }
  showCardPickerModal(
    `${attacker.name} — Choose Attack Target`,
    'Select 1 target to attack.',
    targets,
    (target) => {
      if (!target) return;
      dispatch('ATTACK', {
        attackerUid: attacker.uid,
        defenderUid: target.uid,
        isShield: !!target.isShield,
        isDirect: !!target.isDirect,
      });
    },
    null,
    { mandatory: false, skipLabel: 'Cancel' }
  );
}


// ── handleDrop mutation handlers — registered once, invoked via dispatch() ──
registerAction('RETURN_TO_HAND', ({ slot }) => {
  const card = state.playerField[slot];
  state.playerField[slot] = null;
  if (card.pilot) {
    const dbPilot = cardDb[card.pilot.cardNo];
    const pilotHandCard = dbPilot
      ? buildGameCardFromDb(dbPilot, card.pilot.copyVer)
      : { ...card.pilot, rested: false, damaged: false };
    updateTempState(card.pilot.cardNo, card.pilot.copyVer, 'hand');
    state.hand.push(pilotHandCard);
  }
  const dbEntry = cardDb[card.cardNo];
  const unitHandCard = dbEntry
    ? buildGameCardFromDb(dbEntry, card.copyVer)
    : { ...card, pilot: null, rested: false, damaged: false };
  updateTempState(unitHandCard.cardNo, unitHandCard.copyVer, 'hand');
  state.hand.push(unitHandCard);
  addLog(`Returned ${card.name}${card.pilot ? ` and ${card.pilot.name}` : ''} to hand.`);
});

registerAction('PLAY_CARD', ({ uid, slot }) => {
  const card = state.hand.find(c => c.uid === uid);
  playSfx('card_played');
  // Cost must be read BEFORE the card touches state.playerField — a
  // self-referential handCostReduction condition like ST08-001 Ξ Gundam's
  // "while you have no Lv.6+ Units in play, this card in hand gets cost -1
  // per enemy Unit" would otherwise see its OWN 9-cost self already sitting
  // in playerField by the time the discount is computed, falsifying "no
  // Lv.6+ Units in play" and silently charging full price instead of the
  // discounted cost the player agreed to pay.
  const cost = effectiveHandCost(card);
  // Placed on the field BEFORE spendResources() (not after, as before) —
  // spendResources() fires notifyExResourceSpent() when an EX Resource pays
  // part of the cost, which hooks like GD05-018 Gundam Calibarn's
  // onExResourceSpent use to offer a "reduce damage by 3 this turn" bonus to
  // one of the player's own Units. That candidate list is built from
  // state.playerField at that instant — with the old order the just-played
  // card wasn't in the field yet, so a unit deployed BY spending the very EX
  // Resource that triggers the bonus could never be chosen for it, even
  // though it's the most natural target. Only the resulting NUMBER needs to
  // be computed pre-placement (above) — spendResources() itself doesn't
  // read the card, so this reordering doesn't disturb the Calibarn fix.
  state.playerField[slot] = card;
  spendResources(cost);
  card.tempCantAttack = true; // deploy sickness — cleared by UNREST_ALL on next turn
  // passiveForceNonTokenDeployRested: a link unit's passive forces Lv.3- non-token
  // units to deploy rested (e.g. GD04-022 Kikeroga MS Mode "[During Link]").
  if (!isUnitToken(card) && (parseInt(card.level) || 0) <= 3) {
    const forceRested = state.playerField.some(fc =>
      fc && fc.uid !== card.uid && isLinkUnit(fc) &&
      !!(window.CardEffects?.[fc.cardNo]?.passiveForceNonTokenDeployRested?.(fc, card))
    );
    if (forceRested) {
      card.rested = true;
      addLog(`${card.name} — deployed rested (Link Unit passive).`);
    }
  }
  // forceEnemyDeployRestedThreshold: an ENEMY Unit's passive (from the
  // deploying player's perspective, state.oppField) forces this newly played
  // Unit to enter rested if its Lv. is at or below that Unit's live threshold
  // (e.g. GD05-026 Gundam Aerial Rebuild: threshold = count of the
  // controller's Units with "Gundam Lfrith"/"Gundnode" in their name, plus
  // itself). Checked on the DEPLOYING client so the resolved rested flag is
  // simply copied over to the opponent's mirror with the rest of the card.
  if (!card.rested) {
    const threshold = state.oppField.reduce((max, fc) => {
      if (!fc) return max;
      const t = window.CardEffects?.[fc.cardNo]?.forceEnemyDeployRestedThreshold?.(fc);
      return (typeof t === 'number' && t > max) ? t : max;
    }, -1);
    if (threshold >= 0 && (parseInt(card.level) || 0) <= threshold) {
      card.rested = true;
      addLog(`${card.name} — deployed rested (enemy Unit's passive, Lv.${threshold} or lower).`);
    }
  }
  state.hand = state.hand.filter(c => c.uid !== uid);
  updateTempState(card.cardNo, card.copyVer, 'field');
  addLog(`Played ${card.name}.`);
  triggerCardEffect(card, 'deploy', { slot });
  const trait = card.trait || '';
  if (trait.includes('Superpower Bloc') || trait.includes('(UN)')) {
    state.superpowerDeployedThisTurn = true;
  }
  // Notify other field cards that a player unit was deployed (e.g. GD04-033
  // Neo Zeong: "when one of your (Neo Zeon) Units is deployed, deal 3 damage").
  state.playerField.forEach(c => {
    if (c && c.uid !== card.uid) {
      window.CardEffects?.[c.cardNo]?.onAnyPlayerUnitDeployed?.(c, card);
    }
  });
});

// Shown whenever a Unit/token deploy has nowhere to go because the player's
// field (6 slots) is already full. Lets the player choose one of their own
// field cards to send to trash to make room. Uses the DISCARD_CARD action —
// the same "remove from field into trash" path used for hand-limit discards
// and self-sacrifice effects — which does NOT fire the trashed card's
// [Destroyed] trigger; trashing to make room is not a destruction.
// onDone(freedSlot) fires with the now-empty slot index once resolved, or
// onDone(-1) if the player cancelled (only possible when mandatory: false —
// a mandatory prompt has no Cancel/Skip option).
function promptTrashToMakeRoom(deployLabel, onDone, { mandatory = true } = {}) {
  const candidates = state.playerField.filter(Boolean);
  if (candidates.length === 0) { onDone(-1); return; } // field can't be "full" with nothing on it
  showCardPickerModal(
    'Field is Full',
    `Your field is full (6/6). Choose a Unit to trash to make room${deployLabel ? ` for ${deployLabel}` : ''}.`,
    candidates,
    (chosen) => {
      if (!chosen) { onDone(-1); return; }
      const slot = state.playerField.findIndex(c => c && c.uid === chosen.uid);
      if (slot === -1) { onDone(-1); return; }
      dispatch('DISCARD_CARD', { uid: chosen.uid, source: 'field', slot });
      addLog(`${chosen.name} trashed to make room.`);
      broadcastCounters();
      render();
      setupDropZones();
      onDone(slot);
    },
    null,
    { mandatory, skipLabel: 'Cancel' }
  );
}

// Instantiates a token card (e.g. ST04-012's T-008 "Aile Strike Gundam")
// straight from the database into the first empty field slot — tokens aren't
// part of any deck/tempDeck, so a synthetic copyVer keeps their uid unique
// across multiple tokens of the same cardNo in a single game. Mirrors via the
// existing PLAY_CARD network message (same shape remoteHandlers.PLAY_CARD
// already expects), so no new message type is needed. Returns the placed
// card, or null if there was no such token in cardDb.
function _placeTokenAtSlot(entry, slot, deployRested) {
  const copyVer = `token-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
  const card = buildGameCardFromDb(entry, copyVer);
  playSfx('card_played');
  state.playerField[slot] = card;
  card.tempCantAttack = true; // deploy sickness — cleared by UNREST_ALL on next turn
  if (deployRested) card.rested = true;
  addLog(`Deployed token ${card.name}${deployRested ? ' (rested)' : ''}.`);
  NET.send({ type: 'PLAY_CARD', slot, card, counters: myCounters() });
  // Notify other field units that a player unit was deployed (e.g. GD04-033
  // Neo Zeong: "when one of your (Neo Zeon) Units is deployed, deal 3
  // damage") — same notification the hand-play PLAY_CARD path already
  // sends, but token deploys (Striker Pack, etc.) went through this
  // separate function and had never sent it, so any effect hooking
  // onAnyPlayerUnitDeployed silently missed every token deploy.
  state.playerField.forEach(c => {
    if (c && c.uid !== card.uid) {
      window.CardEffects?.[c.cardNo]?.onAnyPlayerUnitDeployed?.(c, card);
    }
  });
  render();
  setupDropZones();
  return card;
}

// Token deploys are always effect-driven (never a "you may" the player is
// mid-way through declining), so a full field doesn't cancel the deploy —
// it prompts the player to trash a field card to make room instead. That
// step is asynchronous (waits on the player's picker choice), so it can't be
// reflected in this function's own return value the way the room-available
// case can — callers that synchronously use the return value (e.g. to count
// how many tokens got deployed in a loop) won't see this delayed placement
// counted, but the token itself still deploys correctly once the player
// picks.
function deployTokenToField(cardNo, { rested: deployRested = false } = {}) {
  const entry = cardDb[cardNo];
  if (!entry) return null;
  const slot = state.playerField.findIndex(c => !c);
  if (slot !== -1) return _placeTokenAtSlot(entry, slot, deployRested);
  promptTrashToMakeRoom(entry.name, (freedSlot) => {
    if (freedSlot === -1) return;
    _placeTokenAtSlot(entry, freedSlot, deployRested);
  }, { mandatory: true });
  return null;
}

registerAction('SWAP_SLOTS', ({ srcSlot, tgtSlot }) => {
  const card = state.playerField[srcSlot];
  const displaced = state.playerField[tgtSlot];
  state.playerField[tgtSlot] = card;
  state.playerField[srcSlot] = displaced;
  addLog(`Moved ${card.name} to slot ${tgtSlot + 1}.`);
});

// destroy: true (e.g. GD01-002 Unicorn Gundam (Destroy Mode)'s alt-cost
// sacrifice) marks this as an actual destruction rather than a plain trash —
// it fires the card's own [Destroyed] trigger before its pilot is committed
// to trash, so an effect like GD01-005's "[During Link][Destroyed] Return
// this Unit's paired Pilot to its owner's hand" can rescue it (via
// ctx.rescuePilot(), same convention as the battle-destroy paths in
// resolveAttack). A plain trash (hand-limit discards, promptTrashToMakeRoom)
// is NOT a destruction and never fires [Destroyed] — default false preserves
// that for every other existing call site.
registerAction('DISCARD_CARD', ({ uid, source, slot, destroy = false }) => {
  let card;
  if (source === 'hand') {
    card = state.hand.find(c => c.uid === uid);
    state.hand = state.hand.filter(c => c.uid !== uid);
  } else if (source === 'field') {
    card = state.playerField[slot];
    state.playerField[slot] = null;
  }
  let pilotToTrash = card.pilot || null;
  if (destroy) {
    const rescuePilot = () => { pilotToTrash = null; };
    triggerCardEffect(card, 'destroyed', { ownerSide: 'player', rescuePilot });
    if (card.pilot) triggerCardEffect(card.pilot, 'destroyed', { ownerSide: 'player', unit: card, rescuePilot });
  }
  if (pilotToTrash) {
    updateTempState(pilotToTrash.cardNo, pilotToTrash.copyVer, 'trash');
    state.trashCount++;
    state.playerTrash.push(toTrashCard(pilotToTrash));
  }
  if (isUnitToken(card)) {
    addLog(`${card.name} (token) is removed from the game.`);
  } else {
    updateTempState(card.cardNo, card.copyVer, 'trash');
    state.trashCount++;
    state.playerTrash.push(toTrashCard(card));
    addLog(`${card.name} sent to trash.`);
  }
});

registerAction('PLACE_SHIELD', ({ uid }) => {
  const card = state.hand.find(c => c.uid === uid);
  state.hand = state.hand.filter(c => c.uid !== uid);
  state.playerShield++;
  updateTempState(card.cardNo, card.copyVer, 'shield');
  addLog(`${card.name} added to shield. Shield: ${state.playerShield}`);
});

registerAction('DEPLOY_BASE', ({ uid }) => {
  const card = state.hand.find(c => c.uid === uid);
  spendResources(effectiveHandCost(card));
  state.hand = state.hand.filter(c => c.uid !== uid);
  updateTempState(card.cardNo, card.copyVer, 'base');
  state.playerBase = card;
  const drawnUid = applyBaseDeployEffect();
  addLog(`${card.name} set as base.`);
  // Mirror to the peer BEFORE triggering the deploy effect (rather than
  // relying on the generic post-dispatch mirror in network.js) — a base's
  // own [Deploy] effect can immediately open a card picker (e.g. ST09-010
  // Minerva), which sends PICKER_OPEN synchronously inside
  // triggerCardEffect() below. If DEPLOY_BASE arrived after that, the
  // peer's DEPLOY_BASE handler unconditionally hides the decision-waiting
  // overlay (needed for the unrelated "waiting on opponent's base
  // decision" shield-reveal flow) and would wrongly cancel the overlay
  // PICKER_OPEN just correctly showed for this still-open picker.
  NET.send({ type: 'DEPLOY_BASE', card: state.playerBase, counters: myCounters() });
  triggerCardEffect(card, 'deploy', {});
  return drawnUid;
});

registerAction('REPLACE_BASE', ({ uid }) => {
  const oldBase = state.playerBase;
  const oldName = oldBase.name;
  if (!isRemovedFromGameBase(oldBase)) {
    state.trashCount++;
    state.playerTrash.push(toTrashCard(oldBase));
    updateTempState(oldBase.cardNo, oldBase.copyVer, 'trash');
    addLog(`${oldName} sent to trash.`);
    broadcastCardToast(`${oldName} was sent to trash.`, oldBase.img);
  }
  const card = state.hand.find(c => c.uid === uid);
  spendResources(effectiveHandCost(card));
  state.hand = state.hand.filter(c => c.uid !== uid);
  updateTempState(card.cardNo, card.copyVer, 'base');
  state.playerBase = card;
  const drawnUid = applyBaseDeployEffect();
  addLog(`${card.name} replaced ${oldName} as base.`);
  // See DEPLOY_BASE above — mirror before triggering the deploy effect so a
  // picker it opens (e.g. Minerva) can't have its PICKER_OPEN overlay
  // wrongly cancelled by this message arriving afterward.
  NET.send({ type: 'DEPLOY_BASE', card: state.playerBase, counters: myCounters() });
  triggerCardEffect(card, 'deploy', {});
  return drawnUid;
});

// Deploys (or, if one is already active, asks to replace) a Base card
// dropped from hand. Shared by dropping directly on the base zone and
// dropping anywhere on the field — the latter is a convenience so players
// don't have to aim precisely for the base zone, since Base cards never
// actually occupy a field slot either way.
function deployBaseFromHand(card) {
  const cardLevel = parseInt(card.level) || 0;
  const cardCost  = effectiveHandCost(card);
  const totalRes  = state.res + state.exRes;
  if (state.playerLevel < cardLevel) {
    const msg = `Cannot play ${card.name} — requires LV ${cardLevel} (you are LV ${state.playerLevel}).`;
    addLog(msg); showToast(msg, true); return;
  }
  if (cardCost > totalRes) {
    const msg = `Not enough resources to play ${card.name} — cost ${cardCost}, you have ${totalRes}.`;
    addLog(msg); showToast(msg, true); return;
  }
  if (state.playerBase) {
    showBaseReplaceModal(card, state.playerBase, () => {
      const drawnUid = dispatch('REPLACE_BASE', { uid: card.uid });
      render();
      setupDropZones();
      glowHandCard(drawnUid);
    });
    return;
  }
  const drawnUid = dispatch('DEPLOY_BASE', { uid: card.uid });
  console.log(`[handleDrop] base — "${card.name}" is now the active base`);
  render();
  setupDropZones();
  if (typeof drawnUid !== 'undefined') glowHandCard(drawnUid);
}

function handleDrop(targetZone, card, sourceZone) {
  if (!targetZone || !card) return;
  if (state.modalHidden) return;

  console.log(`[handleDrop] resolving — "${card.name}" | source: "${sourceZone}" → target: "${targetZone}"`);

  // Base cards from hand can be dropped on the base zone OR anywhere on the
  // field (for convenience) — either way it triggers the deploy/replace flow
  // below instead of actually occupying a field slot.
  if (card.type === 'base' && sourceZone === 'hand' &&
      targetZone !== 'player-base' && targetZone !== 'player-field' && !targetZone.startsWith('player-slot-')) {
    addLog(`${card.name} is a Base card and can only be placed in the base zone.`);
    return;
  }

  // Extract numeric slot index from zone strings like 'player-slot-2'
  const srcIndex = parseInt((sourceZone || '').split('-').pop());
  const tgtMatch = targetZone.match(/player-slot-(\d+)/); // \d+ handles double-digit slots
  const tgtIndex = tgtMatch ? parseInt(tgtMatch[1]) : -1;
  let snapSlot = -1; // set to the placed slot index so triggerSnapAnimation runs after render
  let drawnUid; // set when a base deploy/replace draws a card, so glowHandCard runs after render

  if (targetZone === 'hand') {
    // ── Return a field card to the hand ──
    if (sourceZone?.startsWith('player-slot-')) {
      console.log(`[handleDrop] return to hand — "${card.name}" from slot ${srcIndex}`);
      dispatch('RETURN_TO_HAND', { slot: srcIndex });
    } else {
      console.warn(`[handleDrop] hand drop ignored — source "${sourceZone}" is not a field slot`);
    }

  } else if (targetZone === 'player-field' || targetZone.startsWith('player-slot-')) {
    if (sourceZone === 'hand') {
      // ── Base cards dropped anywhere on the field redirect to the same
      // deploy/replace flow as dropping on the base zone, as a convenience. ──
      if (card.type === 'base') {
        deployBaseFromHand(card);
        return;
      }
      // prePlayFromHand (if registered) resolves any "you may discard/
      // destroy X for an alternate cost" choice FIRST, before the level/cost
      // gate below — so the gate sees the discounted cost instead of the
      // card's full printed cost. Previously this only ran for Units auto-
      // placed at the empty slot, AFTER the gate had already rejected the
      // drop at full price — which made the discount unreachable for
      // Commands (no empty-slot path exists for them at all, e.g. ST10-014)
      // and could even block Units that could afford the discounted cost
      // but not the full one (e.g. GD01-002).
      const prePlay = window.CardEffects?.[card.cardNo]?.prePlayFromHand;
      const proceedWithHandDrop = () => {
        // ── Level / cost gate ──
        const cardLevel = effectiveHandLevel(card);
        const cardCost  = effectiveHandCost(card);
        const totalRes  = state.res + state.exRes;
        if (state.playerLevel < cardLevel) {
          const msg = `Cannot play ${card.name} — requires LV ${cardLevel} (you are LV ${state.playerLevel}).`;
          addLog(msg); showToast(msg, true);
          return;
        }
        if (cardCost > totalRes) {
          const msg = `Not enough resources to play ${card.name} — cost ${cardCost}, you have ${totalRes}.`;
          addLog(msg); showToast(msg, true);
          return;
        }
        // A pure Pilot card (no Command effect of its own) has no other use —
        // block it immediately if there's no unpaired Unit to receive it.
        // Command Pilot cards (and plain Commands with 【Pilot】) aren't
        // blocked here even with no unpaired Unit, since they can still be
        // activated for their Command effect instead of paired — see the
        // empty-slot branch below. Excludes cantBePaired tokens (e.g. T-014
        // Ad Balloon) so this doesn't claim "every Unit already has a
        // Pilot" when the real reason is that the only unpaired Unit can't
        // be paired at all.
        if (card.type === 'pilot' && !state.playerField.some(c => c && !c.pilot && !window.CardEffects?.[c.cardNo]?.cantBePaired?.(c))) {
          const msg = `${card.name} cannot be played — every Unit on your field already has a Pilot.`;
          addLog(msg); showToast(msg, true);
          return;
        }

        // ── Command cards (without 【Pilot】) never occupy a field slot —
        // confirm activation, then trash them, regardless of which slot
        // they were dropped on.
        if (card.type === 'command' && !card.canPilot) {
          if (!cardEffectCanActivate(card)) {
            const msg = `${card.name} cannot be activated — its effect's requirements aren't met.`;
            addLog(msg); showToast(msg, true);
            return;
          }
          showCommandActivateModal(card);
          return;
        }

        // ── Drop onto an occupied slot: ask for confirmation before pairing ──
        // Gated on card.canPilot — a Unit card can never pair, so an occupied
        // drop target has no pairing interpretation for it at all and must
        // fall through to the auto-place-at-empty-slot branch below instead
        // of being rejected outright. That rejection used to fire unconditionally,
        // which broke cards like GD01-002 Unicorn Gundam (Destroy Mode): its
        // prePlayFromHand alt-cost sacrifice (destroying a linked "Unicorn
        // Mode" Unit) already ran and paid its cost before this check, so if
        // the player happened to drop it onto a DIFFERENT occupied slot (not
        // the one just freed by the sacrifice), the card would get rejected
        // here and never placed — even though the cost was already spent.
        if (tgtIndex !== -1 && state.playerField[tgtIndex] !== null && card.canPilot) {
          const unit = state.playerField[tgtIndex];
          // cantBePaired: a permanent restriction printed on the TARGET Unit
          // itself (e.g. T-014 Ad Balloon, T-022 Wire-Guided Arm: "This Unit
          // can't be paired with a Pilot") — see registry.js for the hook
          // contract. Checked before showPairConfirmModal ever opens.
          if (window.CardEffects?.[unit.cardNo]?.cantBePaired?.(unit)) {
            const msg = `${unit.name} can't be paired with a Pilot.`;
            addLog(msg); showToast(msg, true);
            return;
          }
          console.log(`[handleDrop] occupied slot — showing pair confirmation for "${card.name}" → "${unit.name}"`);
          showPairConfirmModal(card, unit, tgtIndex);
          // Return early — state mutations happen inside the modal callback
          return;
        } else {
          // ── A Command Pilot card (or a plain Command with 【Pilot】) dropped
          // anywhere but onto a Unit means the player wants to activate its
          // Command effect instead of pairing it — prompt like a plain
          // Command card. A pure Pilot card has no Command effect, so it's
          // still just blocked with instructions to drop it onto a Unit.
          if (card.canPilot && card.type !== 'pilot') {
            // Action-only Command Pilots can't be activated outside the Action
            // Step — redirect the player to pair it onto a Unit instead.
            if (card.isActionOnly) {
              const msg = `${card.name} can only be activated during the Action Step — drop it onto a Unit to pair.`;
              addLog(msg); showToast(msg, true);
              return;
            }
            if (!cardEffectCanActivate(card)) {
              const msg = `${card.name} cannot be activated — its effect's requirements aren't met.`;
              addLog(msg); showToast(msg, true);
              return;
            }
            showCommandActivateModal(card);
            return;
          }
          if (card.canPilot) {
            const msg = `${card.name} is a Pilot card — drop it onto a Unit already on the field to pair.`;
            addLog(msg); showToast(msg, true);
            return;
          }
          // ── Play from hand: auto-place at next empty slot ──
          const slot = findNextFieldSlot();
          if (slot !== -1) {
            console.log(`[handleDrop] play from hand — "${card.name}" → slot ${slot}`);
            dispatch('PLAY_CARD', { uid: card.uid, slot });
            snapSlot = slot;
          } else {
            // Field is full — offer to trash one of the player's own field
            // cards to make room. Cancelable (skip = leave the card in hand,
            // nothing trashed) — unlike a mandatory effect deploy, the player
            // hasn't paid any cost yet by dragging a card onto the field.
            console.warn(`[handleDrop] field full — prompting trash-for-room for "${card.name}"`);
            promptTrashToMakeRoom(card.name, (freedSlot) => {
              if (freedSlot === -1) return; // player cancelled
              dispatch('PLAY_CARD', { uid: card.uid, slot: freedSlot });
              render();
              setupDropZones();
              triggerSnapAnimation(freedSlot);
            }, { mandatory: false });
          }
        }
      };
      if (prePlay) {
        prePlay(card, tgtIndex, proceedWithHandDrop);
      } else {
        proceedWithHandDrop();
      }
    } else if (sourceZone?.startsWith('player-slot-') && tgtIndex !== -1) {
      // ── Reposition: swap two field cards ──
      console.log(`[handleDrop] swap slots — slot ${srcIndex} ("${card.name}")`);
      dispatch('SWAP_SLOTS', { srcSlot: srcIndex, tgtSlot: tgtIndex });
    } else {
      console.warn(`[handleDrop] field drop ignored — unhandled source: "${sourceZone}"`);
    }

  } else if (targetZone === 'trash') {
    // ── Discard a card from hand or field ──
    console.log(`[handleDrop] trash — "${card.name}" discarded (source: ${sourceZone})`);
    if (sourceZone === 'hand') {
      dispatch('DISCARD_CARD', { uid: card.uid, source: 'hand' });
    } else if (sourceZone?.startsWith('player-slot-')) {
      dispatch('DISCARD_CARD', { uid: card.uid, source: 'field', slot: srcIndex });
    }

  } else if (targetZone === 'player-shield') {
    // ── Place a hand card face-down as a new shield ──
    if (sourceZone === 'hand') {
      console.log(`[handleDrop] shield — "${card.name}" added to player shield (now ${state.playerShield + 1})`);
      dispatch('PLACE_SHIELD', { uid: card.uid });
    } else {
      console.warn(`[handleDrop] shield drop ignored — source "${sourceZone}" is not hand`);
    }

  } else if (targetZone === 'player-base') {
    // ── Replace the player base image with the dropped card (BASE type only) ──
    if (card.type !== 'base') {
      addLog(`${card.name} cannot be placed here — only Base cards can be placed in the base zone.`);
      return;
    }
    if (sourceZone === 'hand') {
      deployBaseFromHand(card);
    }
    return;

  } else {
    console.warn(`[handleDrop] unrecognised target zone: "${targetZone}"`);
  }

  render();
  setupDropZones(); // re-attach listeners because render() rebuilt the DOM
  if (snapSlot !== -1) triggerSnapAnimation(snapSlot); // bounce the newly placed card
  if (typeof drawnUid !== 'undefined') glowHandCard(drawnUid);
}

// ════════════════════════════════════════════════════════════════════════════
// CARD CLICK — SELECTION
// Clicking any card clears all other selections and selects the clicked card.
// .selected adds the green glow via CSS; render() applies it to the DOM element.
// ════════════════════════════════════════════════════════════════════════════
function onCardClick(card) {
  if (state.modalHidden) return;
  // Attack declaration is via the attack button on each eligible field slot.
  // Activation is via the fmd_bad icon badge. Card preview is via hover.
}


// ════════════════════════════════════════════════════════════════════════════
// LOG TOGGLE
// Shows or hides the slide-out log panel above the bottom bar.
// The panel is always kept up-to-date by addLog(); toggling just controls visibility.
// ════════════════════════════════════════════════════════════════════════════
document.getElementById('btn-log').addEventListener('click', toggleLog);
document.getElementById('btn-report-bug').addEventListener('click', showReportBugModal);

document.getElementById('player-trash-btn').addEventListener('click', () => showTrashModal(state.playerTrash, false));
document.getElementById('opp-trash-btn').addEventListener('click',    () => showTrashModal(state.oppTrash, true));
document.getElementById('player-exile-btn').addEventListener('click', () => showExileModal(state.playerExile, false));
document.getElementById('opp-exile-btn').addEventListener('click',    () => showExileModal(state.oppExile, true));

function toggleLog() {
  const panel = document.getElementById('log-panel');
  panel.style.display = panel.style.display === 'flex' ? 'none' : 'flex';
}

// ════════════════════════════════════════════════════════════════════════════
// LOG CARD-NAME HIGHLIGHTING
// Lazily builds (once cardDb is populated — see loadCardDatabase()) a single
// alternation regex of every known card name, longest first so e.g. "Gundam"
// never shadows "Gundam Exia" at the same text position, plus a name->DB
// entry index for the hover preview below. Rebuilding this per addLog() call
// would mean reconstructing a 1000+ branch regex on every single log line.
// ════════════════════════════════════════════════════════════════════════════
let cardDbByName = {};
let logCardNameRegex = null;

function buildLogCardNameIndex() {
  cardDbByName = {};
  // Two passes so a real printed card always wins a name collision against a
  // synthetic token sharing its name (e.g. T-025 "Gundam Exia", categoryData
  // "unit-token", vs the real ST07-001 "Gundam Exia" unit) — tokens are
  // listed early in nuCardDatabase.json, so a single first-occurrence pass
  // would otherwise make most hand-played cards preview as a bare 0/0
  // token. A log line about an actually-deployed token still gets a
  // reasonable preview this way (the real card's art/stats), which beats
  // the alternative of a name with no preview at all.
  // Resource/EX Resource entries are skipped entirely (not just
  // deprioritized like tokens below) — their names are literally "Resource"
  // and "EX Resource", generic words that show up constantly in unrelated
  // log text ("spent 2 RES", etc.) and would get highlighted/hover-previewed
  // as if they were a specific played card.
  const isSkippable = entry => !entry || !entry.name || entry.categoryData === 'resource';
  for (const cardNo in cardDb) {
    const entry = cardDb[cardNo];
    if (isSkippable(entry) || /-token$/.test(entry.categoryData || '')) continue;
    if (!cardDbByName[entry.name]) cardDbByName[entry.name] = entry;
  }
  for (const cardNo in cardDb) {
    const entry = cardDb[cardNo];
    if (isSkippable(entry)) continue;
    if (!cardDbByName[entry.name]) cardDbByName[entry.name] = entry;
  }
  const names = Object.keys(cardDbByName);
  if (names.length === 0) { logCardNameRegex = null; return; }
  names.sort((a, b) => b.length - a.length);
  const pattern = names.map(n => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  logCardNameRegex = new RegExp(`(${pattern})`, 'g');
}

// Wraps every known card name found in `text` (plain, already-trusted log
// copy — never raw user input) in a hoverable span. escapeHtml() on the
// data-card attribute isn't for XSS (card names come from the local DB) but
// so a name containing a literal '"' can't break out of the attribute.
// cardRefs (optional, see captureLogCardRefs()) supplies the EXACT live card
// instance this specific log line meant, when known — several different
// printings of a card can share a name (different color, different stats:
// e.g. "Jegan" exists as both a blue and a green unit), and a bare name->DB
// lookup has no way to pick the right one. Falls back to the generic DB
// entry (cardDbByName) only when no live instance was captured.
function highlightCardNames(text, cardRefs) {
  if (!logCardNameRegex) return text;
  return text.replace(logCardNameRegex, match => {
    const live = cardRefs && cardRefs[match];
    const dbEntry = cardDbByName[match];
    const isPilot = live
      ? (live.canPilot || live.type === 'pilot' || live.type === 'command pilot')
      : !!(dbEntry && (dbEntry.categoryData === 'pilot' || dbEntry.categoryData === 'command pilot'));
    const cls = isPilot ? 'log-card-name log-pilot-name' : 'log-card-name';
    return `<span class="${cls}" data-card="${escapeHtml(match)}">${match}</span>`;
  });
}

// Finds the specific live card instance a highlighted log-line name refers
// to. Called once, synchronously, at the moment addLog() creates the entry
// (see captureLogCardRefs()) — NOT later at hover time, by which point the
// game state has often moved on and the same search could land on a
// completely different, unrelated card that happens to share the name.
// preferTrash flips the search order for "is destroyed"/"sent to trash"
// lines, where the correct instance has usually just left the field for a
// trash array — searching the field first there would find a DIFFERENT
// same-named card still in play instead (the exact bug this exists to fix).
function resolveLiveCardByName(name, preferTrash) {
  const fieldZones = [state.playerField, state.oppField];
  const trashZones = [state.playerTrash, state.oppTrash];
  const otherZones = [state.playerExile, state.oppExile, state.hand];
  const baseZones = [[state.playerBase], [state.oppBase]];
  const order = preferTrash
    ? [...trashZones, ...fieldZones, ...otherZones, ...baseZones]
    : [...fieldZones, ...trashZones, ...otherZones, ...baseZones];
  for (const zone of order) {
    for (const c of zone) {
      if (!c) continue;
      if (c.name === name) return c;
      if (c.pilot && c.pilot.name === name) return c.pilot;
    }
  }
  return null;
}

// Snapshots { name: liveCardInstance } for every known card name mentioned
// in `msg`, right now — see resolveLiveCardByName() above for why this has
// to happen at log time rather than later. Returns null (not an empty
// object) when there's nothing to capture, so addLog can skip storing a
// pointless empty field on every single entry.
function captureLogCardRefs(msg) {
  if (!logCardNameRegex) return null;
  const preferTrash = /trash|destroyed/i.test(msg);
  let refs = null;
  for (const m of msg.matchAll(logCardNameRegex)) {
    const name = m[0];
    if (refs && refs[name]) continue;
    const live = resolveLiveCardByName(name, preferTrash);
    if (live) { refs = refs || {}; refs[name] = live; }
  }
  return refs;
}

// Same positioning as showCardPreview(), except pinned beside #log-panel
// itself (its right edge, flipping to the left edge if it wouldn't fit)
// rather than beside whichever word was hovered — a card name can sit
// anywhere in a wrapped log line, and anchoring to the word directly would
// let the preview land on top of the panel instead of beside it.
function showCardPreviewFromLog(cardData, anchorEl) {
  showCardPreview(cardData, anchorEl);
  const preview = document.getElementById('card-preview');
  const panel = document.getElementById('log-panel');
  if (!preview || preview.style.display === 'none' || !panel) return;
  const panelRect = panel.getBoundingClientRect();
  const pw = preview.offsetWidth || 620;
  const vw = document.documentElement.clientWidth;
  let left = panelRect.right + 12;
  if (left + pw > vw - 8) left = panelRect.left - pw - 12;
  if (left < 8) left = 8;
  preview.style.left = left + 'px';
}

// Mechanical phase/step bookkeeping the player already sees reflected in the
// turn/phase UI chrome (phase bar, turn counter) — not worth a line in the
// match history. Matched centrally against the final message rather than
// touching every one of these call sites individually.
const LOG_SUPPRESS_PATTERNS = [
  /^Phase → /,               // own "Phase → Draw/Main/Resource/Start/End Phase"
  /^Opponent's Phase → /,    // mirrored opponent phase transitions
  /^Opponent skipped their Action Step\.$/,
  /^Start Phase — /,         // "RES restored to N.", "N EX Resource(s) now active..."
  /^Resource Phase — /,      // "gained 1 RES...", "RES already at max..."
  /^No actions left — automatically proceeding to End Phase\.$/,
  /^No block declared\.$/,
  /^Attack Step\.$/,
  /^End of Battle Step\.$/,
  / — lingering card cleared from opponent's field \(post-battle sync check\)\.$/,
];

// Card-effect "nothing happened" reports (no valid target / condition not
// met) — every card-effect handler across js/effects/*.js independently
// builds its own ad-hoc "${card.name} — <reason>." message for this, with
// no shared helper to hook centrally instead, so this is a best-effort
// pattern match over the overwhelmingly consistent phrasing they land on
// (checked against every such call site in the effects files — none of the
// ~400 are worded any other way) rather than an exact enumeration of each
// one individually. "resolve manually" notices are excluded — that's the
// one "— no ..." case that's actionable (tells the player THEY need to do
// something) rather than a no-op to skip.
const LOG_NO_OP_EFFECT_PATTERN = / — (no|No|already|Already|fewer than|Fewer than) /;
function isNoOpEffectLog(msg) {
  return LOG_NO_OP_EFFECT_PATTERN.test(msg) && !/resolve manually/i.test(msg);
}

// A card effect dealing simultaneous damage to several Units (e.g. "Deal 2
// damage to all enemy Units") destroys each one through its own doDestroy()
// call, staggered only by each one's identical 500ms vibrate-animation
// setTimeout — so the real-world gap between them is milliseconds, not the
// delay itself. 600ms comfortably covers that jitter while staying far
// short of the gap to any genuinely separate, later destroy.
const DESTROY_LOG_BATCH_WINDOW_MS = 600;

function formatDestroyedBatchMsg(names) {
  const list = names.length <= 2
    ? names.join(' and ')
    : `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
  return `${list} ${names.length > 1 ? 'were' : 'was'} destroyed and sent to trash.`;
}

// ════════════════════════════════════════════════════════════════════════════
// ADD LOG ENTRY
// Prepends { side, msg, turnMarker, noPill } to state.log and rebuilds the
// log panel's HTML — unless msg matches LOG_SUPPRESS_PATTERNS above, in
// which case it's dropped before ever being stored. side ('player'/'opp'/
// null) drives the colored name pill (actorDisplayName); resolved at render
// time (not baked into the stored string) so e.g. a mid-match username
// change is reflected on every entry, not just new ones. turnMarker (set
// only by the "Turn N begins"/game-start lines) gets no pill and a distinct
// amber background instead, styled in CSS — it's a timeline marker, not
// something either player "said". noPill alone (e.g. "X is going first.",
// "X's turn has ended.") suppresses just the pill, for a line that already
// names the player inline, without the amber marker treatment. destroyedCard
// (a destroy call site's own card object) merges into the immediately-
// preceding entry if IT was also a destroy within DESTROY_LOG_BATCH_WINDOW_MS
// — several cards destroyed by the same effect collapse into one "A, B and C
// were destroyed and sent to trash." line instead of a separate line each;
// the first destroy of a new batch keeps its own richer message (e.g. "X is
// destroyed by effect damage!") right up until a second one actually merges
// into it. Capped at 50 entries to avoid unbounded growth.
// ════════════════════════════════════════════════════════════════════════════
function addLog(msg, { turnMarker = false, noPill = false, destroyedCard = null } = {}) {
  if (destroyedCard) {
    const top = state.log[0];
    const now = Date.now();
    if (top && top.destroyedCards && top.side === currentLogActor && (now - top.loggedAt) < DESTROY_LOG_BATCH_WINDOW_MS) {
      top.destroyedCards.push(destroyedCard.name);
      top.cardRefs = top.cardRefs || {};
      top.cardRefs[destroyedCard.name] = destroyedCard;
      top.msg = formatDestroyedBatchMsg(top.destroyedCards);
      top.loggedAt = now;
      renderLogPanel();
      return;
    }
  }
  if (!turnMarker && (LOG_SUPPRESS_PATTERNS.some(re => re.test(msg)) || isNoOpEffectLog(msg))) return;
  const cardRefs = captureLogCardRefs(msg);
  const entry = { side: currentLogActor, msg, turnMarker, noPill, cardRefs, loggedAt: Date.now() }; // newest first, capped — on-screen panel only
  if (destroyedCard) {
    entry.destroyedCards = [destroyedCard.name];
    entry.cardRefs = entry.cardRefs || {};
    entry.cardRefs[destroyedCard.name] = destroyedCard;
  }
  state.log.unshift(entry);
  if (state.log.length > 50) state.log.pop();
  renderLogPanel();
}

function renderLogPanel() {
  const panel = document.getElementById('log-panel');
  if (!panel) return;
  const entriesHtml = state.log.map(entry => {
    const name = (!entry.turnMarker && !entry.noPill) ? actorDisplayName(entry.side) : '';
    const pill = name ? `<span class="log-pill log-pill-${entry.side}">${escapeHtml(name)}</span>` : '';
    const cls = ['log-entry', entry.turnMarker ? 'log-entry-turn' : ''].filter(Boolean).join(' ');
    return `<div class="${cls}">${pill}<span class="log-entry-text">${highlightCardNames(entry.msg, entry.cardRefs)}</span></div>`;
  }).join('');
  panel.innerHTML = `
    <div class="log-panel-header">
      <span class="log-panel-title">Match Log</span>
      <button type="button" class="log-panel-close" id="log-panel-close-btn" aria-label="Close log">
        <span class="material-symbols-outlined">close</span>
      </button>
    </div>
    <div class="log-panel-body">${entriesHtml}</div>
  `;
  panel.querySelector('#log-panel-close-btn').addEventListener('click', toggleLog);
  // Index-aligned with state.log: entriesHtml above was built via the exact
  // same unfiltered .map() over state.log, so the i-th ".log-entry" here is
  // always state.log[i] — see highlightCardNames's cardRefs param for why
  // each entry needs its OWN captured refs rather than a single flat lookup.
  panel.querySelectorAll('.log-entry').forEach((entryEl, i) => {
    const entry = state.log[i];
    entryEl.querySelectorAll('.log-card-name').forEach(el => {
      el.addEventListener('mouseenter', () => {
        const name = el.dataset.card;
        const live = entry.cardRefs && entry.cardRefs[name];
        const dbEntry = !live && cardDbByName[name];
        const cardData = live || (dbEntry && buildGameCardFromDb(dbEntry, 'log'));
        if (cardData) showCardPreviewFromLog(cardData, el);
      });
      el.addEventListener('mouseleave', hideCardPreview);
    });
  });
}


// ════════════════════════════════════════════════════════════════════════════
// DECK CLOUD SYNC
// Fires once per sign-in (including page load with an already-active
// session — see the registration in STARTUP below). Makes Supabase's
// public.decks table (see supabase/schema.sql, and js/supabase.js's
// listDecks/saveDeck/deleteDeck) the real durable copy for a signed-in
// account, while leaving localStorage as the synchronous cache every
// existing reader already uses unchanged (showDeckSelectScreen, the Deck
// Builder's own Load list, Best of 3 sideboarding, getAllSavedDecksForExport,
// ...) — none of those needed to change at all, since this just keeps their
// localStorage source of data in sync underneath them.
//
// Pulls every cloud deck down into localStorage (cloud wins on a name
// collision), and pushes up any LOCAL deck that predates this feature —
// saved while already logged in, on this browser, before cloud sync
// existed — and isn't on the cloud yet, so it stops being silently
// local-only (the actual bug report this was built for: decks genuinely
// weren't being saved anywhere durable). GUEST_DECK_NAME ('My Deck',
// showDeckBuilder()) is deliberately excluded from that push — that slot is
// guest-only scratch space by construction (the ONLY name a non-logged-in
// Save ever writes to), never something to promote into a real account's
// cloud decks just because it happens to be sitting in localStorage at
// login time.
async function syncDecksFromCloud() {
  if (!window.Supa || !window.Supa.getSession()) return;
  const { ok, decks } = await window.Supa.listDecks();
  if (!ok) return; // table missing/misconfigured, or a transient network error — leave localStorage as the only copy for this session, same as before this feature existed

  const LS_PREFIX = 'gdeck:';
  const SIDEBOARD_LS_PREFIX = 'gdeck-sb:';
  const cloudNames = new Set(decks.map(d => d.name));

  decks.forEach(({ name, main, sideboard }) => {
    localStorage.setItem(LS_PREFIX + name, JSON.stringify(main));
    localStorage.setItem(SIDEBOARD_LS_PREFIX + name, JSON.stringify(sideboard));
  });

  const localNames = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(LS_PREFIX)) localNames.push(key.slice(LS_PREFIX.length));
  }
  for (const name of localNames) {
    if (name === 'My Deck' || cloudNames.has(name)) continue;
    let main = [], sideboard = [];
    try { main = JSON.parse(localStorage.getItem(LS_PREFIX + name) || '[]'); } catch {}
    if (!Array.isArray(main) || main.length === 0) continue;
    try { sideboard = JSON.parse(localStorage.getItem(SIDEBOARD_LS_PREFIX + name) || '[]'); } catch {}
    if (!Array.isArray(sideboard)) sideboard = [];
    await window.Supa.saveDeck(name, main, sideboard);
  }
}

// Same shape as syncDecksFromCloud() above, but for the single profiles row
// (username + avatar) instead of the many-named decks table: cloud wins on
// a collision (pulled down into localStorage so this browser's main menu
// icon/welcome text picks it up immediately — see the manual
// renderMainMenuTopLeft() call below, needed because this resolves AFTER
// the synchronous onSessionChange render that triggered it, same race the
// Edit Profile Save handler avoids by patching #mm-profile-btn directly).
// If the cloud row is still empty (first sign-in ever, or signing in fresh
// on a brand-new account) but this browser already has a local
// username/avatar set — e.g. picked while signed out, or from a pre-cloud-
// sync version of this feature — push it up instead of letting it be
// silently stranded local-only, same convention as syncDecksFromCloud()'s
// local -> cloud backfill.
async function syncProfileFromCloud() {
  if (!window.Supa || !window.Supa.getSession()) return;
  const { ok, profile } = await window.Supa.loadCloudProfile();
  if (!ok) return; // table/column missing or a transient error — stay local-only, same degrade as deck sync

  const local = loadProfileData();
  const cloudUsername = (profile && profile.username) || '';
  const cloudAvatar = (profile && profile.avatar) || '';

  if (cloudUsername || cloudAvatar) {
    saveProfileData({
      username: cloudUsername || local.username,
      betaCode: local.betaCode,
      avatar: PROFILE_AVATAR_KEYS.includes(cloudAvatar) ? cloudAvatar : local.avatar,
    });
  } else if (local.username || local.avatar) {
    await window.Supa.saveCloudProfile({ username: local.username, avatar: local.avatar });
  }

  const menuOverlay = document.getElementById('main-menu-overlay');
  if (menuOverlay) renderMainMenuTopLeft(menuOverlay);
}

// ════════════════════════════════════════════════════════════════════════════
// STARTUP
// Show the main menu immediately so the game board is never exposed on load.
// The card DB loads in the background; Play Game waits for it before initGame.
// ════════════════════════════════════════════════════════════════════════════
const dbReady = loadCardDatabase().then(() => { enrichCardsFromDb(); buildLogCardNameIndex(); });
showMainMenu(dbReady);

// Consume a one-shot "Challenge Another Bot"/"Deck Builder" request left by
// reloadToPostMatchAction() (showVictoryModal) before this reload — once the
// card DB is ready, transition straight from the main menu into the
// requested screen instead of leaving the player at the bare menu.
{
  const postMatchAction = sessionStorage.getItem(POST_MATCH_ACTION_SS_KEY);
  if (postMatchAction) {
    sessionStorage.removeItem(POST_MATCH_ACTION_SS_KEY);
    dbReady.then(() => {
      if (postMatchAction === 'bot-select') showBotDeckSelectScreen();
      else if (postMatchAction === 'deck-builder') showDeckBuilder();
      else return;
      // Same fade-and-remove as the main menu's own [data-action="bot"/"deck"]
      // click handlers — without this the menu overlay is left behind
      // underneath the new screen instead of actually being dismissed.
      const menuOverlay = document.getElementById('main-menu-overlay');
      if (menuOverlay) {
        menuOverlay.classList.add('mm-fade-out');
        setTimeout(() => menuOverlay.remove(), 400);
      }
    });
  }
}

// Same "call once for whatever already exists, then subscribe for later
// changes" pattern showMainMenu()'s own renderMainMenuTopLeft() uses —
// client.auth.onAuthStateChange's first real firing (confirming a persisted
// session, or none) happens asynchronously after this script finishes
// running, so the immediate call here is a no-op in the common case and
// onSessionChange is what actually catches it; both are kept for safety.
if (window.Supa) {
  if (window.Supa.getSession()) { syncDecksFromCloud(); syncProfileFromCloud(); }
  window.Supa.onSessionChange((session) => { if (session) { syncDecksFromCloud(); syncProfileFromCloud(); } });
}
